import isEqual from "lodash/isEqual";
import omit from "lodash/omit";

import { THREE, CSS3DObject, CSS3DRenderer, OrbitControls, SVGLoader, FontLoader, colorToThree, TransformControls } from "@scrolly/renderer";
import crawl from "../utils/crawl";
import { reparentIfMoved } from "../utils/reparent";
import { disposeMaterials } from "../utils/disposeMaterials";
import { bildPlaneSize, videoPlaneSize } from "../utils/videoPlaneSize";
import { shouldVideoBeAudible } from "../utils/videoAudible";
import { handyAdresse } from "../utils/renditionAdresse";
import { scrubVideoTo, resetScrub, type ScrubbaresVideo } from "../utils/videoScrub";
import { posterUrlFuerVideo } from "../utils/videoPoster";
import { buildSafeAreaStops, safeAreaProgress } from "../utils/safeAreaAlignment";
import { colorToHex, Easings, degreeToRad, radToDegree, interpolateLinearDirectly } from "@scrolly/renderer";

import RendererStore from "./RendererStore";
import { WebGLAssets } from "./webglAssets";
import { AudioLayers } from "./AudioLayers";
import type { AudioLayerContent } from "./AudioLayers";
import { injectReaderStandardFonts, injectReaderTeamFonts } from "./readerStandardFonts";
import { applyDataAnim } from "./dataAnim";

import type {
    AssetContent,
    ImageContent,
    InfographicContent,
    LightContent,
    LottieContent,
    MapContent,
    MeshOptions,
    MeshOptionsBump,
    MeshStandardOptions,
    Object3DContent,
    PointLightContent,
    SpotLightContent,
    SvgContent,
    TextContent,
    VideoContent,
} from "@scrolly/post-types";
import { kartenBild } from "./MapInterpolation";
import { KarteImRaum, istKarteImRaum, kartenStilAufloesen } from "./KarteImRaum";
import { hasMeshBumpOptions, hasMeshOptions, MaterialType } from "@scrolly/renderer";

import { LightType } from "./LightType";
import { resolveSzenenGrundfarbe } from "@scrolly/post-types";
import { hat3dEbenen, hatGedrehteEbenen } from "../utils/hat3dEbenen";
import { texturFlussVersatz } from "../utils/texturFluss";
import { LayerType } from "./LayerType";
import Video from "./Video";
import LottiePlayer from "./Lottie";
import { DEFAULT_FONT, FONTS } from "./assets/fonts";
import assets from "./assets";
import type { PresentationRender } from "./PresentationRender";
import type { LayerRender } from "./LayerRender";
import type SlidePlayer from "./SlidePlayer";
import { logDebug, logError, logWarning } from "../utils/logger";
import type { CustomFonts, EffectContent, ImageAssetBackend, PostJson } from "@scrolly/post-types";
import { AnimationType, istStudioScrolly, resolveVideoPlaybackMode } from "@scrolly/post-types";
import { EFFECT_COUNT_MAX, syncEffect, updateEffect } from "../effects/particles";
import {
    syncImageBlur,
    syncImageMask,
    syncImageShadow,
    syncImageRealShadows,
} from "../effects/image-mask-shadow-blur";
import { sceneLichtGewichte } from "./sceneLichtGewicht";
import { makeText3dGeometry, makeText3dGeometryFast, number3dString, font3dUrl } from "../effects/text3d";
import { buildChessGroup, animateChess } from "../effects/chess";
import { syncEnvironment, type EnvironmentHost } from "../effects/environment-hdri";
import { updateRealtimeReflections, type ReflectionHost } from "../effects/realtime-reflections";
import {
    buildPrimitiveGeometry,
    buildProgressRingGeometry,
    progressRingDrawCount,
    buildStarGeometry,
    makeMagnifierMaterials,
    makePinMaterials,
    BAKED_PRIMITIVES,
    WIRED_PRIMITIVES,
    PRIMITIVE_DEFAULTS,
} from "../effects/primitive-geometry";
import { updateMirrors, leichtGemerkt, type MirrorHost, type MirrorParams, type MirrorRig } from "../effects/planar-mirrors";
import { Text, preloadFont } from "troika-three-text";
import { AssetLibraryCategory, AssetLibraryType } from "./assets/AssetLibrary";

import {
    fontItalianno,
    fontJetBrains,
    fontMerriweather,
    fontMerriweatherSans,
    fontNotoSans,
    fontNotoSerif,
    fontPortLligatSlab,
    fontRoboto,
    fontSourceSans3,
    fontSourceSerif4,
    schriftDatei,
    teamSchnittDatei,
} from "../assets/fonts";
import type { ImageOptimizer } from "../utils/imageLoader";
// Port 1 (Choropleth): reine SVG-Generierung aus Jans Pro-Editor.
import { buildInfographicSvg, infographicPixelSize, isMapChart, quelleDeckkraft, textAufbauDeckkraft, titelDeckkraft } from "../infographic/infographicSvg";
import { updateInfographicMesh, infographicIsHtml, meldeInfografikBauFehler, infografikBauErlaubt, infografikSchattenCss } from "../infographic/infographicMesh";
import { ladeWortwolkenSchriften, wortwolkenSchriftenBereit } from "../infographic/wortwolkenSchriften";
import { istLeererMedienSlot } from "../utils/leererMedienSlot";
import { istMobilesGeraet, deckleTexturFuerMobil } from "../utils/geraet";
import { wirksameDpr } from "../utils/kartenSkala";
import { bildBreitenStufeMitBudget } from "../utils/bildbreite";
import { grundformAufbau } from "../utils/grundformAufbau";
import { grundformEcken } from "../utils/grundformEcken";
import { filtereSvg } from "../utils/htmlFilter";
import { VideoVorlader, holeVideoAlsBlob, langsameVerbindung, type VorladerEintrag } from "./videoVorlader";
import { videoAuswahlJetzt } from "./eigenbewegungLaufzeit";

/** Zeitlimit fuers Blob-Vorladen des Startvideos — unter dem 10-s-Boot-
 *  Watchdog (RendererStore.BOOT_LAYER_TIMEOUT_MS); danach altes Streaming. */
const START_VIDEO_ZEITLIMIT_MS = 8_000;
/** Rücksprung des Editor-Abspielkopfs, der als Stop/Zurück gilt (wie AudioRenderer). */
const EDITOR_RUECKSPRUNG_MS = 250;
/** Laufendes Video, Zaehler steht laenger als das → totalVideoFrames gilt als ungepflegt. */
const BILDZAEHLER_STILLSTAND_MS = 300;
/** Mobil: Luft zwischen START-Klick und Vorlader-Downloads (Startvideo ruckelte). */
const VORLADER_MOBIL_SCHONFRIST_MS = 4_000;

const MIN_VALUE = 1 / Number.MAX_VALUE;
const MIN_SCALE: Vector3Tuple = [MIN_VALUE, MIN_VALUE, MIN_VALUE];
const CLEAR_COLOR = 0x000000;

/**
 * Cutover Schritt 2: schlankes, editor-agnostisches Interface fuer die Editor-
 * Chrome (Grid/Safe-Area/Gizmo/Auswahl/Timer/Video-Cache). OPTIONAL — der Reader
 * liefert es NICHT, dann ist die gesamte Editor-Chrome ein No-op (additiv-sicher).
 * Bewusst NICHT Jans voller EditorStore: nur die Felder, die der Renderer liest.
 * So kann der ProEditor kuenftig `@scrolly/renderer` treiben, ohne dass der
 * geteilte Renderer von der Editor-Codebasis abhaengt.
 */
/**
 * Dickere Anfasser-Linien (Jans Wunsch 09.09.2026: "alle einen Pixel dicker,
 * auch bei Position — sehr duenn zum Anfassen"). three zeichnet Achsen und
 * Ringe als Roehren mit Radius 0,0075 Gizmo-Einheiten und nur 3 Seiten
 * (~1,3 px). Hier: Radius 0,0125 und 8 Seiten (~3 px, rund). Die Picker
 * (unsichtbare Greifflaechen) bleiben unveraendert — sie waren schon dick.
 * Greift auf die private Gizmo-Hierarchie zu; bei einer three-Version ohne
 * `_gizmo`/`parameters` passiert einfach nichts.
 */
export const GIZMO_LINIEN_RADIUS = 0.0125;

/**
 * three's setupGizmo backt Position/Rotation jedes Teils IN die Geometrie
 * (geometry.clone().applyMatrix4(object.matrix)) und setzt die Objekt-
 * Transform auf 0. Ein nackter Geometrie-Tausch verlor diese Transform
 * (X-Linie lag auf der Y-Achse). Hier die eingebackene Matrix aus vier
 * nicht-koplanaren Punktpaaren (Original ohne Transform vs. gebacken)
 * zurueckrechnen: M = B * A^-1. Gleicher Konstruktor => gleiche Punktfolge.
 */
function eingebackeneMatrix(original: THREE.BufferGeometry, gebacken: THREE.BufferGeometry): THREE.Matrix4 | null {
    const a = original.getAttribute("position");
    const b = gebacken.getAttribute("position");
    if (!a || !b || a.count !== b.count || a.count < 4) return null;
    const idx: number[] = [];
    const A = new THREE.Matrix4();
    const setze = (m: THREE.Matrix4, attr: THREE.BufferAttribute | THREE.InterleavedBufferAttribute, ids: number[]) => {
        const e = m.elements;
        for (let c = 0; c < 4; c++) {
            const i = ids[c];
            e[c * 4 + 0] = attr.getX(i);
            e[c * 4 + 1] = attr.getY(i);
            e[c * 4 + 2] = attr.getZ(i);
            e[c * 4 + 3] = 1;
        }
    };
    // vier Punkte suchen, deren homogene Matrix invertierbar ist (die
    // ersten drei liegen bei Zylinder und Torus auf einem Kreis, der vierte
    // muss aus dieser Ebene heraus — wird gesucht)
    for (let i = 0; i < a.count && idx.length < 4; i++) {
        idx.push(i);
        if (idx.length === 4) {
            setze(A, a, idx);
            if (Math.abs(A.determinant()) < 1e-9) idx.pop();
        }
    }
    if (idx.length < 4) return null;
    setze(A, a, idx);
    const B = new THREE.Matrix4();
    setze(B, b, idx);
    return B.multiply(A.clone().invert());
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function dickereGizmoLinien(tc: any): void {
    const gizmo = tc?._gizmo?.gizmo;
    if (!gizmo) return;
    const ORIGINAL = 0.0075;
    const R = GIZMO_LINIEN_RADIUS;
    for (const modus of ["translate", "rotate", "scale"]) {
        const wurzel = gizmo[modus] as THREE.Object3D | undefined;
        if (!wurzel) continue;
        wurzel.traverse((o) => {
            const m = o as THREE.Mesh;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const g = m.geometry as any;
            const p = g?.parameters;
            if (!p) return;
            let original: THREE.BufferGeometry | null = null;
            let dick: THREE.BufferGeometry | null = null;
            if (g.type === "CylinderGeometry" && p.radiusTop === ORIGINAL && p.height === 0.5) {
                // three: lineGeometry2 = Cylinder(0.0075, 0.0075, 0.5, 3).translate(0, 0.25, 0)
                original = new THREE.CylinderGeometry(ORIGINAL, ORIGINAL, 0.5, 3).translate(0, 0.25, 0);
                dick = new THREE.CylinderGeometry(R, R, 0.5, 8).translate(0, 0.25, 0);
            } else if (g.type === "TorusGeometry" && p.tube === ORIGINAL) {
                // three: CircleGeometry(radius, arc) = Torus(radius, 0.0075, 3, 64, arc).rotateY(PI/2).rotateX(PI/2)
                original = new THREE.TorusGeometry(p.radius, ORIGINAL, 3, 64, p.arc).rotateY(Math.PI / 2).rotateX(Math.PI / 2);
                dick = new THREE.TorusGeometry(p.radius, R, 8, 64, p.arc).rotateY(Math.PI / 2).rotateX(Math.PI / 2);
            }
            if (!original || !dick) return;
            const M = eingebackeneMatrix(original, g as THREE.BufferGeometry);
            original.dispose();
            if (!M) {
                dick.dispose();
                return;
            }
            dick.applyMatrix4(M);
            m.geometry = dick;
        });
    }
}

export interface EditorStoreHooks {
    /** Safe-Area-Overlay einblenden. */
    showSafeArea?: boolean;
    /** Helfer-Grid einblenden. */
    showGrid?: boolean;
    /** Transform-Gizmo (Handles) einblenden (Schritt 2b). */
    showHandler?: boolean;
    /** Aktuell ausgewaehlter Layer, fuer Gizmo-Attach (Schritt 2b). */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    layer?: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    currentLayer?: any;
    /** Editor-Timer-Quelle (playablePresentation.slidePlayer.timer) (Schritt 2c). */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    playablePresentation?: any;
    /** Geteilter Video-Cache (Schritt 2c). */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    videoCache?: any;
    /**
     * Schritt 2b: Write-back-Callback fuer den Transform-Gizmo. Der geteilte
     * Renderer berechnet die neue MST-Position + ob der Drag beendet ist und
     * ruft dies auf; DER EDITOR schreibt in seine MST (changeValue/setActive).
     * So bleibt der Renderer editor-agnostisch (invertierte Abhaengigkeit).
     */
    onLayerTransform?: (mstPosition: [number, number, number], timer: number, dragEnded: boolean) => void;
    /**
     * Anfasser-Modus (Jans Wunsch 09.09.2026, "Handler nur Position"):
     * "translate" = Verschieben (Standard), "rotate" = Drehen. Der Editor
     * schaltet ihn (Tasten W/E bzw. Toolbar); der Renderer stellt nur die
     * TransformControls um und liefert beim Drehen die Grad-Werte zurueck.
     */
    gizmoMode?: GizmoMode;
    /**
     * Write-back beim Drehen: Euler-Winkel in GRAD (X/Y/Z), wie sie die
     * MST im rotation-Kanal fuehrt (degreeToRad beim Rendern). Gleiche
     * Semantik wie onLayerTransform (Live waehrend des Drags, dragEnded=true
     * am Ende).
     */
    onLayerRotate?: (mstRotationDeg: [number, number, number], timer: number, dragEnded: boolean) => void;
    /** Editor-Timer, falls der Editor ihn liefert (sonst nimmt der Gizmo 0). */
    timer?: number;
    /** Zielpunkt-Marker der Lichter zeigen (Haekchen „Show" neben TARGET). */
    showLightTarget?: boolean;
}

/** Modi des Transform-Gizmos, die der Editor anbietet. */
export type GizmoMode = "translate" | "rotate";

export interface RendererProps {
    start: (animate: (timer: number) => void) => void;
    stop: () => void;
    renderer: RendererStore;
    slidePlayer: SlidePlayer;
    isProduction?: boolean;
    /** Cutover Schritt 2: optionale Editor-Chrome-Hooks. Reader: undefined. */
    editorStore?: EditorStoreHooks;
    presentation: PresentationRender;
    /** When true, Sentence-Bloom HTML SVG stays hidden (cover / beforeStory zone). */
    isCoverActive?: () => boolean;
    /**
     * INSTANZ-editorMode (statt der globalen WebGLAssets-Flagge): Im
     * Pro-Editor laufen Editor-Canvas UND Vorschau-Dialog gleichzeitig —
     * die globale Flagge hielt auch den Dialog für einen Editor und
     * schaltete dort z. B. die Reader-Audio-Wiedergabe stumm (Jans Fund
     * 09.08.2026). Gesetzt aus ScrollyViewer.proEditor; undefined =
     * globale Flagge (Alt-Verhalten).
     */
    editorMode?: boolean;
    /**
     * Obergrenze für die angeforderte Bild-Texturbreite (imgproxy rs:fit).
     * Die Breitenwahl unten richtet sich sonst NUR nach der Originalgröße des
     * Assets — eine 420-px-Hover-Kachel der Template-Galerie dekodiert damit
     * dieselben 1024–2048er-Texturen wie der Voll-Reader, mitten im Auto-Play
     * (Jans Ruckel-Fund 08.08.2026, verschärft seit die Mediathek echte
     * Originale referenziert). Kachel-Vorschauen setzen hier z. B. 640.
     * undefined = unbegrenzt (Reader/Editoren unverändert).
     */
    maxTextureWidth?: number;
}

/**
 * Strich in Abschnitte nach einem SVG-Strichmuster (stroke-dasharray) zerlegen —
 * SVGLoader.pointsToStroke kennt keine Muster (Jan 22.10.2026, gestrichelte
 * Linie in 3D). Ohne Muster: der ganze Strich.
 */
function strichAbschnitte(punkte: THREE.Vector2[], eingabe: number[]): THREE.Vector2[][] {
    const muster = eingabe.length % 2 ? [...eingabe, ...eingabe] : eingabe;
    if (punkte.length < 2 || !muster.length || muster.every((m) => m <= 0)) return [punkte];
    const teile: THREE.Vector2[][] = [];
    let index = 0;
    let rest = muster[0];
    let sichtbar = true;
    let aktuell: THREE.Vector2[] | null = [punkte[0].clone()];
    for (let i = 1; i < punkte.length; i++) {
        const a = punkte[i - 1];
        const b = punkte[i];
        const laenge = a.distanceTo(b);
        let pos = 0;
        while (laenge - pos > rest) {
            pos += rest;
            const punkt = a.clone().lerp(b, pos / laenge);
            if (sichtbar) {
                aktuell!.push(punkt);
                teile.push(aktuell!);
                aktuell = null;
            } else {
                aktuell = [punkt];
            }
            sichtbar = !sichtbar;
            index = (index + 1) % muster.length;
            rest = muster[index];
        }
        rest -= laenge - pos;
        if (sichtbar && aktuell) aktuell.push(b.clone());
    }
    if (sichtbar && aktuell && aktuell.length > 1) teile.push(aktuell);
    return teile;
}

/**
 * AUFBAU von Linie und Pfeil in 3D (Grundformen ohne HTML, Jan 22.10.2026:
 * „beim Ausschalten von Als HTML rendern geht der Fortschritt nicht mehr“).
 * Gegenstück zu utils/grundformAufbau (HTML): die Spitze steht bei q·weg
 * (Linie: q·Linienende); Abschnitte davor ganz, der angeschnittene gestaucht,
 * dahinter unsichtbar; die Pfeilspitze wandert mit.
 */
function grundformAufbau3d(anker: THREE.Object3D, p: number) {
    const q = Math.max(0, Math.min(1, Number.isFinite(p) ? p : 1));
    if (anker.userData.aufbauStand === q) return;
    anker.userData.aufbauStand = q;
    // Vom LINIENANFANG aus (Nullpunkt am Anfang, Jan 23.10.2026: die Linie
    // liegt dann in der rechten Bildhälfte, nicht mehr ab x = 0)
    let anfang = Infinity;
    let ende = 0;
    anker.traverse((o) => {
        const a = o.userData.aufbau as { start: number; ende: number } | undefined;
        if (a) {
            anfang = Math.min(anfang, a.start);
            ende = Math.max(ende, a.ende);
        }
    });
    if (!Number.isFinite(anfang)) anfang = 0;
    const spitze = anfang + q * (ende - anfang);
    anker.traverse((o) => {
        const a = o.userData.aufbau as { start: number; ende: number } | undefined;
        if (a) {
            if (a.ende <= spitze) {
                o.visible = true;
                o.scale.x = 1;
                o.position.x = 0;
            } else if (a.start >= spitze || q === 0) {
                o.visible = false;
            } else {
                const s = Math.max(0.0001, (spitze - a.start) / (a.ende - a.start));
                o.visible = true;
                o.scale.x = s;
                o.position.x = a.start * (1 - s);
            }
        }
        const k = o.userData.aufbauKopf as { basisX: number; weg: number } | undefined;
        if (k) {
            o.visible = q > 0;
            o.position.x = k.basisX - (1 - q) * k.weg;
        }
    });
}

function getLayerContentUrl(assetContent: AssetContent, buehneKlein = false) {
    const url = assetContent.ppiUrl ? assetContent.ppiUrl : assetContent.url;
    return mobilRenditionsAdresse(url, buehneKlein);
}

/**
 * Mobilfassung reicht — kleine Buehne (Jans Slow-4G-Test 06.09.2026): Das
 * 9:16-Scrolly steht auf dem Laptop in einem ~300 px schmalen Handyrahmen,
 * bekam aber die Desktop-Fassung (16 MB Original), weil nur das Geraet
 * zaehlte. Die 720p-Mobilfassung (kurze Seite 720 px) deckt jede Buehne bis
 * 720 Geraetepixel Breite ohne sichtbaren Verlust. DPR wie beim Textur-
 * Deckel auf 2 gekappt.
 */
const MOBILFASSUNG_BIS_GERAETEPIXEL = 720;
function buehneKleinFuerMobilfassung(cssBreite: number, dpr: number): boolean {
    if (!(cssBreite > 0)) return false;
    return cssBreite * Math.min(dpr || 1, 2) <= MOBILFASSUNG_BIS_GERAETEPIXEL;
}

/** Datensparmodus des Nutzers (Network Information API). */
const datensparmodusAktiv = () =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    typeof navigator !== "undefined" && !!(navigator as any).connection?.saveData;

/**
 * Phase 2 Video-Renditions (Benedikts Go 03.09.2026): Der Reader bekommt vom
 * Backend `…/resource/<id>?r=web&v=<webId>&vm=<mobileId>`, wenn ZUSAETZLICH
 * eine 720p-Mobilfassung bereitliegt. Auf Touch-Geraeten (istMobilesGeraet,
 * dieselbe Heuristik wie der Textur-Deckel) wird daraus `r=mobile` — die
 * Adresse bleibt ueber `vm` cache-eindeutig. Ohne `vm` (keine Mobilfassung)
 * bleibt es bei web: keine Anfrage, die das Backend nur zurueckfallen liesse.
 * Bewusst HIER, damit jeder Video-Pfad (Boot, Live-Update, Vorlader,
 * Startvideo-Blob) dieselbe Adresse sieht.
 */
function mobilRenditionsAdresse<T>(url: T, buehneKlein = false): T {
    if (typeof url !== "string") return url;
    // Mobilfassung bei: Touch-Geraet (wie bisher), kleiner Buehne (9:16 im
    // Desktop-Handyrahmen), langsamer Verbindung (2G/3G laut Browser) oder
    // Datensparmodus — ueberall dort ist die Desktop-Fassung nur Wartezeit.
    const handy =
        istMobilesGeraet() || buehneKlein || langsameVerbindung() || datensparmodusAktiv();
    // Die Regel selbst steht in utils/renditionAdresse (ohne Browser pruefbar):
    // Templates tragen `r=web&…&vm=`, Pro-Editor-Scrollys nur `vm=` — dort
    // bleibt der Schreibtisch beim Original des Autors.
    return handyAdresse(url, handy) as unknown as T;
}

// Port "Licht-Schatten-Qualitaet" (Jans Original, 1:1 — Jans Fund "Textur
// unsauber" am extrudierten SVG = Shadow Acne, weil der geteilte Renderer
// weder normalBias noch passende Schattenkamera-Bereiche setzte):
// Largest valid spotlight cone half-angle. three.js only supports up to PI/2;
// the small epsilon keeps the shadow camera's fov (= 2*angle) just under 180°
// so its projection matrix stays well-conditioned (sharp/broken shadows above).
const MAX_SPOT_ANGLE = Math.PI / 2 - 0.01;

// Licht-Paritaet zu Jans 1.0 (three r139, Legacy-Licht + Linear-Output): der
// geteilte Renderer laeuft auf three 0.183 (physikalische Lichter + sRGB-
// Output + ColorManagement) — gleiche Intensitaetswerte rendern dort MASSIV
// heller. Empirisch kalibriert per A/B gegen Jans laufendes 1.0 (Kugel +
// Standard-Sonne 0.5: Port braucht 0.02 fuer denselben Look; Jans UI-Befund
// "20 statt 500"). Gilt fuer RICHTUNGSLICHT; Point/Spot/Ambient werden separat
// kalibriert (eigene Physik: Candela + decay-Falloff).
const LEGACY_DIRECTIONAL_INTENSITY_SCALE = 1 / 25;
// Spotlight shadow-map resolution scaled to the cone angle: a wider cone has a
// larger shadow-camera fov, so the map is spread over more area and the soft
// edges fray — give wide cones more texels, narrow ones fewer (cheaper). Memory
// roughly doubles per step (8192 ≈ 256 MB), so only the widest cones pay it.
// NB: die schmale Stufe bleibt bei 2048 (kurz 4096 gegen PCF-Banding): 4096 =
// 4x VRAM pro Spot, Multi-Licht-Szenen sprengten damit das GPU-Budget (OOM).
const spotShadowMapSize = (angle: number) => (angle > 1.2 ? 8192 : angle > 0.6 ? 4096 : 2048);

// Map the SpotLight's penumbra (0..1, "Halbschatten") to the cast shadow's
// blur radius. three's penumbra only softens the LIT-cone edge, invisible on
// unlit 2D images — shadow.radius blurs the shadow-map edge, which IS what
// the user sees on a receiving surface. 0 = crisp, 1 = very soft.
const spotShadowRadius = (penumbra: number) => 1 + Math.max(0, Math.min(1, penumbra || 0)) * 13;

// Halbe Kantenlaenge der orthografischen Schattenkamera eines Richtungslichts,
// in SLIDE-Einheiten (Jans 1.0-Wert: deckt die 4096er Buehne mit Reserve).
// WIRD PRO BILD MIT `scaleFactor` IN WELT-EINHEITEN UMGERECHNET, siehe
// scaleDirectionalShadowFrustum().
const DIRECTIONAL_SHADOW_EXTENT = 3000;

// Jans 1.0 lief in UNskalierten Slide-Einheiten, der geteilte Renderer schrumpft
// die ganze Szene per `groupRoot.scale = scaleFactor` (~0,26 bei 1:1). three
// uebernimmt in DirectionalLightShadow#updateMatrices aber nur die POSITION des
// Lichts in die Schattenkamera — die Skalierung faellt raus, also bleiben
// ±3000 volle WELT-Einheiten und spannen die 2048er Map ueber das ~8-fache der
// Buehne (gemessen: 0,34 Texel pro Welt-Einheit). Ergebnis war Jans Fund am
// Donut: ausgefranste, koernige Schattenraender. Extent mitskalieren stellt die
// Texeldichte der 1.0 exakt wieder her (A/B im Harness: Rand sauber).
const scaleDirectionalShadowFrustum = (light: TODOAny, scaleFactor: number) => {
    const cam = light.shadow?.camera;
    if (!cam) return;
    const extent = DIRECTIONAL_SHADOW_EXTENT * scaleFactor;
    if (cam.right === extent) return; // nur bei echter Aenderung neu projizieren
    cam.left = -extent;
    cam.right = extent;
    cam.top = extent;
    cam.bottom = -extent;
    cam.updateProjectionMatrix();
};

/** Linearer Verlauf einer SVG-Füllung (s. inlineSvgComputedStyles). */
type SvgVerlauf = {
    /** true = userSpaceOnUse, sonst objectBoundingBox (0..1 im Pfad-Kasten) */
    raum: boolean;
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    stopps: { o: number; farbe: string; a: number }[];
};

/**
 * VERLAUF AUF DEM 3D-KÖRPER (Jan 25.10.2026: „ein Verlauf geht beim
 * SVG-Asset als 3D-Objekt nicht?“): 1D-Textur mit den Stopps + u-Koordinate
 * je Punkt = Lage auf der Verlaufsachse. Die Textur liest pro Pixel, darum
 * stimmen auch drei Stopps (Mitte) exakt; Deckel und Seiten teilen x/y.
 */
const svgVerlaufFuerPfad = (
    path: TODOAny,
): { textur: THREE.CanvasTexture; u: (x: number, y: number) => number; durchsichtig: boolean } | null => {
    if (typeof document === "undefined") return null;
    const roh = path?.userData?.node?.getAttribute?.("data-verlauf");
    if (!roh) return null;
    let v: SvgVerlauf;
    try {
        v = JSON.parse(roh);
    } catch {
        return null;
    }
    let [ax, ay, bx, by] = [v.x1, v.y1, v.x2, v.y2];
    if (!v.raum) {
        // objectBoundingBox: im Kasten der Pfad-Punkte
        let [minX, minY, maxX, maxY] = [Infinity, Infinity, -Infinity, -Infinity];
        for (const sp of path.subPaths ?? []) {
            for (const pt of sp.getPoints()) {
                minX = Math.min(minX, pt.x);
                minY = Math.min(minY, pt.y);
                maxX = Math.max(maxX, pt.x);
                maxY = Math.max(maxY, pt.y);
            }
        }
        if (!Number.isFinite(minX)) return null;
        const [w, h] = [maxX - minX, maxY - minY];
        [ax, ay, bx, by] = [minX + ax * w, minY + ay * h, minX + bx * w, minY + by * h];
    }
    const [dx, dy] = [bx - ax, by - ay];
    const laenge2 = dx * dx + dy * dy;
    if (!(laenge2 > 0)) return null;
    const canvas = document.createElement("canvas");
    canvas.width = 256;
    canvas.height = 1;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    const gr = ctx.createLinearGradient(0, 0, 256, 0);
    for (const st of v.stopps) {
        // getHex liefert sRGB (c.r/g/b sind linear — die Stopps wurden zu dunkel)
        const hex = new THREE.Color(st.farbe).getHex();
        const a = Number.isFinite(st.a) ? st.a : 1;
        gr.addColorStop(
            Math.max(0, Math.min(1, st.o)),
            `rgba(${(hex >> 16) & 255},${(hex >> 8) & 255},${hex & 255},${a})`,
        );
    }
    ctx.fillStyle = gr;
    ctx.fillRect(0, 0, 256, 1);
    const textur = new THREE.CanvasTexture(canvas);
    textur.colorSpace = THREE.SRGBColorSpace;
    textur.wrapS = THREE.ClampToEdgeWrapping;
    textur.wrapT = THREE.ClampToEdgeWrapping;
    textur.generateMipmaps = false;
    textur.minFilter = THREE.LinearFilter;
    return {
        textur,
        // halbes Texel Rand, damit Anfang/Ende genau die Stopp-Farbe treffen
        u: (x, y) => (0.5 + Math.max(0, Math.min(1, ((x - ax) * dx + (y - ay) * dy) / laenge2)) * 255) / 256,
        durchsichtig: v.stopps.some((st) => st.a < 1),
    };
};

/** u-Koordinaten des Verlaufs auf eine Geometrie aus Pfad-Formen legen. */
const svgVerlaufAufGeometrie = (geometry: THREE.BufferGeometry, u: (x: number, y: number) => number) => {
    const pos = geometry.getAttribute("position");
    const uv = new Float32Array(pos.count * 2);
    for (let i = 0; i < pos.count; i++) {
        uv[i * 2] = u(pos.getX(i), pos.getY(i));
        uv[i * 2 + 1] = 0.5;
    }
    geometry.setAttribute("uv", new THREE.BufferAttribute(uv, 2));
};

// Port "Vektor-Layer" (1:1 aus Jans Original-updateSvg): SVGLoader ignoriert
// CSS-<style>-Bloecke (z. B. Illustrator-Exporte mit .st0-Klassen) — den
// Browser die Styles berechnen lassen und als Attribute inlinen, DANN parsen.
// SSR-Guard: ohne document unveraendert zurueckgeben (Renderer mountet nur im
// Browser, aber das Paket wird auch unter vike/SSR typegecheckt/geladen).
const inlineSvgComputedStyles = (svgText: string): string => {
    if (typeof document === "undefined") return svgText;
    const container = document.createElement("div");
    container.style.position = "absolute";
    container.style.left = "-99999px";
    container.style.visibility = "hidden";
    // GEFILTERT einsetzen (s. utils/htmlFilter): Die Datei stammt von einem
    // Nutzer. Roh ins Dokument gehaengt, liefen `onerror=`/`onload=` darin
    // in unserer Seite. Der Loader unten bekommt weiter den Text selbst —
    // er liest ihn nur, fuehrt nichts aus.
    container.innerHTML = filtereSvg(svgText);
    document.body.appendChild(container);
    const svg = container.querySelector("svg");
    let result = svgText;
    if (svg) {
        // FARBVERLÄUFE (Jan 24.10.2026, Grundformen mit Verlauf): SVGLoader
        // kennt kein url(#verlauf) — die Farbe blieb Weiß. Als Farbe gilt
        // ersatzweise die ERSTE Farbe des Verlaufs; ein linearer Verlauf der
        // Füllung reist zusätzlich als data-verlauf mit (s. svgVerlauf), der
        // 3D-Weg legt ihn als Textur auf (Jan 25.10.2026).
        const verlaufVon = (wert: string): Element | null => {
            const m = /^url\(\s*["']?#([^"')]+)["']?\s*\)/.exec(wert);
            if (!m) return null;
            return Array.from(svg.querySelectorAll("linearGradient, radialGradient")).find((v) => v.id === m[1]) ?? null;
        };
        const verlaufsFarbe = (wert: string): string | null => {
            const stop = verlaufVon(wert)?.querySelector("stop");
            if (!stop) return null;
            return window.getComputedStyle(stop).stopColor || stop.getAttribute("stop-color");
        };
        const verlaufsDaten = (wert: string): string | null => {
            const v = verlaufVon(wert);
            if (!v || v.tagName.toLowerCase() !== "lineargradient") return null;
            const zahl = (name: string, standard: number) => {
                const roh = v.getAttribute(name);
                if (roh == null) return standard;
                const n = parseFloat(roh);
                return Number.isFinite(n) ? (roh.trim().endsWith("%") ? n / 100 : n) : standard;
            };
            const stopps = Array.from(v.querySelectorAll("stop")).map((st) => {
                const c = window.getComputedStyle(st);
                const o = parseFloat(st.getAttribute("offset") ?? "0");
                return {
                    o: (st.getAttribute("offset") ?? "").trim().endsWith("%") ? o / 100 : o,
                    farbe: c.stopColor || st.getAttribute("stop-color") || "#000",
                    a: parseFloat(c.stopOpacity || st.getAttribute("stop-opacity") || "1"),
                };
            });
            if (stopps.length < 2) return null;
            const daten: SvgVerlauf = {
                raum: v.getAttribute("gradientUnits") === "userSpaceOnUse",
                x1: zahl("x1", 0),
                y1: zahl("y1", 0),
                x2: zahl("x2", 1),
                y2: zahl("y2", 0),
                stopps,
            };
            return JSON.stringify(daten);
        };
        svg.querySelectorAll<SVGElement>("*").forEach((element) => {
            if (element.tagName === "style") return;
            const computed = window.getComputedStyle(element);
            const { fillOpacity, strokeWidth, opacity } = computed;
            const fill = verlaufsFarbe(computed.fill) ?? computed.fill;
            const fuellVerlauf = verlaufsDaten(computed.fill);
            if (fuellVerlauf) element.setAttribute("data-verlauf", fuellVerlauf);
            const stroke = verlaufsFarbe(computed.stroke) ?? computed.stroke;
            if (fill) element.setAttribute("fill", fill);
            if (stroke && stroke !== "none") {
                element.setAttribute("stroke", stroke);
                if (strokeWidth) element.setAttribute("stroke-width", strokeWidth);
            }
            if (fillOpacity && fillOpacity !== "1") {
                element.setAttribute("fill-opacity", fillOpacity);
            }
            if (opacity && opacity !== "1") {
                element.setAttribute("opacity", opacity);
            }
            element.removeAttribute("class");
        });
        svg.querySelectorAll("style").forEach((styleTag) => styleTag.remove());
        result = svg.outerHTML;
    }
    document.body.removeChild(container);
    return result;
};

// Port "Text-Schatten" (Jans Fund #6, 1:1 aus Jans Original-updateText):
// echter 3D-Schatten via castShadow (Troika erzeugt das Glyphen-Depth-Material
// selbst) + Fake-Schlagschatten ueber Troikas Glyph-Outline (Offset + Blur +
// Opacity = Drop Shadow). Offsets/Blur sind Prozent der Fontgroesse, skalieren
// also mit dem Text; positives "Versatz Y" schiebt den Schatten nach unten
// (wie bei IMAGE/SVG) — Werte werden exakt wie in 1.0 durchgereicht.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function applyTextShadow(text: any, textContent: TODOAny) {
    // echter Schatten hat Vorrang vor dem Fake-Outline-Schlagschatten
    text.castShadow = !!textContent.castShadowReal;
    if (textContent.shadowEnabled && !textContent.castShadowReal) {
        const c = textContent.shadowColor ?? { r: 0, g: 0, b: 0, a: 1 };
        text.outlineWidth = 0;
        text.outlineColor = colorToHex(c);
        text.outlineOpacity = textContent.shadowOpacity ?? 0.25;
        text.outlineBlur = `${textContent.shadowBlur ?? 25}%`;
        text.outlineOffsetX = `${textContent.shadowOffsetX ?? 2}%`;
        text.outlineOffsetY = `${textContent.shadowOffsetY ?? 2}%`;
    } else {
        text.outlineWidth = 0;
        text.outlineOpacity = 0;
        text.outlineBlur = 0;
        text.outlineOffsetX = 0;
        text.outlineOffsetY = 0;
    }
}

export default class Renderer {
    width = 0;
    height = 0;
    camera: THREE.PerspectiveCamera | undefined;
    webglRendererDom: HTMLDivElement;
    cssRendererDom: HTMLDivElement;
    webGLRenderer: THREE.WebGLRenderer | undefined;
    css3DRenderer: CSS3DRenderer;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    templateJson: any;

    setImagesOnStart: boolean = false;
    setVideosOnStart: boolean = false;
    setGpuTextOnStart: boolean = false;
    setVideoJit: boolean = false;
    /**
     * Reader: Videos ausser dem Startvideo laden beim Boot NICHT mit
     * (preload="none", Jans Slow-4G-Test 05.09.2026 — sieben Streams teilten
     * sich die Leitung, das Startvideo hungerte). Nach START einmalig auf
     * "auto" schalten und load() rufen; der Vorlader holt sie danach der
     * Reihe nach als Blob.
     */
    /** Einmal-Schalter: zurueckgehaltene Video-Quellen nach dem Boot freigegeben. */
    nachBootFreigegeben = false;

    visibilityOverrides: Record<string, boolean> = {};

    // Offscreen-Ziel fuers GPU-Aufwaermen einzelner Ebenen (uploadToGpu) —
    // s. dort; lazy, ein einziges 4x4-Target fuer alle Aufrufe.
    private aufwaermZiel: THREE.WebGLRenderTarget | null = null;
    groups: { [id: string]: THREE.Group } = {};
    groupsCSS: { [id: string]: CSS3DObject } = {};
    layerMeshes: {
        [id: string]: THREE.Mesh | THREE.SpotLight | THREE.DirectionalLight | THREE.PointLight;
    } = {};
    slideLayerMeshes: { [id: string]: THREE.Mesh } = {};
    // Port 3 (Bild-Masken/Schatten/Blur): erfuellt ImageEffectHost. Globaler
    // Schatten-Aufhell-Uniform (0 = voller Schatten) + custom-Masken-Caches.
    shadowLiftUniform = { value: 0 };
    // Globale "Schatten-Staerke" (3D-WELT-Slider): 1 = wie geautort, 0 = kein
    // Schatten. Pro Frame aus presentationOptions.shadowStrength gelesen und
    // im Licht-Pass auf jedes light.shadow.intensity gespiegelt (three-0.183-
    // Builtin — Jans 1.0 brauchte dafuer noch einen ShaderChunk-Patch).
    shadowStrength = 1;
    maskSourceCache: { [url: string]: HTMLCanvasElement } = {};
    maskTextureLoading: { [url: string]: boolean } = {};
    // Kachel-Vorschauen (maxTextureWidth gesetzt) fahren auch die
    // Masken-Leinwand kleiner — Begruendung an ImageEffectHost.maskCanvasSize.
    // JEDE maskierte Bild-Ebene hat ihre eigene Leinwand, die nie freigegeben
    // wird: mit 2048² sind das 16 MB im Speicher + 16 MB Textur je Ebene.
    // Jans iPhone-Absturz 21.10.2026 („scene-check-images“, Slide 25): ab
    // den Rauten-Scenes (4+4+9+9+9 maskierte Bilder) ~1 GB nur für Masken →
    // iOS beendete die Seite. Eine Form-Kante (Raute, Kreis …) braucht das
    // nicht: Touch 512², sonst 1024².
    get maskCanvasSize(): number | undefined {
        if (this.props.maxTextureWidth) return this.props.maxTextureWidth;
        return istMobilesGeraet() ? 512 : 1024;
    }
    // Port "3D-Text": gecachte Typefaces fuer text3d/number3d-Primitive,
    // per Font-URL. Jede Schrift wird beim ersten Gebrauch geladen.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    text3dFonts: { [url: string]: any } = {};
    // Port "HDRI": erfuellt EnvironmentHost. Image-Based-Lighting-Zustand —
    // PMREM-Env + rotierbares Equirect + optionaler Skydome. Getrieben allein
    // von presentationOptions.environment* (kein HDRI -> No-op fuer den Bestand).
    pmremGenerator: THREE.PMREMGenerator | undefined;
    environmentTexture: THREE.Texture | undefined;
    environmentEquirect: THREE.Texture | undefined;
    envSourceTexture: THREE.Texture | undefined;
    envRotatedRT: THREE.WebGLRenderTarget | undefined;
    pmremRT: THREE.WebGLRenderTarget | undefined;
    envBlitScene: THREE.Scene | undefined;
    envBlitCamera: THREE.OrthographicCamera | undefined;
    envBlitMaterial: THREE.ShaderMaterial | undefined;
    skyDome: THREE.Mesh | undefined;
    skyDomeMaterial: THREE.ShaderMaterial | undefined;
    appliedEnvironmentHdri = "";
    appliedEnvironmentIntensity = 1;
    appliedEnvironmentRotation = 0;
    pmremBakedRotation = NaN;
    environmentLoading = false;
    // Port "Mirrors" (Teil A): erfuellt ReflectionHost. Opt-in Real-Time-Cube-
    // Reflexionen — per-Layer-Flag (aus content.realtimeReflections) + gecachtes
    // CubeCamera-Rig pro reflektierendem Objekt. Leer = No-op fuer den Bestand.
    reflectiveLayers: { [id: string]: boolean } = {};
    reflectionRigs: { [id: string]: { camera: THREE.CubeCamera; rt: THREE.WebGLCubeRenderTarget } } = {};
    // Port "Mirrors" Teil B: erfuellt MirrorHost. Planare THREE.Reflector-Spiegel
    // auf `plane`-Primitiven — per-Layer-Flag (content.planarMirror) + Params +
    // gecachtes Rig. Leer = No-op fuer den Bestand.
    mirrorLayers: { [id: string]: boolean } = {};
    mirrorParams: { [id: string]: MirrorParams } = {};
    mirrorRigs: { [id: string]: MirrorRig } = {};
    /** Spiegel-Auslauf: Zielfarbe (Hintergrundfarbe der Szene, linear) + Bildhoehe in Welt-Einheiten. */
    spiegelAuslaufZiel: { r: number; g: number; b: number } | undefined;
    spiegelBildHoehe: number | undefined;
    // true nur waehrend des GPU-Aufwaerm-Bilds (uploadToGpu)
    spiegelAusgesetzt = false;
    // Bild-Nummer fuer die Spiegel (einmal je Bild erneuern, s. MirrorHost.spiegelBildNr)
    spiegelBildNr = 0;
    // Editor-Hilfszeichen, die der Spiegel nicht zeigen darf (s. planar-mirrors)
    // Safe Area + Raster gehoeren ebenso dazu (Jans Fund 22.09.2026: der
    // Rahmen der Safe Area stand als Linie im Spiegel).
    spiegelEditorZeichen = (): THREE.Object3D[] =>
        [
            ...Object.values(this.lightTargetMarkers),
            ...Object.values(this.pivotsCircles),
            this.safeSquare,
            this.grid,
        ].filter(Boolean) as THREE.Object3D[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    layerContentCache: { [id: string]: any } = {};
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    objectCache: { [id: string]: any } = {};
    layer3DCache: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [id: string]: any;
    } = {};

    webGlScene: THREE.Scene | undefined;
    css3DScene: THREE.Scene | undefined;
    groupRoot: THREE.Group | undefined;
    groupRootCSS: CSS3DObject | undefined;
    uiRoot: THREE.Group | undefined;
    ambientLight: THREE.AmbientLight | undefined;

    controls: OrbitControls | undefined;

    // Port "GLOBAL-Tab Kamera-Anzeige" (Jans Original): Frustum-Drahtgitter der
    // Presentation-Kamera + kleines Kamera-Gizmo, sichtbar bei showCamera.
    cameraHelper: THREE.CameraHelper | undefined;

    cameraBody: THREE.Group | undefined;

    presentationCamera: THREE.PerspectiveCamera | undefined;

    // JSON-Signatur des zuletzt angewendeten content.material pro object3d-Layer
    // (Live-Material-Updates ohne Rebuild, siehe updateObject)
    materialSigCache: { [key: string]: string } = {};

    // JSON-Signatur des zuletzt angewendeten Licht-Contents pro Licht-Layer
    // (Wert-Dirty-Check statt Referenz, siehe updateLight)
    lightSigCache: { [key: string]: string } = {};

    // Gelbe Zielpunkt-Kugeln pro Licht-Layer (Editor-Chrome, siehe renderLayer)
    lightTargetMarkers: { [key: string]: THREE.Mesh } = {};

    videoCache: {
        [key: string]: Video;
    } = {};

    // Hintergrund-Vorlader fuer Videos (Begruendung in videoVorlader.ts) —
    // startet nach dem START-Klick, siehe animate.
    videoVorlader = new VideoVorlader();

    // Reader-Audio (siehe AudioLayers.ts); im Editor ungenutzt
    audioLayers = new AudioLayers();

    lottieCache: {
        [key: string]: LottiePlayer;
    } = {};

    layerState3d: {
        [key: string]: THREE.Group;
    } = {};

    layerStateCSS3d: {
        [key: string]: CSS3DObject;
    } = {};

    lightState: {
        [key: string]: THREE.PointLight | THREE.DirectionalLight;
    } = {};

    lightHelpers: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: any;
    } = {};

    lightShadowHelpers: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: any;
    } = {};

    layerIsLoading: {
        [key: string]: boolean;
    } = {};

    /**
     * BILD-EBENEN: NEUER VERSUCH NACH FEHLSCHLAG (Jans Fund 16.10.2026, „bei
     * den Scenes fehlt öfter der Kontakt zu den Fotos — raus aus dem Editor,
     * neu laden, dann ist alles da"). Schlugen Optimierungs- UND Original-
     * Adresse fehl (typisch: kalte imgproxy-Umrechnung, 5xx/Abbruch), blieb
     * `layerIsLoading` für immer gesetzt — die Ebene lud in DIESER Renderer-
     * Instanz nie wieder, auch nicht nach einem Bildtausch. Jetzt: Merker
     * zurück, neuer Anlauf nach 2/4/8/16/30/30 s (höchstens 6), eine NEUE
     * Adresse sofort. `bildLadeStart` erkennt hängende Abrufe (> 15 s), damit
     * ein Bildtausch nicht ewig wartet; `bildLadeKennung` verwirft verspätete
     * Ergebnisse eines überholten Abrufs.
     */
    bildNeuVersuch: Record<string, { url: string; anzahl: number; ab: number }> = {};
    bildLadeStart: Record<string, number> = {};
    bildLadeKennung: Record<string, number> = {};

    layerScrollBindingsFired: Record<string, Set<string>> = {};

    svgOverlays: { [key: string]: HTMLDivElement } = {};
    /** asHtml-SVG: zuletzt gescheiterte Adresse je Ebene (kein Neu-Holen pro Frame). */
    svgHtmlFehlgeschlagen: Record<string, string> = {};
    // Port 1: Signatur des zuletzt gerenderten Infografik-Contents pro Layer,
    // damit buildInfographicSvg nur bei Änderung neu läuft (nicht pro Frame).
    infographicSig: { [key: string]: string } = {};
    // Selbstheilung nach Fehlbau (siehe meldeInfografikBauFehler).
    infographicRetryAt: { [key: string]: number } = {};
    /** Wortwolke wartet auf ihre Schriften (s. wortwolkenSchriftenBereit). */
    wolkeWartet: { [key: string]: boolean } = {};
    infographicFehlerSig: { [key: string]: string } = {};

    // Interactive map layer state (MapTiler SDK / MapLibre GL)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mapInstances: { [key: string]: any } = {};
    /** Pro-Editor-Karten als Fläche im 3D-Raum (KarteImRaum.ts). */
    kartenImRaum: { [key: string]: KarteImRaum } = {};
    mapContainers: { [key: string]: HTMLDivElement } = {};
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private mapSdk: any = null; // cached @maptiler/sdk module

    pivots: {
        [key: string]: THREE.Group;
    } = {};

    pivotsCSS: {
        [key: string]: CSS3DObject;
    } = {};

    pivotsCircles: {
        [key: string]: THREE.Mesh;
    } = {};

    // Port "Vektor-Layer" (Jans Original): geparste SVG-Pfade pro Layer cachen,
    // damit Tiefe/Fase/Silhouette/Schatten-Aenderungen die Geometrie aus dem
    // Cache neu bauen koennen — ohne Re-Fetch der SVG-Datei.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    svgPathsCache: { [id: string]: { url: string; paths: any } } = {};

    safeSquare: THREE.Mesh | undefined;
    /** Cutover Schritt 2: Editor-Helfer-Grid (lazy, nur wenn editorStore es einblendet). */
    grid: THREE.LineSegments | undefined;
    // Cutover Schritt 2b: Transform-Gizmo (lazy, nur mit editorStore). Eigener
    // transparenter Canvas ueber dem Content, damit die Handles vorn liegen.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    transformControls: any | undefined;
    // Port "asHtml-Proxy" (Jans Original svgHandlerProxy): HTML-Overlay-Layer
    // (asHtml-SVG, HTML-Infografik) haben kein WebGL-Pivot — diese unsichtbare
    // Gruppe unter groupRoot traegt dieselbe Welt-Transform, damit Gizmo und
    // Drehpunkt-Kugel etwas zum Anfassen haben.
    svgHandlerProxy: THREE.Group | undefined;
    /** Beendet ein hängendes Gizmo-Ziehen (s. ensureGizmo); für stop(). */
    private _gizmoZiehEnde: (() => void) | undefined;
    /** Ebene, an der der Gizmo zuletzt hing (s. syncGizmo). */
    private _gizmoZielId: string | undefined;
    gizmoScene: THREE.Scene | undefined;
    gizmoRenderer: THREE.WebGLRenderer | undefined;
    gizmoCamera: THREE.PerspectiveCamera | undefined;
    isDraggingGizmo = false;
    /**
     * „Alle Ebenen registriert" — PRO INSTANZ (Jans Fund 10.09.2026: im
     * Pro-Editor blieben Infografik-Aufbau und SVG-Animation stehen, WebGL-
     * Ebenen liefen weiter). WebGLAssets.allLayersSet ist global: stoppt
     * irgendein zweiter Viewer auf derselben Seite (Vorschau-Dialog,
     * Szenen-Kachel, vorige Route), setzt er den Schalter fuer ALLE zurueck —
     * und updateLayer (Inhalt/Fortschritt pro Bild) lief im Editor nie wieder,
     * bis zum Neuladen. Der Schalter gehoert dem Renderer, der seine Ebenen
     * registriert hat. Das globale Feld bleibt fuer Altcode gesetzt.
     */
    private alleEbenenRegistriert = false;
    /** Dreh-Modus: Euler-Winkel (Grad) beim Drag-Start und zuletzt gemeldeter Wert. */
    private gizmoStartRotDeg: [number, number, number] | null = null;
    private gizmoLetzteRotDeg: [number, number, number] | null = null;

    props: RendererProps;

    animationTimeOut: ReturnType<typeof setTimeout> | undefined;
    interval: ReturnType<typeof setTimeout> | undefined;
    imageOptimizer: ImageOptimizer;
    _visibilityHandler: (() => void) | undefined;
    _pagehideHandler: ((ev: PageTransitionEvent) => void) | undefined;
    // Nachzuegler-Priming (s. primeVideos): Geste-Listener, der spaeter
    // entstandene Videoelemente nachfreischaltet.
    _nachzueglerPrimer: (() => void) | undefined;
    // ?tondebug=1 — Live-HUD der Video-Tonzustaende (Diagnose, s. unten).
    _tonDebugTimer: ReturnType<typeof setInterval> | undefined;
    _tonDebugHud: HTMLDivElement | undefined;
    // Videos, deren Hoerbarkeit in DIESEM Frame vom Render-Baum verwaltet
    // wurde (Audibility-Block in renderLayer). Alle anderen werden nach dem
    // Crawl stummgeschaltet — s. Kehrgang in animate (27.08.2026).
    private _tonGepflegteVideos = new Set<string>();
    // Einmaliges Shader-Aufwaermen nach Ladeschluss (animate) — siehe dort.
    private shaderAufwaermenErledigt = false;
    // Schrittweises Textur-Aufwaermen nach Ladeschluss (animate) — siehe dort.
    private texturAufwaermenErledigt = false;
    private aufgewaermteTexturen = new WeakSet<THREE.Texture>();
    // Troikas SDF-Atlas (einer je Glyphengroesse, von allen Texten geteilt) —
    // s. updateText-sync und animate.
    private troikaAtlanten = new Set<THREE.Texture>();
    // Aufwaerm-Stand fuer den Ladebalken (s. animate)
    private shaderKompiliert = false;
    private offeneTextSyncs = 0;
    private ladeEndeSeit: number | null = null;

    /**
     * Das HDRI (syncEnvironment) laedt neben den Ebenen her und zaehlt nicht
     * zum Ladebalken. Kam es erst nach dem Aufwaermen, brauchte jedes PBR-
     * Material neue Programme mit Umgebungs-Map — gemessen 8 Programme kurz
     * nach START (3D-Power-Test, Safari). Darum wartet das Aufwaermen darauf;
     * ein fehlgeschlagenes HDRI gilt als fertig, und nach 3 s geht es ohnehin los.
     */
    private umgebungBereit(): boolean {
        const optionen = (this.props.presentation as { presentationOptions?: { environmentHdri?: string } } | undefined)
            ?.presentationOptions;
        const hdri = optionen?.environmentHdri ?? "";
        if (!hdri) return true;
        if (this.appliedEnvironmentHdri === hdri && !this.environmentLoading) return true;
        this.ladeEndeSeit ??= performance.now();
        return performance.now() - this.ladeEndeSeit > 3_000;
    }

    constructor(
        webglRendererDom: HTMLDivElement,
        cssRendererDom: HTMLDivElement,
        props: RendererProps,
        imageOptimizer: ImageOptimizer
    ) {
        this.webglRendererDom = webglRendererDom;
        this.cssRendererDom = cssRendererDom;
        this.animate = this.animate.bind(this);
        this.props = {
            renderer: props.renderer,
            slidePlayer: props.slidePlayer,
            editorStore: props.editorStore,
            start: props.start,
            stop: props.stop,
            isProduction: props.isProduction,
            presentation: props.presentation,
            isCoverActive: props.isCoverActive,
            maxTextureWidth: props.maxTextureWidth,
            editorMode: props.editorMode,
        };
        this.css3DRenderer = new CSS3DRenderer();
        this.imageOptimizer = imageOptimizer;
        // GLOBAL-STATIC-RESET (Jans Template+-Fund 11.10.2026, gleiche Klasse
        // wie der WK-Video-Fund 01.09.2026): setImagesOnStart/setGpuTextOnStart
        // werden beim Editor-Livetausch gesetzt und überlebten als Statics
        // JEDEN Renderer-Neustart im selben Tab. Der nächste Boot übersprang
        // dann den Erst-Ladelauf ALLER Bilder bzw. Texte — sichtbar als
        // Regenbogen-Ersatztexturen, tote Kachel-Fotos und eingefrorene
        // Vorschau, sobald nach einem Bildtausch irgendetwas den Renderer neu
        // aufbaute (im Template+ tut das jede Strukturänderung). Ein NEUER
        // Renderer beginnt immer mit frischem Erst-Ladelauf.
        WebGLAssets.setImagesOnStart = false;
        WebGLAssets.setGpuTextOnStart = false;
        // Gesten-Priming bereitstellen (Begruendung am Store-Feld): stumm
        // anspielen+pausieren schaltet jedes Videoelement fuer spaeteres
        // programmatisches play() frei (Safari: Freigabe gilt PRO Element);
        // die Stummschaltung verwaltet danach die Hoerbarkeits-Schicht.
        this.props.renderer.primeVideos = () => this.primeVideos();
        // Boot durch (Startvideo abspielbereit) → restliche Videos laden lassen.
        this.props.renderer.beiLadeEnde?.(() => this.zurueckgehalteneVideosFreigeben());
    }

    /** Gesten-Priming — Begruendung am Store-Feld primeVideos.
     *
     *  Zwei Modi (27.08.2026, Jans Doppel-Fund):
     *  - STRIKT (START-Klick, Default): exakt das Ur-Verhalten — ALLE
     *    Elemente zwangs-stummschalten + stumm anspielen/pausieren. Die
     *    Zwangs-Stummschaltung ist tragend: sie raeumt jeden zu frueh
     *    hoerbaren Ton ab (unmute-on-play des Wrappers), die Hoerbarkeits-
     *    Schicht entstummt danach nur im richtigen Fenster.
     *  - SANFT (Nachzuegler-Listener bei jeder Geste): nur ungeprimte
     *    Elemente freischalten, laufende weder stummschalten noch
     *    pausieren — sonst wuerde jeder Klick mitten in der Story ein
     *    gerade hoerbares Video anfassen. */
    primeVideos(sanft: boolean = false) {
        // GESTAFFELT AUF MOBIL (Jans iPhone-Fund 02.09.2026, „Vorhang geht
        // mit dem Video etwas ruckelig auf"): Der START-Klick spielte ALLE
        // Video-Elemente gleichzeitig an und pausierte sie — fuenf Decoder-
        // Anlaeufe im selben Moment wie Vorhang-Blende, Intro-Fahrt und das
        // Startvideo. Auf dem Telefon jetzt nur die Videos der ersten beiden
        // Slides im Klick; den Rest primt die naechste Geste nach (der
        // Nachzuegler-Primer auf pointerdown, seit #505) — auf dem Telefon
        // ist jede Weiterfahrt eine Beruehrung. Desktop: unveraendert alle.
        let frueheIds: Set<string> | null = null;
        if (!sanft && istMobilesGeraet()) {
            const slides = ((this.props.presentation as TODOAny)?.slides ?? []) as { from?: number }[];
            const grenze = slides[2]?.from ?? Infinity;
            frueheIds = new Set<string>();
            crawl(
                (this.props.presentation as TODOAny)?.groupTree,
                (layer: LayerRender) => {
                    if (layer?.type === LayerType.video && (layer.from ?? 0) < grenze) {
                        frueheIds!.add(layer.id);
                    }
                },
                { getChildren: (l: TODOAny) => l?.layers }
            );
        }
        Object.entries(this.videoCache).forEach(([layerId, eintrag]) => {
            const v = eintrag as TODOAny;
            const el = v?.videoPlayer as HTMLVideoElement | undefined;
            if (!el || v.geprimt) return;
            if (frueheIds && !frueheIds.has(layerId)) return;
            v.geprimt = true;
            if (sanft && el.paused && v.isPlaying) {
                // BLOCKIERTER ABSPIELWUNSCH (Jans HUD-Fund 28.08.2026:
                // wrapPlay=1, Element PAUSE, TON, geprimt=0): Nach dem
                // Live-Platzieren laeuft wrapper.play() asynchron AUSSERHALB
                // der Nutzer-Geste (Upload -> Textur -> Debounce) — der
                // Browser lehnt das unstumme play() ab, der Wrapper haelt
                // sich aber fuer spielend und versucht es nie wieder
                // ("Ton erst nach Schliessen/Neuoeffnen"). IN der Geste
                // nachgestartet ist es erlaubt; Hoerbarkeit regelt weiter
                // die Schicht.
                const pr = el.play();
                if (pr && typeof pr.catch === "function") pr.catch(() => {});
                return;
            }
            if (sanft && !el.paused) {
                const pr = el.play();
                if (pr && typeof pr.catch === "function") pr.catch(() => {});
                return;
            }
            el.muted = true;
            if (!sanft && !el.paused) {
                // STRIKT auf laufendem Element: stummschalten reicht —
                // Wiedergabe nicht unterbrechen (stand vorher auch nie
                // wirklich still: play->pause auf Laufendem war ein No-op-
                // Paar mit Stotter-Risiko).
                const pr = el.play();
                if (pr && typeof pr.catch === "function") pr.catch(() => {});
                return;
            }
            const pr = el.play();
            if (pr && typeof pr.then === "function") {
                pr.then(() => el.pause()).catch(() => {});
            }
        });
    }

    setProps(props: RendererProps) {
        this.props = {
            renderer: props.renderer,
            slidePlayer: props.slidePlayer,
            editorStore: props.editorStore,
            start: props.start,
            stop: props.stop,
            isProduction: props.isProduction,
            presentation: props.presentation,
            isCoverActive: props.isCoverActive,
            maxTextureWidth: props.maxTextureWidth,
            editorMode: props.editorMode,
        };
    }

    uploadToGpu(object3D: THREE.Group) {
        if (!this.webGLRenderer || !this.camera || !this.webGlScene) {
            throw new Error("Called RendererWebGL#uploadToGpu before calling init function.");
        }
        const { visible } = object3D;
        const originalPosition = object3D.position.clone();
        object3D.position.set(0, 0, 0);
        object3D.visible = true;
        this.webGlScene.add(object3D);

        const culling = object3D.frustumCulled;
        object3D.frustumCulled = false;
        //  render — der Spiegel setzt dabei aus (s. MirrorHost.spiegelAusgesetzt):
        //  das Objekt steht hier unskaliert im Ursprung und gehoert in kein Spiegelbild.
        //  OFFSCREEN (Jans Fund 13.10.2026, "ausgeblendetes Hintergrundbild
        //  blitzt beim Laden auf"): dieses Aufwaerm-Bild ging bisher in den
        //  SICHTBAREN Canvas — die frisch gebaute Ebene stand dafuer sichtbar
        //  im Ursprung und konnte einen Frame lang auf dem Bildschirm landen,
        //  auch wenn sie ausgeblendet ist. Jetzt wie beim 3D-Aufwaerm-Bild
        //  (waermeBildRendern) in ein 4x4-Render-Target: Textur-Upload und
        //  Programmaufbau passieren genauso, nichts erreicht den Bildschirm.
        //  (Die Bildschirm-Programmvariante kompiliert dann erst beim ersten
        //  echten Erscheinen — billig gegen den Blitzer.)
        this.aufwaermZiel ??= new THREE.WebGLRenderTarget(4, 4);
        const zielVorher = this.webGLRenderer.getRenderTarget();
        this.spiegelAusgesetzt = true;
        try {
            this.webGLRenderer.setRenderTarget(this.aufwaermZiel);
            this.webGLRenderer.render(this.webGlScene, this.camera);
        } finally {
            this.webGLRenderer.setRenderTarget(zielVorher);
            this.spiegelAusgesetzt = false;
        }
        object3D.position.copy(originalPosition);
        object3D.visible = visible;

        //  pop
        this.webGlScene.remove(object3D);
        object3D.frustumCulled = culling;
    }

    init() {
        const rendererStore = this.props.renderer;

        if (!this.webglRendererDom) {
            return;
        }

        // Standard-Schriften des Pro-Editors registrieren (Reader-Seite):
        // Infografiken/Karten/HTML-Boxen tragen Familiennamen wie
        // 'BarlowSemiCondensed' — ohne @font-face faellt der Reader auf
        // Browser-Defaults zurueck (Jans Fund 01.08.2026). Idempotent, im
        // Editor harmlos-doppelt (identische Dateien).
        injectReaderStandardFonts();
        // Team-/Brandschriften dieses Scrollys (slideOptions.fontInfos, vom
        // Editor beim Anwenden geschrieben): im HTML-Fenster braucht der
        // Reader dafuer eine @font-face-Regel, sonst faellt die Familie still
        // auf die Ersatzschrift zurueck.
        injectReaderTeamFonts(
            (this.props.presentation?.presentationOptions as TODOAny)?.slideOptions?.fontInfos
        );

        /*
        const message = 'ready';
        parent.postMessage(message, 'https://de-scrolly-frontend-test.ppi.media/');
        parent.postMessage(message, 'https://de-scrolly-frontend-dev.ppi.media/');

        window.addEventListener('message', (e) => {
      console.log("incoming message");
      console.log(e);
      if( e.data.messageId === "selectedSlide" ) {

          WebGLAssets.selectedIndex = e.data.selectedSlideIndex;
          console.log( "selectedIndex: " + WebGLAssets.selectedIndex);

      }

      if( e.data.messageId === "json" ) {

          console.log( "currentimageobject: ");
          WebGLAssets.currentImageObject = e.data.json.data.data.layers[WebGLAssets.selectedIndex];
          console.log( WebGLAssets.currentImageObject )

      }
        });

        document.addEventListener('pointerdown', (e) => {
      console.log("touchstart");
        });

        document.addEventListener('pointerup', (e) => {
      console.log("touchend");
        });

        document.body.addEventListener('pointerdown', (e) => {
      console.log("pointer down");
        });

        document.body.addEventListener('pointerup', (e) => {
      console.log("pointer up");
        });

        this.webglRendererDom.addEventListener('pointerup', (e) => {
      console.log("renderdom pointer up");
        });
        */

        this._visibilityHandler = () => {
            if (document.hidden) {
                for (const video of Object.values(this.videoCache)) {
                    video.pause();
                    video.seek(0);
                    // Zusaetzlich stumm (Jans Fund 03.09.2026, "Ton laeuft nach
                    // dem Schliessen weiter"): Sollte WebKit einen gemerkten
                    // Play-Wunsch im Hintergrund doch noch ausfuehren, ist er
                    // wenigstens lautlos. Die Hoerbarkeits-Schicht setzt muted
                    // pro Frame neu, sobald der Tab wieder sichtbar ist.
                    if (video.videoPlayer) video.videoPlayer.muted = true;
                }
                for (const lottiePlayer of Object.values(this.lottieCache)) {
                    lottiePlayer.pause();
                }
                // Audio-Layer ebenfalls stoppen: RAF steht im Hintergrund-Tab,
                // ein gerade laufender Ton liefe sonst dort endlos weiter.
                this.audioLayers.pauseAll();
            }
        };
        document.addEventListener("visibilitychange", this._visibilityHandler);

        // TON AUS BEIM VERLASSEN (Jans Fund 03.09.2026: Video mit Ton laeuft,
        // Browser/Tab zu — der Ton spielt weiter, obwohl kein Fenster mehr
        // offen ist). Die Video-Elemente haengen nicht im DOM; WebKit kann
        // ihre Medien-Pipeline ueber das Seitenende hinaus am Leben halten.
        // pagehide feuert bei Tab-Schliessen, Navigation und App-Wechsel
        // zuverlaessig (visibilitychange nicht immer): alles HART anhalten
        // (ohne Ausblendung) und stumm schalten; kommt die Seite nicht in den
        // Vor-/Zurueck-Cache (persisted=false), die Quelle ganz loesen —
        // damit ist die Pipeline abgebaut, nichts kann weiterklingen.
        this._pagehideHandler = (ev: PageTransitionEvent) => {
            for (const video of Object.values(this.videoCache)) {
                const el = video.videoPlayer;
                if (!el) continue;
                try {
                    el.muted = true;
                    el.pause();
                    if (!ev.persisted) {
                        el.removeAttribute("src");
                        el.load();
                    }
                } catch {
                    /* ignore */
                }
            }
            for (const lottiePlayer of Object.values(this.lottieCache)) {
                lottiePlayer.pause();
            }
            this.audioLayers.pauseAll();
        };
        window.addEventListener("pagehide", this._pagehideHandler);

        // Nachzuegler-Priming (Jans Fund 27.08.2026, "Ton erst nach
        // Neuladen"): primeVideos lief nur EINMAL im START-Klick. Videos,
        // die DANACH entstehen (Editor: Video platzieren; updateVideo-
        // liveUpdate baut neue Elemente), verpassten die Pro-Element-
        // Freigabe — Safari verweigerte ihr spaeteres unmuted play().
        // Jede weitere Geste primt Nachzuegler nach; das geprimt-Flag
        // macht den Handler im Normalfall zum No-op.
        this._nachzueglerPrimer = () => this.primeVideos(true);
        document.addEventListener("pointerdown", this._nachzueglerPrimer, {
            capture: true,
            passive: true,
        });

        // TON-DIAGNOSE-HUD (?tondebug=1, 27.08.2026): zeigt live je Video
        // stumm/laufend/Lautstaerke/geprimt/contentMuted + tonVideoId und
        // playbackStarted — damit "Ton geht nicht"-Meldungen Zahlen tragen
        // statt Gehoer (gleiche Idee wie ?scrolldebug). Dauerwerkzeug,
        // kostet ohne Flag nichts.
        if (
            typeof window !== "undefined" &&
            window.location.search.includes("tondebug=1")
        ) {
            const hud = document.createElement("div");
            hud.style.cssText =
                "position:fixed;bottom:8px;left:8px;z-index:2147483647;" +
                "background:rgba(0,0,0,0.85);color:#0f0;font:10px/1.5 monospace;" +
                "padding:6px 8px;white-space:pre;pointer-events:none;max-width:95vw;overflow:hidden;";
            document.body.appendChild(hud);
            this._tonDebugHud = hud;
            this._tonDebugTimer = setInterval(() => {
                const st = this.props.renderer as TODOAny;
                const zeilen = Object.entries(this.videoCache).map(([id, eintrag]) => {
                    const v = eintrag as TODOAny;
                    const el = v?.videoPlayer as HTMLVideoElement | undefined;
                    if (!el) return `${id.slice(-8)}  (kein element)`;
                    // Auswahl-Eingaenge aus der videodiag-Sonde dazulegen,
                    // wenn ?videodiag ebenfalls aktiv ist (Fenster/Sichtbar/
                    // Mittenabstand entscheiden, wen der Ton-Waehler nimmt).
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const diag = (window as any).__videoDiag?.[id];
                    const diagText = diag
                        ? `  fenster=${diag.imFenster === false ? 0 : diag.imFenster === true ? 1 : "?"}` +
                          `  sichtbar=${diag.sichtbar === false ? 0 : diag.sichtbar === true ? 1 : "?"}` +
                          `  d=${diag.mitteAbstand ?? "?"}  ton=${diag.ton ? 1 : 0}`
                        : "";
                    return (
                        `${id.slice(-8)}  ` +
                        `${el.paused ? "PAUSE " : "SPIELT"}  ` +
                        `${el.muted ? "STUMM " : "TON   "}  ` +
                        `vol=${el.volume.toFixed(2)}  ` +
                        `geprimt=${v.geprimt ? 1 : 0}  ` +
                        `wrapPlay=${v.isPlaying ? 1 : 0}  ` +
                        `cMuted=${v.contentMuted ? 1 : 0}  ` +
                        `fadeAus=${v.tonFadeAus ? 1 : 0}` +
                        diagText
                    );
                });
                hud.textContent =
                    `TON-DEBUG  playbackStarted=${st.playbackStarted ? 1 : 0}  ` +
                    `tonVideoId=${st.tonVideoId ? String(st.tonVideoId).slice(-8) : "-"}  ` +
                    `editor=${st.isUsedInEditor?.() ? 1 : 0}` +
                    "\n" +
                    (zeilen.length ? zeilen.join("\n") : "(keine videos im cache)");
            }, 300);
        }

        // START-DIAGNOSE-HUD (?startdebug=1, 02.09.2026): zeigt live je Video
        // Quelle (blob/netz), readyState, Position, Trim, ob noch das Poster
        // steht — plus wie das Startvideo geladen wurde und ob die Wiedergabe
        // begonnen hat. Fuer Geraetetests (iPhone), auf denen man keine
        // Konsole hat: Screenshot statt Vermutung. Ohne Flag kostet es nichts.
        if (
            typeof window !== "undefined" &&
            window.location.search.includes("startdebug=1")
        ) {
            const hud = document.createElement("div");
            hud.style.cssText =
                "position:fixed;top:8px;left:8px;z-index:2147483647;" +
                "background:rgba(0,0,0,0.85);color:#ff0;font:10px/1.5 monospace;" +
                "padding:6px 8px;white-space:pre;pointer-events:none;max-width:95vw;overflow:hidden;";
            document.body.appendChild(hud);
            this._startDebugHud = hud;
            const hudStart = typeof performance !== "undefined" ? performance.now() : Date.now();
            this._startDebugTimer = setInterval(() => {
                const st = this.props.renderer as TODOAny;
                const jetzt = typeof performance !== "undefined" ? performance.now() : Date.now();
                const zeilen = Object.entries(this.videoCache).map(([id, eintrag]) => {
                    const v = eintrag as TODOAny;
                    const el = v?.videoPlayer as HTMLVideoElement | undefined;
                    if (!el) return `${id.slice(-8)}  (kein element)`;
                    const mesh = this.layerMeshes[id] as THREE.Mesh | undefined;
                    const lade = this.startVideoLadeInfo[id];
                    return (
                        `${id.slice(-8)}  ` +
                        `${el.src.startsWith("blob:") ? "BLOB" : "NETZ"}  ` +
                        `rs=${el.readyState}  ` +
                        `${el.paused ? (el.ended ? "ENDE  " : "PAUSE ") : "SPIELT"}${v.isPlaying ? "*" : " "} ` +
                        `t=${el.currentTime.toFixed(1)}${el.seeking ? "~" : " "} ` +
                        `${el.loop ? "loop " : "once "}` +
                        `trim=${(v.trimStart ?? 0).toFixed(1)}  ` +
                        `buf=${el.buffered.length ? el.buffered.end(el.buffered.length - 1).toFixed(1) : "-"}  ` +
                        `${!mesh ? "KEIN-MESH" : mesh.userData?.posterInterim ? "POSTER" : "VIDEO "}  ` +
                        // Boot-Buchhaltung je Video (05.09.2026): zaehlt der Layer
                        // im Boot mit (in Liste), und ist er schon gemeldet?
                        `${st.layers.includes(id) ? "boot" : "kein-boot"}${st.loadedLayers.includes(id) ? "+gemeldet" : ""}  ` +
                        // Zurueckgehaltene Quelle: noch offen, oder wann freigegeben?
                        (v.aufgeschobeneQuelle
                            ? "quelle:zurueck  "
                            : v.quelleFreigegebenBei
                              ? `quelle:frei@${(v.quelleFreigegebenBei / 1000).toFixed(1)}s  `
                              : "") +
                        (lade ? `start:${lade.weg} ${lade.ms}ms ${Math.round(lade.bytes / 1024)}KB` : "")
                    );
                });
                hud.textContent =
                    `START-DEBUG  ${((jetzt - hudStart) / 1000).toFixed(1)}s  ` +
                    `geladen=${st.finishedLoading ? 1 : 0} (${st.loadedLayers.length}/${st.layers.length})  ` +
                    `gestartet=${st.playbackStarted ? 1 : 0}  ` +
                    `timer=${Math.round((this.props.slidePlayer as TODOAny)?.timer ?? -1)}  ` +
                    `hidden=${typeof document !== "undefined" && document.hidden ? 1 : 0}` +
                    "\n" +
                    (zeilen.length ? zeilen.join("\n") : "(keine videos im cache)");
            }, 250);
        }

        const width = this.webglRendererDom.clientWidth;
        const height = this.webglRendererDom.clientHeight;

        const FOV = rendererStore.fov;
        const FAR = rendererStore.far;
        const { cameraLookAt } = rendererStore;
        const { cameraPosition } = rendererStore;

        // I made some experiments with the Orthographic Camera, but they were not satisfying, so
        // a camera with a low FOV seems to be the way to go
        //
        // NEAR=50 statt 1 (26.08.2026, Jans Fund „ausgefranste Schnittkanten
        // bei sich durchdringenden 3D-Objekten"): Die Tiefenpuffer-Aufloesung
        // faellt quadratisch mit der Distanz und linear mit near — bei near=1
        // blieb auf Standard-Objektdistanz (~4000, Kamera-z) nur ~1 Welt-
        // einheit Praezision, die Schnittlinie zweier Flaechen zappelte
        // pixelweise. near=50 gibt das 50-fache (~0,02 Einheiten, unter
        // Pixelgroesse). Preis: Inhalte naeher als 50 Einheiten vor der Linse
        // werden geclippt — bei fov=10 (Tele) ist so eine Naehe ohnehin
        // jenseits jeder sinnvollen Einstellung. Logarithmischer Tiefenpuffer
        // waere die Radikalloesung, kollidiert aber mit unseren Custom-
        // Shadern mit Tiefenschreibung (VideoMaterial).
        this.camera = new THREE.PerspectiveCamera(FOV, width / height, 50, FAR);

        this.camera.position.copy(new THREE.Vector3(...cameraPosition!));
        this.camera.lookAt(new THREE.Vector3(...cameraLookAt!));
        this.camera.updateProjectionMatrix();

        // This should later be extended for debugging, gives the ability to easily navigate the camera
        this.controls = new OrbitControls(this.camera, this.webglRendererDom);
        this.controls.enabled = rendererStore.useOrbitControls;

        // WEBGL Renderer
        // WebGLAssets.editorMode ist hier bereits gueltig: der Pro-Editor-Adapter
        // setzt es SYNCHRON im Render, vor dem Mount des Viewers (und damit vor
        // dieser Konstruktion). Der Reader laesst es false → Optionen unveraendert.
        this.webGLRenderer = new THREE.WebGLRenderer({
            // it is important that we set alpha equals true, so the mixed mode works
            alpha: true,
            // EDITOR: Kantenglaettung erzwingen wie in Jans Original (dessen
            // Store-Default ist true; der geteilte Store defaultet false).
            // Ohne MSAA schnappen die 1px-Hilfslinien (Grid, Safe-Area) pro
            // Frame hart auf Puffer-Pixel — unter dem Interface-Zoom (<100%,
            // CSS-Downscale) flackerte das beim Abspielen stark (Jans Fund;
            // bei 100% unauffaellig, exakt dieses Muster).
            // READER: MSAA, sobald die Presentation echte 3D-Inhalte traegt —
            // sonst zeigten 3D-Szenen im Viewer Treppenkanten, die der Editor
            // nie zeigte (Jans iPhone-Fund 19.09.2026; s. utils/hat3dEbenen).
            antialias:
                this.props.renderer.antialiasing ||
                WebGLAssets.editorMode ||
                (this.hat3d = hat3dEbenen((this.props.presentation as TODOAny)?.groupTree)) ||
                // auch flache Bilder, sobald sie schraeg stehen oder sich drehen
                // (Jans „in Motion"-Template 29.09.2026, s. hatGedrehteEbenen)
                hatGedrehteEbenen((this.props.presentation as TODOAny)?.groupTree),
            // 'highp' ueberall (vorher: nur Editor, Reader 'lowp'). Jans
            // Original begruendet es fuer Schatten ("'lowp' corrupted the
            // RGBA-packed shadow depth"); im Reader kam dazu: Glas-Materialien
            // (transmission > 0, z. B. Jans Counter mit Preset "glass") wurden
            // unter 'lowp' gar nicht gezeichnet — im Editor sichtbar, im
            // veroeffentlichten Scrolly unsichtbar (Jans Komplex-Scrolly
            // 01.08.2026). Moderne GPUs rechnen intern ohnehin meist highp;
            // der Perf-Gewinn von 'lowp' war theoretisch, der Schaden real.
            precision: "highp",
            // premultipliedAlpha: true,
        });

        this.webGLRenderer.shadowMap.enabled = true;
        this.webGLRenderer.shadowMap.type = THREE.PCFSoftShadowMap;

        // Glas-Budget mobil (Jans iPhone-Absturz 26.08.2026): Transmission
        // rendert die Szene EXTRA in ein Vollbild-Target (inkl. Mip-Kette fuer
        // die Rauheits-Unschaerfe). Auf Touch-Geraeten halbe Aufloesung =
        // Viertel der Pixel — Glas bricht/verwischt ohnehin, der Unterschied
        // ist auf Handy-Groesse nicht sichtbar. Desktop bleibt bei 1.0.
        if (istMobilesGeraet()) {
            (this.webGLRenderer as TODOAny).transmissionResolutionScale = 0.5;
        }

        this.webGLRenderer.setSize(width, height);
        this.css3DRenderer.setSize(width, height);

        // Override inline pixel styles with percentage-based sizing so the canvas
        // fills its container when the viewport mode changes. The buffer dimensions
        // are updated separately via setSize(w, h, false) in animate().
        this.webGLRenderer.domElement.style.width = '100%';
        this.webGLRenderer.domElement.style.height = '100%';

        // Transparent clear so the wrapper element's background (set by VisualStoryPlayer
        // from slide_options.background_color) shows through. Without this, the scene
        // shows a hardcoded black behind any SVG/DOM overlays smaller than the viewport.
        this.webGLRenderer.setClearColor(CLEAR_COLOR, 0);

        // Accessibility: mark the WebGL canvas for screen readers
        this.webGLRenderer.domElement.setAttribute('role', 'img');
        this.webGLRenderer.domElement.setAttribute('aria-label', 'Scrolly Animation');

        // append doms
        this.webglRendererDom.appendChild(this.webGLRenderer.domElement);
        //this.cssRendererDom.appendChild(this.css3DRenderer.domElement);

        const webGLScene = new THREE.Scene();
        //const css3DScene = new THREE.Scene();
        this.webGlScene = webGLScene;
        //this.css3DScene = css3DScene;
        // window.THREE removed — was leaking the entire Three.js library as a global

        // The WebGL scene needs at least one light in order to be able to see the
        // layers. It does not necessarily have to be an ambient light though.
        const light = new THREE.AmbientLight(0xffffff, 0.2);
        this.ambientLight = light;
        this.webGlScene.add(light);
    }

    async updateMaterial(layerId: string, content: Object3DContent, updateDirectly = false) {
        const cachedLayerContent = this.layerContentCache[layerId];
        const contentChange = !cachedLayerContent || cachedLayerContent.material !== content.material;
        const isDirty = contentChange;
        const isLoading = this.layerIsLoading[layerId];
        const object = this.objectCache[layerId];

        if (((isDirty && !isLoading) || updateDirectly) && object) {
            if (!updateDirectly) {
                this.layerIsLoading[layerId] = true;
            }
            const loadingPromises: Array<Promise<void>> = [];

            // const qualityToUse = this.props.renderer.quality!;
            const resizeUrlIfPossible = (map: ImageAssetBackend) => {
                if (map.key) {
                    // TODO DESCR-218: revisit
                    // return createCdnImageUrl(map, qualityToUse.fraction * map.width);
                    return map.ppiUrl || map.url;
                } else {
                    // TODO Remove this once we make sure all stories have only assets with "key"
                    logWarning(`Did not found 'key' in layer '${layerId}', falling back to url: ${map.url}`);
                    return map.url;
                }
            };
            const object3DContent = content;
            const { type } = object3DContent.material;

            if (object3DContent.material.overwriteMaterial) {
                const getTextureAsset = (asset: ImageAssetBackend) => {
                    let result = assets.getAsset(asset.id);
                    if (!result) {
                        const resizedUrl = resizeUrlIfPossible(asset);
                        let resolve: () => void;
                        loadingPromises.push(
                            new Promise((r) => {
                                resolve = r;
                            })
                        );
                        result = assets.loadAsset(
                            {
                                id: asset.id,
                                content: resizedUrl,
                                category: AssetLibraryCategory.texture,
                                type: AssetLibraryType.texture,
                            },
                            () => {
                                resolve();
                            }
                        );
                    }
                    return result;
                };

                // Kern-Merge Richtung (b): das Override-Material wird lokal gebaut und
                // erst am Ende auf die Kind-Meshes des geladenen Modells angewendet — NICHT
                // auf die umschließende Gruppe (die kein Material rendert). Das war der
                // Grund, warum options (color/metalness/wireframe) bei geladenen .obj nie
                // sichtbar wurden (Befund KERN-MERGE-BEFUND, Punkt 2).
                let builtMaterial: THREE.Material | null = null;
                switch (type) {
                    // THREE JS Materials
                    case MaterialType.meshStandard: {
                        // MeshPhysicalMaterial ist ein Superset von MeshStandard
                        // (identisch bei Default-Physical-Params) — als Basis
                        // schaltet es Clearcoat + Sheen frei, ohne bestehende
                        // Standard-Materialien zu veraendern (Jans Original).
                        const material = new THREE.MeshPhysicalMaterial();
                        builtMaterial = material;
                        const standardMeshOptions = object3DContent.material.options as MeshStandardOptions;
                        if (
                            standardMeshOptions.metalnessMap &&
                            (standardMeshOptions as TODOAny).metalnessMapEnabled !== false
                        ) {
                            material.metalnessMap = getTextureAsset(standardMeshOptions.metalnessMap) ?? null;
                        }
                        if (
                            standardMeshOptions.roughnessMap &&
                            (standardMeshOptions as TODOAny).roughnessMapEnabled !== false
                        ) {
                            material.roughnessMap = getTextureAsset(standardMeshOptions.roughnessMap) ?? null;
                        }
                        if (standardMeshOptions.metalness !== undefined) {
                            material.metalness = standardMeshOptions.metalness;
                        }
                        // Port "HDRI": roughness steuert die Reflexionsschaerfe (0 =
                        // Spiegel). Faithful aus Jans Fassung, additiv im :true-Pfad.
                        if (standardMeshOptions.roughness !== undefined) {
                            material.roughness = standardMeshOptions.roughness;
                        }
                        if (standardMeshOptions.refractionRatio !== undefined) {
                            // "Brechungsverhaeltnis": refractionRatio (n1/n2) gibt es
                            // auf modernen three-Materialien nicht mehr — das Pendant
                            // ist ior (n2/n1), das beim Physical-Material die
                            // dielektrische Reflexionsstaerke (F0) steuert. 1/ratio,
                            // geklemmt auf threes ior-Bereich [1, 2.333]: Regler 1.0
                            // = keine Extra-Reflexe, kleinere Werte = kraeftigere
                            // Reflexe. Nur bei beruehrtem Regler — sonst behaelt das
                            // Material den ior-Default 1.5 (Bestand unveraendert).
                            const ratio = standardMeshOptions.refractionRatio;
                            if (ratio > 0) {
                                material.ior = Math.max(1, Math.min(2.333, 1 / ratio));
                            }
                        }
                        // Selbstleuchten (Glow) + Staerke der Umgebungs-Reflexion
                        // (Jans Original — fehlte im Port komplett)
                        const so = standardMeshOptions as TODOAny;
                        if (so.emissive) material.emissive = colorToThree(so.emissive);
                        if (so.emissiveIntensity !== undefined) material.emissiveIntensity = so.emissiveIntensity;
                        if (so.envMapIntensity !== undefined) {
                            material.envMapIntensity = so.envMapIntensity;
                            // Merker fuer den per-Frame-Scan in syncEnvironment:
                            // der wuerde den Wert sonst einen Frame spaeter auf
                            // die GLOBALE Umgebungs-Intensitaet zurueckdruecken
                            // (Jans Fund "Slider tut sich wenig"). Mit Merker
                            // rechnet der Scan global x eigenem Wert.
                            material.userData.__envIntensityOwn = so.envMapIntensity;
                        }
                        // Klarlack-Schicht + Stoff-Sheen — Physical-only, Default 0
                        // laesst ein reines Standard-Material unveraendert
                        if (so.clearcoat !== undefined) material.clearcoat = so.clearcoat;
                        if (so.clearcoatRoughness !== undefined) material.clearcoatRoughness = so.clearcoatRoughness;
                        if (so.sheen !== undefined) material.sheen = so.sheen;
                        if (so.sheenRoughness !== undefined) material.sheenRoughness = so.sheenRoughness;
                        if (so.sheenColor) material.sheenColor = colorToThree(so.sheenColor);
                        break;
                    }
                    case MaterialType.meshPhysical: {
                        // Glas (Jans Original): PBR-Basis + Transmission/IOR/Dicke —
                        // bricht die Szene (am besten mit geladenem HDRI-Environment).
                        const material = new THREE.MeshPhysicalMaterial();
                        builtMaterial = material;
                        const o = object3DContent.material.options as TODOAny;
                        if (o.metalnessMap && o.metalnessMapEnabled !== false) {
                            material.metalnessMap = getTextureAsset(o.metalnessMap) ?? null;
                        }
                        if (o.roughnessMap && o.roughnessMapEnabled !== false) {
                            material.roughnessMap = getTextureAsset(o.roughnessMap) ?? null;
                        }
                        material.metalness = o.metalness ?? 0;
                        material.roughness = o.roughness ?? 0;
                        material.transmission = o.transmission ?? 1;
                        material.ior = o.ior ?? 1.5;
                        material.thickness = o.thickness ?? 0.5;
                        if (o.clearcoat !== undefined) material.clearcoat = o.clearcoat;
                        if (o.clearcoatRoughness !== undefined) material.clearcoatRoughness = o.clearcoatRoughness;
                        // Volumen-Toenung (gefaerbtes/dichteres Glas)
                        if (o.attenuationColor) material.attenuationColor = colorToThree(o.attenuationColor);
                        if (o.attenuationDistance !== undefined) material.attenuationDistance = o.attenuationDistance;
                        // Dielektrisches Glanzlicht: Staerke + Toenung
                        if (o.specularIntensity !== undefined) material.specularIntensity = o.specularIntensity;
                        if (o.specularColor) material.specularColor = colorToThree(o.specularColor);
                        break;
                    }
                    case MaterialType.meshBasic: {
                        const material = new THREE.MeshBasicMaterial();
                        builtMaterial = material;
                        const bo = object3DContent.material.options as TODOAny;
                        if (bo.reflectivity !== undefined) material.reflectivity = bo.reflectivity;
                        break;
                    }
                    case MaterialType.meshLambert: {
                        const material = new THREE.MeshLambertMaterial();
                        builtMaterial = material;
                        const lo = object3DContent.material.options as TODOAny;
                        if (lo.emissive) material.emissive = colorToThree(lo.emissive);
                        if (lo.emissiveIntensity !== undefined) material.emissiveIntensity = lo.emissiveIntensity;
                        if (lo.reflectivity !== undefined) material.reflectivity = lo.reflectivity;
                        break;
                    }
                    case MaterialType.meshPhong: {
                        const material = new THREE.MeshPhongMaterial();
                        builtMaterial = material;
                        const po = object3DContent.material.options as TODOAny;
                        material.shininess = po.shininess || 0;
                        // Glanzlicht-Farbe + Reflexionsstaerke der EnvMap
                        if (po.specular) material.specular = colorToThree(po.specular);
                        if (po.reflectivity !== undefined) material.reflectivity = po.reflectivity;
                        break;
                    }
                    default:
                }

                if (builtMaterial) {
                    // Loses Typing wie im Bestand (object.material war any) — die Felder
                    // existieren je nach Material-Typ; für Basic/Lambert sind sie no-ops.
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const material = builtMaterial as any;
                    if (hasMeshOptions(content)) {
                        const options = object3DContent.material.options as MeshOptions;
                        material.wireframe = options.wireframe;
                        // Nur bei ECHTEM Alpha-Bedarf transparent (Jans Original):
                        // explizite transparent-Option, aktive alphaMap oder Glas-
                        // Transmission. Opake Materialien MUESSEN im opaken Pass
                        // bleiben — sonst fehlen sie in threes Transmission-Target
                        // und sind DURCH GLAS unsichtbar (Jans Lupen-Fund: Linse
                        // zeigte nur das HDRI, keine Objekte). Per-Frame-Fades
                        // schalten transparent bei Bedarf wieder ein (renderLayer).
                        {
                            const alphaNeeded =
                                !!(options as TODOAny).transparent ||
                                (!!(options as TODOAny).alphaMap &&
                                    (options as TODOAny).alphaMapEnabled !== false) ||
                                ((material as TODOAny).transmission ?? 0) > 0;
                            material.userData = material.userData || {};
                            material.userData.alphaNeeded = alphaNeeded;
                            material.transparent = alphaNeeded;
                        }
                        // colorToThree: unsere Color ist 0..255, THREE.Color 0..1 — die rohe
                        // Zuweisung object.material.color = options.color war fehlerhaft.
                        // Cast: cross-package Color-Identität (post-types src vs dist).
                        if (options.color) {
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            material.color = colorToThree(options.color as any);
                        }
                        // Nur bei aktiver Checkbox zuweisen — eine "deaktivierte"
                        // Map darf weder blenden noch (via alphaTest) ausstanzen.
                        if (options.alphaMap && (options as TODOAny).alphaMapEnabled !== false) {
                            material.alphaMap = getTextureAsset(options.alphaMap);
                        }
                        if (options.map && (options as TODOAny).mapEnabled !== false) {
                            material.map = getTextureAsset(options.map);
                        }
                        if (options.envMap && (options as TODOAny).envMapEnabled !== false) {
                            material.envMap = getTextureAsset(options.envMap);
                        }
                        // Gebackene Ambient Occlusion (Jans Original — fehlte im Port)
                        if ((options as TODOAny).aoMap && (options as TODOAny).aoMapEnabled !== false) {
                            material.aoMap = getTextureAsset((options as TODOAny).aoMap);
                            this.ensureSecondUv(object);
                        }
                        if ((options as TODOAny).aoMapIntensity !== undefined) {
                            material.aoMapIntensity = (options as TODOAny).aoMapIntensity;
                        }
                        // Emissions-/Light-Map (fehlten im Port — das Menü bot sie
                        // an, der Renderer wies sie nie zu). meshBasic ignoriert
                        // die Felder im Shader; das Menü blendet sie dort aus.
                        if ((options as TODOAny).emissiveMap && (options as TODOAny).emissiveMapEnabled !== false) {
                            material.emissiveMap = getTextureAsset((options as TODOAny).emissiveMap);
                            // three multipliziert die Map mit der Leuchtfarbe —
                            // deren Default ist SCHWARZ, die Map waere unsichtbar
                            // (Jans Fund "keine Veraenderung"). Ohne/mit schwarzer
                            // Leuchtfarbe neutral Weiss setzen: dann definiert die
                            // Map selbst, wo und in welcher Farbe es leuchtet.
                            const em = (options as TODOAny).emissive;
                            if (!em || (em.r === 0 && em.g === 0 && em.b === 0)) {
                                material.emissive = new THREE.Color(1, 1, 1);
                            }
                        }
                        if ((options as TODOAny).lightMap && (options as TODOAny).lightMapEnabled !== false) {
                            material.lightMap = getTextureAsset((options as TODOAny).lightMap);
                            this.ensureSecondUv(object);
                        }
                        if ((options as TODOAny).lightMapIntensity !== undefined) {
                            material.lightMapIntensity = (options as TODOAny).lightMapIntensity;
                        }
                    }
                    if (hasMeshBumpOptions(content)) {
                        const options = object3DContent.material.options as MeshOptionsBump;
                        if (options.bumpMap && (options as TODOAny).bumpMapEnabled !== false) {
                            material.bumpMap = getTextureAsset(options.bumpMap);
                        }
                        if (options.displacementMap && (options as TODOAny).displacementMapEnabled !== false) {
                            material.displacementMap = getTextureAsset(options.displacementMap);
                        }
                        if (options.normalMap && (options as TODOAny).normalMapEnabled !== false) {
                            material.normalMap = getTextureAsset(options.normalMap);
                        }
                        if (options.bumpScale !== undefined) {
                            material.bumpScale = options.bumpScale;
                        }
                        if (options.displacementScale !== undefined) {
                            material.displacementScale = options.displacementScale;
                        }
                        if (options.displacementBias !== undefined) {
                            material.displacementBias = options.displacementBias;
                        }
                    }

                    // Textur-Wiederholung/-Versatz (Jans Original — fehlte im
                    // Port): klont die Farb-Map mit RepeatWrapping und setzt
                    // repeat/offset; Rueckbau bei Default-Werten.
                    this.applyMapTiling(material, object3DContent.material);

                    // Schatten-Ausstanzung: der Schatten-Tiefenpass zeichnet die
                    // Geometrie sonst als Vollkoerper und ignoriert die Alpha-Map
                    // (Jans Fund: Loecher im Objekt, aber massiver Schatten).
                    // threes WebGLShadowMap stanzt nur bei alphaTest > 0 aus und
                    // ueberschreibt dabei sogar den alphaTest eines Custom-Depth-
                    // Materials mit dem des Beauty-Materials — der einzige
                    // tragfaehige Hebel ist also alphaTest am Material selbst.
                    // 0.05 verwirft nur praktisch unsichtbare Texel (Beauty-
                    // Blending bleibt weich), reisst aber die schwarzen Map-
                    // Bereiche aus Depth-Pass und Schatten.
                    if (material.alphaMap) {
                        material.alphaTest = 0.05;
                    }

                    // Auf alle Meshes anwenden. traverse() besucht auch den Knoten selbst,
                    // deckt also den object3dChild-Pfad (object == Mesh) mit ab.
                    // bakedMaterial-Meshes (Schach-Brett/-Figuren/-Labels) behalten
                    // ihre eigenen Materialien — sonst wird das ganze Set weiss.
                    // planarMirror-Reflector AUSNEHMEN (Jans Fund 26.08.2026):
                    // er haengt als Mesh im selben Teilbaum — die Zuweisung
                    // ueberschrieb seinen gepatchten Spiegel-Shader, jede
                    // Material-Regler-Bewegung (z. B. Emission) toetete die
                    // Spiegelung dauerhaft.
                    object.traverse((child: THREE.Object3D) => {
                        if (
                            (child as THREE.Mesh).isMesh &&
                            !(child as TODOAny).userData?.bakedMaterial &&
                            child.userData?.tiefenVorpass !== true &&
                            child.name !== "planarMirror"
                        ) {
                            (child as THREE.Mesh).material = material;
                        }
                    });
                }
            }

            // Shadow-Flags gehören auf die Meshes, nicht die umschließende Gruppe (deren
            // Flags propagieren nicht auf die Kind-Meshes).
            object.traverse((child: THREE.Object3D) => {
                if ((child as THREE.Mesh).isMesh) {
                    child.receiveShadow = object3DContent.material.receiveShadow;
                    child.castShadow = object3DContent.material.castShadow;
                }
            });

            this.layerContentCache[layerId] = content;

            // TODO Find a story that uses overwriteMaterial to test
            await Promise.allSettled(loadingPromises);
            if (!updateDirectly) {
                this.layerIsLoading[layerId] = false;
                this.props.renderer.loadedLayer(layerId, "material");
            }
        }
    }

    private tiefenVorpassAnlegen(obj: THREE.Object3D) {
        const meshes: THREE.Mesh[] = [];
        obj.traverse((o) => {
            const m = o as THREE.Mesh & { isSkinnedMesh?: boolean };
            // Skinned/Morph-Meshes verformen sich im Shader — ein statischer
            // Vorpass laege daneben; die bleiben beim alten Verhalten.
            if (m.isMesh && !m.isSkinnedMesh && !m.morphTargetInfluences && m.userData.tiefenVorpass !== true) {
                meshes.push(m);
            }
        });
        for (const mesh of meshes) {
            const quelle = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material;
            const vorpass = new THREE.Mesh(
                mesh.geometry,
                new THREE.MeshBasicMaterial({
                    colorWrite: false,
                    depthWrite: true,
                    transparent: true,
                    side: quelle?.side ?? THREE.FrontSide,
                })
            );
            vorpass.name = "tiefenVorpass";
            vorpass.userData.tiefenVorpass = true;
            vorpass.visible = false;
            vorpass.castShadow = false;
            vorpass.receiveShadow = false;
            vorpass.raycast = () => {};
            mesh.add(vorpass);
            mesh.userData.tiefenVorpassMesh = vorpass;
        }
    }

    // AO- und Light-Maps sampeln den 2. UV-Satz. Geladene OBJ/FBX-Modelle und
    // die parametrischen Primitive haben meist nur den 1. — ohne Kopie wirkt
    // die Map lautlos gar nicht (Jans Original; dort uv→uv2 fuer three r139,
    // hier uv→uv1: three >= r151 liest Kanal 1 aus dem Attribut "uv1").
    ensureSecondUv(root: THREE.Object3D | null | undefined) {
        if (!root) return;
        root.traverse((o: TODOAny) => {
            const g = o.geometry;
            if (g && g.attributes && g.attributes.uv && !g.attributes.uv1) {
                g.setAttribute("uv1", g.attributes.uv);
            }
        });
    }

    // Textur-Wiederholung/-Versatz (Jans Original, 1:1): three teilt eine
    // Textur-Instanz zwischen Materialien — fuer Tiling wird die Farb-Map
    // pro Material geklont (RepeatWrapping) und repeat/offset dort gesetzt.
    // Bei Default-Werten wird der Klon zurueckgebaut. Der animierte Textur-
    // Flow (textureScrollX/Y) nutzt denselben Klon.
    applyMapTiling(material: TODOAny, materialModel: TODOAny) {
        if (!material) return;
        const opts = materialModel?.options;
        const rx = Number(opts?.textureRepeatX ?? 1) || 1;
        const ry = Number(opts?.textureRepeatY ?? 1) || 1;
        const ox = Number(opts?.textureOffsetX ?? 0) || 0;
        const oy = Number(opts?.textureOffsetY ?? 0) || 0;
        const sx = Number(opts?.textureScrollX ?? 0) || 0;
        const sy = Number(opts?.textureScrollY ?? 0) || 0;
        const tiled = rx !== 1 || ry !== 1 || ox !== 0 || oy !== 0 || sx !== 0 || sy !== 0;
        const ud = (material.userData = material.userData || {});

        if (!tiled) {
            if (ud.__tileClone) {
                if (ud.__tileSrc && material.map === ud.__tileClone) {
                    material.map = ud.__tileSrc;
                }
                ud.__tileClone.dispose?.();
                ud.__tileClone = null;
                ud.__tileSrc = null;
                material.needsUpdate = true;
            }
            return;
        }

        const current = material.map;
        if (!current) return;
        const src = ud.__tileSrc && current === ud.__tileClone ? ud.__tileSrc : current;

        let clone = ud.__tileClone;
        if (!clone || ud.__tileSrc !== src) {
            clone?.dispose?.();
            clone = src.clone();
            clone.wrapS = THREE.RepeatWrapping;
            clone.wrapT = THREE.RepeatWrapping;
            clone.needsUpdate = true;
            ud.__tileClone = clone;
            ud.__tileSrc = src;
            material.needsUpdate = true;
        }
        // Klon-Bild mit der (async geladenen) Quelle synchron halten
        if (clone.image !== src.image) {
            clone.image = src.image;
            clone.needsUpdate = true;
        }
        clone.repeat.set(rx, ry);
        clone.offset.set(ox, oy);
        clone.needsUpdate = true;
        material.map = clone;
    }

    // Ratio-Quelle fuer "Match texture ratio" (Jans Original, 1:1): die Maße
    // der Farb-Textur kommen synchron aus den ASSET-Metadaten (options.map).
    planeTextureRatioSource(content: Object3DContent): { w: number; h: number } | null {
        const opts = (content as TODOAny)?.material?.options;
        const map = opts && opts.mapEnabled !== false ? opts.map : null;
        return map?.width && map?.height ? { w: map.width, h: map.height } : null;
    }

    // Plane mit fitToTexture: Hoehe aus dem Seitenverhaeltnis der Textur
    // ableiten (Breite bleibt). Andere Primitive / keine Textur: unveraendert.
    planeParamsWithTextureRatio(content: Object3DContent) {
        const params = content.primitiveParams as TODOAny;
        if (content.primitive !== "plane" || !params?.fitToTexture) return params;
        const tex = this.planeTextureRatioSource(content);
        if (!tex) return params;
        const width = params.width ?? (PRIMITIVE_DEFAULTS.plane as TODOAny)?.width ?? 700;
        return { ...params, height: width * (tex.h / tex.w) };
    }

    updateObject(layerId: string, content: Object3DContent, callback: (obj: THREE.Mesh) => void) {
        // Preset reset (Jans Original): kein Primitive und keine Model-URL →
        // nichts rendern. Caches fallen lassen und den Tree neu bauen, damit
        // das bestehende Mesh verschwindet. Verhindert auch, dass ein leerer
        // Layer layerIsLoading=true setzt, ohne dass je ein Loader-Pfad feuert
        // — das blockierte sonst jeden späteren Vorlagen-Wechsel dauerhaft.
        if (!content.primitive && !content.url) {
            if (this.layerState3d[layerId] || this.layerMeshes[layerId]) {
                delete this.layerState3d[layerId];
                delete this.layerMeshes[layerId];
                delete this.objectCache[layerId];
                delete this.layerContentCache[layerId];
                this.invalidateTree();
            }
            this.layerIsLoading[layerId] = false;
            return;
        }

        const animatable3DLayer = this.layerState3d[layerId];
        const cachedLayerContent = this.layerContentCache[layerId] as Object3DContent | undefined;
        // reload when the url changed — or (Jans Original) wenn sich bei
        // Built-in-Primitives die Form oder irgendein Parameter ändert
        // (Preset→Preset wechselt keine URL; ohne diesen Check baut der
        // Renderer nie um und "Vorlage austauschen" bleibt wirkungslos).
        // fitToTexture-Plane: auch neu bauen, wenn sich die Farb-Textur (und
        // damit das abgeleitete Seitenverhaeltnis) aendert (Jans planeFitSig).
        const planeFitSig = (c?: Object3DContent) => {
            if (c?.primitive !== "plane" || !(c?.primitiveParams as TODOAny)?.fitToTexture) return "";
            const t = this.planeTextureRatioSource(c);
            return t ? `${t.w}x${t.h}` : "";
        };
        const primitiveChange =
            content.primitive !== cachedLayerContent?.primitive ||
            JSON.stringify(content.primitiveParams) !==
                JSON.stringify(cachedLayerContent?.primitiveParams) ||
            planeFitSig(content) !== planeFitSig(cachedLayerContent as Object3DContent);
        const contentChange =
            // TODO Change to .key once we are sure all stories have only assets with .key
            !cachedLayerContent || content.url !== cachedLayerContent.url || primitiveChange;
        const isDirty = !animatable3DLayer || contentChange;
        const isLoading = this.layerIsLoading[layerId];

        // Live-Material-Aenderungen (Farbe/Metalness/Rauheit/…) OHNE Geometrie-
        // Rebuild. Jans Original verglich Material-REFERENZEN — der Adapter
        // serialisiert aber bei jeder MST-Aenderung frischen Content, Referenzen
        // sind hier immer neu. Daher Wert-Vergleich per JSON-Signatur; nur im
        // Nicht-Dirty-Fall (der Bau-Pfad wendet das Material selbst an) und nie
        // fuer Baked-Primitive (magnifier/pin behalten ihre Multi-Materialien).
        const materialSig = JSON.stringify(content.material ?? null);
        if (
            !isDirty &&
            !isLoading &&
            this.objectCache[layerId] &&
            this.materialSigCache[layerId] !== materialSig &&
            !(content.primitive && BAKED_PRIMITIVES.has(content.primitive))
        ) {
            void this.updateMaterial(layerId, content, true);
        }
        this.materialSigCache[layerId] = materialSig;

        if (isDirty && !isLoading) {
            this.layerIsLoading[layerId] = true;

            // TODO DESCR-218: revisit
            // const url = createCdnAssetUrl(content);
            // Kompat-Shim fuer BESTANDS-Scrollys: der Objekt-Platzhalter (Globus)
            // wurde frueher als Vite-Import gespeichert und backte damit die
            // Dev-URL `/src/proeditor/assets/models/globe.obj` in den Scrolly ein
            // — im Viewer/Build ein 404 (Jans Fund 01.08.2026). Auf den stabilen
            // Public-Pfad `/models/<datei>` uebersetzen; neue Scrollys speichern
            // diesen direkt (LayerContent.object3DContent).
            const url = content.url?.startsWith("/src/proeditor/assets/models/")
                ? content.url.replace("/src/proeditor/assets/models/", "/models/")
                : content.url;
            // Editor-Notfall-Ablage ("local-object:<id>", IndexedDB): three's
            // Loader kennt dieses Schema nicht — Konsole lief voll mit
            // "URL scheme 'local-object' is not supported", die Ebene blieb
            // leer (Jans GLB-Fund 09.08.2026). Erst zur Objekt-URL aufloesen;
            // ohne Resolver (Reader) bleibt die URL unveraendert und der
            // normale Fehlerpfad der Loader greift.
            const ladbareUrl: Promise<string> =
                typeof url === "string" && url.startsWith("local-") && WebGLAssets.resolveLocalMediaUrl
                    ? WebGLAssets.resolveLocalMediaUrl(url).then((u) => u || url)
                    : Promise.resolve(url);

            const objLoaded = async (obj: TODOAny) => {
                // object does not have any children, abort
                if (!obj.children) {
                    logDebug("Object does not have any children");
                }

                // Mobile-Textur-Deckel (26.08.2026): eingebettete Modell-
                // Texturen (GLB-Porsche & Co.) kommen in Originalgroesse aus
                // dem Loader — auf Touch-Geraeten alle Material-Maps vor dem
                // ersten Render auf 2048 Kante begrenzen.
                if (istMobilesGeraet()) {
                    obj.traverse?.((kind: TODOAny) => {
                        if (!kind?.isMesh) return;
                        const mats = Array.isArray(kind.material) ? kind.material : [kind.material];
                        for (const m of mats) {
                            if (!m) continue;
                            for (const feld of [
                                "map",
                                "normalMap",
                                "roughnessMap",
                                "metalnessMap",
                                "aoMap",
                                "emissiveMap",
                                "bumpMap",
                                "alphaMap",
                            ]) {
                                if (m[feld]) deckleTexturFuerMobil(m[feld]);
                            }
                        }
                    });
                }

                // Auto-fit importierter Modelle in den sicheren Bereich (Jans
                // 1.0, 1:1): viele Modelle sind in winzigen Einheiten gebaut
                // (glTF-Duck ~2 Einheiten, die Slide misst 4096) und waeren
                // sonst unsichtbar klein — oder riesig (Fox = 250). Groesste
                // Ausdehnung auf ~45% der Slide-Breite bringen und auf den
                // Ursprung zentrieren. In die KIND-Transformationen eingebacken
                // — NICHT obj.scale, das ueberschreibt der Renderer jedes Bild
                // mit der Layer-Skalierung; der Skalieren-Regler multipliziert
                // also weiter obendrauf. Primitives sind bereits in sinnvoller
                // Groesse gebaut und bleiben aussen vor. Galt im Port bisher
                // nur fuer GLB/GLTF (im Loader-Zweig) — OBJ und FBX kamen roh
                // herein (Jans Fund); jetzt einheitlich HIER fuer alle Formate.
                if (!content.primitive && Array.isArray(obj.children) && obj.children.length > 0) {
                    // in obj-lokalen Koordinaten messen (eigene Transform erst
                    // zuruecksetzen; der Renderer setzt sie spaeter ohnehin)
                    obj.position.set(0, 0, 0);
                    obj.rotation.set(0, 0, 0);
                    obj.scale.set(1, 1, 1);
                    obj.updateMatrixWorld(true);
                    const box = new THREE.Box3().setFromObject(obj);
                    if (!box.isEmpty()) {
                        const size = box.getSize(new THREE.Vector3());
                        const maxDim = Math.max(size.x, size.y, size.z);
                        if (Number.isFinite(maxDim) && maxDim > 0) {
                            // ~45% der Slide: praesent, bleibt aber auch bei
                            // Letterbox-/Ultrawide-Formaten im sichtbaren Bild
                            const target = RendererStore.SLIDE_WIDTH * 0.45;
                            const factor = target / maxDim;
                            const center = box.getCenter(new THREE.Vector3());
                            // p' = factor * (p - center): um den Mittelpunkt
                            // skalieren, auf den Ursprung zentrieren.
                            const fit = new THREE.Matrix4()
                                .makeTranslation(
                                    -center.x * factor,
                                    -center.y * factor,
                                    -center.z * factor
                                )
                                .multiply(new THREE.Matrix4().makeScale(factor, factor, factor));
                            for (const child of [...obj.children]) {
                                child.applyMatrix4(fit);
                            }
                        }
                    }
                }

                let index = 0;
                this.objectCache[layerId] = obj;
                if (obj.material) {
                    obj.material.transparent = true;
                }
                obj.traverse((child: TODOAny) => {
                    const storyforgerId = `${layerId}-${index}`;
                    if (child.isMesh) {
                        child.castShadow = true;
                        child.receiveShadow = true;
                        child.material.transparent = true;
                    }
                    child.storyforgerId = storyforgerId;
                    this.objectCache[storyforgerId] = child;
                    index += 1;
                });

                // TIEFEN-VORPASS fuer geladene Modelle (Jans Schuh 14.10.2026:
                // beim Ein-/Ausblenden ein Mosaik aus Texturschnipseln). Beim
                // Faden schreibt das Modell keine Tiefe (s. renderLayer) — ein
                // verschachtelter Scan malt dann Innen- und Rueckseiten in
                // Dreiecks-Reihenfolge uebereinander. Der Vorpass zeichnet
                // unmittelbar davor nur die Tiefe; das Modell selbst traegt so
                // nur noch seine vorderste Flaeche bei. Sichtbar nur beim Faden.
                // Nach der Index-Vergabe angehaengt (storyforgerIds bleiben).
                if (!content.primitive) this.tiefenVorpassAnlegen(obj as THREE.Object3D);

                callback(obj);
                this.uploadToGpu(obj);
                await this.updateMaterial(layerId, content, true);
                this.props.renderer.loadedLayer(layerId, url);

                this.layerState3d[layerId] = obj;
                this.layerIsLoading[layerId] = false;
                // cache content
                this.layerContentCache[layerId] = content;
                this.layerMeshes[layerId] = obj;
            };

            // Port "3D-Text": extrudierte Typografie braucht eine async geladene
            // Typeface — kann nicht durch die url/extension-Strecke unten.
            // Guarded: feuert nur fuer text3d/number3d (0 Bestands-Scrollys,
            // Gate 2026-07-22). Registriert das Mesh SELBST (nicht via objLoaded
            // -> kein updateMaterial), damit text3d vom Material-System getrennt
            // und additiv-sicher bleibt.
            const prim3d = content.primitive;
            if (prim3d === "text3d" || prim3d === "number3d") {
                const p = (content.primitiveParams || {}) as TODOAny;
                const fontUrl = font3dUrl(p.font as string | undefined);
                const registerText3d = (font: TODOAny) => {
                    // number3d startet bei `start`; renderLayer baut die Ziffern
                    // pro Bild neu, waehrend der Playhead ueber den Layer laeuft.
                    const initial =
                        prim3d === "number3d" ? number3dString(p, 0) : p.text || "Text";
                    const mesh = new THREE.Mesh(
                        makeText3dGeometry(initial, p, font),
                        new THREE.MeshStandardMaterial()
                    );
                    mesh.name = prim3d;
                    const group = new THREE.Group();
                    // aufgeloeste Schrift am Group merken — der per-Frame-
                    // number3d-Neuaufbau nutzt dieselbe Typeface.
                    group.userData.__font3d = font;
                    group.add(mesh);
                    this.objectCache[layerId] = group as TODOAny;
                    callback(group as TODOAny);
                    // MATERIAL ANWENDEN (Jans Fund 08.09.2026, Counter): Dieser
                    // Pfad umging das Material-System — jede Farbzuweisung galt
                    // nur bis zum naechsten Neuaufbau (Parameter-Aenderung,
                    // Neuoeffnen), dann stand wieder das weisse Standardmaterial,
                    // auch im Viewer. Jetzt wie bei jedem anderen 3D-Objekt
                    // (objLoaded): gespeichertes Material direkt anwenden.
                    this.materialSigCache[layerId] = JSON.stringify(content.material ?? null);
                    void this.updateMaterial(layerId, content, true).catch(() => {
                        /* Material ist Kuer — der Text bleibt sichtbar */
                    });
                    this.uploadToGpu(group as TODOAny);
                    this.props.renderer.loadedLayer(layerId, fontUrl);
                    this.layerState3d[layerId] = group as TODOAny;
                    this.layerIsLoading[layerId] = false;
                    this.layerContentCache[layerId] = content;
                    this.layerMeshes[layerId] = group as TODOAny;
                };
                const cachedFont = this.text3dFonts[fontUrl];
                if (cachedFont) {
                    registerText3d(cachedFont);
                } else {
                    new FontLoader().load(
                        fontUrl,
                        (font: TODOAny) => {
                            this.text3dFonts[fontUrl] = font;
                            registerText3d(font);
                        },
                        undefined,
                        (err: TODOAny) => {
                            this.layerIsLoading[layerId] = false;
                            logError(err as Error);
                        }
                    );
                }
                return;
            }

            // Port "Primitive": statische Ein-Material-Formen (box/sphere/plane/...)
            // synchron aus buildPrimitiveGeometry bauen — kein Asset-Load. Danach
            // content.material via updateMaterial(updateDirectly) auf die Mesh
            // anwenden (Kern-Merge-Pfad, traversiert die Group). Guarded auf
            // WIRED_PRIMITIVES; 0 Bestands-Scrollys nutzen Primitive -> additiv-sicher.
            if (prim3d && WIRED_PRIMITIVES.has(prim3d)) {
                const geom = buildPrimitiveGeometry(prim3d, this.planeParamsWithTextureRatio(content) || {});
                const mesh = new THREE.Mesh(geom, new THREE.MeshStandardMaterial());
                mesh.name = prim3d;
                const group = new THREE.Group();
                group.add(mesh);
                this.objectCache[layerId] = group as TODOAny;
                callback(group as TODOAny);
                this.uploadToGpu(group as TODOAny);
                this.props.renderer.loadedLayer(layerId, `primitive:${prim3d}`);
                this.layerState3d[layerId] = group as TODOAny;
                this.layerIsLoading[layerId] = false;
                this.layerContentCache[layerId] = content;
                this.layerMeshes[layerId] = group as TODOAny;
                // Material aus content.material anwenden (traversiert die Group -> Mesh).
                this.updateMaterial(layerId, content, true);
                return;
            }

            // Voll-3D-Schach: Gruppe (64 Brett-Boxen + Figuren-GLBs) async
            // gebaut, pro Frame ueber den progress-Kanal posiert (Jans 1.0).
            // Eigener Pfad — weder Einzel-Geometrie noch Modell-Load, und VOR
            // dem generischen BAKED-Zweig (chess ist BAKED, braucht aber den
            // async GLB-Build statt buildPrimitiveGeometry). KEIN updateMaterial;
            // jedes Mesh traegt zusaetzlich userData.bakedMaterial. Layer.setObject
            // ueberspringt chess, sonst fluten ~96 Kind-Layer die Timeline.
            if (prim3d === "chess") {
                buildChessGroup(content)
                    .then((grp) => {
                        this.objectCache[layerId] = grp as TODOAny;
                        callback(grp as TODOAny);
                        this.uploadToGpu(grp as TODOAny);
                        this.props.renderer.loadedLayer(layerId, "primitive:chess");
                        this.layerState3d[layerId] = grp as TODOAny;
                        this.layerIsLoading[layerId] = false;
                        this.layerContentCache[layerId] = content;
                        this.layerMeshes[layerId] = grp as TODOAny;
                    })
                    .catch((err) => {
                        this.layerIsLoading[layerId] = false;
                        this.props.renderer.loadedLayer(layerId, "primitive:chess");
                        logError(err as Error);
                    });
                return;
            }

            // Feature-Paritaet: Baked-Multi-Material-Primitive (magnifier = Glas-
            // Linse + Metall-Rahmen/Griff; pin = Body + Accent). Geometrie hat
            // Material-Gruppen -> Mesh mit Material-ARRAY. KEIN updateMaterial
            // (isBaked) -> content.material ueberschreibt die gebackenen nicht.
            if (prim3d && BAKED_PRIMITIVES.has(prim3d)) {
                const p = (content.primitiveParams || {}) as TODOAny;
                const geom = buildPrimitiveGeometry(prim3d, p);
                const mats = prim3d === "magnifier" ? makeMagnifierMaterials(p) : makePinMaterials(p);
                const mesh = new THREE.Mesh(geom, mats);
                mesh.name = prim3d;
                const group = new THREE.Group();
                group.add(mesh);
                this.objectCache[layerId] = group as TODOAny;
                callback(group as TODOAny);
                this.uploadToGpu(group as TODOAny);
                this.props.renderer.loadedLayer(layerId, `primitive:${prim3d}`);
                this.layerState3d[layerId] = group as TODOAny;
                this.layerIsLoading[layerId] = false;
                this.layerContentCache[layerId] = content;
                this.layerMeshes[layerId] = group as TODOAny;
                return;
            }

            if (content.extension === "obj") {
                // Lazy-load OBJLoader chunk (M2 Plan-1.9-Phase-1-CP2): only fetched
                // when an object3d-Layer with .obj extension is instantiated.
                import('three/examples/jsm/loaders/OBJLoader.js').then(async ({ OBJLoader }) => {
                    const objLoader = new OBJLoader();
                    // MIT Fehler-Callback: ohne ihn liess ein 404 (z. B. tote
                    // eingebackene Asset-URL) den Layer fuer immer in
                    // layerIsLoading haengen — nichts erschien, kein Log
                    // (Jans Komplex-Scrolly 01.08.2026). Gilt genauso fuer FBX.
                    objLoader.load(await ladbareUrl, objLoaded, undefined, (err) => {
                        logError(new Error(`OBJ '${url}' konnte nicht geladen werden: ${err}`));
                        this.layerIsLoading[layerId] = false;
                        this.props.renderer.loadedLayer(layerId, url);
                    });
                }).catch((err) => {
                    logError(new Error(`Failed to load OBJLoader chunk: ${err}`));
                    this.layerIsLoading[layerId] = false;
                    this.props.renderer.loadedLayer(layerId, url);
                });
            }

            if (content.extension === "json") {
                // @ts-expect-error ts-migrate(2339) FIXME: Property 'JSONLoader' does not exist on type 'type... Remove this comment to see the full error message
                const jsonLoader = new THREE.JSONLoader();
                jsonLoader.load(url, (geometry: TODOAny, materials: TODOAny) => {
                    const object = new THREE.Mesh(geometry, materials);
                    objLoaded(object);
                });
            }

            if (content.extension === "fbx") {
                // Lazy-load FBXLoader chunk (M2 Plan-1.9-Phase-1-CP2).
                import('three/examples/jsm/loaders/FBXLoader.js').then(async ({ FBXLoader }) => {
                    const fbxLoader = new FBXLoader();
                    fbxLoader.load(await ladbareUrl, objLoaded, undefined, (err) => {
                        logError(new Error(`FBX '${url}' konnte nicht geladen werden: ${err}`));
                        this.layerIsLoading[layerId] = false;
                        this.props.renderer.loadedLayer(layerId, url);
                    });
                }).catch((err) => {
                    logError(new Error(`Failed to load FBXLoader chunk: ${err}`));
                    this.layerIsLoading[layerId] = false;
                    this.props.renderer.loadedLayer(layerId, url);
                });
            }

            // GLTF / GLB — modern Web 3D format. Smaller than FBX, supports
            // PBR materials and embedded animations.
            if (content.extension === "gltf" || content.extension === "glb") {
                // Lazy-load GLTFLoader chunk (M2 Plan-1.9-Phase-1-CP2): only fetched
                // when an object3d-Layer with .gltf/.glb extension is instantiated.
                import('three/examples/jsm/loaders/GLTFLoader.js').then(async ({ GLTFLoader }) => {
                    const gltfLoader = new GLTFLoader();
                    gltfLoader.load(
                        await ladbareUrl,
                        (gltf) => {
                            const innerScene = gltf.scene || gltf.scenes?.[0];
                            if (!innerScene) {
                                logError(new Error(`GLTF '${url}' has no scene`));
                                this.layerIsLoading[layerId] = false;
                                this.props.renderer.loadedLayer(layerId, url);
                                return;
                            }

                            // Wrapper-Group als layerMesh; das Auto-Fit auf die
                            // Slide-Groesse passiert seit dem OBJ/FBX-Abgleich
                            // EINHEITLICH in objLoaded (Jans 1.0-Block) — der
                            // fruehere GLB-eigene Fit (TARGET_SIZE 1500) haette
                            // sonst doppelt normiert.
                            const wrapper = new THREE.Group();
                            wrapper.add(innerScene);

                            // Stash GLTF animations on the wrapper so they can be
                            // hooked up to scroll progress later if needed.
                            if (gltf.animations && gltf.animations.length > 0) {
                                (wrapper as TODOAny).gltfAnimations = gltf.animations;
                            }
                            objLoaded(wrapper);
                        },
                        undefined,
                        (error) => {
                            logError(new Error(`Failed to load GLTF '${url}': ${error}`));
                            this.layerIsLoading[layerId] = false;
                            this.props.renderer.loadedLayer(layerId, url);
                        },
                    );
                }).catch((err) => {
                    logError(new Error(`Failed to load GLTFLoader chunk: ${err}`));
                    this.layerIsLoading[layerId] = false;
                    this.props.renderer.loadedLayer(layerId, url);
                });
            }
        }
    }

    // Port "Licht-Schatten-Qualitaet" (Jans configureLightShadow, 1:1): scharfe
    // 2048er-Maps (Spot nach Kegelwinkel skaliert) + normalBias gegen Self-
    // Shadow-Acne. Bewusst KEIN shadow.bias: der ist in normalisierter Tiefe
    // und wird bei dieser Szenen-Skala (~4096 Einheiten, far 8000-20000) zu
    // einem riesigen Welt-Offset, der flache/duenne Schatten ganz wegschiebt.
    // normalBias verschiebt den Empfaenger in WELT-Einheiten entlang seiner
    // Normalen — killt das koernige Acne-Muster (Jans Fund am extrudierten
    // SVG), ohne echte Schatten zu "peter-pannen". Invers zur Aufloesung
    // skaliert (feinere Maps = kleinere Texel = weniger Offset noetig).
    // Entsorgt eine bereits allozierte (kleinere) Map, damit die neue Groesse
    // wirksam wird.
    configureLightShadow(light: TODOAny) {
        const shadowMapSize = light.isSpotLight ? spotShadowMapSize(light.angle ?? 0) : 2048;
        light.shadow.mapSize.set(shadowMapSize, shadowMapSize);
        light.shadow.normalBias = 8 * (2048 / shadowMapSize);
        if (light.shadow.map) {
            light.shadow.map.dispose();
            light.shadow.map = null;
        }
    }

    updateLight(layerId: string, content: LightContent) {
        if (!this.webGlScene) {
            throw new Error("Called RendererWebGL#updateLight before calling init.");
        }

        const light: THREE.PointLight | THREE.DirectionalLight = this.lightState[layerId];
        const lightContent: LightContent = content;
        // WERT-Vergleich statt Referenz (Jans Original verglich MST-Node-
        // Referenzen; der Adapter serialisiert aber bei JEDER Aenderung frischen
        // Content — Referenzen sind hier immer neu). Mit Referenzvergleich wurde
        // das Licht bei JEDEM Frame neu gebaut: Schattenmap-Allokation pro Frame,
        // Dauer-loadedLayer-Logs und ein Alt/Neu-Wettrennen beim Einhaengen in
        // den Tree (Szene malt veraltete Licht-Instanz).
        const lightSig = JSON.stringify(lightContent);
        const contentChange = this.lightSigCache[layerId] !== lightSig;
        const isDirty = !light || contentChange;

        this.lightSigCache[layerId] = lightSig;
        this.layerContentCache[layerId] = lightContent;

        const SPHERE_SIZE = 100;

        if (isDirty) {
            const color = new THREE.Color(lightContent.color.r, lightContent.color.g, lightContent.color.b);

            let newLight:
                | THREE.PointLight
                | THREE.SpotLight
                | THREE.DirectionalLight
                | THREE.AmbientLight
                | THREE.HemisphereLight;
            if (lightContent.type === LightType.pointLight) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const pointLightContent: PointLightContent = content as any;
                newLight = new THREE.PointLight(
                    color,
                    pointLightContent.intensity,
                    pointLightContent.distance,
                    pointLightContent.decay
                );
                newLight.castShadow = lightContent.castShadow;
                // Schattenkamera immer vorkonfigurieren (auch bei castShadow
                // off), sonst startet ein spaeteres Einschalten auf Mini-Bounds.
                this.configureLightShadow(newLight);
                newLight.shadow.camera.near = 1;
                newLight.shadow.camera.far = Math.max(pointLightContent.distance || 0, 6000);
            }

            if (lightContent.type === LightType.spotLight) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const spotLightContent: SpotLightContent = content as any;
                newLight = new THREE.SpotLight(
                    color,
                    spotLightContent.intensity,
                    spotLightContent.distance,
                    // three.js SpotLight.angle ist nur bis PI/2 gueltig; darueber
                    // kippt der Kegel (cos<0) und die Schattenkamera (fov=2*angle)
                    // bricht. Clamp knapp unter PI/2 (Jans Original).
                    Math.min(spotLightContent.angle, MAX_SPOT_ANGLE),
                    spotLightContent.penumbra,
                    spotLightContent.decay
                );
                newLight.castShadow = lightContent.castShadow;
                this.configureLightShadow(newLight);
                newLight.shadow.camera.near = 1;
                newLight.shadow.camera.far = Math.max(spotLightContent.distance || 0, 8000);
                // penumbra treibt die Kantenweichheit des GEWORFENEN Schattens
                newLight.shadow.radius = spotShadowRadius(spotLightContent.penumbra);
            }

            if (lightContent.type === LightType.directionalLight) {
                newLight = new THREE.DirectionalLight(
                    color,
                    lightContent.intensity * LEGACY_DIRECTIONAL_INTENSITY_SCALE
                );
                newLight.castShadow = lightContent.castShadow;
                this.configureLightShadow(newLight);
                // Directional nutzt eine Ortho-Schattenkamera; die three-Default-
                // Bounds (±5) decken die ~4096er-Szene nicht — aufziehen. Die
                // Seitenlaenge ist hier noch in SLIDE-Einheiten; der Licht-Pass
                // in animate() rechnet sie pro Bild auf Welt-Einheiten um
                // (scaleDirectionalShadowFrustum), sobald `scaleFactor` bekannt ist.
                {
                    const cam = newLight.shadow.camera as THREE.OrthographicCamera;
                    cam.near = 1;
                    cam.far = 20000;
                    cam.left = -DIRECTIONAL_SHADOW_EXTENT;
                    cam.right = DIRECTIONAL_SHADOW_EXTENT;
                    cam.top = DIRECTIONAL_SHADOW_EXTENT;
                    cam.bottom = -DIRECTIONAL_SHADOW_EXTENT;
                    cam.updateProjectionMatrix();
                }
            }

            // Globale Lichter (Jans Original 1:1): Ambient und Hemisphere haben
            // weder Position noch Schatten — ohne diese Zweige blieb newLight
            // undefined, der Layer bekam NIE ein three-Licht und alle Regler
            // liefen ins Leere (Jans Fund "bei Slider-Bewegung aendert sich
            // nichts").
            if (lightContent.type === LightType.ambientLight) {
                newLight = new THREE.AmbientLight(color, lightContent.intensity);
            }
            if (lightContent.type === LightType.hemisphereLight) {
                const gc = (lightContent as TODOAny).groundColor;
                const groundColor = new THREE.Color(
                    (gc?.r ?? 38) / 255,
                    (gc?.g ?? 38) / 255,
                    (gc?.b ?? 38) / 255
                );
                newLight = new THREE.HemisphereLight(color, groundColor, lightContent.intensity);
            }

            // Beim Umbau (Wert-/Typ-Aenderung) das neue Licht SOFORT an Ort und
            // Stelle des alten einwechseln: Parent + Transform uebernehmen und
            // das alte abhaengen. Sonst haengt das neue Licht bis zum naechsten
            // Tree-Einsammeln unskaliert im Szenen-Root — Position/Drahtgitter
            // springen wild (Jans Fund "neu platziertes Licht sieht im Orbit
            // falsch aus / Werte aendern wirbelt die Darstellung durcheinander").
            if (light && (light as TODOAny).parent) {
                const oldLight = light as TODOAny;
                newLight!.position.copy(oldLight.position);
                newLight!.rotation.copy(oldLight.rotation);
                newLight!.scale.copy(oldLight.scale);
                oldLight.parent.add(newLight!);
                if ((oldLight as TODOAny).target?.parent) {
                    (oldLight as TODOAny).target.parent.remove((oldLight as TODOAny).target);
                }
                oldLight.parent.remove(oldLight);
            }
            this.lightState[layerId] = newLight! as TODOAny;
            this.layerMeshes[layerId] = newLight! as TODOAny;
            this.props.renderer.loadedLayer(layerId, "light");
        }

        const lightHelper = this.lightHelpers[layerId];
        const lightShadowHelper = this.lightShadowHelpers[layerId];

        // Globale Lichter (Ambient/Hemisphere) haben keine Position und keine
        // Schattenkamera → kein Gizmo/Helper. Ohne dieses Gate lief der Switch
        // unten in den default-Fall (Helper undefined) und `light.shadow.camera`
        // existiert bei Ambient nicht → Exception in JEDEM Frame → Szene
        // eingefroren, sobald showLights aktiv war. (Jans Original, 1:1)
        const isGlobalLight =
            lightContent.type === LightType.ambientLight || lightContent.type === LightType.hemisphereLight;

        if ((!this.props.renderer.showLights || isGlobalLight) && lightHelper) {
            this.webGlScene.remove(lightHelper);
            this.gizmoScene?.remove(lightHelper);
            if (lightShadowHelper) this.webGlScene.remove(lightShadowHelper);
            this.lightHelpers[layerId] = null;
            this.lightShadowHelpers[layerId] = null;
        }

        if (this.props.renderer.showLights && !isGlobalLight && (isDirty || !lightHelper)) {
            if (lightHelper) {
                this.webGlScene.remove(lightHelper);
                this.gizmoScene?.remove(lightHelper);
            }
            if (lightShadowHelper) this.webGlScene.remove(lightShadowHelper);
            const layerLightState = this.lightState[layerId];
            let newLightHelper: THREE.PointLightHelper | THREE.SpotLightHelper | THREE.DirectionalLightHelper;
            switch (lightContent.type) {
                case LightType.pointLight:
                    newLightHelper = new THREE.PointLightHelper(layerLightState as THREE.PointLight, SPHERE_SIZE);
                    break;
                case LightType.spotLight:
                    newLightHelper = new THREE.SpotLightHelper(layerLightState as unknown as THREE.SpotLight);
                    break;
                case LightType.directionalLight:
                    newLightHelper = new THREE.DirectionalLightHelper(
                        layerLightState as THREE.DirectionalLight,
                        SPHERE_SIZE
                    );
                    break;
                default:
            }
            if (!newLightHelper!) return;
            // Nur das Licht-Gizmo (Position/Richtung). Das Schattenkamera-Frustum
            // (CameraHelper von light.shadow.camera) wurde in Jans Original
            // bewusst abgeschafft — ueber die ganze Szene gespannt und immer
            // obenauf machte es schon zwei Lichter unlesbar.
            this.makeHelperAlwaysOnTop(newLightHelper!);
            // Overlay-Szene (ueber CSS3D), damit HTML-gerenderte Layer die
            // Helper-Linien beim Orbiten nicht verdecken.
            (this.gizmoScene ?? this.webGlScene).add(newLightHelper!);
            newLightHelper!.update();
            this.lightHelpers[layerId] = newLightHelper!;
            this.lightShadowHelpers[layerId] = null;
        }
    }

    updateHTMLSvg(
        layerId: string,
        content: SvgContent,
        timer: number,
        forceReload = false,
    ) {
        const existingOverlay = this.svgOverlays[layerId];
        const cachedLayerContent = this.layerContentCache[layerId] as SvgContent | undefined;
        const url = getLayerContentUrl(content);
        const cachedUrl = cachedLayerContent
            ? getLayerContentUrl(cachedLayerContent)
            : null;
        // eigenes Gedächtnis des Overlays (wie beim 3D-Weg, Jan 22.10.2026):
        // layerContentCache schreibt auch updateSvg — nach Änderungen im
        // 3D-Modus zeigte das Overlay sonst die alte SVG
        const contentChange = (existingOverlay?.dataset.svgQuelle ?? cachedUrl) !== url;
        const isDirty = forceReload || !existingOverlay || contentChange;
        // Using different key than usual to avoid sharing the same
        // layerIsLoading entry with updateSvg, since isDirty does not take
        // asHtml changes into account.
        const isLoadingKey = `${layerId}html`;
        const isLoading = this.layerIsLoading[isLoadingKey];
        // Diese Adresse ist schon einmal gescheitert: nicht in jedem Frame neu holen.
        const schonGescheitert = !forceReload && this.svgHtmlFehlgeschlagen[layerId] === url;

        if (isDirty && !isLoading && !schonGescheitert) {
            this.layerIsLoading[isLoadingKey] = true;
            // NUR ECHTE SVG EINSETZEN (warpowersresolution, 28.09.2026): Die
            // Studio-Datei unter /svg-examples/ fehlt auf .com, der Viewer
            // antwortet dort mit seiner 404-Seite als HTML. Die landete per
            // innerHTML samt Navigation im Scrolly ("Diese Seite existiert
            // nicht"). Fehlerstatus oder Antwort ohne <svg> = Ebene bleibt leer.
            fetch(url)
                .then((res) => {
                    if (!res.ok) throw new Error(`HTTP ${res.status}`);
                    return res.text();
                })
                .then((rawSvg) => {
                    if (!/<svg[\s>]/i.test(rawSvg)) throw new Error("Antwort ist keine SVG");
                    // Strip ALL full-width background/cover rects in the SVG.
                    // The background color comes from the CSS container, not the SVG.
                    const svg = rawSvg.replace(
                        /(<rect\s[^>]*width="1080"[^>]*fill="[^"]*")(\s*fill-opacity="[^"]*")?(\s*\/>)/g,
                        '$1 fill-opacity="0"$3',
                    );
                    const vorhanden = this.svgOverlays[layerId];
                    const element = vorhanden || document.createElement("div") as HTMLDivElement;
                    element.style.position = 'absolute';
                    element.style.top = '0';
                    element.style.left = '0';
                    element.style.width = '100%';
                    element.style.height = '100%';
                    element.style.pointerEvents = 'none';
                    element.style.transformOrigin = 'center center';
                    // Nur ein NEUES Overlay bleibt versteckt, bis renderLayerHTML es
                    // zeigt. Ein schon sichtbares behält seinen Zustand (Jans Fund
                    // 24.10.2026: „flackert beim Verschieben der Slider“ — jede
                    // Änderung einer Grundform erzeugt ein neues SVG; versteckt bis
                    // zum nächsten Vorschau-Bild war die Form gemessen in über der
                    // Hälfte der Augenblicke weg).
                    if (!vorhanden) element.style.display = 'none';
                    // Gefiltert einsetzen — die SVG-Datei stammt von einem Nutzer
                    // (s. utils/htmlFilter).
                    element.innerHTML = filtereSvg(svg);
                    element.dataset.svgQuelle = url;
                    const svgEl = element.querySelector('svg');
                    // ZWEI Overlay-Geometrien (Jans Vektor-Fund "viel zu gross"):
                    // 1) Vollbild-Story-SVGs (Sentence-Bloom 'svg-layer-1' bzw.
                    //    scroll_bindings-Phasen aus dem Studio) fuellen wie bisher
                    //    den ganzen Rahmen.
                    // 2) Jans positionierte SVG-LAYER belegen ihre content-Masse
                    //    in SLIDE-Einheiten (Breite/4096 als %-Anteil, zentriert,
                    //    Hoehe ueber das Seitenverhaeltnis) — exakt wie das
                    //    WebGL-Mesh, damit der Moduswechsel die Groesse NICHT
                    //    mehr veraendert. (Im Original erledigte CSS3D das
                    //    automatisch: Element-Pixel == Welt-Einheiten.)
                    const isFullFrameStorySvg =
                        layerId === 'svg-layer-1' ||
                        !!(content.scroll_bindings || (content as TODOAny).scrollBindings)?.length ||
                        !content.width ||
                        !content.height;
                    // Flag fuer renderLayerHTML: Jans Layer-SVGs bekommen dort
                    // die WebGL-Paritaets-Transform (50%-Position, 3D-Rotation,
                    // Z-Tiefe); Vollbild-Story-SVGs behalten die Legacy-Formel.
                    if (isFullFrameStorySvg) {
                        delete element.dataset.peLayerSvg;
                    } else {
                        element.dataset.peLayerSvg = '1';
                    }
                    if (svgEl && isFullFrameStorySvg) {
                        svgEl.setAttribute('width', '100%');
                        svgEl.setAttribute('height', '100%');
                        svgEl.style.display = 'block';
                    } else if (svgEl) {
                        svgEl.style.display = 'block';
                        svgEl.style.position = 'absolute';
                        svgEl.style.left = '50%';
                        svgEl.style.top = '50%';
                        svgEl.style.transform = 'translate(-50%, -50%)';
                        svgEl.style.width = `${(content.width / RendererStore.SLIDE_WIDTH) * 100}%`;
                        svgEl.style.height = 'auto';
                        // Fallback fuer Assets ohne viewBox (height:auto braucht
                        // sonst ein intrinsisches Seitenverhaeltnis)
                        svgEl.style.aspectRatio = `${content.width} / ${content.height}`;
                    }
                    // Auto-register svgTriggers for phase-* groups
                    // This enables scroll_bindings to show/hide SVG phases
                    if (svgEl) {
                        const triggers: Record<string, () => void> = {};
                        const phaseGroups = svgEl.querySelectorAll('[id^="phase-"]');
                        phaseGroups.forEach((group) => {
                            const g = group as SVGElement;
                            const phaseId = g.getAttribute('id')!;
                            // Store initial opacity for reset
                            const initialOpacity = g.getAttribute('opacity') || '1';
                            g.setAttribute('data-initial-opacity', initialOpacity);
                            g.style.opacity = initialOpacity;

                            const triggerName = `show-${phaseId}`;
                            triggers[triggerName] = () => {
                                g.style.transition = 'opacity 0.8s ease-in-out';
                                g.style.opacity = '1';
                            };
                        });
                        if (Object.keys(triggers).length > 0) {
                            (svgEl as any).svgTriggers = triggers;
                        }
                    }

                    if (!this.svgOverlays[layerId]) {
                        this.cssRendererDom.appendChild(element);
                    }
                    this.svgOverlays[layerId] = element;
                    this.layerContentCache[layerId] = content;
                    this.layerIsLoading[isLoadingKey] = false;
                    // Hinter dem Startbildschirm: ein NEUES Overlay versteckt
                    // lassen; ein vorhandenes führt renderLayerHTML (sonst
                    // flackerte es im Editor auf dem Titel-Slide bei jeder
                    // Änderung, s. oben)
                    if (!vorhanden && this.props.isCoverActive?.()) {
                        element.style.display = 'none';
                    }
                    this.props.renderer.loadedLayer(layerId, url);
                })
                .catch((error) => {
                    logError(new Error(`Failed to load SVG image from '${url}': ${error}`));
                    this.svgHtmlFehlgeschlagen[layerId] = url;
                    this.layerIsLoading[isLoadingKey] = false;
                    this.props.renderer.loadedLayer(layerId, url);
                });
        }

        // Reset legacy .visible classes wenn timer zurückgesetzt
        if (timer === 0 && this.layerScrollBindingsFired[layerId]) {
            this.layerScrollBindingsFired[layerId] = new Set();
            const element = this.svgOverlays[layerId];
            if (element) {
                element.querySelectorAll('.visible').forEach(el => {
                    el.classList.remove('visible');
                });
            }
        }

        // Scroll-driven SVG phase animation system.
        // Supports two animation types per phase group:
        // 1. Opacity fade (for text, labels, shapes) — elements fade in/out
        // 2. Stroke drawing (for polyline, path, line) — lines draw themselves
        //
        // Both are continuously proportional to scroll position and fully reversible.
        const scrollBindings = content.scroll_bindings || (content as any).scrollBindings;
        if (scrollBindings && scrollBindings.length > 0) {
            const element = this.svgOverlays[layerId];
            if (element) {
                const svgEl = element.querySelector('svg');
                if (svgEl) {
                    // Honour reduced-motion once per frame: data-anim elements
                    // jump straight to their end state (see the data-anim block).
                    const reduceMotion =
                        typeof window !== 'undefined' &&
                        typeof window.matchMedia === 'function' &&
                        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
                    const phaseGroups = svgEl.querySelectorAll('[id^="phase-"]');
                    for (const group of phaseGroups) {
                        const g = group as SVGElement;
                        const phaseId = g.getAttribute('id')!;
                        const triggerName = `show-${phaseId}`;

                        const binding = scrollBindings.find(
                            (b: { trigger: string }) => b.trigger === triggerName
                        );

                        if (!binding) {
                            // No binding = always visible (phase-1)
                            // Do NOT animate strokes — phase-1 contains static elements
                            // like axes, labels, headlines that should appear immediately
                            g.style.opacity = '1';
                            // Reset any stroke-dashoffset from previous animations
                            g.querySelectorAll('polyline, path, line').forEach((el) => {
                                const svgEl = el as SVGGeometryElement;
                                if (svgEl.dataset.totalLength) {
                                    svgEl.style.strokeDashoffset = '0';
                                }
                            });
                            continue;
                        }

                        // Phase reveal range. Default fade is 1500 scroll
                        // units, but a phase can opt into a longer window
                        // via data-fade-duration (used by typewriter, which
                        // needs ~6000 units to type 30 words at a readable
                        // pace).
                        const fadeStart = binding.at;
                        const customFade = parseFloat(
                            g.getAttribute('data-fade-duration') || '0',
                        );
                        const fadeDuration = customFade > 0 ? customFade : 1500;
                        const fadeEnd = fadeStart + fadeDuration;
                        const isTypewriterPhase = g.hasAttribute('data-typewriter');
                        // Animated phases (data-anim) drive their own per-element
                        // opacity, so the group itself must stay fully opaque.
                        const hasDataAnim = g.querySelector('[data-anim]') !== null;
                        // Stroke drawing range (longer, covers the full phase)
                        const strokeDuration = 3000;

                        let progress: number;
                        if (timer < fadeStart) {
                            progress = 0;
                        } else if (timer >= fadeEnd) {
                            progress = 1;
                        } else {
                            progress = (timer - fadeStart) / fadeDuration;
                        }
                        progress = Math.min(1, Math.max(0, progress));

                        // Opacity for non-stroke elements (text, rect, circle).
                        // Typewriter phase stays fully opaque — the per-word
                        // visibility hook (below) drives the reveal instead,
                        // so words appear at full strength as they're typed.
                        g.style.opacity =
                            isTypewriterPhase || hasDataAnim ? '1' : String(progress);
                        g.style.transition = 'none';

                        // Stroke drawing for lines/paths within this phase
                        this.animatePhaseStrokes(g, timer, fadeStart, strokeDuration);

                        // Bubble grow-in animation: animate radius from 0 to target
                        // Eased with cubic-out for satisfying "pop into view" feel
                        const bubbleCircles = g.querySelectorAll('circle[data-bubble-r]');
                        if (bubbleCircles.length > 0) {
                            const easedProgress = 1 - Math.pow(1 - progress, 3);
                            bubbleCircles.forEach((c) => {
                                const targetR = parseFloat((c as SVGCircleElement).getAttribute('data-bubble-r') || '0');
                                const currentR = targetR * easedProgress;
                                (c as SVGCircleElement).setAttribute('r', String(currentR));
                            });
                        }

                        // Typewriter word-by-word reveal: <text> elements with
                        // data-typewriter-index inside a phase group are shown
                        // progressively as the user scrolls past the phase
                        // trigger. visibleCount = floor(progress * totalWords),
                        // so scroll-speed determines typing speed naturally.
                        // Matches both <text> and <tspan> with data-typewriter-index.
                        // Lines+tspans is the production layout (browser handles
                        // word spacing); per-text fallback kept for old SVGs.
                        const typewriterWords = g.querySelectorAll('[data-typewriter-index]');
                        if (typewriterWords.length > 0) {
                            const totalWords = typewriterWords.length;
                            const visibleCount = Math.min(
                                totalWords,
                                Math.floor(progress * (totalWords + 0.5)),
                            );
                            typewriterWords.forEach((el, i) => {
                                (el as SVGElement).style.visibility =
                                    i < visibleCount ? 'visible' : 'hidden';
                            });
                        }

                        // Generic declarative per-element motion (data-anim) —
                        // ported from Studio-Motion-Live.html; a pure function of
                        // this phase's `progress` (deterministic + reversible).
                        // Vocabulary (count/rise/scale/grow-x) lives in dataAnim.ts.
                        applyDataAnim(g, progress, reduceMotion);
                    }

                    // ── Camera pan/zoom across phases ───────────────
                    // If the SVG has a #camera-viewport element, the phases
                    // inside it may carry data-camera-bbox="x y w h" to drive
                    // a cinematic pan-and-zoom. The camera starts at the full
                    // map extent, then linearly interpolates to each region's
                    // bbox during that phase's fade. Between phases the camera
                    // holds the last bbox.
                    const cameraViewport = svgEl.querySelector('#camera-viewport') as SVGGElement | null;
                    if (cameraViewport) {
                        const MAP_X = 100;
                        const MAP_Y = 440;
                        const MAP_W = 880;
                        const MAP_H = 1280;
                        const fullBBox: [number, number, number, number] = [MAP_X, MAP_Y, MAP_W, MAP_H];

                        interface CamPhase {
                            bbox: [number, number, number, number];
                            at: number;
                            fadeDuration: number;
                        }
                        const camPhases: CamPhase[] = [];
                        const camPhaseGroups = cameraViewport.querySelectorAll('[data-camera-bbox]');
                        for (const cp of camPhaseGroups) {
                            const id = cp.getAttribute('id');
                            if (!id) continue;
                            const b = cp.getAttribute('data-camera-bbox')!
                                .split(/\s+/).map(parseFloat);
                            if (b.length !== 4 || b.some(isNaN)) continue;
                            const binding = scrollBindings.find(
                                (bb: { trigger: string }) => bb.trigger === `show-${id}`,
                            );
                            if (!binding) continue;
                            const customFade = parseFloat(cp.getAttribute('data-fade-duration') || '0');
                            camPhases.push({
                                bbox: [b[0], b[1], b[2], b[3]],
                                at: binding.at,
                                fadeDuration: customFade > 0 ? customFade : 1500,
                            });
                        }
                        camPhases.sort((a, b) => a.at - b.at);

                        if (camPhases.length > 0) {
                            // Ease-in-out cubic for smooth pan
                            const ease = (t: number) =>
                                t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
                            const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
                            const lerpBBox = (
                                a: [number, number, number, number],
                                b: [number, number, number, number],
                                t: number,
                            ): [number, number, number, number] => [
                                lerp(a[0], b[0], t), lerp(a[1], b[1], t),
                                lerp(a[2], b[2], t), lerp(a[3], b[3], t),
                            ];

                            let currentBBox: [number, number, number, number] = fullBBox;
                            for (let i = 0; i < camPhases.length; i++) {
                                const cp = camPhases[i];
                                const fadeEnd = cp.at + cp.fadeDuration;
                                if (timer >= fadeEnd) {
                                    currentBBox = cp.bbox;
                                    continue;
                                }
                                if (timer <= cp.at) {
                                    currentBBox = i === 0 ? fullBBox : camPhases[i - 1].bbox;
                                    break;
                                }
                                const t = ease((timer - cp.at) / cp.fadeDuration);
                                const prev = i === 0 ? fullBBox : camPhases[i - 1].bbox;
                                currentBBox = lerpBBox(prev, cp.bbox, t);
                                break;
                            }

                            // Compute scale so bbox fits in the map area, then
                            // translate so bbox center aligns with map center.
                            const scaleX = MAP_W / currentBBox[2];
                            const scaleY = MAP_H / currentBBox[3];
                            const scale = Math.min(scaleX, scaleY);
                            const targetCx = MAP_X + MAP_W / 2;
                            const targetCy = MAP_Y + MAP_H / 2;
                            const bboxCx = currentBBox[0] + currentBBox[2] / 2;
                            const bboxCy = currentBBox[1] + currentBBox[3] / 2;
                            const tx = targetCx - bboxCx * scale;
                            const ty = targetCy - bboxCy * scale;
                            cameraViewport.setAttribute(
                                'transform',
                                `translate(${tx.toFixed(2)} ${ty.toFixed(2)}) scale(${scale.toFixed(4)})`,
                            );

                            // Inverse-scale labels and dots so they stay at
                            // their declared pixel size even when the camera
                            // zooms in. Without this, a 5× zoom renders 18px
                            // labels as 90px monsters.
                            const invScale = 1 / scale;
                            cameraViewport.querySelectorAll('[data-base-font-size]').forEach((el) => {
                                const base = parseFloat(el.getAttribute('data-base-font-size') || '0');
                                if (base > 0) {
                                    (el as SVGTextElement).setAttribute('font-size', String(base * invScale));
                                }
                            });
                            cameraViewport.querySelectorAll('[data-base-r]').forEach((el) => {
                                const base = parseFloat(el.getAttribute('data-base-r') || '0');
                                if (base > 0) {
                                    (el as SVGCircleElement).setAttribute('r', String(base * invScale));
                                }
                            });
                        }
                    }
                }
            }
        }
    }

    // ── Interactive Map Layer ──────────────────────────────────────────────

    /**
     * Create / update a MapTiler SDK (MapLibre GL) map instance for a map layer.
     * Follows the same DOM-overlay pattern as updateHTMLSvg.
     */
    async updateMap(layerId: string, content: MapContent) {
        if (this.mapInstances[layerId]) {
            // Pro-Editor-Karte (09.10.2026): Stil im Menue gewechselt → neu laden,
            // statt erst nach dem Neuöffnen.
            const map = this.mapInstances[layerId];
            if (this.mapSdk && map.__scrollyStil !== content.style) {
                map.__scrollyStil = content.style;
                try { map.setStyle(this.kartenStil(content.style)); } catch { /* Karte noch nicht bereit */ }
            }
            return; // already initialised
        }
        if (this.layerIsLoading[layerId]) return;

        this.layerIsLoading[layerId] = true;

        try {
            const maptilersdk = await import('@maptiler/sdk');
            this.mapSdk = maptilersdk; // cache for sync marker creation

            // Create container div (same pattern as SVG overlays)
            const container = document.createElement('div');
            container.style.position = 'absolute';
            container.style.top = '0';
            container.style.left = '0';
            container.style.width = '100%';
            container.style.height = '100%';
            container.style.pointerEvents = content.interactive ? 'auto' : 'none';
            container.style.display = 'none'; // hidden until renderLayerHTML shows it
            container.style.overflow = 'hidden';

            // Hide all MapLibre UI controls via scoped CSS
            const style = document.createElement('style');
            style.textContent = `
                .maplibregl-ctrl-top-left, .maplibregl-ctrl-top-right,
                .maplibregl-ctrl-bottom-left, .maplibregl-ctrl-bottom-right,
                .maplibregl-ctrl { display: none !important; }
            `;
            container.appendChild(style);
            this.cssRendererDom.appendChild(container);
            this.mapContainers[layerId] = container;

            // Set API key
            maptilersdk.config.apiKey = (content as any).apiKey
                || (window as any).__MAPTILER_API_KEY__
                || '';

            const firstWp = content.waypoints[0];
            // Quellzeile (Pflicht laut MapTiler) — nur, wenn die Ebene sie will;
            // Studio-Karten schreiben sie als eigenen Text unter die Karte.
            if (content.quellzeile) {
                const quelle = document.createElement('div');
                quelle.textContent = 'Karte: © MapTiler © OpenStreetMap-Mitwirkende';
                quelle.style.cssText = 'position:absolute;right:6px;bottom:4px;z-index:2;'
                    + 'font:500 9px/1.3 "DM Sans",system-ui,sans-serif;color:#3c3c3c;'
                    + 'background:rgba(255,255,255,0.72);padding:1px 5px;border-radius:3px;pointer-events:none;';
                container.appendChild(quelle);
            }

            // Initial camera from first waypoint
            const map = new maptilersdk.Map({
                container,
                style: this.kartenStil(content.style),
                center: [firstWp?.lng ?? 0, firstWp?.lat ?? 0],
                zoom: firstWp?.zoom ?? 2,
                bearing: firstWp?.bearing ?? 0,
                pitch: firstWp?.pitch ?? 0,
                interactive: content.interactive ?? false,
                // Kacheln (Jans Fund 10.10.2026 „laden sehr schlecht“): höchstens
                // doppelte Pixeldichte, kein Einblenden, mehr Kacheln im Speicher.
                pixelRatio: Math.min(2, window.devicePixelRatio || 1),
                fadeDuration: 0,
                maxTileCacheSize: 400,
                // MapLibre GL v4 removed `false` from AttributionControlOptions' type
                // union, but runtime still accepts the boolean to disable the control.
                // Cast to keep the runtime-correct config without widening types elsewhere.
                attributionControl: false as unknown as object,
            });

            // Remove all default UI controls (zoom, compass, geolocate)
            map.on('load', () => {
                // Remove navigation control if present
                const controls = (map as any)._controls || [];
                for (const ctrl of [...controls]) {
                    try { map.removeControl(ctrl); } catch { /* some are not removable */ }
                }
            });

            (map as any).__scrollyStil = content.style;
            this.mapInstances[layerId] = map;
            this.layerIsLoading[layerId] = false;
            this.props.renderer.loadedLayer(layerId, `map:${content.style}`);
        } catch (error) {
            logError(new Error(`Failed to load MapTiler SDK for map layer '${layerId}': ${error}`));
            this.layerIsLoading[layerId] = false;
            this.props.renderer.loadedLayer(layerId, `map:error`);
        }
    }

    /**
     * Pro-Editor-Karte als Fläche im 3D-Raum anlegen bzw. auffrischen
     * (Jan 10.10.2026: „echt im 3D-Raum“ — alle Drehachsen, richtige Tiefe).
     */
    updateKarteImRaum(layerId: string, content: MapContent) {
        const vorhanden = this.kartenImRaum[layerId];
        const masz = this.sceneUnitsToPx();
        if (vorhanden) {
            vorhanden.aktualisiere(content, masz);
            this.layerContentCache[layerId] = content;
            return;
        }
        const karte = new KarteImRaum(layerId, content, () => {});
        this.kartenImRaum[layerId] = karte;
        this.layerMeshes[layerId] = karte.mesh;
        this.layerState3d[layerId] = karte.mesh as any;
        this.layerContentCache[layerId] = content;
        this.uploadToGpu?.(karte.mesh as any);
        // Nicht auf Kacheln warten: Die Fläche steht, die Karte füllt sich nach.
        karte
            .starte(masz)
            .catch((error) => logError(new Error(`Karte im Raum '${layerId}' ließ sich nicht laden: ${error}`)))
            .finally(() => this.markLayerLoaded(layerId, "map:raum"));
    }

    /** MapTiler-Stil aus der gespeicherten Stil-ID ('dataviz', 'streets', 'satellite' …). */
    private kartenStil(stil: string | undefined): any {
        // Varianten wie „dataviz.dark“ auflösen (gleiche Regel wie KarteImRaum)
        return this.mapSdk ? kartenStilAufloesen(this.mapSdk, stil) : stil;
    }

    /**
     * Nadel mit Namen (Pro-Editor-Karte, 09.10.2026) — wie Benedikts Beat „Ort“:
     * Punkt mit hellem Ring und weißes Schild mit dem Ortsnamen darunter.
     */
    private kartenNadel(mk: { label?: string; color?: string }): HTMLDivElement {
        const farbe = mk.color ? `#${mk.color.replace('#', '')}` : '#F02873';
        const el = document.createElement('div');
        el.style.cssText = 'display:flex;flex-direction:column;align-items:center;pointer-events:none;'
            + 'transform:translateY(-9px);';
        const punkt = document.createElement('div');
        punkt.style.cssText = `width:12px;height:12px;border-radius:50%;background:${farbe};`
            + `border:3px solid #fff;box-shadow:0 0 0 7px ${farbe}2e,0 1px 4px rgba(0,0,0,0.3);`;
        const schild = document.createElement('div');
        schild.textContent = mk.label ?? '';
        schild.style.cssText = 'margin-top:9px;padding:5px 11px;border-radius:7px;white-space:nowrap;'
            + 'background:rgba(255,255,255,0.94);color:#1A1A18;box-shadow:0 1px 6px rgba(0,0,0,0.18);'
            + 'font:600 17px/1.15 "Barlow Semi Condensed","Arial Narrow",sans-serif;';
        el.appendChild(punkt);
        el.appendChild(schild);
        return el;
    }

    /**
     * Per-frame update: drive map camera position from scroll timer via waypoint interpolation.
     * Called from renderLayerHTML when a map layer is encountered.
     */
    renderMapLayer(
        layer: LayerRender,
        parent: CSS3DObject | null,
        timer: number,
    ) {
        const container = this.mapContainers[layer.id];
        const map = this.mapInstances[layer.id];
        if (!container || !map) return;

        const content = layer.content as MapContent;
        // Ende EINSCHLIESSLICH wie alle Ebenen (Jans Entscheidung 12.10.2026);
        // to <= from bleibt der „läuft für immer"-Sonderfall.
        const isInLifetime = layer.from <= timer && (layer.to >= timer || layer.to <= layer.from);
        // Eltern-Sichtbarkeit wie beim SVG-/Infografik-Overlay (Jans
        // Gruppen-Durchgang 12.10.2026): der Karten-Container haengt als
        // Geschwister am Host und erbt sonst nichts.
        const elternSichtbar = !parent || parent.visible !== false;
        const visible = (this.visibilityOverrides[layer?.id] ?? layer?.visible ?? true) && isInLifetime && elternSichtbar;

        if (!visible) {
            container.style.display = 'none';
            return;
        }

        const layer3D = layer.layer3D(timer);
        const parentOpacity = parent ? parent.userData?.opacity || 1 : 1;
        const effectiveOpacity = (layer3D.opacity ?? 1) * parentOpacity;

        if (effectiveOpacity === 0) {
            container.style.display = 'none';
            return;
        }

        // Accumulate transforms from parent (same pattern as SVG overlays)
        const parentScale = parent?.userData?.scale || [1, 1, 1];
        const scaleX = (layer3D.scale?.[0] ?? 1) * parentScale[0];
        const scaleY = (layer3D.scale?.[1] ?? 1) * parentScale[1];
        const parentPos = parent?.userData?.position || [0, 0];
        const posX = ((layer3D.position?.[0] ?? 0) + parentPos[0]) * 100;
        const posY = ((layer3D.position?.[1] ?? 0) + parentPos[1]) * -100;
        const parentRotation = parent?.userData?.rotation || [0, 0, 0];
        const rotationZ = (layer3D.rotation?.[2] ?? 0) + parentRotation[2];

        const transforms: string[] = [];
        if (posX !== 0 || posY !== 0) transforms.push(`translate(${posX}%, ${posY}%)`);
        if (scaleX !== 1 || scaleY !== 1) transforms.push(`scale(${scaleX}, ${scaleY})`);
        if (rotationZ !== 0) transforms.push(`rotate(${rotationZ}deg)`);

        container.style.display = 'block';
        container.style.opacity = `${effectiveOpacity}`;
        container.style.transform = transforms.join(' ');
        container.style.transformOrigin = 'center center';

        // Drive map camera from scroll position.
        // jumpTo() queues the camera change; triggerRepaint() forces MapLibre
        // to actually render it (its internal rAF loop may be idle when
        // interactive=false). resize() is needed after the container goes
        // from display:none to display:block so MapLibre knows the real size.
        // Pro-Editor-Karte: Orte mit `anteil` hängen an der Laufzeit der Ebene.
        const { kamera: camera, nadeln: activeMarkers } = kartenBild(
            content.waypoints, layer.from, layer.to, timer, layer3D.mapPlace,
        );
        try {
            // Resize once when container first becomes visible
            if (!(container as any).__mapResized) {
                map.resize();
                (container as any).__mapResized = true;
            }
            // Nur bei echter Änderung bewegen (Jans Kachel-Fund 10.10.2026):
            // vorher bekam die Karte JEDES Bild einen neuen Kamera-Auftrag samt
            // Neuzeichnen und rechnete ihre Kacheln ständig neu durch.
            const kameraSchluessel = [camera.lng, camera.lat, camera.zoom, camera.bearing, camera.pitch]
                .map((v) => v.toFixed(5))
                .join(",");
            if ((container as any).__kamera !== kameraSchluessel) {
                (container as any).__kamera = kameraSchluessel;
                map.jumpTo({
                    center: [camera.lng, camera.lat],
                    zoom: camera.zoom,
                    bearing: camera.bearing,
                    pitch: camera.pitch,
                });
                map.triggerRepaint();
            }
        } catch {
            // Map not ready yet — silently skip this frame
        }

        // Update markers (show markers for the closest waypoint)
        const markerKey = `${layer.id}_markers`;
        const existingMarkers = (this as any)[markerKey] as any[] | undefined;
        const markerHash = JSON.stringify(activeMarkers);
        const prevHash = (this as any)[`${markerKey}_hash`] as string | undefined;

        if (markerHash !== prevHash) {
            // Remove old markers
            if (existingMarkers) {
                existingMarkers.forEach((m: any) => m.remove());
            }
            // Add new markers (lazy — only if maptilersdk is loaded)
            const newMarkers: any[] = [];
            if (this.mapSdk) {
                for (const mk of activeMarkers) {
                    try {
                        const el = mk.label ? this.kartenNadel(mk) : document.createElement('div');
                        if (!mk.label) {
                            el.style.width = '14px';
                            el.style.height = '14px';
                            el.style.borderRadius = '50%';
                            el.style.backgroundColor = mk.color ? `#${mk.color.replace('#', '')}` : '#BA7517';
                            el.style.border = '2px solid white';
                            el.style.boxShadow = '0 1px 4px rgba(0,0,0,0.3)';
                        }
                        // Mit Namen: die Spitze der Nadel sitzt auf dem Ort.
                        const marker = new this.mapSdk.Marker(mk.label ? { element: el, anchor: 'top' } : { element: el });
                        marker.setLngLat([mk.lng, mk.lat]).addTo(map);
                        newMarkers.push(marker);
                    } catch {
                        // Marker creation failed silently
                    }
                }
            }
            (this as any)[markerKey] = newMarkers;
            (this as any)[`${markerKey}_hash`] = markerHash;
        }
    }

    /**
     * Animate stroke-dashoffset on all stroked elements within a phase group.
     * Lines draw themselves proportionally to the scroll position.
     */
    private animatePhaseStrokes(group: SVGElement, timer: number, startAt: number, duration: number) {
        const strokeElements = group.querySelectorAll('polyline, path, line');
        for (const el of strokeElements) {
            const svgEl = el as SVGGeometryElement;

            // Skip elements without stroke (fills only)
            const stroke = svgEl.getAttribute('stroke') || svgEl.style.stroke;
            if (!stroke || stroke === 'none') continue;

            // Calculate total length (cache it)
            if (!svgEl.dataset.totalLength) {
                try {
                    const len = svgEl.getTotalLength();
                    if (len > 0) {
                        svgEl.dataset.totalLength = String(len);
                        svgEl.style.strokeDasharray = String(len);
                        svgEl.style.strokeDashoffset = String(len);
                    }
                } catch {
                    continue; // getTotalLength not supported on this element
                }
            }

            const totalLength = parseFloat(svgEl.dataset.totalLength || '0');
            if (totalLength <= 0) continue;

            // Calculate draw progress
            let drawProgress: number;
            if (timer < startAt) {
                drawProgress = 0;
            } else if (timer >= startAt + duration) {
                drawProgress = 1;
            } else {
                drawProgress = (timer - startAt) / duration;
            }
            drawProgress = Math.min(1, Math.max(0, drawProgress));

            // Set stroke-dashoffset: totalLength (hidden) → 0 (fully drawn)
            svgEl.style.strokeDashoffset = String(totalLength * (1 - drawProgress));
        }
    }

    /**
     * Team-/Brandschriften (Jans Font-Sync): die im Scrolly hinterlegten
     * fontInfos (slideOptions.fontInfos, familyName → fontUrl) direkt nach
     * der Familie eines TEXT-Layers befragen — anders als die fontMapping-
     * Kette oben gilt das pro Layer, nicht nur für die eine Template-Familie.
     * Primärquelle ist der LIVE-Render-Tree (props.presentation, wird bei
     * jedem Editor-Edit neu gezeigt), Fallback templateJson (Reader-Pfad).
     */
    /**
     * Zweites Netz der Font-Eingangspruefung (02.09.2026): Kann Troika eine
     * KUNDEN-Schrift nicht parsen/laden, meldet sich sync() NIE — der Text
     * bliebe fuer immer unsichtbar (Ernstfall im dev-Harness gemessen:
     * "Failure loading font … RangeError", kein Fallback, kein Fehlerbild).
     *
     * ⚠️ Nachtraeglicher Tausch heilt NICHT (ebenfalls gemessen): eine
     * Text-Instanz, die einmal die kaputte Schrift probiert hat, haengt fuer
     * immer im ersten sync fest — auch text.font = neu + sync() feuert nie
     * wieder. Deshalb PROBE VOR ZUWEISUNG: der Text startet in der
     * Standard-Schrift (sofort sichtbar) und bekommt die Kunden-Schrift
     * erst, wenn preloadFont sie nachweislich geladen hat. Erfolgreiche
     * Proben werden gecacht (kein Mehraufwand im Normalfall), Fehlversuche
     * nicht (naechster Anlauf darf neu probieren).
     */
    private fontProbeCache: Record<string, Promise<boolean>> = {};

    private probeCustomFont(url: string): Promise<boolean> {
        if (!(url in this.fontProbeCache)) {
            this.fontProbeCache[url] = new Promise<boolean>((resolve) => {
                const timer = setTimeout(() => resolve(false), 15000);
                try {
                    preloadFont({ font: url, characters: "abc" }, () => {
                        clearTimeout(timer);
                        resolve(true);
                    });
                } catch {
                    clearTimeout(timer);
                    resolve(false);
                }
            });
            void this.fontProbeCache[url].then((ok) => {
                if (!ok) delete this.fontProbeCache[url];
            });
        }
        return this.fontProbeCache[url];
    }

    private applyFontSafely(text: TODOAny, wunschFont: string, standardFont: string) {
        if (wunschFont === standardFont) {
            text.font = standardFont;
            return;
        }
        text.font = standardFont;
        void this.probeCustomFont(wunschFont).then((ok) => {
            if (!ok) {
                // eslint-disable-next-line no-console
                console.warn(
                    "[FontFallback] Schrift nicht ladbar, Ersatzschrift bleibt aktiv:",
                    String(wunschFont).slice(0, 100),
                );
                return;
            }
            text.font = wunschFont;
            text.sync(() => {
                if (this.webGLRenderer && this.webGlScene && this.camera) {
                    this.webGLRenderer.render(this.webGlScene, this.camera);
                }
            });
        });
    }

    private resolveTeamFontUrl(family: string | undefined, weight?: number): string | null {
        if (!family) return null;
        const fromTree = (this.props.presentation?.presentationOptions as TODOAny)?.slideOptions?.fontInfos;
        const fromJson = this.templateJson?.data?.data?.presentationOptions?.slideOptions?.fontInfos;
        for (const infos of [fromTree, fromJson]) {
            // Mehrere Schnitte je Familie moeglich (06.10.2026): der passende
            const hit = teamSchnittDatei(infos, family, weight);
            if (hit) return hit;
        }
        return null;
    }

    /** Wiedergabe-Start (START-Klick) an den Store melden — Gate für die
     *  Video-Hörbarkeit vor dem Start (s. RendererStore.playbackStarted). */
    setPlaybackStarted(flag: boolean) {
        this.props.renderer.setPlaybackStarted(flag);
    }

    getJson(jsonData: PostJson) {
        this.templateJson = jsonData;

        // Build visibility override map from JSON for the animate-loop.
        // The PlayablePresentation tree is immutable after creation, so we
        // store overrides separately and check them in processLayer().
        // We must always write the explicit value (not just delete on true)
        // because the PlayablePresentation may have been constructed with
        // visible=false for layers that were saved as hidden — deleting the
        // override would fall back to that stale false.
        const jsonLayers = jsonData?.data?.data?.layers;
        if (Array.isArray(jsonLayers)) {
            for (const jl of jsonLayers) {
                if (jl?.id && jl.visible === false) {
                    this.visibilityOverrides[jl.id] = false;
                } else if (jl?.id) {
                    this.visibilityOverrides[jl.id] = true;
                }
            }

            const bloomSvg = jsonLayers.find(
                (l) => l?.id === 'svg-layer-1' && l.type === LayerType.svg,
            );
            if (bloomSvg?.content) {
                const content = bloomSvg.content as SvgContent;
                const url = getLayerContentUrl(content);
                const cached = this.layerContentCache['svg-layer-1'] as SvgContent | undefined;
                const cachedUrl = cached ? getLayerContentUrl(cached) : null;
                const asHtml = Boolean(
                    content.asHtml || (bloomSvg as { asHtml?: boolean }).asHtml,
                );
                if (asHtml && url && url !== cachedUrl) {
                    const timer = this.props.slidePlayer?.timer ?? 0;
                    this.updateHTMLSvg('svg-layer-1', content, timer, true);
                }
            }
        }
    }

    getFonts(_fontFamilies: CustomFonts, _liveUpdate: boolean = false) {
        // KEIN Vorrechnen aller Schriften mehr (Jans 3D-Power-Test 13.10.2026,
        // „hakt am Anfang extrem"): Hier liefen fuer JEDE Familie der Auswahl
        // (seit den neuen Familien ~30) Download + SDF-Glyphen in Troikas
        // EINEN gemeinsamen Atlas. In Safari wurden die erst nach START fertig,
        // und jede fertige Schrift schickte den ganzen Atlas (bis 2048x2048)
        // erneut auf die GPU — gemessen 10 Bilder je 70–330 ms. Die benutzten
        // Schriften standen obendrein hinter allen anderen in der Warteschlange.
        // Die Schriften der Text-Ebenen synct updateText selbst (und laedt den
        // Atlas dort vor); der Grundsatz aus fonts.ts (Sofia, Italianno, Noto)
        // bleibt beim Modul-Import.
    }

    /** Bis wann eine Text-Ebene nach ihrem Live-Umbau nicht vom Dokument
     *  umgebaut wird (s. updateText). */
    private textLiveBis: Record<string, number> = {};
    updateText(layerId: string, content: TextContent, liveUpdate: boolean = false) {
        /* eslint-disable */
        const animatable3DLayer = this.layerState3d[layerId];
        const cachedLayerContent = this.layerContentCache[layerId];
        // WERT- statt REFERENZ-Vergleich (Jans Fund 09.09.2026, „Schrift
        // verschwindet beim Verschieben"): Der Editor-Adapter serialisiert bei
        // JEDER MST-Aenderung — auch beim reinen Verschieben — frischen Content,
        // die Referenz war hier also immer neu. Folge: jeder Schritt am Anfasser
        // oder Regler baute den Troika-Text komplett neu (dispose + new Text +
        // async sync), und bis der SDF-Atlas stand, war der Text unsichtbar;
        // mit Team-Schrift kam obendrein die Ersatzschrift kurz ins Bild
        // (Probe vor Zuweisung). Alle anderen Ebenentypen vergleichen laengst
        // nach Wert (Video: isEqual/omit, Bild/Objekt: url/primitive).
        let contentChange = !isEqual(cachedLayerContent, content);
        const isDirty = !animatable3DLayer || contentChange;
        const isLoading = this.layerIsLoading[layerId];

        // STIL-WECHSEL NACH DEM ERSTEN TIPPEN (Jans Fund 21.10.2026, „Hell/
        // Dunkel/Marke ändern die Schrift — aber nicht im Preview“): Nach dem
        // ersten Live-Text-Edit ist setGpuTextOnStart global wahr, und der
        // Weg unten baut nur noch NEUE Text-Ebenen. Geänderter Inhalt einer
        // bestehenden Ebene (Farbe/Schrift per Stil, Scene-Neubau) blieb im
        // Bild stehen. Dann wie beim Tippen umbauen.
        // Nicht direkt nach dem Tippen: das Dokument kommt ein paar Bilder
        // nach dem Live-Ereignis an und drehte den getippten Text sonst kurz
        // auf einen älteren Stand zurück.
        if (
            !liveUpdate &&
            contentChange &&
            !isLoading &&
            WebGLAssets.setGpuTextOnStart &&
            this.layerMeshes[layerId] &&
            performance.now() > (this.textLiveBis[layerId] ?? 0)
        ) {
            this.updateText(layerId, content, true);
            return;
        }

        if (liveUpdate) {
            WebGLAssets.setGpuTextOnStart = true;

            let txtObj3d = this.layerMeshes[layerId] as THREE.Mesh;
            if (txtObj3d) {
                const textContent: TextContent = content;
                let getText = textContent.text;
                // Dispose old troika Text mesh before removing — prevents SDF texture leak
                const oldText = txtObj3d.children[0] as any;
                if (oldText) {
                    if (oldText.dispose) oldText.dispose();
                    // MATERIAL-ARRAY beachten (Jans Fund 04.08.2026: "das
                    // Textelement STARK wollte ich in COOL umbenennen — wird
                    // nicht angezeigt im Preview").
                    //
                    // Troika liefert ein Material-ARRAY, sobald eine Outline /
                    // ein Schlagschatten konfiguriert ist ([outline, fill]).
                    // Die alte Zeile `if (oldText.material.map) ...map.dispose()`
                    // fiel darauf herein: ein Array hat SELBST eine
                    // `.map`-Methode, die Pruefung hielt sie fuer eine Textur
                    // und rief `.dispose()` darauf auf. Ergebnis:
                    // "oldText.material.map.dispose is not a function" —
                    // updateText brach GENAU HIER ab, das alte Text-Mesh wurde
                    // nie ersetzt und die Vorschau zeigte weiter den alten
                    // Text. Betroffen war jede Textebene mit Outline; ohne
                    // Outline lief es durch, daher "mal geht es, mal nicht".
                    disposeMaterials(oldText.material);
                }
                txtObj3d.remove(oldText);

                // Familie + Schnitt (Jans Feld „Schnitt“, 06.10.2026); ohne Schnitt
                // der Normalschnitt, Nicht-Standard-Familien wie bisher über FONTS.
                const fontFamily =
                    schriftDatei(textContent.family, textContent.weight) ||
                    FONTS[textContent.family as keyof typeof FONTS] ||
                    DEFAULT_FONT;

                let cFont = null;

                // BEWUSST gewählte Ebenen-Schrift gewinnt gegen das Mapping (Jans
                // TEXT-Regel 12.10.2026, Marker familieGewaehlt — gesetzt von den
                // Schriftfeldern der Template-Editoren). Ohne Marker wie bisher:
                // Dokument-/Branding-Mapping überstimmt die Ebene.
                if (!textContent.familieGewaehlt && this.templateJson?.data?.data?.presentationOptions?.slideOptions?.fontMapping?.text?.family) {
                    // Mehrere Schnitte je Familie moeglich (06.10.2026): der zum
                    // Schnitt der Ebene passende statt der letzten Fundstelle
                    cFont = teamSchnittDatei(
                        this.templateJson.data.data.presentationOptions.slideOptions.fontInfos,
                        this.templateJson.data.data.presentationOptions.slideOptions.fontMapping.text.family,
                        textContent.weight
                    );
                }

                // Team-/Brandschrift des Layers (Jans Font-Sync) vor dem
                // Standard-FONTS-Fallback aufloesen.
                if (!cFont) cFont = this.resolveTeamFontUrl(textContent.family, textContent.weight);
                if (!cFont) cFont = fontFamily;

                const text = new Text();
                text.textAlign = textContent.align;
                text.text = getText
                    .split("\n")
                    .map((l) => l.trim())
                    .join("\n");
                this.applyFontSafely(text, cFont, fontFamily);
                text.fontSize = textContent.size;
                // Troika behandelt lineHeight 0 als falsy -> 'normal'; kleiner
                // Ersatzwert, damit 0 wirklich "Zeilen aufeinander" heisst (Jan-Original).
                text.lineHeight = textContent.lineHeight > 0 ? textContent.lineHeight : 0.001;
                text.maxWidth = textContent.useWidth ? textContent.width : Infinity;
                text.whiteSpace = textContent.mode === "nowrap" ? "nowrap" : "normal";
                text.letterSpacing = textContent.letterSpacing;

                text.material = new THREE.MeshBasicMaterial({
                    // content.color ist bereits Editor-Format (0..255) — NICHT durch
                    // threeToColor jagen (erwartet THREE 0..1, multipliziert nochmal
                    // mit 255 -> ungueltiger Hex -> THREE-Fallback WEISS; Jans Fund #6).
                    color: colorToHex(textContent.color),
                    transparent: true,
                });
                text.material.needsUpdate = true;
                applyTextShadow(text, textContent);

                // TODO clean up with text.dispose()
                text.anchorX = textContent.xAlign;
                text.anchorY = textContent.yAlign === "center" ? "middle" : textContent.yAlign;

                const scaleFactor = 2.0;
                text.scale.set(scaleFactor, scaleFactor, scaleFactor);

                text.sync(() => {
                    if (this.webGLRenderer && this.webGlScene && this.camera) {
                        this.webGLRenderer.render(this.webGlScene, this.camera);
                    }
                });

                txtObj3d.add(text);
                // gebauter Stand = Vergleichsbasis (s. o.), sonst baute der
                // nächste Bild-Durchlauf denselben Text gleich noch einmal
                this.textLiveBis[layerId] = performance.now() + 600;
                this.layerContentCache[layerId] = content;
                liveUpdate = false;
            }
        }

        // NEUE Text-Ebene trotz Live-Flagge anlegen (Jans Template+-Fund
        // 12.10.2026, „Quote durch Text area ersetzt — wird nicht angezeigt"):
        // setGpuTextOnStart wird beim ERSTEN Live-Text-Edit global wahr, der
        // Live-Zweig oben baut aber nur Meshes um, die schon existieren.
        // Eine DANACH hinzugekommene Text-Ebene (Scene-Tausch/-Platzierung im
        // Template+-Editor) bekam so nie ein Mesh und blieb unsichtbar.
        if (!WebGLAssets.setGpuTextOnStart || !this.layerMeshes[layerId]) {
            const objLoaded = (obj: TODOAny) => {
                this.props.renderer.loadedLayer(layerId, content.text);
                this.layerState3d[layerId] = obj;
                this.layerIsLoading[layerId] = false;
                // cache content
                this.layerContentCache[layerId] = content;
                this.layerMeshes[layerId] = obj;
            };

            if (isDirty && !isLoading) {
                this.layerIsLoading[layerId] = true;

                const textContent: TextContent = content;
                // Familie + Schnitt wie in updateText (Jans Feld „Schnitt“, 06.10.2026)
                let fontFamily =
                    schriftDatei(textContent.family, textContent.weight) ||
                    FONTS[textContent.family as keyof typeof FONTS] ||
                    DEFAULT_FONT;

                let cFont = null;

                //console.log(this.templateJson.data.data.presentationOptions.slideOptions)

                // BEWUSST gewählte Ebenen-Schrift gewinnt gegen das Mapping (Jans
                // TEXT-Regel 12.10.2026, Marker familieGewaehlt — gesetzt von den
                // Schriftfeldern der Template-Editoren). Ohne Marker wie bisher:
                // Dokument-/Branding-Mapping überstimmt die Ebene.
                if (!textContent.familieGewaehlt && this.templateJson?.data?.data?.presentationOptions?.slideOptions?.fontMapping?.text?.family) {
                    //console.log("checking for font");
                    //console.log(this.templateJson.data.data.presentationOptions.slideOptions.fontMapping.text.family);

                    if (
                        this.templateJson.data.data.presentationOptions.slideOptions.fontMapping.text.family
                            .toString()
                            .indexOf("Italianno-Regular", 0) !== -1
                    ) {
                        cFont = fontItalianno;
                    } else if (
                        this.templateJson.data.data.presentationOptions.slideOptions.fontMapping.text.family
                            .toString()
                            .indexOf("JetBrainsMono", 0) !== -1
                    ) {
                        cFont = fontJetBrains;
                    } else if (
                        this.templateJson.data.data.presentationOptions.slideOptions.fontMapping.text.family
                            .toString()
                            .indexOf("Merriweather-Variable", 0) !== -1
                    ) {
                        cFont = fontMerriweather;
                    } else if (
                        this.templateJson.data.data.presentationOptions.slideOptions.fontMapping.text.family
                            .toString()
                            .indexOf("MerriweatherSans", 0) !== -1
                    ) {
                        cFont = fontMerriweatherSans;
                    } else if (
                        this.templateJson.data.data.presentationOptions.slideOptions.fontMapping.text.family
                            .toString()
                            .indexOf("NotoSans", 0) !== -1
                    ) {
                        cFont = fontNotoSans;
                    } else if (
                        this.templateJson.data.data.presentationOptions.slideOptions.fontMapping.text.family
                            .toString()
                            .indexOf("NotoSerif", 0) !== -1
                    ) {
                        cFont = fontNotoSerif;
                    } else if (
                        this.templateJson.data.data.presentationOptions.slideOptions.fontMapping.text.family
                            .toString()
                            .indexOf("PortLligatSlab-Regular", 0) !== -1
                    ) {
                        cFont = fontPortLligatSlab;
                    } else if (
                        this.templateJson.data.data.presentationOptions.slideOptions.fontMapping.text.family
                            .toString()
                            .indexOf("RobotoCondensed-Variable", 0) !== -1
                    ) {
                        cFont = fontRoboto;
                    } else if (
                        this.templateJson.data.data.presentationOptions.slideOptions.fontMapping.text.family
                            .toString()
                            .indexOf("SourceSans3-Variable", 0) !== -1
                    ) {
                        cFont = fontSourceSans3;
                    } else if (
                        this.templateJson.data.data.presentationOptions.slideOptions.fontMapping.text.family
                            .toString()
                            .indexOf("SourceSerif4-Variable", 0) !== -1
                    ) {
                        cFont = fontSourceSerif4;
                    } else {
                        // Mehrere Schnitte je Familie moeglich (06.10.2026): der zum
                        // Schnitt der Ebene passende statt der letzten Fundstelle
                        cFont = teamSchnittDatei(
                            this.templateJson.data.data.presentationOptions.slideOptions.fontInfos,
                            this.templateJson.data.data.presentationOptions.slideOptions.fontMapping.text.family,
                            textContent.weight
                        );
                    }
                }

                // Team-/Brandschrift des Layers (Jans Font-Sync) vor dem
                // Standard-FONTS-Fallback aufloesen.
                if (!cFont) cFont = this.resolveTeamFontUrl(textContent.family, textContent.weight);
                if (!cFont) cFont = fontFamily;

                const text = new Text();
                text.textAlign = textContent.align;
                text.text = textContent.text
                    .split("\n")
                    .map((l) => l.trim())
                    .join("\n");
                this.applyFontSafely(text, cFont, fontFamily);

                text.fontSize = textContent.size;
                // Troika behandelt lineHeight 0 als falsy -> 'normal'; kleiner
                // Ersatzwert, damit 0 wirklich "Zeilen aufeinander" heisst (Jan-Original).
                text.lineHeight = textContent.lineHeight > 0 ? textContent.lineHeight : 0.001;
                text.maxWidth = textContent.useWidth ? textContent.width : Infinity;
                text.whiteSpace = textContent.mode === "nowrap" ? "nowrap" : "normal";
                text.letterSpacing = textContent.letterSpacing;

                text.material = new THREE.MeshBasicMaterial({
                    // content.color ist bereits Editor-Format (0..255) — NICHT durch
                    // threeToColor jagen (erwartet THREE 0..1, multipliziert nochmal
                    // mit 255 -> ungueltiger Hex -> THREE-Fallback WEISS; Jans Fund #6).
                    color: colorToHex(textContent.color),
                    side: textContent.doubleSide ? THREE.DoubleSide : THREE.FrontSide,
                    transparent: true,
                });
                text.material.needsUpdate = true;
                applyTextShadow(text, textContent);

                // TODO clean up with text.dispose()
                text.anchorX = textContent.xAlign;
                text.anchorY = textContent.yAlign === "center" ? "middle" : textContent.yAlign;

                const scaleFactor = 2.0;
                text.scale.set(scaleFactor, scaleFactor, scaleFactor);

                // Explicitly trigger troika's async SDF atlas build.
                // Without this, troika relies on Three.js's onBeforeRender
                // hook which may never fire if the render loop is stopped
                // (the editor calls stop() after initial load). The sync
                // callback re-renders the scene once the atlas is ready.
                this.offeneTextSyncs += 1;
                text.sync(() => {
                    this.offeneTextSyncs = Math.max(0, this.offeneTextSyncs - 1);
                    // Font atlas built — force a Three.js re-render so the
                    // correct font appears even if the animation loop is stopped.
                    if (this.webGLRenderer && this.webGlScene && this.camera) {
                        // Atlas jetzt auf die GPU, nicht erst beim ersten
                        // sichtbaren Bild des Textes (nach START, Safari: je
                        // Upload bis 300 ms) — der Text ist hier meist noch
                        // ausgeblendet und wird vom render darunter nicht gezeichnet.
                        const atlas = (text as TODOAny).textRenderInfo?.sdfTexture;
                        if (atlas) {
                            this.troikaAtlanten.add(atlas);
                            this.webGLRenderer.initTexture(atlas);
                        }
                        this.webGLRenderer.render(this.webGlScene, this.camera);
                    }
                });

                const textAnchor = new THREE.Object3D();
                textAnchor.add(text);

                objLoaded(textAnchor);
            }
        }
        /* eslint-enable */
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    // Port "Vektor-Layer" (Jans Fund, 1:1 aus Jans Original-updateSvg): vorher
    // konnte der geteilte Renderer nur flache SVGs bei URL-Wechsel laden —
    // 3D-Tiefe/Fase, Silhouette, 3D-Schatten-Flags und deren Re-Dirty-Trigger
    // fehlten komplett, ebenso das CSS-Style-Inlining fuer Illustrator-Exporte.
    updateSvg(layerId: string, content: any) {
        const animatable3DLayer = this.layerState3d[layerId];
        // EIGENES GEDÄCHTNIS des 3D-Objekts (Jan 22.10.2026: „SVG mit
        // Eckenrundung als 3D-Objekt umgewandelt — die Rundung verschwindet,
        // die Raute wird größer“): layerContentCache teilt sich die Ebene mit
        // dem HTML-Weg. Wurde dort weitergeändert, hielt der 3D-Weg sein ALTES
        // Objekt für aktuell. Verglichen wird mit dem Inhalt, aus dem das
        // 3D-Objekt gebaut wurde.
        const cachedLayerContent =
            (animatable3DLayer as TODOAny)?.userData?.svgInhalt ?? this.layerContentCache[layerId];
        const urlChanged =
            // TODO Change to .key once we are sure all stories have only assets with .key
            !cachedLayerContent || content.url !== cachedLayerContent.url;
        // re-extrude when the 3D depth or bevel changes (rebuilds geometry from
        // cached paths, no re-fetch)
        const depthChanged = (cachedLayerContent?.extrudeDepth ?? 0) !== (content.extrudeDepth ?? 0);
        const bevelChanged = (cachedLayerContent?.extrudeBevel ?? 12) !== (content.extrudeBevel ?? 12);
        // re-apply the (extruded) shadow flags by rebuilding from cached paths
        const shadowChanged =
            (cachedLayerContent?.castShadowReal ?? true) !== (content.castShadowReal ?? true) ||
            (cachedLayerContent?.receiveShadowReal ?? true) !== (content.receiveShadowReal ?? true);
        const silhouetteChanged =
            (cachedLayerContent?.extrudeSilhouette ?? false) !== (content.extrudeSilhouette ?? false) ||
            JSON.stringify(cachedLayerContent?.silhouetteColor) !== JSON.stringify(content.silhouetteColor);
        const isDirty =
            !animatable3DLayer || urlChanged || depthChanged || bevelChanged || shadowChanged || silhouetteChanged;
        if (!isDirty || this.layerIsLoading[layerId]) return;

        const url = getLayerContentUrl(content);

        // Build the 3D group from parsed SVG paths. depth 0 = flat unlit shapes
        // (original behaviour); depth > 0 = extruded solid with a lit material so
        // an uploaded logo/icon becomes a real 3D object. Depth is a percentage
        // of the SVG's smaller dimension, so it scales with any viewBox.
        const buildGroup = (allePfade: TODOAny): TODOAny => {
            // FLÄCHEN nur aus Pfaden MIT Füllung (Jan 22.10.2026, Grundformen
            // ohne HTML: „Kreis nur mit Kontur“ wurde eine volle Scheibe,
            // eine Linie ein Klumpen) — fill="none" heißt keine Fläche; die
            // Kontur zeichnet konturen() unten.
            const paths = (allePfade as TODOAny[]).filter(
                (p: TODOAny) => p.userData?.style?.fill !== "none",
            );
            // Kurven feiner zerlegen (Jan 25.10.2026: „der Kreis ist beim
            // Extrudieren nicht so richtig schön rund“) — mit 12 Stücken je
            // Kurve war ein Kreis ein Zwölfeck. Deckel und Körper gleich fein.
            const KURVEN_SEGMENTE = 48;
            // FLÄCHEN AUS DEM PFAD: toShapes kommt mit SICH SELBST SCHNEIDENDEN
            // Pfaden nicht klar (Jan 25.10.2026, Stern „Linien kreuzen“ als
            // 3D-Objekt: Teile der Füllung fehlten) — createShapes rechnet die
            // Schnittpunkte und die Füllregel (nonzero/evenodd = „Mitte
            // aussparen“) wie der Browser. Nur für Grundformen; eigene SVGs
            // bleiben beim bewährten Weg.
            const formenVon = (path: TODOAny): TODOAny[] =>
                content.grundform ? SVGLoader.createShapes(path) : path.toShapes(true);
            // Verlauf je Pfad einmal (Textur geteilt von Deckeln und Seiten)
            const verlaeufe = new Map<TODOAny, ReturnType<typeof svgVerlaufFuerPfad>>();
            const verlaufVon = (path: TODOAny) => {
                if (!verlaeufe.has(path)) verlaeufe.set(path, svgVerlaufFuerPfad(path));
                return verlaeufe.get(path) ?? null;
            };
            const pfadFarbe = (path: TODOAny) => {
                const verlauf = verlaufVon(path);
                return verlauf
                    ? { color: new THREE.Color(1, 1, 1), map: verlauf.textur, transparent: verlauf.durchsichtig }
                    : { color: path.color };
            };
            const ref = Math.min(content.width || 0, content.height || 0) || 100;
            const depth = (Math.max(0, content.extrudeDepth ?? 0) / 100) * ref;
            const extruded = depth > 0;
            const group = new THREE.Group();
            const silhouette = extruded && !!content.extrudeSilhouette;
            if (silhouette) {
                // extrude EVERY path's shapes together as ONE solid single-colour
                // body (instead of a separate extruded body per path) so complex/
                // overlapping SVGs don't fragment into the "exploded" look
                const bevel = Math.min((Math.max(0, content.extrudeBevel ?? 12) / 100) * depth, depth);
                const allShapes: TODOAny[] = [];
                for (const path of paths) {
                    for (const shape of formenVon(path)) allShapes.push(shape);
                }
                if (allShapes.length) {
                    const geometry = new THREE.ExtrudeGeometry(allShapes, {
                        depth,
                        bevelEnabled: bevel > 0,
                        bevelThickness: bevel,
                        bevelSize: bevel,
                        // smooth rounded edge: three.js bevels along a quarter-
                        // circle (cos/sin) with bevelThickness == bevelSize, so
                        // more segments turn the chunky 2-facet chamfer into a
                        // real round edge
                        bevelSegments: 6,
                        curveSegments: KURVEN_SEGMENTE,
                    });
                    geometry.translate(0, 0, -depth / 2);
                    const material = new THREE.MeshStandardMaterial({
                        color: content.silhouetteColor
                            ? colorToThree(content.silhouetteColor)
                            : new THREE.Color(0.7, 0.7, 0.72),
                        side: THREE.DoubleSide,
                        metalness: 0,
                        roughness: 0.55,
                    });
                    const mesh = new THREE.Mesh(geometry, material);
                    mesh.castShadow = content.castShadowReal ?? true;
                    mesh.receiveShadow = content.receiveShadowReal ?? true;
                    group.add(mesh);
                }
            }
            if (!silhouette && extruded) {
                // ERKENNTNIS (Scrolly-Star-Analyse): viele Grafiken sind ein
                // PUZZLE disjunkter Teile (Kante an Kante), keine Stapel — jede
                // TEIL-Fase erzeugt dann an jeder inneren Stosskante eine Rille
                // ("unterschiedliche Hoehen", Jans Fund). Deshalb zwei Modi:
                // • Runde Kanten > 0: EIN verschmolzener Koerper traegt die Fase
                //   NUR AUSSEN (Jans Vorgabe "nur die Pfade am Rand"); die
                //   Grafik liegt als buendige Farb-Deckel auf beiden Plateaus
                //   (Vorfahrt via polygonOffset-Units, kein Geometrie-Versatz).
                //   Seiten-/Fasenfarbe = Silhouetten-Farbe (waehlbar im Panel).
                // • Runde Kanten = 0: scharfe Einzelteile mit farbigen Seiten
                //   (1.0-Look) — ohne Fase stossen sie fugenlos aneinander.
                const bevel = Math.min((Math.max(0, content.extrudeBevel ?? 12) / 100) * depth, depth);
                if (bevel > 0) {
                    const allShapes: TODOAny[] = [];
                    for (const path of paths) {
                        for (const shape of formenVon(path)) allShapes.push(shape);
                    }
                    if (allShapes.length) {
                        const geometry = new THREE.ExtrudeGeometry(allShapes, {
                            depth,
                            bevelEnabled: true,
                            bevelThickness: bevel,
                            bevelSize: bevel,
                            // smooth rounded edge (quarter-circle bevel)
                            bevelSegments: 6,
                            curveSegments: KURVEN_SEGMENTE,
                        });
                        geometry.translate(0, 0, -depth / 2);
                        const bodyMaterial = new THREE.MeshStandardMaterial({
                            color: content.silhouetteColor
                                ? colorToThree(content.silhouetteColor)
                                : new THREE.Color(0.7, 0.7, 0.72),
                            side: THREE.DoubleSide,
                            metalness: 0,
                            roughness: 0.55,
                        });
                        const body = new THREE.Mesh(geometry, bodyMaterial);
                        body.castShadow = content.castShadowReal ?? true;
                        body.receiveShadow = content.receiveShadowReal ?? true;
                        group.add(body);
                    }
                    // Buendige Farb-Deckel (beide Plateaus); polygonOffset gibt
                    // ihnen Vorfahrt auf der deckungsgleichen Flaeche.
                    // WICHTIG (Jans Fund "hinterer Stern guckt durch"): Deckel
                    // sind FrontSide-only und die Rueckseite ist um Y gespiegelt
                    // (zeigt nach hinten) — von vorn werden Rueck-Deckel damit
                    // per Backface-Culling verworfen und koennen an den
                    // Silhouettenkanten nicht mehr durch den Koerper bluten.
                    // Bias klein halten und PRO SEITE zaehlen: der alte
                    // durchlaufende Zaehler summierte sich ueber ~22 Deckel auf
                    // ~80 Tiefenstufen — genug, um die Koerperwand zu schlagen.
                    const capZ = depth / 2 + bevel;
                    // Die Gruppe wird unten per rotateZ(PI)+rotateY(PI) geflippt
                    // (= 180° um X): Gruppenraum-z=-capZ landet VORN (Welt +z).
                    // Statt Dreh-/Spiegel-Hacks regeln die CULLING-Seiten die
                    // Sichtbarkeit: der vordere Deckel (side -1) rendert als
                    // BackSide (von vorn sichtbar, Artwork unverzerrt), der
                    // hintere (side +1) als FrontSide (nur von hinten sichtbar,
                    // dort physikalisch korrekt gespiegelt). So kann kein
                    // Rueck-Deckel mehr durch den Koerper bluten (Jans Fund).
                    for (const side of [1, -1]) {
                        let capIndex = 0;
                        for (const path of paths) {
                            const verlauf = verlaufVon(path);
                            for (const shape of formenVon(path)) {
                                const faceGeometry = new THREE.ShapeGeometry(shape, KURVEN_SEGMENTE);
                                if (verlauf) svgVerlaufAufGeometrie(faceGeometry, verlauf.u);
                                const faceMaterial = new THREE.MeshBasicMaterial({
                                    ...pfadFarbe(path),
                                    side: side === -1 ? THREE.BackSide : THREE.FrontSide,
                                    fog: false,
                                    polygonOffset: true,
                                    polygonOffsetFactor: 0,
                                    polygonOffsetUnits: -(capIndex + 1) * 2,
                                });
                                const face = new THREE.Mesh(faceGeometry, faceMaterial);
                                face.position.z = side * capZ;
                                group.add(face);
                                capIndex += 1;
                            }
                        }
                    }
                } else {
                    // Scharfe Einzelteile: Deckel = unbeleuchtete Grafikfarbe,
                    // Seiten = beleuchtete Pfadfarbe (Material-Gruppen).
                    let bodyIndex = 0;
                    for (const path of paths) {
                        const verlauf = verlaufVon(path);
                        for (const shape of formenVon(path)) {
                            const geometry = new THREE.ExtrudeGeometry(shape, {
                                depth,
                                bevelEnabled: false,
                                curveSegments: KURVEN_SEGMENTE,
                            });
                            geometry.translate(0, 0, -depth / 2);
                            if (verlauf) svgVerlaufAufGeometrie(geometry, verlauf.u);
                            const offsetProps = {
                                polygonOffset: true,
                                polygonOffsetFactor: 0,
                                polygonOffsetUnits: -(bodyIndex + 1) * 4,
                            };
                            const capMaterial = new THREE.MeshBasicMaterial({
                                ...pfadFarbe(path),
                                side: THREE.DoubleSide,
                                fog: false,
                                ...offsetProps,
                            });
                            const sideMaterial = new THREE.MeshStandardMaterial({
                                ...pfadFarbe(path),
                                side: THREE.DoubleSide,
                                metalness: 0,
                                roughness: 0.55,
                                ...offsetProps,
                            });
                            const mesh = new THREE.Mesh(geometry, [capMaterial, sideMaterial]);
                            mesh.castShadow = content.castShadowReal ?? true;
                            mesh.receiveShadow = content.receiveShadowReal ?? true;
                            bodyIndex += 1;
                            group.add(mesh);
                        }
                    }
                }
            }
            if (!silhouette && !extruded) {
                for (const path of paths) {
                    const verlauf = verlaufVon(path);
                    const shapes = formenVon(path);
                    for (const shape of shapes) {
                        const geometry = new THREE.ShapeGeometry(shape, KURVEN_SEGMENTE);
                        if (verlauf) svgVerlaufAufGeometrie(geometry, verlauf.u);
                        const material = new THREE.MeshBasicMaterial({
                            ...pfadFarbe(path),
                            opacity: 1.0,
                            side: THREE.DoubleSide,
                            depthWrite: false,
                            transparent: true,
                            fog: false,
                        });
                        group.add(new THREE.Mesh(geometry, material));
                    }
                }
            }
            // KONTUREN in 3D (Jan 22.10.2026: „gebe ich eine weiße Kontur,
            // dann passiert nichts“): toShapes kennt nur Flächen, Striche
            // stehen bloß als Stil am Pfad. Sie werden als flache Fläche
            // (SVGLoader.pointsToStroke) auf die VORDERSEITE gelegt — bei
            // Tiefe 0 über die Flächen. Gruppenraum-z negativ = vorn (die
            // Gruppe wird unten um X gedreht). Strichmuster (gestrichelt)
            // kennt pointsToStroke nicht — darum zerlegt strichAbschnitte()
            // den Strich vorher selbst (Jan 22.10.2026: „die Checkbox
            // gestrichelt funktioniert irgendwann nicht mehr“). Grundform
            // Linie/Pfeil: jeder Abschnitt merkt seine Lage für den AUFBAU in
            // 3D (grundformAufbau3d, pro Frame), die Pfeilspitze wandert mit.
            {
                const kante = extruded
                    ? Math.min((Math.max(0, content.extrudeBevel ?? 12) / 100) * depth, depth)
                    : 0;
                const mitKante = silhouette || (extruded && kante > 0);
                const vorn = extruded ? -(depth / 2 + (mitKante ? kante : 0)) - 0.5 : -0.5;
                let strichIndex = 0;
                for (const pfad of allePfade as TODOAny[]) {
                    const stil = pfad.userData?.style;
                    if (!stil?.stroke || stil.stroke === "none" || !(stil.strokeWidth > 0)) continue;
                    const deckkraft = Math.max(0, Math.min(1, stil.strokeOpacity ?? 1));
                    const material = new THREE.MeshBasicMaterial({
                        color: new THREE.Color().setStyle(stil.stroke),
                        side: THREE.DoubleSide,
                        fog: false,
                        transparent: deckkraft < 1,
                        opacity: deckkraft,
                        polygonOffset: true,
                        polygonOffsetFactor: 0,
                        polygonOffsetUnits: -60 - strichIndex * 2,
                    });
                    material.userData.eigeneDeckkraft = deckkraft;
                    const knoten = pfad.userData?.node as Element | undefined;
                    const muster = String(knoten?.getAttribute?.("stroke-dasharray") ?? "")
                        .split(/[\s,]+/)
                        .map(Number)
                        .filter((n) => Number.isFinite(n) && n >= 0);
                    const linienAufbau = !!content.grundform && knoten?.nodeName === "line";
                    for (const teil of pfad.subPaths ?? []) {
                        for (const abschnitt of strichAbschnitte(teil.getPoints(), muster)) {
                            const geometrie = SVGLoader.pointsToStroke(abschnitt, stil);
                            if (!geometrie) continue;
                            const strich = new THREE.Mesh(geometrie, material);
                            strich.position.z = vorn;
                            strich.renderOrder = 1;
                            if (linienAufbau) {
                                const xs = abschnitt.map((v: TODOAny) => v.x);
                                strich.userData.aufbau = { start: Math.min(...xs), ende: Math.max(...xs) };
                            }
                            group.add(strich);
                        }
                    }
                    strichIndex += 1;
                }
                // Pfeilspitze = alle Flächen des Pfeils (die Linie hat keine)
                if (content.grundform?.art === "pfeil") {
                    const linie = (allePfade as TODOAny[]).find((p) => p.userData?.node?.nodeName === "line");
                    // Weg der Spitze = Linienlänge (x2 − x1): beim Nullpunkt am
                    // Anfang beginnt die Linie nicht mehr bei x = 0
                    const knotenLinie = linie?.userData?.node as Element | undefined;
                    const weg =
                        (Number(knotenLinie?.getAttribute?.("x2")) || 0) -
                        (Number(knotenLinie?.getAttribute?.("x1")) || 0);
                    for (const kind of group.children) {
                        if (kind.userData.aufbau) continue;
                        kind.userData.aufbauKopf = { basisX: kind.position.x, weg };
                    }
                }
            }
            group.position.set(-content.width / 2, content.height / 2, 0);
            const anchor = new THREE.Object3D();
            group.rotateZ(Math.PI);
            group.rotateY(Math.PI);
            anchor.add(group);
            return anchor;
        };

        const finish = (anchor: TODOAny) => {
            anchor.userData.svgInhalt = content;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            this.uploadToGpu(anchor as any);
            this.props.renderer.loadedLayer(layerId, url);
            this.layerState3d[layerId] = anchor;
            this.layerContentCache[layerId] = content;
            this.layerMeshes[layerId] = anchor;
            this.layerIsLoading[layerId] = false;
        };

        // depth-only change: rebuild geometry from the cached paths, no re-fetch
        const cached = this.svgPathsCache[layerId];
        if (!urlChanged && cached && cached.url === url) {
            finish(buildGroup(cached.paths));
            return;
        }

        this.layerIsLoading[layerId] = true;
        const svgLoader = new SVGLoader();
        // SVGLoader ignores CSS <style> blocks (e.g. Illustrator exports with
        // .st0 classes) — let the browser compute the styles and inline them
        // first (fetch + parse statt svgLoader.load, wie Jans Original)
        fetch(url)
            .then((response) => response.text())
            .then((svgText) => {
                const data = svgLoader.parse(inlineSvgComputedStyles(svgText));
                this.svgPathsCache[layerId] = { url, paths: data.paths };
                finish(buildGroup(data.paths));
            })
            .catch((error) => {
                console.error(`[RendererWebGL] Failed to load SVG: ${url}`, error);
                this.layerIsLoading[layerId] = false;
                this.props.renderer.loadedLayer(layerId, url);
            });
    }

    /** Lokale Blob-URLs der im Boot vorgeladenen Startvideos (Freigabe in dispose). */
    private startVideoBlobUrls: string[] = [];
    /** Ab wann der Vorlader nach dem START-Klick loslegen darf (0 = noch nicht gestartet). */
    private vorladerFreiAb = 0;
    /** Wie das Startvideo geladen wurde — fuers ?startdebug-HUD (Geraetetests). */
    private startVideoLadeInfo: Record<string, { weg: string; ms: number; bytes: number }> = {};
    // ?startdebug=1 — Live-HUD des Video-Ladezustands (Diagnose am Geraet).
    _startDebugTimer: ReturnType<typeof setInterval> | undefined;
    _startDebugHud: HTMLDivElement | undefined;

    /**
     * Ist dieses Video schon auf dem Startbildschirm zu sehen? Dann gehoert
     * es zum Boot wie jedes andere Asset (Begruendung an holeVideoAlsBlob).
     * `layerFrom` = Lebensdauer-Beginn des Layers; Startslide = erste Slide.
     */
    private istStartVideo(layerFrom: number | undefined, url: string): boolean {
        if (layerFrom === undefined) return false;
        if (WebGLAssets.editorMode || this.props.editorMode) return false;
        if (!/^https?:/.test(url)) return false;
        const slides = (this.props.presentation as TODOAny)?.slides as { from?: number }[] | undefined;
        const startSlideFrom = slides?.[0]?.from ?? 0;
        return layerFrom <= startSlideFrom;
    }

    updateVideo(
        layerId: string,
        content: VideoContent,
        liveUpdate: boolean = false,
        layerFrom?: number
    ) {
        // Leerer Video-Slot — nichts laden. #5-Fix (Laura 2026-07-28): via
        // "Entfernen" geleerter Slot mit bestehendem Mesh → Mesh verstecken +
        // Video pausieren, sonst bliebe das alte Video sichtbar/aktiv.
        if (!content || !content.url) {
            const existing = this.layerMeshes[layerId] as THREE.Mesh | undefined;
            if (existing) existing.visible = false;
            const cachedVideo = this.videoCache[layerId];
            if (cachedVideo) cachedVideo.pause();
            this.layerContentCache[layerId] = content;
            // Als geladen melden (Benedikts Fund 02.09.2026: Scrolly mit via
            // "Entfernen" geleertem Video-Slot brauchte im Viewer 10 s zum
            // Start): setLayers registriert JEDEN Video-Layer in der Boot-
            // Ladeliste, dieser Fruehausstieg meldete aber nie loadedLayer —
            // der Boot wartete stur auf den 10-s-Watchdog. Idempotent.
            this.props.renderer.loadedLayer(layerId, "leerer-medien-slot");
            return;
        }
        const rendererStore = this.props.renderer;
        const cachedLayerContent = this.layerContentCache[layerId];
        const { layerMeshes } = this;
        const layerContent: VideoContent = content;

        // Ton-/Trim-Regler wirken LIVE am laufenden Video (Jans 1.0). Ohne das
        // wuerde jede Reglerbewegung als Content-Aenderung gelten und das Video
        // komplett neu laden — Ruckeln, Neustart, und die Regler blieben
        // faktisch wirkungslos, weil der Renderer sie nie an das Element gab.
        // `muted` gehoert dazu (Jans Fund 08.09.2026): der Stumm-Schalter im
        // Editor baute das Video sonst bei jedem Klick komplett neu — Zucken
        // auf der Buehne und Neuaufbau aller davon abhaengigen Anzeigen.
        const LIVE_KEYS = ["volume", "fadeIn", "fadeOut", "trimStart", "trimEnd", "scrollDriven", "muted"];
        const existingVideo = this.videoCache[layerId];
        if (existingVideo) {
            existingVideo.setAudioOptions(
                layerContent.volume ?? 1,
                layerContent.fadeIn ?? 0,
                layerContent.fadeOut ?? 0
            );
            existingVideo.setTrim(layerContent.trimStart ?? 0, layerContent.trimEnd);
            existingVideo.setMuted(!!layerContent.muted);
        }

        const contentChange = !isEqual(
            omit(cachedLayerContent as TODOAny, LIVE_KEYS),
            omit(layerContent as TODOAny, LIVE_KEYS)
        );
        const isDirty = contentChange;
        if (liveUpdate) {
            // KEIN WebGLAssets.setVideosOnStart mehr (Jans WK-Fund 01.09.2026,
            // "Videos nach Upload spielen mal, mal nicht — bis zum Neuoeffnen"):
            // Das globale Static wurde hier gesetzt und NIE zurueckgesetzt —
            // ab dem ersten Live-Update war der normale Neuaufbau-Pfad unten
            // (`if (!WebGLAssets.setVideosOnStart)`) fuer ALLE Videos der
            // Sitzung tot; jeder weitere Video-Wechsel blieb stumm liegen.
            // Der Doppel-Aufbau, den das Flag verhindern sollte, ist laengst
            // anders abgesichert: layerContentCache wird im then aktualisiert,
            // contentChange ist danach false.
            this.layerIsLoading[layerId] = true;
            const oldVideo = this.videoCache[layerId];
            if (oldVideo) {
                oldVideo.pause();
                oldVideo.delete(); // fully release old video element + texture
            }

            const video: Video = new Video();
            const url = getLayerContentUrl(layerContent, this.buehneKlein());

            video.generateTexture(layerContent, url).then(({ material, width, height }) => {
                this.setVideoJit = true;
                // Dispose old mesh resources before replacing
                const oldMesh = layerMeshes[layerId] as THREE.Mesh;
                if (oldMesh) {
                    const oldMat = oldMesh.material as THREE.MeshBasicMaterial;
                    if (oldMat) {
                        if (oldMat.map) oldMat.map.dispose();
                        oldMat.dispose();
                    }
                    oldMesh.geometry?.dispose();
                }
                const geometry = new THREE.PlaneGeometry(1, 1);
                // Maße des GELADENEN Videos verwenden (Jans 1.0). Die im Layer
                // gespeicherten Werte stimmen nicht zwangslaeufig mit dem echten
                // Bild ueberein — bei .mov mit Rotations-Metadaten (Hochformat
                // vom Telefon) stehen dort die unrotierten Container-Maße, das
                // Video wurde dadurch verzerrt (Jans Fund). Fallback auf die
                // Content-Maße, falls das Element keine liefert.
                // SEITENVERHAELTNIS aus der Datei, GROESSE aus dem Layer
                // (Jans Fund 04.08.2026: "das Video wird bei natuerlich
                // geringer px-Groesse viel kleiner platziert").
                //
                // Der Rotations-Fix oben ist richtig — nur nahm er neben dem
                // Seitenverhaeltnis auch die GROESSE aus der Datei. Ein
                // 360x640-Video wurde dadurch zu einer 360x640-Flaeche, egal
                // wie gross der Template-Platz war: nachgemessen lagen beide
                // Videos bei 360x640, waehrend das Bild daneben 3072x4096 mass.
                // Deshalb wirkte ein getauschtes Video winzig.
                //
                // Jetzt: die Hoehe des Platzes behalten und die Breite aus dem
                // ECHTEN Seitenverhaeltnis ableiten — dieselbe Regel, die der
                // Template-Editor beim Tausch schon anwendet. Rotations-
                // Metadaten koennen dadurch weiterhin nicht verzerren, und ohne
                // Platz-Angabe bleibt es beim bisherigen Verhalten.
                const { w: getCalcWidth, h: getCalcHeight } = videoPlaneSize(
                    width,
                    height,
                    content.calcWidth,
                    content.calcHeight,
                    content.width,
                    content.height
                );
                geometry.scale(getCalcWidth, getCalcHeight, 1);
                const mesh1: THREE.Mesh = new THREE.Mesh(geometry, material);
                layerMeshes[layerId] = mesh1;
                mesh1.userData.videoLiveUpdate = true;
                this.layerContentCache[layerId] = content;
                // Fertig geladen — wie im Normal-Pfad unten. Blieb das Flag
                // haengen, galt der Layer dauerhaft als "laedt noch".
                this.layerIsLoading[layerId] = false;
            });
            this.videoCache[layerId] = video;
            // Cutover Schritt 2c: den Video-Cache mit dem Editor teilen (fuer
            // dessen Timeline-Steuerung). No-op ohne editorStore (Reader).
            if (this.props.editorStore) this.props.editorStore.videoCache = this.videoCache;
            // cache the layer
            this.layerContentCache[layerId] = content;
        }
        if (!WebGLAssets.setVideosOnStart) {
            if (isDirty && !this.layerIsLoading[layerId]) {
                this.layerIsLoading[layerId] = true;
                const oldVideo = this.videoCache[layerId];
                if (oldVideo) {
                    oldVideo.pause();
                    oldVideo.delete(); // fully release old video element + texture
                }
                const video = new Video();
                // TODO DESCR-218: revisit
                // const url = createCdnAssetUrl(layerContent);
                const url = getLayerContentUrl(layerContent, this.buehneKlein());
                // STARTVIDEO GEHOERT ZUM BOOT (Jans iPhone-Fund 02.09.2026,
                // Begruendung an holeVideoAlsBlob): Alle anderen Videos
                // gelten sofort als geladen und streamen — nur das Video, das
                // schon auf dem Startbildschirm laeuft, wird wie ein Bild
                // komplett vorgeholt und meldet sich erst dann; der Ladebalken
                // wartet darauf (begrenzt, s. START_VIDEO_ZEITLIMIT_MS).
                const startVideo = this.istStartVideo(layerFrom, url);
                // always load the layer, does not mater if it fails or succeeds
                if (!startVideo) rendererStore.loadedLayer(layerId, url);

                let getCalcWidth: number = 0;
                let getCalcHeight: number = 0;

                if (layerContent.calcWidth) {
                    if (layerContent.calcWidth > 0) {
                        getCalcWidth = layerContent.calcWidth;
                        getCalcHeight = layerContent.calcHeight;
                    }
                } else {
                    getCalcWidth = layerContent.width;
                    getCalcHeight = layerContent.height;
                }

                // POSTER-STRATEGIE (WK-Befund 1, Jan+Benedikt 31.08.2026):
                // Das Video-Mesh entsteht erst nach loadedmetadata — beim
                // Blob-Vorladen also erst nach KOMPLETTEM Download. Bis dahin
                // war die Flaeche leer (Standbild-Luecke, auf Mobilfunk
                // sekundenlang). Das beim Upload erzeugte Poster (erstes
                // Frame, /resource/:id/poster) steht sofort als Zwischenmesh
                // auf der Buehne; sobald das Video bereit ist, wird NUR das
                // Material getauscht und die Geometrie nachgezogen — kein
                // Szenenbaum-Umbau (buildLayerTree-Falle). Ohne Poster
                // (Alt-Bestand, Legacy-URLs, 404) bleibt alles wie bisher.
                // Transparente Videos (Chroma-Key-Shader) bewusst ohne
                // Poster — ein JPEG hat keine Stanzung.
                // POSTER-SCHONFRIST (Jans Go 02.09.2026, „Poster-Zucken"):
                // Auf schnellen Verbindungen stand das Poster nur fuer
                // Sekundenbruchteile und der Tausch Poster→Film zuckte
                // sichtbar (weiches /img-Webp gegen scharfes Video-Frame).
                // Das Poster ist ein NETZ fuer langsame Verbindungen, kein
                // Pflicht-Zwischenbild — es erscheint erst, wenn das Video
                // nach der Schonfrist immer noch nicht bereit ist. Schnelle
                // Verbindung: Hintergrund→Film ohne Zwischenbild.
                const POSTER_SCHONFRIST_MS = 600;
                const posterStart = typeof performance !== "undefined" ? performance.now() : Date.now();
                let videoFertig = false;
                // Getrimmte Videos OHNE Poster (Jans iPhone-Fund 02.09.2026,
                // „Video springt am Anfang"): Das Upload-Poster ist immer Bild 0
                // der Datei — bei Trim-Start > 0 zeigt es eine Stelle, die nie zu
                // sehen sein soll, und der Wechsel auf das echte Bild wirkt als
                // Sprung. Lieber kurz nichts als das falsche Bild.
                const getrimmt = (layerContent.trimStart ?? 0) > 0.1;
                const posterUrl = layerContent.transparent || getrimmt
                    ? null
                    : posterUrlFuerVideo(url);
                if (posterUrl) {
                    new THREE.ImageLoader().load(
                        posterUrl,
                        (bild) => {
                            if (videoFertig || layerMeshes[layerId]) return;
                            const zeigePoster = () => {
                                // Video kann waehrend der Schonfrist fertig
                                // geworden sein — dann kein Poster mehr.
                                if (videoFertig || layerMeshes[layerId]) return;
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                const posterTexture: any = new THREE.Texture(bild);
                                deckleTexturFuerMobil(posterTexture);
                                posterTexture.minFilter = THREE.LinearFilter;
                                posterTexture.magFilter = THREE.LinearFilter;
                                posterTexture.needsUpdate = true;
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                posterTexture.colorSpace = (THREE as any).SRGBColorSpace;
                                const posterMaterial = new THREE.MeshBasicMaterial({
                                    map: posterTexture,
                                    alphaTest: 0.01,
                                    transparent: true,
                                    depthWrite: false,
                                });
                                const geometry = new THREE.PlaneGeometry(1, 1);
                                const masse = videoPlaneSize(
                                    bild.width,
                                    bild.height,
                                    getCalcWidth,
                                    getCalcHeight
                                );
                                geometry.scale(masse.w, masse.h, 1);
                                const posterMesh = new THREE.Mesh(geometry, posterMaterial);
                                posterMesh.userData.posterInterim = true;
                                posterMesh.userData.masse = masse;
                                layerMeshes[layerId] = posterMesh;
                            };
                            const jetzt = typeof performance !== "undefined" ? performance.now() : Date.now();
                            const rest = POSTER_SCHONFRIST_MS - (jetzt - posterStart);
                            if (rest <= 0) zeigePoster();
                            else setTimeout(zeigePoster, rest);
                        },
                        undefined,
                        () => {
                            /* kein Poster vorhanden — Verhalten wie bisher */
                        }
                    );
                }

                // Startvideo: erst der Blob (lokal, spielt beim Klick sofort),
                // dann das Element — mit der Netz-URL als Rueckfall, wenn der
                // Blob nicht kommt. Das Poster oben laeuft unabhaengig weiter
                // und faengt auch diesen Fall ab.
                const ladeBeginn = typeof performance !== "undefined" ? performance.now() : Date.now();
                const ladeQuelle: Promise<string> = startVideo
                    ? holeVideoAlsBlob(url, START_VIDEO_ZEITLIMIT_MS).then((blob) => {
                          if (blob) this.startVideoBlobUrls.push(blob.blobUrl);
                          const jetzt = typeof performance !== "undefined" ? performance.now() : Date.now();
                          this.startVideoLadeInfo[layerId] = {
                              weg: blob ? "blob" : "stream (blob nicht gekommen)",
                              ms: Math.round(jetzt - ladeBeginn),
                              bytes: blob?.groesse ?? 0,
                          };
                          if (blob) {
                              rendererStore.loadedLayer(layerId, url);
                          } else {
                              // STREAM-FALL: START ERST, WENN DAS STARTVIDEO
                              // ABSPIELBEREIT IST (Jans Slow-4G-Test 05.09.2026:
                              // „ich kann START druecken, obwohl das erste Video
                              // noch nicht geladen ist"). Ohne Blob meldete sich
                              // das Startvideo sofort als fertig, der Boot war
                              // durch, START frei — und das Video pufferte erst
                              // dann. Jetzt zaehlt es erst mit genug Puffer
                              // (readyState >= HAVE_FUTURE_DATA) als geladen,
                              // spaetestens nach dem Zeitlimit — der Boot-
                              // Watchdog (10 s) bleibt die harte Grenze.
                              const el = video.videoPlayer;
                              let gemeldet = false;
                              const melden = (grund: string) => {
                                  if (gemeldet) return;
                                  gemeldet = true;
                                  el.removeEventListener("canplay", pruefen);
                                  el.removeEventListener("loadeddata", pruefen);
                                  el.removeEventListener("error", pruefen);
                                  // Fuer das ?startdebug-HUD: wann/warum der Boot
                                  // das Startvideo freigegeben hat.
                                  const info = this.startVideoLadeInfo[layerId];
                                  if (info) {
                                      const jetztMs =
                                          typeof performance !== "undefined" ? performance.now() : Date.now();
                                      info.weg = `${info.weg} → frei: ${grund} rs=${el.readyState} nach ${Math.round(jetztMs - ladeBeginn)}ms`;
                                  }
                                  rendererStore.loadedLayer(layerId, `${url} (${grund})`);
                              };
                              const pruefen = () => {
                                  if (el.error) melden("fehler");
                                  else if (el.readyState >= 3) melden("puffer");
                              };
                              el.addEventListener("canplay", pruefen);
                              el.addEventListener("loadeddata", pruefen);
                              el.addEventListener("error", pruefen);
                              setTimeout(() => melden("zeitlimit"), START_VIDEO_ZEITLIMIT_MS);
                              pruefen();
                          }
                          return blob ? blob.blobUrl : url;
                      })
                    : Promise.resolve(url);
                // NUR DAS STARTVIDEO LAEDT BEIM BOOT (Jans Slow-4G-Test 05.09.2026):
                // Vorher standen alle Video-Elemente mit preload="auto" da und
                // zogen ab dem Boot parallel je ~10 s Puffer — sieben Streams
                // teilten sich die Leitung, das 16,8-MB-Startvideo hungerte, zwei
                // hintere Videos waren zuerst da. Im Reader bekommen alle
                // anderen Videos zunaechst KEINE Quelle (preload="none" +
                // Video.aufgeschobeneQuelle), bis der Boot durch ist — also das
                // Startvideo abspielbereit (zurueckgehalteneVideosFreigeben).
                // Nicht erst bei START (Jan 05.09.): wer nach dem Start schnell
                // scrollt, soll keine schwarzen Videos sehen. Editor: "auto".
                const imEditor = !!(WebGLAssets.editorMode || this.props.editorMode);
                const bootDurch =
                    !!(this.props.renderer as TODOAny).playbackStarted || rendererStore.finishedLoading;
                const vorladen: "auto" | "none" = startVideo || imEditor || bootDurch ? "auto" : "none";
                ladeQuelle
                    .then((quelle) => video.generateTexture(layerContent, quelle, vorladen))
                    .then(({ material, width, height }) => {
                    videoFertig = true;
                    // s. o.: Seitenverhaeltnis aus der Datei (Rotations-
                    // Metadaten bei .mov), Groesse aus dem Layer-Platz.
                    const masse = videoPlaneSize(width, height, getCalcWidth, getCalcHeight);
                    // ERST MIT BILD AUF DIE BUEHNE (Jans iPhone-Fund 02.09.2026):
                    // Bisher kam die Video-Flaeche schon bei den Metadaten — auf
                    // iOS heisst das: ein VideoTexture OHNE dekodiertes Bild
                    // (Safari laedt vor play() keine Bilddaten) loeste das Poster
                    // ab, die Flaeche stand schwarz/leer, bis das erste Bild kam;
                    // getrimmt zusaetzlich auf der falschen Stelle. Jetzt bleibt
                    // das Poster (bzw. nichts) stehen, bis das Element wirklich
                    // ein Bild an seiner Position hat — readyState >= 2, sonst
                    // das naechste loadeddata/seeked/playing. Auf dem Desktop
                    // ist das derselbe Moment wie bisher.
                    const el = video.videoPlayer;
                    let getauscht = false;
                    const aufBuehne = () => {
                        if (getauscht) return;
                        getauscht = true;
                        const interim = layerMeshes[layerId] as THREE.Mesh | undefined;
                        if (interim?.userData?.posterInterim) {
                            // Material-Tausch am BESTEHENDEN Mesh (Begruendung oben).
                            const altesMaterial = interim.material as THREE.MeshBasicMaterial;
                            altesMaterial.map?.dispose();
                            altesMaterial.dispose();
                            interim.material = material;
                            const alteMasse = interim.userData.masse as { w: number; h: number };
                            if (alteMasse?.w > 0 && alteMasse?.h > 0) {
                                interim.geometry.scale(masse.w / alteMasse.w, masse.h / alteMasse.h, 1);
                            }
                            interim.userData.posterInterim = false;
                        } else if (interim) {
                            // INHALTSWECHSEL AM BESTEHENDEN VIDEO-MESH (Jans Fund
                            // 04.09.2026: "Videos werden schwarz, wenn man etwas
                            // in den Einstellungen aendert, z. B. Beidseitig").
                            // Jede Content-Aenderung (Beidseitig, Tausch, Maske,
                            // Schleife …) loescht oben das alte Video samt Textur
                            // und laedt ein neues. Bis #602 wurde hier IMMER ein
                            // neues Mesh angelegt; seitdem griff der Zweig nur
                            // noch ohne vorhandenes Mesh — das alte blieb mit
                            // der Textur des geloeschten Elements stehen und
                            // zeigte Schwarz. Wie beim Poster: Material am
                            // bestehenden Mesh tauschen (kein Szenenbaum-Umbau),
                            // Geometrie auf die neuen Masse setzen.
                            const altesMaterial = interim.material as THREE.MeshBasicMaterial;
                            altesMaterial.map?.dispose();
                            altesMaterial.dispose();
                            interim.material = material;
                            interim.geometry?.dispose();
                            const geometry = new THREE.PlaneGeometry(1, 1);
                            geometry.scale(masse.w, masse.h, 1);
                            interim.geometry = geometry;
                            interim.userData.masse = masse;
                        } else {
                            const geometry = new THREE.PlaneGeometry(1, 1);
                            geometry.scale(masse.w, masse.h, 1);
                            const mesh = new THREE.Mesh(geometry, material);
                            mesh.userData.masse = masse;
                            layerMeshes[layerId] = mesh;
                        }
                    };
                    if (!el || el.readyState >= 2) {
                        aufBuehne();
                    } else {
                        const einmal = () => {
                            el.removeEventListener("loadeddata", einmal);
                            el.removeEventListener("seeked", einmal);
                            el.removeEventListener("playing", einmal);
                            if (el.readyState >= 2) aufBuehne();
                            else if (!getauscht) {
                                // Noch kein Bild — beim naechsten Ereignis erneut pruefen.
                                el.addEventListener("loadeddata", einmal);
                                el.addEventListener("seeked", einmal);
                                el.addEventListener("playing", einmal);
                            }
                        };
                        el.addEventListener("loadeddata", einmal);
                        el.addEventListener("seeked", einmal);
                        el.addEventListener("playing", einmal);
                    }
                    this.layerIsLoading[layerId] = false;
                    this.layerContentCache[layerId] = content;
                });
                this.videoCache[layerId] = video;
                // Cutover Schritt 2c: Video-Cache mit dem Editor teilen (No-op ohne editorStore).
                if (this.props.editorStore) this.props.editorStore.videoCache = this.videoCache;
                // cache the layer
                this.layerContentCache[layerId] = content;
            }
        }
    }

    updateLottie(layerId: string, content: LottieContent, liveUpdate: boolean = false) {
        const rendererStore = this.props.renderer;
        const cachedLayerContent = this.layerContentCache[layerId];
        const { layerMeshes } = this;
        const layerContent: LottieContent = content;

        const contentChange = !isEqual(cachedLayerContent, layerContent);
        const isDirty = contentChange;

        if (liveUpdate) {
            this.layerIsLoading[layerId] = true;
            const oldLottie = this.lottieCache[layerId];
            if (oldLottie) {
                oldLottie.pause();
                oldLottie.delete();
            }

            const lottiePlayer = new LottiePlayer();
            const url = getLayerContentUrl(layerContent);

            lottiePlayer.generateTexture(layerContent, url).then(({ material }) => {
                const geometry = new THREE.PlaneGeometry(1, 1);
                const getCalcWidth: number = content.calcWidth || content.width || 1;
                const getCalcHeight: number = content.calcHeight || content.height || 1;
                geometry.scale(getCalcWidth, getCalcHeight, 1);
                const mesh = new THREE.Mesh(geometry, material);
                layerMeshes[layerId] = mesh;
                mesh.userData.lottieLiveUpdate = true;
                this.layerContentCache[layerId] = content;
            });
            this.lottieCache[layerId] = lottiePlayer;
            this.layerContentCache[layerId] = content;
        }

        if (isDirty && !this.layerIsLoading[layerId]) {
            this.layerIsLoading[layerId] = true;
            const oldLottie = this.lottieCache[layerId];
            if (oldLottie) {
                oldLottie.pause();
                oldLottie.delete();
            }
            const lottiePlayer = new LottiePlayer();
            const url = getLayerContentUrl(layerContent);
            rendererStore.loadedLayer(layerId, url);

            let getCalcWidth: number = 0;
            let getCalcHeight: number = 0;

            if (layerContent.calcWidth) {
                if (layerContent.calcWidth > 0) {
                    getCalcWidth = layerContent.calcWidth;
                    getCalcHeight = layerContent.calcHeight;
                }
            } else {
                getCalcWidth = layerContent.width;
                getCalcHeight = layerContent.height;
            }

            lottiePlayer.generateTexture(layerContent, url).then(({ material }) => {
                const geometry = new THREE.PlaneGeometry(1, 1);
                geometry.scale(getCalcWidth, getCalcHeight, 1);
                const mesh = new THREE.Mesh(geometry, material);
                layerMeshes[layerId] = mesh;
                this.layerIsLoading[layerId] = false;
                this.layerContentCache[layerId] = content;
            });
            this.lottieCache[layerId] = lottiePlayer;
            this.layerContentCache[layerId] = content;
        }
    }

    /**
     * Gilt die Studio-Backdrop-Dämpfung fuer dieses Dokument?
     *
     * Zwei Welten benutzen dasselbe Flag `isBackground`: Studio markiert damit
     * Backdrop-Fotos hinter Charts (die sollen gedaempft rendern), Templates
     * markieren damit ihr VOLLWERTIGES Hintergrundbild fuer die
     * „Titel & Hintergrund"-Kachel (neue Exporte via
     * markTemplateBackgroundLayers, Alt-Templates von Hand) — das darf NICHT
     * gedaempft werden (Jans Fund 06.08.2026: Templates wirkten weiss
     * ueberlagert). Unterschieden wird POSITIV am Studio-Ersteller im JSON
     * (istStudioScrolly, post-types) — `templateStructure` schreiben beide
     * Welten und trennt sie nicht (Alt-Template „Timeline" nachgemessen:
     * isBackground ohne templateSlideId, ABER frames vorhanden).
     */
    private studioBackdropAktiv(): boolean {
        return istStudioScrolly(this.templateJson as TODOAny);
    }

    /**
     * Aufwaerm-Bild: ein einziges Bild mit ALLEN Ebenen (auch den ausserhalb
     * ihrer Lebensdauer abgehaengten) in ein 4x4-Render-Target, mit
     * erzwungenem Schattenpass. Zweck: Programme, die three nur beim Rendern
     * anlegt — Tiefenmaterialien des Schattenpasses und Render-Target-
     * Varianten (srgb-linear) — entstehen jetzt hinter dem Ladebalken statt
     * beim ersten Erscheinen im Scroll (Jans Welcome-Scrolly, 08.09.2026:
     * 25-40 ms Haenger je Objekt, davor 207 ms mit Geometrie-Neuaufbau).
     *
     * Nur wenn es lohnt: Scrollys ohne 3D-Objekte haben weder Schattenpass
     * noch Zwischenpuffer-Varianten — dort passiert nichts. Auch im Editor
     * (Jans Fund 01.10.2026: der Welcome-Scrolly hakte dort beim ersten
     * Erscheinen von Ring und Counter je ~170 ms, 2 bzw. 7 Programme) — fuer
     * den Stand beim Oeffnen; spaeter angelegte Ebenen kompilieren weiter lazy.
     * Alles wird synchron wieder zurueckgesetzt: Anhaengung, Sichtbarkeit,
     * Frustum-Culling; nichts davon erreicht den Bildschirm.
     */
    private waermeBildErledigt = false;

    private waermeBildRendern() {
        if (this.waermeBildErledigt) return;
        this.waermeBildErledigt = true;
        const renderer = this.webGLRenderer;
        const scene = this.webGlScene;
        const camera = this.camera;
        if (!renderer || !scene || !camera) return;
        if (Object.keys(this.layerState3d || {}).length === 0) return;

        const angehaengt: THREE.Object3D[] = [];
        const sichtbarGemacht: THREE.Object3D[] = [];
        const cullingAus: THREE.Object3D[] = [];
        try {
            for (const id in this.layerMeshes) {
                const wurzel = this.layerMeshes[id] as TODOAny;
                if (!wurzel) continue;
                if (!wurzel.parent) {
                    scene.add(wurzel);
                    angehaengt.push(wurzel);
                }
                wurzel.traverse?.((o: TODOAny) => {
                    if (!o.visible) {
                        o.visible = true;
                        sichtbarGemacht.push(o);
                    }
                    // Objekte ausserhalb des Kamerabilds wuerden sonst gar nicht
                    // gezeichnet — und damit auch nicht kompiliert.
                    if (o.frustumCulled) {
                        o.frustumCulled = false;
                        cullingAus.push(o);
                    }
                });
            }
            // SPIEGEL VORAB (Jans Power-Test 11.09.2026): Cube-Rigs und
            // planare Spiegel entstanden bisher erst im ersten Bild ihrer
            // Ebene — samt Render-Targets, Reflector-Shader und der envMap-
            // Programmvariante der Materialien. Gemessen: 188 ms und 148 ms
            // (+1 Programm) beim Eintritt, im zweiten Durchlauf 0. Darum hier,
            // solange alle Wurzeln haengen und sichtbar sind: Flags aus dem
            // geladenen Content ableiten, Rigs anlegen und einmal fuellen,
            // Spiegel bauen — dann kompiliert das Aufwaerm-Bild unten auch die
            // envMap-Varianten. Die Flags setzt updateLayer je Bild ohnehin neu.
            try {
                for (const id in this.layerState3d || {}) {
                    const inhalt = this.layerContentCache[id] as Object3DContent | undefined;
                    if (inhalt) this.spiegelFlagsSetzen(id, inhalt);
                }
                updateRealtimeReflections(this as unknown as ReflectionHost);
                updateMirrors(this as unknown as MirrorHost);
            } catch {
                // reine Optimierung
            }
            const ziel = new THREE.WebGLRenderTarget(4, 4);
            const vorher = renderer.getRenderTarget();
            const shadowMap = renderer.shadowMap as TODOAny;
            const autoVorher = shadowMap.autoUpdate;
            shadowMap.needsUpdate = true;
            renderer.setRenderTarget(ziel);
            renderer.render(scene, camera);
            // ZWEITER DURCHGANG MIT GEKIPPTER TRANSPARENZ: renderLayer schaltet
            // `material.transparent` pro Bild um (Ein-/Ausblenden: opacity < 1
            // → transparent), und `transparent` steckt im Programm-Schluessel
            // von three. Ein Objekt, das beim Aufwaermen noch durchscheinend
            // war, brauchte beim Erreichen voller Deckkraft neue Programme
            // (gemessen 08.09.: vier Nachzuegler am Ring, je Bildschirm- und
            // Render-Target-Variante). Darum beide Zustaende vorab: gekippt
            // einmal ins Render-Target zeichnen (RT-Variante) und die Szene
            // kompilieren (Bildschirm-Variante), dann zuruecksetzen.
            const materialien = new Set<TODOAny>();
            for (const id in this.layerState3d || {}) {
                const wurzel = (this.layerState3d as TODOAny)[id];
                wurzel?.traverse?.((o: TODOAny) => {
                    if (!o.isMesh || !o.material) return;
                    const mats = Array.isArray(o.material) ? o.material : [o.material];
                    mats.forEach((m: TODOAny) => m && materialien.add(m));
                });
            }
            if (materialien.size) {
                materialien.forEach((m) => {
                    m.transparent = !m.transparent;
                    m.needsUpdate = true;
                });
                renderer.render(scene, camera);
                renderer.setRenderTarget(vorher);
                void renderer.compileAsync(scene, camera).catch(() => {
                    /* reine Optimierung */
                });
                materialien.forEach((m) => {
                    m.transparent = !m.transparent;
                    m.needsUpdate = true;
                });
            }
            renderer.setRenderTarget(vorher);
            shadowMap.autoUpdate = autoVorher;
            ziel.dispose();
        } catch {
            // Reine Optimierung — ein Fehler hier darf das Rendern nie betreffen.
        } finally {
            cullingAus.forEach((o) => {
                o.frustumCulled = true;
            });
            sichtbarGemacht.forEach((o) => {
                o.visible = false;
            });
            angehaengt.forEach((w) => {
                scene.remove(w);
            });
        }
    }

    /**
     * Waermt pro Frame ein paar Bild-Texturen vor (initTexture = Decode +
     * GPU-Upload), bis alle durch sind. Laeuft NACH finishedLoading aus
     * animate() — die Einmalkosten liegen damit weder im ersten Scrollen
     * (Ur-Problem: Ruckeln je Erst-Anblick) noch in der Ladezeit (Folge-
     * Problem: Template-Vorschau startete spaeter), sondern in der Verweil-
     * zeit auf dem Startbildschirm. Videotexturen laden prinzipbedingt je
     * Frame — auslassen. Blur-Texturen (userData.blurTex) kommen mit.
     */
    private waermeTexturenSchrittweise() {
        const BUDGET_PRO_FRAME = 2;
        // ALLE Material-Maps, nicht nur .map — und per traverse auch die
        // Kind-Meshes geladener Modelle (GLB/OBJ): deren PBR-Texturen wurden
        // vorher nie vorgewaermt und luden erst beim ersten Sichtbarwerden
        // hoch (Jans Ruckler beim Szenenwechsel, 26.08.2026).
        const MAP_FELDER = [
            "map",
            "normalMap",
            "roughnessMap",
            "metalnessMap",
            "aoMap",
            "emissiveMap",
            "bumpMap",
            "alphaMap",
            "envMap",
            "lightMap",
            "displacementMap",
        ] as const;
        const kandidaten: THREE.Texture[] = [];
        for (const id in this.layerMeshes) {
            const wurzel = this.layerMeshes[id] as TODOAny;
            if (!wurzel) continue;
            if (wurzel.userData?.blurTex && !this.aufgewaermteTexturen.has(wurzel.userData.blurTex)) {
                kandidaten.push(wurzel.userData.blurTex);
            }
            wurzel.traverse?.((kind: TODOAny) => {
                if (!kind?.isMesh) return;
                const mats = Array.isArray(kind.material) ? kind.material : [kind.material];
                for (const m of mats) {
                    if (!m) continue;
                    for (const feld of MAP_FELDER) {
                        const tex = m[feld] as TODOAny;
                        if (tex?.isTexture && !tex.isVideoTexture && !this.aufgewaermteTexturen.has(tex)) {
                            kandidaten.push(tex);
                        }
                    }
                }
            });
        }
        let getan = 0;
        for (const tex of kandidaten) {
            if (getan >= BUDGET_PRO_FRAME) break;
            if (this.aufgewaermteTexturen.has(tex)) continue;
            this.webGLRenderer?.initTexture(tex);
            this.aufgewaermteTexturen.add(tex);
            getan += 1;
        }
        if (getan === 0) {
            this.texturAufwaermenErledigt = true;
        }
    }

    /** Anzahl Bild-Ebenen der aktuellen Presentation (fuer das Textur-Budget), je Presentation gecacht. */
    /** Traegt die Presentation echte 3D-Ebenen? (bei der Kontext-Erzeugung bestimmt; s. hat3dEbenen) */
    private hat3d = false;
    private bildAnzahlCache: { quelle: unknown; n: number } | null = null;
    private anzahlBildEbenen(): number {
        const quelle = (this.props.presentation as TODOAny)?.groupTree;
        if (!quelle) return 0;
        if (this.bildAnzahlCache && this.bildAnzahlCache.quelle === quelle) return this.bildAnzahlCache.n;
        let n = 0;
        crawl(quelle, (l: TODOAny) => { if (l?.type === LayerType.image) n += 1; }, { getChildren: (l: TODOAny) => l?.layers });
        this.bildAnzahlCache = { quelle, n };
        return n;
    }

    updateImage(layerId: string, content: ImageContent, liveUpdate: boolean = false) {
        // Leerer Bild-Slot — nichts laden. Zwei Fälle:
        //  a) frisch angelegte Ebene ohne Upload (content.url === ''),
        //  b) #5-Fix (Laura 2026-07-28): via "Entfernen" geleerter Slot, der schon
        //     ein Mesh hatte → Mesh verstecken, sonst bliebe das alte Bild sichtbar.
        // Sonst würde der ImageLoader '' laden → Konsolenfehler + Fallback pro Frame.
        if (!content || !content.url) {
            const existing = this.layerMeshes[layerId] as THREE.Mesh | undefined;
            if (existing) existing.visible = false;
            this.layerContentCache[layerId] = content;
            // s. gleiche Stelle in updateVideo (Benedikts 10-s-Boot-Fund
            // 02.09.2026): leerer Slot zaehlt sonst nie als geladen und der
            // Boot haengt am Watchdog. Idempotent.
            this.props.renderer.loadedLayer(layerId, "leerer-medien-slot");
            return;
        }

        const rendererStore = this.props.renderer;

        const { quality } = this.props.renderer;
        const cachedLayerContent = this.layerContentCache[layerId];
        const { layerMeshes } = this;
        const layerContent: ImageContent = content;

        // Background image layers render at reduced opacity so the photo
        // acts as a subtle backdrop behind the chart SVG overlay.
        // NUR in Studio-Dokumenten: Templates markieren ihr vollwertiges
        // Hintergrundbild ebenfalls mit isBackground (studioBackdropAktiv).
        const isBgLayer = (this.templateJson?.data?.data?.layers?.some(
            (l: TODOAny) => l && l.id === layerId && l.isBackground === true
        ) ?? false) && this.studioBackdropAktiv();

        const contentChange = !isEqual(cachedLayerContent, layerContent);
        let isDirty = contentChange;
        const layerMesh = layerMeshes[layerId];
        const qualityChanged = layerMesh && layerMesh.userData.quality !== quality!.quality;
        if (liveUpdate) {
            WebGLAssets.setImagesOnStart = true;
            // NEUE Ebene ohne Mesh: dirty lassen, damit der Erst-Ladepfad unten
            // greift (gleiche Falle wie bei Text — s. Kommentar dort, 12.10.2026).
            isDirty = layerMesh ? false : contentChange;
            for (const i in this.videoCache) {
                // Ueber den WRAPPER pausieren, nicht direkt am Element (Jans
                // Fund 26.08.2026): der Direktzugriff liess isPlaying auf true
                // stehen — die Wiederaufnahme prueft genau dieses Flag und
                // feuerte nie wieder. Nach einem Video-Tausch spielten die
                // NACHBAR-Videos deshalb bis zum Neuladen nicht mehr.
                this.videoCache[i].pause();
            }

            let getCalcWidth: number = 0;
            let getCalcHeight: number = 0;

            if (content.calcWidth && content.calcWidth > 0) {
                getCalcWidth = content.calcWidth;
                getCalcHeight = content.calcHeight;
            } else if (content.width && content.height) {
                getCalcWidth = content.width;
                getCalcHeight = content.height;
            }

            let mesh = layerMeshes[layerId] as THREE.Mesh;

            const loader = new THREE.ImageLoader();
            const url = layerContent.url;
            if (mesh && mesh.material) {
                const liveUpdateSuccess = (imageTag: HTMLImageElement) => {
                    // Dispose old material + texture before replacing — prevents texture leak
                    const oldMaterial = mesh.material as THREE.MeshBasicMaterial;
                    if (oldMaterial) {
                        if (oldMaterial.map) oldMaterial.map.dispose();
                        oldMaterial.dispose();
                    }

                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const imageTexture: any = new THREE.Texture(imageTag);
                    // Mobile-Textur-Deckel (26.08.2026): grosse Bild-Ebenen
                    // vor dem GPU-Upload auf 2048 Kante begrenzen.
                    deckleTexturFuerMobil(imageTexture);
                    imageTexture.minFilter = THREE.LinearFilter;
                    imageTexture.magFilter = THREE.LinearFilter;
                    imageTexture.needsUpdate = true;
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const getThreeColorSpace: any = THREE;
                    imageTexture.colorSpace = getThreeColorSpace.SRGBColorSpace;
                    const material = new THREE.MeshBasicMaterial({
                        map: imageTexture,
                        depthWrite: false, // remove artifacts
                        side: layerContent.doubleSide ? THREE.DoubleSide : THREE.FrontSide,
                        alphaTest: 0.01,
                        transparent: true,
                        opacity: isBgLayer ? 0.2 : 1,
                        name: String(layerId),
                    });
                    // Aufwaermen wie im Erst-Ladepfad unten: Upload sofort,
                    // nicht erst beim ersten Sichtbarwerden (Editor-Livetausch).
                    this.webGLRenderer?.initTexture(imageTexture);
                    mesh.material = material;

                    if (getCalcWidth > 1) {
                        // Dispose old geometry before replacing
                        mesh.geometry?.dispose();
                        const geometry = new THREE.PlaneGeometry(1, 1);
                        // NIE VERZERREN (Jans Auftrag 24.09.2026): Hoehe vom
                        // Platz, Breite aus dem echten Seitenverhaeltnis der
                        // geladenen Datei — dieselbe Sicherung, die Videos
                        // seit dem 04.08.2026 haben (s. bildPlaneSize).
                        const { w: flaecheW, h: flaecheH } = bildPlaneSize(
                            imageTag.naturalWidth || imageTag.width,
                            imageTag.naturalHeight || imageTag.height,
                            getCalcWidth,
                            getCalcHeight,
                            content.width,
                            content.height
                        );
                        geometry.scale(flaecheW, flaecheH, 1);
                        const mesh1: THREE.Mesh = new THREE.Mesh(geometry, material);
                        mesh = mesh1;
                        layerMeshes[layerId] = mesh;
                        this.layerContentCache[layerId] = content;
                    }

                    //}
                };
                loader.load(url, liveUpdateSuccess, undefined, undefined);
            }
        }
        // NEUE Bild-Ebene trotz Live-Flagge anlegen (12.10.2026, wie beim Text:
        // nach dem ersten Live-Bildtausch bekamen später platzierte Scene-Bilder
        // nie ein Mesh und blieben unsichtbar).
        if (!WebGLAssets.setImagesOnStart || !layerMeshes[layerId]) {
            // Hängt ein Abruf und die Ebene will inzwischen ein ANDERES Bild,
            // nicht länger warten (s. bildNeuVersuch).
            if (
                this.layerIsLoading[layerId] &&
                isDirty &&
                Date.now() - (this.bildLadeStart[layerId] ?? Infinity) > 15_000
            ) {
                this.layerIsLoading[layerId] = false;
            }
            const neuVersuch = this.bildNeuVersuch[layerId];
            const wartetNochAufNeuVersuch =
                !!neuVersuch &&
                neuVersuch.url === getLayerContentUrl(layerContent) &&
                (neuVersuch.anzahl >= 6 || Date.now() < neuVersuch.ab);
            if ((isDirty || qualityChanged) && !this.layerIsLoading[layerId] && !wartetNochAufNeuVersuch) {
                this.layerIsLoading[layerId] = true;
                this.bildLadeStart[layerId] = Date.now();
                const kennung = (this.bildLadeKennung[layerId] ?? 0) + 1;
                this.bildLadeKennung[layerId] = kennung;
                //console.log("layerId: " + layerId);

                const loadedImage = (
                    material: THREE.MeshBasicMaterial,
                    texture: THREE.Texture,
                    height: number,
                    width: number
                ) => {
                    // Überholter Abruf (inzwischen ein anderes Bild angefordert)?
                    if (this.bildLadeKennung[layerId] !== kennung) {
                        texture.dispose();
                        material.dispose();
                        return;
                    }
                    delete this.bildNeuVersuch[layerId];
                    // when only the quality changed, we don't create a new mesh
                    if (qualityChanged && !this.setVideoJit) {
                        const mesh = layerMeshes[layerId] as THREE.Mesh;
                        const meshMaterial = mesh.material as THREE.MeshBasicMaterial;
                        meshMaterial.map!.dispose();
                        meshMaterial.map = texture;
                        meshMaterial.needsUpdate = true;
                        mesh.geometry.scale(width / mesh.userData.width, height / mesh.userData.height, 1);
                    } else {
                        const geometry = new THREE.PlaneGeometry(1, 1);
                        geometry.scale(width, height, 1);
                        const mesh = new THREE.Mesh(geometry, material);
                        layerMeshes[layerId] = mesh;
                        // Statischen Weichzeichner sofort mitbacken — sonst
                        // passiert das erst beim ersten Sichtbarwerden im
                        // renderLayer-Pfad (syncImageBlur) und ruckelt mitten
                        // im Scrollen (Jans Fund 06.08.2026). Der GPU-Upload
                        // der Blur-Textur laeuft wie bei den scharfen Texturen
                        // schrittweise nach finishedLoading (animate) — nicht
                        // hier, das verlaengerte die Ladezeit. Animierter Blur
                        // startet weiter lazy, profitiert aber von der
                        // Canvas-Wiederverwendung in syncImageBlur.
                        if ((layerContent.blur ?? 0) > 0) {
                            syncImageBlur(mesh, layerContent, undefined, this.props.maxTextureWidth);
                        }
                        // only load layer initially
                        rendererStore.loadedLayer(layerId, url);
                    }
                    layerMeshes[layerId].userData.quality = quality!.quality;
                    // we can't really capture this again
                    layerMeshes[layerId].userData.width = width;
                    layerMeshes[layerId].userData.height = height;
                    this.layerIsLoading[layerId] = false;
                    this.layerContentCache[layerId] = content;
                };
                let fallbackAttempted = false;
                const loader = new THREE.ImageLoader();

                // const qualityToUse = this.props.renderer.quality!;
                // disabled for now, as people used it wrongly!
                /*
            if (layerContent.doNotResize) {
              qualityToUse = QUALITIES.find(q => q.quality === Quality.Q4096);
            }
            */
                // TODO DESCR-218: revisit
                // const url = createCdnImageUrl(layerContent, qualityToUse.fraction * layerContent.width);
                const url = getLayerContentUrl(layerContent);
                const success = (imageTag: HTMLImageElement) => {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const imageTexture: any = new THREE.Texture(imageTag);
                    // Mobile-Textur-Deckel (26.08.2026): grosse Bild-Ebenen
                    // vor dem GPU-Upload auf 2048 Kante begrenzen.
                    deckleTexturFuerMobil(imageTexture);
                    imageTexture.minFilter = THREE.LinearFilter;
                    imageTexture.magFilter = THREE.LinearFilter;
                    imageTexture.needsUpdate = true;
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const getThreeColorSpace: any = THREE;
                    imageTexture.colorSpace = getThreeColorSpace.SRGBColorSpace;
                    const material = new THREE.MeshBasicMaterial({
                        map: imageTexture,
                        depthWrite: false, // remove artifacts
                        side: layerContent.doubleSide ? THREE.DoubleSide : THREE.FrontSide,
                        alphaTest: 0.01,
                        transparent: true,
                        opacity: isBgLayer ? 0.2 : 1,
                        name: String(layerId),
                    });
                    // Aufwaermen passiert NICHT mehr hier (verlaengerte die
                    // Ladezeit — Jans Fund an der Template-Vorschau), sondern
                    // schrittweise nach finishedLoading, waehrend der Nutzer
                    // auf den Startbildschirm schaut (waermeTexturenSchrittweise
                    // in animate).
                    //console.log("materialname: " + material.name);
                    WebGLAssets.layerIndex += 1;
                    // NIE VERZERREN (Jans Auftrag 24.09.2026, s. bildPlaneSize):
                    // Hoehe vom Platz, Breite aus dem echten Seitenverhaeltnis
                    // der geladenen Datei. Stimmen Daten und Datei ueberein
                    // (Normalfall), kommt genau das Bisherige heraus.
                    const geladen = imageTexture.image as
                        | { naturalWidth?: number; naturalHeight?: number; width?: number; height?: number }
                        | undefined;
                    const { w: flaecheW, h: flaecheH } = bildPlaneSize(
                        geladen?.naturalWidth || geladen?.width,
                        geladen?.naturalHeight || geladen?.height,
                        layerContent.calcWidth,
                        layerContent.calcHeight,
                        layerContent.width,
                        layerContent.height
                    );
                    loadedImage(material, imageTexture, flaecheH, flaecheW);
                };
                const onError = () => {
                    // NOTE: PNGs that have been converted to WEBP sometimes fail on Safari browsers. It may be connected to this
                    // issue: https://bugs.webkit.org/show_bug.cgi?id=219977
                    if (!fallbackAttempted) {
                        if (url.toString().indexOf("https://cdn.visualpedia.igg", 0) === -1) {
                            logError(
                                new Error(
                                    `Image could not be found: '${url}' (${layerContent.url}). Attempting to request fallback image.`
                                )
                            );
                        }
                        // TODO DESCR-218: revisit
                        // const originalUrl = createCdnOriginalFormatImageUrl(
                        //     layerContent,
                        //     qualityToUse.fraction * layerContent.width
                        // );
                        const originalUrl = getLayerContentUrl(layerContent);

                        loader.load(originalUrl, success, undefined, onError);
                        fallbackAttempted = true;
                    } else {
                        if (url.toString().indexOf("https://cdn.visualpedia.igg", 0) === -1) {
                            logError(
                                new Error(
                                    `Attempted fallback and image and it could still not be found: '${url}' (${layerContent.url}).`
                                )
                            );
                        }
                        rendererStore.loadedLayer(layerId, url);
                        // Nicht aufgeben (s. bildNeuVersuch): Merker zurück,
                        // Inhalt vergessen → ein späteres Bild lädt neu.
                        if (this.bildLadeKennung[layerId] === kennung) {
                            const bisher = this.bildNeuVersuch[layerId];
                            const anzahl = bisher && bisher.url === url ? bisher.anzahl + 1 : 1;
                            this.bildNeuVersuch[layerId] = {
                                url,
                                anzahl,
                                ab: Date.now() + Math.min(30_000, 2_000 * 2 ** (anzahl - 1)),
                            };
                            this.layerIsLoading[layerId] = false;
                            delete this.layerContentCache[layerId];
                        }
                    }
                };
                //console.log("url is: "  + url)
                //console.log(getImage)
                //console.log(this.templateJson)
                //console.log(layerContent.width)

                // BILDBREITE NACH BILDSCHIRM (2026-09-08, Lauras WK-Fund "Titelbild
                // mobil verpixelt"): Die fruehere Formel (PPI-Erbe) teilte die
                // DATEI-Breite durch die Pixeldichte und halbierte grosse Bilder —
                // iPhone (dpr 3) bekam rs:fit:533, Retina-Desktop rs:fit:800, nur
                // ein dpr-1-Desktop 1600. Jetzt: laengste Viewport-Kante × dpr, auf
                // eine feste Stufenleiter gerundet (Begruendung + Leiter in
                // utils/bildbreite.ts). Feste Stufen = warme Cache-Ops: der Warmer
                // (user-scrollys.service rendererProcessingStufen) feuert beim
                // Publish exakt diese Stufen vor — LOCKSTEP. Ganzzahlige Op, weil
                // das .com-/img (sharp) fraktionale Breiten nicht sauber cacht
                // (gemessen 2026-08-11). Das Datei-Original wird nie vergroessert
                // (Handler: withoutEnlargement).
                // TEXTUR-BUDGET auf Touch-Geraeten (Jans iPhone-Absturz 16.09.2026,
                // Everest mit 124 Bildern, mit Benedikt abgestimmt): die Stufe nach
                // Bildschirm gilt pro Bild — die SUMME zaehlt den Bild-Ebenen des
                // Scrollys entsprechend herunter (utils/bildbreite.ts).
                let targetWidth: number = bildBreitenStufeMitBudget(
                    rendererStore.width,
                    rendererStore.height,
                    // vergroesserte Karte: Bild fuer die SICHTBARE Groesse bestellen
                    wirksameDpr(rendererStore.dpr),
                    this.anzahlBildEbenen(),
                    istMobilesGeraet()
                );

                // Kachel-Vorschauen (Template-Galerie) begrenzen die Breite —
                // Begründung an RendererProps.maxTextureWidth.
                if (this.props.maxTextureWidth && targetWidth > this.props.maxTextureWidth) {
                    targetWidth = this.props.maxTextureWidth;
                }
                const params = new Map<string, string>();
                params.set("processing", `rs:fit:${Math.round(targetWidth)}`);
                const optimizedUrl = this.imageOptimizer(url, params);
                loader.load(optimizedUrl, success, undefined, onError);
                // cache the layer
                this.layerContentCache[layerId] = content;
            }
        }
    }

    /**
     * Sichtbarkeit der VORFAHREN entlang der THREE-Elternkette (Jans Fund
     * 12.10.2026, "Audio in ausgeblendeter Gruppe spielt weiter"): Kanaele
     * ohne eigenes Mesh (Audio) bzw. mit eigener Ton-Logik (Video-Auswahl)
     * erben die Gruppen-Sichtbarkeit nicht vom Szenengraph und muessen die
     * Kette selbst abklettern.
     */
    private sindVorfahrenSichtbar(parent: THREE.Object3D | null | undefined): boolean {
        for (let o: THREE.Object3D | null = parent ?? null; o; o = o.parent) {
            if (o.visible === false) return false;
        }
        return true;
    }

    /** Licht-Gewichte der Scene-Wurzeln im aktuellen Bild (s. sceneLichtGewicht). */
    private _sceneLichtGewichte: Map<string, number> = new Map();

    /**
     * Schatten einer Scene-Ebene an ihr Licht-Gewicht koppeln (Jans Fund
     * 19.10.2026): solange sie mit einer anderen Licht-Scene überlappt
     * (Gewicht unter 1), wirft sie keinen Schatten; danach wieder genau die
     * Meshes, die vorher geworfen haben (Merker `schattenAus`, damit nichts
     * Neues zu werfen beginnt).
     */
    private sceneSchattenNachGewicht(layerMesh: THREE.Object3D, gewicht: number): void {
        const aus = gewicht < 1;
        if (aus) {
            layerMesh.traverse((o) => {
                // Lichter nicht: ihr castShadow umzuschalten ändert die Zahl
                // der Schatten-Lichter → alle Shader kompilieren neu (Ruckler)
                if (o.castShadow && !(o as THREE.Light).isLight) {
                    o.castShadow = false;
                    o.userData.schattenAus = true;
                }
            });
            layerMesh.userData.schattenGedimmt = true;
        } else if (layerMesh.userData.schattenGedimmt) {
            layerMesh.traverse((o) => {
                if (o.userData.schattenAus) {
                    o.castShadow = true;
                    delete o.userData.schattenAus;
                }
            });
            layerMesh.userData.schattenGedimmt = false;
        }
    }

    renderLayer = (
        layer: LayerRender,
        parent: THREE.Object3D,
        timer: number,
        backwards: boolean,
        maxVector: TODOAny,
        isActive: boolean,
        order: number,
        responsiveScaleFactor: number
    ): Array<THREE.Object3D> => {
        const usedChildren: Array<THREE.Object3D> = [];
        // Infografik: im HTML-Modus ein DOM-Overlay (renderLayerHTML), hier also
        // nichts zu tun. Ist "Als HTML rendern" aus, liegt sie als echte Flaeche
        // in der Szene und laeuft ab hier wie jeder andere Layer durch.
        if (
            layer.type === LayerType.infographic &&
            infographicIsHtml(layer.content as unknown as InfographicContent)
        ) {
            // Gegenrichtung des Moduswechsels: die alte Flaeche unsichtbar
            // schalten (nicht abhaengen — der usedChildren-Mechanismus reagiert
            // empfindlich auf Entfernen/Wieder-Anhaengen, siehe SVG unten).
            const stalePivot = this.pivots[layer.id];
            if (stalePivot) {
                stalePivot.visible = false;
                usedChildren.push(stalePivot);
            }
            return usedChildren;
        }
        if (layer.type === LayerType.infographic) {
            const pivot = this.pivots[layer.id];
            if (pivot) pivot.visible = true;
        }
        // Mode-Switch-Aufraeumen, Gegenrichtung zu renderLayerHTML (Jans
        // Vektor-Fund): wechselt ein SVG-Layer ZU "Als HTML rendern", bleibt
        // sein altes WebGL-Mesh sonst montiert und rendert doppelt. Unsichtbar
        // schalten statt abhaengen — der usedChildren-Mechanismus reagiert
        // empfindlich auf Entfernen/Wieder-Anhaengen (Rebuild-Schleifen).
        if (layer.type === LayerType.svg && layer.content.asHtml) {
            const stalePivot = this.pivots[layer.id];
            if (stalePivot) {
                stalePivot.visible = false;
                usedChildren.push(stalePivot);
            }
            return usedChildren;
        }
        if (layer.type === LayerType.svg) {
            const pivot = this.pivots[layer.id];
            if (pivot) pivot.visible = true;
        }
        // Audio-Layer: nichts Sichtbares — im READER hier abspielen (Jans
        // Komplex-Scrolly 01.08.2026, "Audio funktioniert nicht": der Viewer
        // kannte den Typ gar nicht). Im EDITOR spielt weiterhin Jans
        // AudioRenderer (Storyforger) — ohne den Guard liefe der Ton doppelt.
        if (layer.type === LayerType.audio) {
            // Instanz vor Global (s. RendererProps.editorMode): der Dialog im
            // Pro-Editor ist KEIN Editor, auch wenn die globale Flagge an ist.
            const audioEditorMode = !!(
                this.props.editorStore ??
                (this.props.editorMode !== undefined ? this.props.editorMode : WebGLAssets.editorMode)
            );
            if (!audioEditorMode) {
                const isAudioInLifetime =
                    this.sindVorfahrenSichtbar(parent) &&
                    (layer as TODOAny).visible !== false && layer.from <= timer && layer.to >= timer;
                this.audioLayers.update(
                    layer.id,
                    layer.content as unknown as AudioLayerContent,
                    isAudioInLifetime,
                    timer
                );
            }
            return usedChildren;
        }

        let layerMesh = this.layerMeshes[layer.id] as THREE.Mesh;
        const isGroup = layer.type === LayerType.group;

        if (layer.type === LayerType.object3d || layer.type === LayerType.text || layer.type === LayerType.svg) {
            const obj = this.layerState3d[layer.id];
            if (!obj) {
                return usedChildren;
            }
        }

        if (isGroup) {
            if (!layerMesh || !this.groups[layer.id]) {
                layerMesh = new THREE.Group() as TODOAny;
                this.layerMeshes[layer.id] = layerMesh;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                this.groups[layer.id] = layerMesh as any;
                layerMesh.name = layer.name;
            }
        }

        if (layer.type === LayerType.object3dChild) {
            const childObject: THREE.Object3D = this.objectCache[layer.id];
            layerMesh = childObject as THREE.Mesh;
        }

        if (!layerMesh) {
            return usedChildren;
        }

        let pivotGroup = this.pivots[layer.id];

        // add the layerMesh to the scene, but only when it's not a group
        // special handling for object3dchild
        if (!pivotGroup && layer.type !== LayerType.object3dChild) {
            // first we create a pivot object
            pivotGroup = new THREE.Group();
            this.pivots[layer.id] = pivotGroup;
            pivotGroup.add(layerMesh);
            if (!this.props.isProduction) {
                const geometry = new THREE.SphereGeometry(100, 4, 2);
                const material = new THREE.MeshBasicMaterial({
                    color: 0xff0000,
                    wireframe: true,
                });
                const pivotCircle = new THREE.Mesh(geometry, material);
                pivotCircle.visible = false;
                this.pivotsCircles[layer.id] = pivotCircle;
                pivotGroup.add(pivotCircle);
            }
            parent.add(pivotGroup);
        }
        if (pivotGroup) {
            // UMHAENGEN, wenn die Ebene den Elternteil gewechselt hat (Jans Fund
            // 04.08.2026: "Bei Gruppen kann ich diese manchmal nicht animieren
            // oder auch per Handler bewegen").
            //
            // `parent.add(pivotGroup)` steht nur im Erzeugungs-Zweig darueber.
            // Gruppiert man BESTEHENDE Ebenen ("Gruppieren"), existiert deren
            // Pivot laengst — es blieb deshalb am alten Elternteil haengen. Die
            // neue Gruppe blieb LEER: ihre eigene Transform lief zwar korrekt
            // (Position, Drehung, Skalierung, Deckkraft standen richtig am
            // Pivot), bewegte aber nichts, weil nichts darunter hing. Genau
            // Jans "manchmal": nach einem Neuladen baut der Baum von vorn auf
            // und dieselbe Gruppe funktioniert.
            //
            // Der Aufraeum-Durchlauf in animate() fand das nicht: er vergleicht
            // nur die vorhandenen THREE-Kinder einer Gruppe gegen usedChildren
            // (findet also ZUVIELE), nie die FEHLENDEN.
            //
            // Kein Endlos-Rebuild moeglich: nach dem Umhaengen ist die Bedingung
            // falsch, und wir loesen keine Baum-Invalidierung aus.
            reparentIfMoved(pivotGroup, parent);
            usedChildren.push(pivotGroup);
            if (pivotGroup.children[0] !== layerMesh) {
                pivotGroup.remove(pivotGroup.children[0]);
                const circle = pivotGroup.children[0]; // only exists in non-production (debug pivot circle)
                if (circle) pivotGroup.remove(circle);
                pivotGroup.add(layerMesh);
                if (!this.setVideoJit && circle) pivotGroup.add(circle);
            }
        }

        const isInLifetime =
            isGroup || layer.type === LayerType.object3dChild ? true : layer.from <= timer && layer.to >= timer;

        // Geleerter Bild-/Video-Slot: nichts zeichnen, nichts abspielen.
        // Der zwischengespeicherte Inhalt gewinnt vor dem Layer-Baum — beim
        // Loeschen im Template-Editor wird er sofort geleert, waehrend der Baum
        // erst mit dem naechsten Neuaufbau nachzieht.
        const leererMedienSlot = istLeererMedienSlot(
            this.layerContentCache[layer.id] ?? layer.content,
            layer.type
        );
        if (leererMedienSlot) {
            const stummesVideo = this.videoCache[layer.id];
            if (stummesVideo) {
                if (stummesVideo.isPlaying) {
                    stummesVideo.isPlaying = false;
                    stummesVideo.pause();
                }
                // Auch stumm schalten: `pause()` allein liesse einen gerade
                // laufenden Ton bis zum naechsten Bild weiterklingen.
                if (stummesVideo.videoPlayer && !stummesVideo.videoPlayer.muted) {
                    stummesVideo.videoPlayer.muted = true;
                }
            }
        }

        const checkForFrustum = (layerMesh: TODOAny, camera: THREE.PerspectiveCamera) => {
            let isInFrustum: boolean = false;
            const boundingBox: THREE.Box3 = new THREE.Box3().setFromObject(layerMesh);
            const frustum: THREE.Frustum = new THREE.Frustum();
            frustum.setFromProjectionMatrix(camera.projectionMatrix);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            frustum.planes.forEach((plane: any) => {
                plane.applyMatrix4(camera.matrixWorld);
            });
            if (frustum.intersectsBox(boundingBox)) {
                isInFrustum = true;
            }
            return isInFrustum;
        };

        let checkFrustum: boolean = false;
        let checkForOnScreen: boolean = true;
        // Abstand der projizierten Video-Mitte zur Bildmitte (px) — fuer die
        // "welches Video ist zentriert?"-Auswahl weiter unten. Infinity = nicht im Bild.
        let videoCenterDistPx = Infinity;
        // Kameranähe des Videos (Welt-Distanz) — Stechen der Ton-Auswahl:
        // bei nahezu gleichem Mittenabstand gewinnt das VORDERSTE Video
        // (Jans Regel 24.08.2026).
        let videoKameraDist = Infinity;

        if (layerMesh.userData.videoLiveUpdate && !leererMedienSlot) {
            if (this.camera) checkFrustum = checkForFrustum(layerMesh, this.camera);

            if (this.camera && this.webGLRenderer) {
                const vector: THREE.Vector3 = new THREE.Vector3();

                const widthHalf: number = 0.5 * this.webGLRenderer.getContext().canvas.width;
                const heightHalf: number = 0.5 * this.webGLRenderer.getContext().canvas.height;

                layerMesh.updateMatrixWorld();
                vector.setFromMatrixPosition(layerMesh.matrixWorld);
                // Kameranähe VOR der Projektion festhalten (project() mutiert).
                videoKameraDist = vector.distanceTo(this.camera.position);
                vector.project(this.camera);

                vector.x = vector.x * widthHalf + widthHalf;
                vector.y = -(vector.y * heightHalf) + heightHalf;

                const boundingBox: THREE.Box3 = new THREE.Box3().setFromObject(layerMesh);

                if (
                    vector.y - boundingBox.max.y / 2 < 0 ||
                    vector.y - boundingBox.max.y / 2 > this.webGLRenderer.getContext().canvas.height
                ) {
                    checkForOnScreen = false;
                } else if (
                    vector.x - boundingBox.max.x / 2 < 0 ||
                    vector.x - boundingBox.max.x / 2 > this.webGLRenderer.getContext().canvas.width
                ) {
                    checkForOnScreen = false;
                }
            }

            const video = this.videoCache[layer.id];

            if (!checkForOnScreen) {
                video.pause();
                video.seek(0);
            }

            if (!checkFrustum) {
                video.pause();
                video.seek(0);
            } else {
                // Wahrheit ist das ELEMENT, nicht das Schatten-Flag: nach
                // jedem Fremd-Pause-Pfad (Video-Tausch, Browser-Throttling)
                // koennen beide auseinanderlaufen — dann blieb das Video
                // dauerhaft stehen (Jans Fund 26.08.2026).
                if (video.videoPlayer.paused || !video.isPlaying) {
                    video.isPlaying = true;
                    video.play();
                }
            }
        }

        // LIVE-Typ beruecksichtigen (Jans WK-Fund 01.09.2026: Bild durch
        // Video ERSETZT -> der immutable Praesentationsbaum kennt den Layer
        // weiter als image, dieser komplette Video-Block wurde uebersprungen
        // und das frisch platzierte Video spielte bis zum Neuoeffnen nie).
        // Gleicher Workaround wie beim playbackMode unten: templateJson ist
        // das lebende JSON des Editors.
        const liveVideoLayer =
            layer.type === LayerType.video ||
            this.templateJson?.data?.data?.layers?.find((l: TODOAny) => l?.id === layer.id)
                ?.type === "video";
        if (liveVideoLayer && !leererMedienSlot) {
            if (this.camera) checkFrustum = checkForFrustum(layerMesh, this.camera);

            if (this.camera && this.webGLRenderer) {
                const vector: THREE.Vector3 = new THREE.Vector3();

                const widthHalf: number = 0.5 * this.webGLRenderer.getContext().canvas.width;
                const heightHalf: number = 0.5 * this.webGLRenderer.getContext().canvas.height;

                layerMesh.updateMatrixWorld();
                vector.setFromMatrixPosition(layerMesh.matrixWorld);
                // Kameranähe VOR der Projektion festhalten (project() mutiert) —
                // fehlte in DIESEM Pfad: Video-LAYER lieferten z=Infinity, das
                // Distanz-Stechen der Ton-Auswahl lief ins Leere (Jans Fund
                // 25.08.2026, Fading-Template: zweites Ton-Video blieb stumm).
                videoKameraDist = vector.distanceTo(this.camera.position);
                vector.project(this.camera);

                vector.x = vector.x * widthHalf + widthHalf;
                vector.y = -(vector.y * heightHalf) + heightHalf;

                // Abstand zur Bildmitte (Bildmitte = widthHalf) — waehlt spaeter das
                // zentrierte Video aus, das als einziges toenen darf.
                videoCenterDistPx = Math.abs(vector.x - widthHalf);

                const boundingBox: THREE.Box3 = new THREE.Box3().setFromObject(layerMesh);

                if (
                    vector.y - boundingBox.max.y / 2 < 0 ||
                    vector.y - boundingBox.max.y / 2 > this.webGLRenderer.getContext().canvas.height
                ) {
                    checkForOnScreen = false;
                } else if (
                    vector.x - boundingBox.max.x / 2 < 0 ||
                    vector.x - boundingBox.max.x / 2 > this.webGLRenderer.getContext().canvas.width
                ) {
                    checkForOnScreen = false;
                }
            }

            // Clamp layer.from at 1 to avoid trying to play videos before ANY user
            // interaction (autoplay disallowed pre-gesture). ABER: sobald der Nutzer
            // interagiert hat (im gegateten Viewer IMMER der Fall — der Renderer
            // mountet erst nach dem START-Klick, ViewerChrome.chromeHasStarted), muss
            // der Clamp WEG. Sonst sind `from=0`-Videos beim Zurueckscrollen zu
            // timer=0 dauerhaft out-of-lifetime → pause()+seek(0) → WebGL-Textur
            // schwarz (WESER-KURIER 2026-08-12, code-belegt: 5 Videos from=0 once).
            // `navigator.userActivation.hasBeenActive` ist der browser-native
            // "hat je interagiert"-Marker (Safari 16.4+); fehlt er (alte Safari),
            // greift der defensive Clamp weiter.
            const hasUserActivated =
                typeof navigator !== "undefined" &&
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (navigator as any).userActivation?.hasBeenActive === true;
            const effectiveFrom = hasUserActivated
                ? layer.from
                : Math.max(1, layer.from);
            const isVideoInLifetime = isInLifetime && effectiveFrom <= timer;

            // SICHTBARKEIT + ZENTRIERUNG statt nur Lebenszeit. Die WESER-KURIER-
            // Videos tragen alle die ganze Lebenszeit (from=0..Ende) und PANNEN per
            // animationsGroups.position horizontal durchs Bild (nur eines zentriert,
            // die anderen daneben). Code-belegt (Messsonde 2026-08-12): bei timer=0
            // starteten alle 5 gleichzeitig, `once` lief ins Leere → hasEnded → am
            // letzten Frame eingefroren (Startscreen fror ein, kam beim Zurueck-
            // scrollen nie zurueck), und bis zu 3 Videos toenten gleichzeitig.
            //   videoOnScreen  = ueberhaupt im Bild (rendern, aber evtl. still).
            //   isActiveVideo  = das der Bildmitte NAECHSTE der frisch sichtbaren
            //                    Videos → nur DAS spielt+toent (Ton-Serialisierung).
            // Auswahl ueber den Store (1 Frame Latenz, unmerklich): jedes Video
            // schreibt pro Frame seinen Mittenabstand; der kleinste frische gewinnt.
            // AUSWAHL DES ZENTRIERTEN VIDEOS (18.08.2026).
            //
            // Bewusst NUR `checkFrustum`. Der zweite Test daneben,
            // `checkForOnScreen`, vergleicht BILDSCHIRM-Pixel (vector.x nach der
            // Umrechnung) mit WELT-Einheiten (boundingBox aus Box3.setFromObject)
            // und schliesst deshalb jedes Video links der Bildmitte aus. Uebrig
            // blieb genau eines — und weil diese Groesse die Ton-Auswahl steuert,
            // toente ein WEITER HINTEN liegendes Video statt des zentrierten
            // (Bene am Geraet, 18.08.). `checkForFrustum` rechnet dagegen mit
            // einer echten THREE.Frustum gegen die Welt-Box und behandelt auch
            // den Fall hinter der Kamera richtig.
            //
            // BEWUSST NICHT angefasst: das Abspiel- und Ruecksetz-Geruest. Nach
            // wie vor ist genau EIN Video aktiv, alle anderen werden
            // zurueckgesetzt. Genau daran sind die Versuche #389/#393/#395
            // gescheitert — sie haben die Ruecksetz-Kruecke weggezogen.
            const videoOnScreen = checkFrustum;
            const centerScores = this.props.renderer.videoCenterScores;
            const nowTs = videoAuswahlJetzt();
            // STAPEL-Index (Jans WK-Messung 25.08.2026): Template-Videos
            // liegen deckungsgleich uebereinander — d, z und Deckkraft sind
            // fuer ALLE identisch (gemessen: 5x d=0, z=4000, sichtbar).
            // Das einzige unterscheidende Signal ist die Zeichenreihenfolge:
            // das Video der AKTUELLEN Slide liegt obenauf. Geschwister-Index
            // des Pivots = spaeter gezeichnet = weiter oben.
            // TEMPLATE-SLIDE-FENSTER (Jans Pro-Editor-Screenshot 25.08.2026):
            // 'Bild durch Video ersetzt'-Ebenen ERBEN die Lebensdauer des
            // Bildes (z. B. Slide 2-7), tragen ihre Heimat-Slide aber in
            // templateSlide (1-basiert). Ton und Abspielen gehoeren NUR in
            // dieses Fenster — sonst uebernimmt das naechste Video schon ab
            // dem Text-Slide davor den Ton (Esther-Fall im Fading-Template).
            let imSlideFenster: boolean | undefined = undefined;
            const heimatSlide = (layer as { templateSlide?: number })
                .templateSlide;
            if (typeof heimatSlide === "number" && heimatSlide > 0) {
                const alleSlides = this.templateJson?.data?.data?.slides as
                    | Array<{ from?: number }>
                    | undefined;
                if (alleSlides?.[heimatSlide - 1]) {
                    // Fenster beginnt EINE Slide frueher (Jans Abnahme
                    // 25.08.2026: "Ton spielt erst ab, wenn man weiterscrollt
                    // — das Video laeuft bereits"): die Ueberblendung zur
                    // Heimat-Slide laeuft VOR deren from-Grenze; der Ton soll
                    // mit dem sichtbaren Einblenden starten. Zu frueh toenen
                    // kann das Video trotzdem nicht — solange es transparent
                    // ist, faellt es am sichtbar-Filter.
                    const fensterStart =
                        alleSlides[heimatSlide - 2]?.from ?? 0;
                    // Fenster endet auch EINE Slide spaeter (Jans Fund
                    // 26.08.2026, Fading-Template: das Video ist waehrend der
                    // Ueberblendung auf der Folge-Slide noch zu SEHEN, war
                    // aber schon stumm — und das naechste Fenster beginnt
                    // bewusst frueher, sodass genau dort das falsche Video
                    // toente). Symmetrie zur fruehen Kante: sichtbar bleibt
                    // die Leitplanke, unsichtbare Kandidaten fallen weiter
                    // am Deckkraft-Filter.
                    const fensterEnde =
                        alleSlides[heimatSlide + 1]?.from ?? Infinity;
                    imSlideFenster = timer >= fensterStart && timer < fensterEnde;
                }
            }
            const pivotFuerStapel = this.pivots[layer.id];
            const stapelIndex = pivotFuerStapel?.parent
                ? pivotFuerStapel.parent.children.indexOf(pivotFuerStapel)
                : -1;
            // DECKKRAFT direkt hier berechnen (nicht aus dem Hoerbarkeits-
            // Block zuruecktragen — der haengt am lazily erzeugten
            // videoPlayer und lief in der Messung fuer frisch geladene
            // Ebenen noch gar nicht). Im Fading-/Stapel-Template ist die
            // Deckkraft das EINZIGE Signal, das die deckungsgleichen Videos
            // unterscheidet (Messung 25.08.2026: 5x d=0, z gleich).
            const scoreOpacity =
                ((layer.layer3D(timer).opacity ?? 1) as number) *
                (parent ? parent.userData?.opacity || 1 : 1);
            centerScores.set(layer.id, {
                d: videoOnScreen ? videoCenterDistPx : Infinity,
                t: nowTs,
                z: videoKameraDist,
                stumm: !!(layer.content as VideoContent)?.muted,
                // Deckkraft ODER hartes visible-Flag: 'Bremerin' (Mesh-Dump
                // 25.08.2026) blendet die gestapelten Videos NICHT per
                // Deckkraft aus (alle opacity=1), sondern schaltet
                // mesh.visible — genau eines ist sichtbar. Das Fading-
                // Template arbeitet umgekehrt (visible=true, Deckkraft
                // blendet). Beide Signale zusammen decken beide Welten ab.
                // ... und die AHNEN-Kette (Jans Gruppen-Fund 12.10.2026):
                // ein Video in einer ausgeblendeten Gruppe ist unsichtbar,
                // obwohl sein eigenes Mesh-Flag true bleibt — ohne die Kette
                // konnte es trotzdem die Ton-Auswahl gewinnen.
                sichtbar: scoreOpacity > 0.05 && layerMesh.visible !== false && this.sindVorfahrenSichtbar(parent),
                deckkraft: scoreOpacity,
                stapel: stapelIndex,
                imFenster: imSlideFenster,
            });
            // VERGLEICHSKETTE (25.08.2026, Jans WK-Messung an 'Bremerin'):
            // Mittenabstand (mit Hysterese) -> Kameranaehe -> STAPEL (oben
            // gewinnt). Deckungsgleich gestapelte Template-Videos sind in d,
            // z und Deckkraft identisch — vorher klebte die Auswahl deshalb
            // fuer immer am ersten Video (4 von 5 Videos liefen NIE an,
            // currentTime 0.0 ueber den ganzen Scrollweg; dieselbe Messung
            // wie in Benes WK-Bericht 18.08.). 'Vorne' in Jans Ton-Regel
            // heisst in einem Stapel: obenauf.
            const HYSTERESE_PX = 40;
            // DECKKRAFT vor STAPEL (Jans Fund 26.08.2026, Fading-Template):
            // In einer Ueberblendung sind beide Videos im Fenster und am
            // selben Ort — hoeren soll man das besser SICHTBARE. Die
            // 0,15-Schwelle verhindert Geflatter um den Kreuzungspunkt;
            // im Bremerin-Stapel (alle Deckkraft 1) entscheidet weiter der
            // Stapel-Index.
            const DECKKRAFT_SCHWELLE = 0.15;
            const istBesser = (
                a: { d: number; z: number; deckkraft?: number; stapel?: number },
                b: { d: number; z: number; deckkraft?: number; stapel?: number },
            ): boolean => {
                if (a.d < b.d - HYSTERESE_PX) return true;
                if (Math.abs(a.d - b.d) > HYSTERESE_PX) return false;
                if (a.z < b.z) return true;
                if (a.z > b.z) return false;
                const da = a.deckkraft ?? 1;
                const db = b.deckkraft ?? 1;
                if (da > db + DECKKRAFT_SCHWELLE) return true;
                if (db > da + DECKKRAFT_SCHWELLE) return false;
                return (a.stapel ?? -1) > (b.stapel ?? -1);
            };
            let bestVideoId: string | null = null;
            let bestEintrag: { d: number; z: number; stapel?: number } | null =
                null;
            centerScores.forEach((entry, id) => {
                // nur frische Eintraege (verworfene Layer / alte Scrollys
                // ignorieren); Unsichtbare (Deckkraft ~0) sind keine
                // Kandidaten — undefined = noch nicht gemessen zaehlt mit.
                if (nowTs - entry.t >= 250 || entry.sichtbar === false) return;
                if (entry.imFenster === false) return;
                if (!bestEintrag || istBesser(entry, bestEintrag)) {
                    bestEintrag = entry;
                    bestVideoId = id;
                }
            });
            // AMTSINHABER-Schutz nur gegen FLATTERN: der Bisherige bleibt,
            // solange ihn der Herausforderer nicht nach der Vergleichskette
            // schlaegt UND er selbst frisch+sichtbar ist. (Die alte Regel
            // schuetzte ihn schon bei ungefaehr gleichem Abstand — im Stapel
            // hiess das: fuer immer.)
            const bisher = this.props.renderer.aktivesVideoId;
            let gewinnerId: string | null = bestVideoId;
            if (bisher && bisher !== bestVideoId) {
                const alt = centerScores.get(bisher);
                if (
                    alt &&
                    nowTs - alt.t < 250 &&
                    alt.sichtbar !== false &&
                    alt.imFenster !== false &&
                    bestEintrag &&
                    !istBesser(bestEintrag, alt)
                ) {
                    gewinnerId = bisher;
                }
            }
            this.props.renderer.aktivesVideoId = gewinnerId;

            // TON-AUSWAHL (Jans Regel 24.08.2026): Höchstens EIN Video ist
            // hörbar — das zentralste; bei nahezu gleichem Mittenabstand
            // (TIE-Fenster) das VORDERSTE (kleinste Kameradistanz). Gilt in
            // ALLEN Modi (vorher tönten mehrere unstumme Loop-Videos
            // gleichzeitig — das Sonnenfinsternis-Beispiel aus dem
            // revertierten Regelwerk #395). BEWUSST getrennt vom
            // Abspiel-aktivesVideoId oben: an dessen Rücksetz-Gerüst sind
            // #389/#393/#395 gescheitert — die Ton-Auswahl fasst es nicht an.
            // Kandidaten: frisch, unstumm, im Bild. Gleiche Hysterese wie
            // oben, damit der Ton bei zwei ähnlich zentrierten Videos nicht
            // pro Frame springt.
            {
                // Gleiche Vergleichskette wie die Abspiel-Auswahl oben —
                // zusaetzlich fliegen stumme und ganz unsichtbare Videos raus.
                let tonBestId: string | null = null;
                let tonBest: { d: number; z: number; stapel?: number } | null =
                    null;
                centerScores.forEach((entry, id) => {
                    if (nowTs - entry.t >= 250 || entry.stumm || entry.d === Infinity) return;
                    if (entry.sichtbar === false) return;
                    if (entry.imFenster === false) return;
                    if (!tonBest || istBesser(entry, tonBest)) {
                        tonBest = entry;
                        tonBestId = id;
                    }
                });
                const tonBisher = this.props.renderer.tonVideoId;
                let tonGewinner: string | null = tonBestId;
                if (tonBisher && tonBisher !== tonBestId) {
                    const altEintrag = centerScores.get(tonBisher);
                    if (
                        altEintrag &&
                        nowTs - altEintrag.t < 250 &&
                        !altEintrag.stumm &&
                        altEintrag.d !== Infinity &&
                        altEintrag.sichtbar !== false &&
                        altEintrag.imFenster !== false &&
                        tonBest &&
                        !istBesser(tonBest, altEintrag)
                    ) {
                        tonGewinner = tonBisher;
                    }
                }
                this.props.renderer.tonVideoId = tonGewinner;
            }

            // ABSPIELEN FOLGT DEM TON (Jans Fund 16.09.2026, „Von allem zu viel":
            // Becker-Video mit Ton blieb stehen). Zwei Videos im Bild — ein
            // stummes Tagesschau-Video (bisheriger Abspiel-Sieger, ein paar Pixel
            // naeher an der Mitte, innerhalb der Hysterese) und das Becker-Video
            // mit Ton. Die Ton-Auswahl oben (nur unstumme Kandidaten) kuerte
            // Becker und entstummte es, die Abspiel-Auswahl liess das stumme
            // Video als Amtsinhaber weiterlaufen — Becker war „hoerbar", aber
            // pausiert: kein Bild, kein Ton. Gemessen (videodiag, 9 Positionen):
            // aktiv=false/spielt=false/ton=true ueber die ganze Lebenszeit.
            // Regel: gibt es einen Ton-Sieger, ist ER das aktive Video — ein
            // Video, das toenen soll, muss laufen. Ohne Ton-Sieger (alle stumm)
            // bleibt die bisherige Mitten-Auswahl.
            const tonSiegerId = this.props.renderer.tonVideoId;
            const abspielSiegerId = tonSiegerId ?? gewinnerId;
            if (tonSiegerId) this.props.renderer.aktivesVideoId = tonSiegerId;
            const isActiveVideo = videoOnScreen && abspielSiegerId === layer.id;

            // Messsonde, nur mit ?videodiag in der Adresse — sonst kostet sie
            // nichts. Damit der naechste Geraetetest Zahlen liefert statt einer
            // weiteren Vermutung: window.__videoDiag zeigt je Ebene, welcher
            // Test was sagt und wer den Ton bekommt.
            if (typeof window !== "undefined" && window.location?.search?.includes("videodiag")) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const w = window as any;
                (w.__videoDiag ||= {})[layer.id] = {
                    name: (layer as { name?: string }).name,
                    frustum: checkFrustum,
                    onScreenAlt: checkForOnScreen,
                    mitteAbstand: Math.round(videoCenterDistPx),
                    aktiv: isActiveVideo,
                    spielt: this.videoCache[layer.id]?.isPlaying,
                    stumm: this.videoCache[layer.id]?.videoPlayer?.muted,
                    // 25.08.2026: Auswahl-Eingaenge sichtbar machen
                    sichtbar: centerScores.get(layer.id)?.sichtbar,
                    imFenster: centerScores.get(layer.id)?.imFenster,
                    heimatSlide,
                    stapel: stapelIndex,
                    ton: this.props.renderer.tonVideoId === layer.id,
                    geprimt: !!(this.videoCache[layer.id] as TODOAny)?.geprimt,
                };
            }

            const video = this.videoCache[layer.id];
            // Read playbackMode from latest JSON (templateJson), not from the immutable
            // PlayablePresentation tree — this allows live mode switching via editor buttons.
            const jsonLayer = this.templateJson?.data?.data?.layers?.find((l: any) => l?.id === layer.id);
            // Beim Bild->Video-Tausch traegt der Baum noch den BILD-Content —
            // dann den lebenden Video-Content aus dem JSON nehmen (s. oben).
            const content = (
                layer.type !== LayerType.video && jsonLayer?.type === "video" && jsonLayer.content
                    ? jsonLayer.content
                    : layer.content
            ) as VideoContent;
            if (jsonLayer?.content?.playbackMode) {
                (content as any).playbackMode = jsonLayer.content.playbackMode;
            }
            const editorMode = !!(this.props.editorStore || WebGLAssets.editorMode);
            // Drei Felder beschreiben denselben Sachverhalt (loop, scrollDriven,
            // playbackMode) — die Rangfolge liegt in @scrolly/post-types, damit
            // Renderer und Editor-Menue sie nicht unterschiedlich auslegen
            // koennen. Begruendung dort; die beiden Faelle, die uns das
            // eingebrockt haben: Jans "Beim Scrollen abspielen"-Haekchen
            // (scrollDriven) war nie mit playbackMode verbunden, und sein
            // loop:false ("einmal abspielen") ging gegen den Default "loop"
            // unter (Komplex-Scrolly 01.08.2026).
            const playbackMode = resolveVideoPlaybackMode(content);
            // Standbild-Scrubbing gilt nur noch fuer den Scroll-Modus. Vorher
            // stand im Editor JEDES Video still (Anti-Flacker-Massnahme aus dem
            // Reader-Zweig) — Autoplay-Videos liefen dort also nie, anders als in
            // Jans 1.0. Entscheidung Jan 2026-07-30: im Editor soll Autoplay
            // laufen; das Standbild bleibt dem Scroll-Modus vorbehalten.
            const editorScrub = editorMode && playbackMode === "scroll";
            if (isInLifetime && !editorScrub) {
                // Textur pro Bild frisch hochladen, solange wirklich abgespielt
                // wird. Beim Scrubbing macht das der Zweig unten gezielt nur bei
                // echtem Frame-Wechsel (sonst Schimmern auf der Ebene darueber).
                //
                // NUR wenn der Browser schon ein Bild dekodiert hat (WK-Fund
                // 31.08.2026, Benedikts Analyse + Lasses "Haken beim ersten
                // Laden"): Ohne den readyState-Waechter feuerte diese Stelle
                // beim kalten Start ~60x/s `texImage2D: no video` gegen ein
                // Video ohne Daten (Safari-Konsole: 180+ Fehler) — verlorene
                // Hauptstrang-Zeit genau dann, wenn der Browser sie zum
                // Dekodieren des ersten Bilds braucht. Der transparente Pfad
                // (VideoMaterial.markFrame) hat denselben Waechter schon immer.
                //
                // NUR BEI NEUEM VIDEOBILD (Jans iPhone-Fund 02.09.2026, Start-
                // video ruckelt): Vorher erzwang diese Stelle einen Textur-Upload
                // PRO RENDER-FRAME (62+/s, auch bei pausierten Videos) — bei vier
                // gleichzeitig laufenden Videos die teuerste Einzelarbeit auf dem
                // Telefon. Jetzt: pausiert = kein Upload; laeuft das Video, nur
                // wenn der Browser seit dem letzten Upload ein NEUES Bild
                // dekodiert hat (getVideoPlaybackQuality().totalVideoFrames,
                // WebKit + Chrome). Ohne die API bleibt der Upload pro Frame.
                //
                // NICHT ueber requestVideoFrameCallback (Jans Regression-Fund
                // 02.09.2026 spaet, „jetzt springt es auch auf dem Laptop"):
                // WebKit liefert den Callback fuer Videos, die nicht im DOM
                // haengen, nicht zuverlaessig — die Textur fror beim Anlaufen
                // jedes Videos bis zum Rueckfall ein und sprang dann vor.
                if (content.transparent && video.videoMaterial) {
                    video.videoMaterial.update();
                } else if (
                    !content.transparent &&
                    video.videoTexture &&
                    video.videoPlayer &&
                    video.videoPlayer.readyState >= video.videoPlayer.HAVE_CURRENT_DATA
                ) {
                    const el = video.videoPlayer;
                    const tex = video.videoTexture as TODOAny;
                    const vv = video as TODOAny;
                    if (vv.scrubUploadAusstehend && !el.seeking) {
                        // Scroll-Modus (Reader): der gemerkte Sprung ist fertig,
                        // das Zielbild liegt vor — genau jetzt hochladen.
                        vv.scrubUploadAusstehend = false;
                        vv.uploadBilder = typeof el.getVideoPlaybackQuality === "function"
                            ? el.getVideoPlaybackQuality().totalVideoFrames
                            : vv.uploadBilder;
                        tex.needsUpdate = true;
                    } else if (typeof el.getVideoPlaybackQuality !== "function" || vv.bildzaehlerTot) {
                        tex.needsUpdate = true;
                    } else if (!el.paused || el.seeking || vv.uploadBilder === undefined) {
                        const bilder = el.getVideoPlaybackQuality().totalVideoFrames;
                        if (bilder !== vv.uploadBilder) {
                            vv.uploadBilder = bilder;
                            vv.uploadBilderTs = undefined;
                            tex.needsUpdate = true;
                        } else if (!el.paused) {
                            // SICHERUNG: Laeuft die Position, aber der Zaehler
                            // steht laenger als 300 ms, pflegt dieser Browser
                            // ihn nicht — dann dauerhaft wieder pro Frame
                            // (klebrig, kein Flattern, kein Einfrieren).
                            const jetztMs = typeof performance !== "undefined" ? performance.now() : Date.now();
                            if (vv.uploadBilderTs === undefined) {
                                vv.uploadBilderTs = jetztMs;
                                vv.uploadBilderCt = el.currentTime;
                            } else if (
                                jetztMs - vv.uploadBilderTs > BILDZAEHLER_STILLSTAND_MS &&
                                el.currentTime - (vv.uploadBilderCt ?? el.currentTime) > 0.1
                            ) {
                                vv.bildzaehlerTot = true;
                                tex.needsUpdate = true;
                            }
                        }
                    }
                }
            }

            const { videosThatShouldBePlaying, useVideoPlayWorkaround } = this.props.renderer;
            const playQueueNeeded = useVideoPlayWorkaround && !content.muted;
            // ELEMENT IST DIE WAHRHEIT (Jans iPhone-HUD 03.09.2026: Startvideo
            // PAUSE bei t=9,1, obwohl in der Lebenszeit; nach dem Zurueck-
            // scrollen blieb es stehen): iOS pausiert ein laufendes Video mit
            // Ton von AUSSEN, sobald ein anderes Element mit Ton startet (Ton-
            // Wechsel an Slide 2). Der Wrapper hielt es weiter fuer spielend,
            // der Renderer startete es nie neu. Steht das Element, obwohl es
            // laufen soll — und ist nicht absichtlich geparkt (Standbild am
            // Trim-Ende, Ping-Pong) und kein Start eingeplant —, gilt es als
            // NICHT spielend und wird unten wieder angestossen.
            const elementSteht =
                !!video.videoPlayer &&
                video.videoPlayer.paused &&
                video.isPlaying &&
                !(video as TODOAny).virtuellGeparkt &&
                !(video as TODOAny).playPending &&
                !video.hasEnded;

            // Ruecksprung-Ziel ist der TRIM-ANFANG, nicht der Dateianfang (Jans
            // 1.0). Sonst zeigt der Editor nach dem Trimmen erst das allererste
            // Bild der Datei und springt dann sichtbar zum Trim-Anfang (Jans Fund).
            const resetTo = content.trimStart ?? 0;

            if (!checkFrustum) {
                video.pause();
                video.seek(resetTo);
            }

            if (!checkForOnScreen) {
                video.pause();
                video.seek(resetTo);
            }

            // EDITOR: STOP / ZUM ANFANG HAELT VIDEOS AN (Jans Fund 18.10.2026:
            // „das Video läuft ständig und stoppt nicht, wenn ich auf Zurück/
            // Anfang klicke — beim mp3 funktioniert es"). Dieselbe Regel wie
            // der Ton im Editor (AudioRenderer): am Anfang (Timer 0) und nach
            // einem Rücksprung bei angehaltener Wiedergabe (Stop, Zurück-
            // Knöpfe) steht das Video auf dem Trim-Anfang. Weiter geht es erst,
            // wenn die Wiedergabe läuft oder der Abspielkopf vorwärts geht.
            // Nur im Pro-Editor (eigene Zeitleiste); Vorschauen und Leser
            // bleiben unberührt.
            let editorHalt = false;
            const editorZeitleiste = this.props.editorStore?.playablePresentation?.slidePlayer;
            if (editorMode && !editorScrub && editorZeitleiste) {
                const vv = video as TODOAny;
                const laeuft = !!editorZeitleiste.running;
                const letzter = vv.editorLetzterTimer as number | undefined;
                vv.editorLetzterTimer = timer;
                const ruecksprung = !laeuft && letzter !== undefined && timer - letzter < -EDITOR_RUECKSPRUNG_MS;
                if (timer <= 0 || ruecksprung) vv.editorHalt = true;
                else if (laeuft || (letzter !== undefined && timer > letzter)) vv.editorHalt = false;
                editorHalt = !!vv.editorHalt;
            }

            if (editorScrub) {
                // EDITOR + Scroll-Modus: STANDBILD zur aktuellen Abspielposition
                // statt Wiedergabe (Autoplay-Videos laufen im Editor, s. oben).
                // Pause + seek wie im Reader-Scroll-Zweig, damit die Vorschau der
                // Scrub-Position entspricht.
                if (video.isPlaying) {
                    video.isPlaying = false;
                    video.pause();
                }
                if (video.videoPlayer && isVideoInLifetime) {
                    const duration = video.videoPlayer.duration || 1;
                    const layerDuration = Math.max(1, layer.to - layer.from);
                    // Scroll-Videos haengen am SCROLLEN, nicht am Zeitgeber
                    // (Jans Entscheidung 04.08.2026). Der Zeitgeber steht in der
                    // Lesepause einer Slide still — das Video rastete dort ein.
                    // Begruendung: WebGLAssets.scrollSmoothTimer.
                    const scrubTimer = WebGLAssets.scrollSmoothTimer ?? timer;
                    const progress = Math.max(0, Math.min(1, (scrubTimer - layer.from) / layerDuration));
                    // Auf das TRIM-FENSTER abbilden, nicht auf die volle Datei
                    // (Jans 1.0). Ohne das ignorierte das Scrubben die Trim-Regler
                    // und lief immer ueber die gesamte Videolaenge.
                    const vStart = content.trimStart ?? 0;
                    const vEnd = content.trimEnd ?? duration;
                    const target = vStart + progress * Math.max(vEnd - vStart, 0);
                    // Springt nur, wenn das Ziel sich bewegt hat UND gerade kein
                    // Sprung laeuft — Begruendung in utils/videoScrub. Ein
                    // stehendes Video macht so pro Bild null Arbeit (sonst
                    // flimmert der Stapel darueber durch staendige
                    // Textur-Uploads), und beim Scrollen staut sich nichts auf.
                    //
                    // UPLOAD ERST NACH DEM SPRUNG (Benedikts Fund 02.09.2026:
                    // "wenn ich auf Scroll stelle, wird das Video schwarz"; lokal
                    // reproduziert, Konsole: `texImage2D: no video`): Vorher wurde
                    // die Textur im SELBEN Bild hochgeladen, in dem currentTime
                    // gesetzt wurde — der Browser hatte das Zielbild da noch nicht
                    // dekodiert (readyState faellt waehrend des Seeks auf
                    // HAVE_METADATA), WebGL bekam "kein Video" und blieb schwarz;
                    // weil das Ziel danach als "erreicht" galt, kam nie ein
                    // zweiter Upload. Jetzt: Sprung merken, hochladen sobald der
                    // Seek fertig ist und ein Bild vorliegt. Beim ersten Eintritt
                    // in den Scroll-Modus ebenfalls einmal (nie gespieltes Video).
                    const vv = video as TODOAny;
                    const el = video.videoPlayer as HTMLVideoElement;
                    if (scrubVideoTo(video as unknown as ScrubbaresVideo, target) || !vv.scrubErstbildOk) {
                        vv.scrubUploadAusstehend = true;
                    }
                    if (
                        vv.scrubUploadAusstehend &&
                        !el.seeking &&
                        el.readyState >= el.HAVE_CURRENT_DATA
                    ) {
                        vv.scrubUploadAusstehend = false;
                        vv.scrubErstbildOk = true;
                        if (content.transparent && video.videoMaterial) {
                            video.videoMaterial.update();
                        } else if (!content.transparent && video.videoTexture) {
                            video.videoTexture.needsUpdate = true;
                        }
                    }
                } else if (video.videoPlayer) {
                    video.pause();
                    video.seek(resetTo);
                    resetScrub(video as unknown as ScrubbaresVideo);
                }
            } else if (editorHalt) {
                // Standbild am Trim-Anfang (s. editorHalt); der Upload-Zweig
                // oben lädt das Bild hoch, sobald der Sprung fertig ist.
                if (video.isPlaying || (video.videoPlayer && !video.videoPlayer.paused)) video.pause();
                const vp = video.videoPlayer;
                if (vp && !vp.seeking && Math.abs(vp.currentTime - resetTo) > 0.05) {
                    video.seek(resetTo);
                    (video as TODOAny).scrubUploadAusstehend = true;
                }
                video.hasEnded = false;
                if (playbackMode === "pingpong") video.resetPingPong();
            } else if (!this.props.renderer.isUsedInEditor() && !this.props.renderer.playbackStarted) {
                // VOR-START-SPERRE (Jans Regel 24.08.2026, Handy-Test): Vor dem
                // START-Klick läuft KEIN Video — auf keiner Plattform. Bisher
                // entschied die Autoplay-Richtlinie des Browsers (Safari:
                // still; Mobile-Chrome: spielte stumm los; Desktop: spielte).
                // Stattdessen ein STANDBILD am Trim-Anfang, wie im Scrub-Pfad.
                // Der Editor ist über isUsedInEditor ausgenommen (dort ist
                // playbackStarted nie gesetzt — Autoplay-Videos auf der
                // Leinwand würden sonst einfrieren); Benes Schwarz-Bild-Sorge
                // (nie gespieltes <video>) fängt der einmalige Textur-Upload
                // ab, sobald Daten da sind (readyState>=2).
                if (video.isPlaying) {
                    video.isPlaying = false;
                }
                video.pause();
                const vp = video.videoPlayer;
                if (vp) {
                    if (Math.abs(vp.currentTime - resetTo) > 0.05) {
                        video.seek(resetTo);
                    }
                    if (vp.readyState >= 2 && !(video as TODOAny).vorStartStandbildOk) {
                        (video as TODOAny).vorStartStandbildOk = true;
                        if (content.transparent && video.videoMaterial) {
                            video.videoMaterial.update();
                        } else if (video.videoTexture) {
                            video.videoTexture.needsUpdate = true;
                        }
                    }
                }
            } else if (playbackMode === 'scroll') {
                // Scroll-driven: video position follows scroll progress
                if (video.isPlaying) video.pause();
                if (isVideoInLifetime && video.videoPlayer) {
                    const duration = video.videoPlayer.duration || 1;
                    const layerDuration = Math.max(1, layer.to - layer.from);
                    // wie im Editor-Zweig: am SCROLLEN haengen, nicht am
                    // Zeitgeber (sonst rastet das Video in der Lesepause ein).
                    const scrubTimer = WebGLAssets.scrollSmoothTimer ?? timer;
                    const progress = Math.max(0, Math.min(1, (scrubTimer - layer.from) / layerDuration));
                    // wie im Editor-Zweig: Trim-Fenster statt voller Datei (Jans 1.0)
                    const vStart = content.trimStart ?? 0;
                    const vEnd = content.trimEnd ?? duration;
                    // Auch hier nur EIN Sprung gleichzeitig (utils/videoScrub).
                    // Der Reader hatte bisher gar keine Bremse und setzte
                    // currentTime in jedem Bild neu — derselbe Rueckstau wie im
                    // Editor, nur unbeobachtet.
                    // Sprung merken — der Upload-Zweig oben laedt die Textur
                    // dann hoch, sobald der Seek FERTIG ist (s. Editor-Zweig:
                    // sonst "kein Video" waehrend des Seeks und danach nie wieder,
                    // weil der Bildzaehler bei pausiertem Element nicht mehr
                    // geprueft wird).
                    if (
                        scrubVideoTo(
                            video as unknown as ScrubbaresVideo,
                            vStart + progress * Math.max(vEnd - vStart, 0)
                        )
                    ) {
                        (video as TODOAny).scrubUploadAusstehend = true;
                    }
                }
            } else if (playbackMode === 'pingpong') {
                // Ping-pong: manual currentTime control (forward then reverse)
                // We keep the video paused and drive currentTime ourselves,
                // because HTMLVideoElement does not support negative playbackRate.
                if (video.videoPlayer) {
                    video.videoPlayer.loop = false;
                }
                if (!isVideoInLifetime) {
                    if (video.isPlaying) video.pause();
                    video.seek(resetTo);
                    video.resetPingPong();
                } else if (isVideoInLifetime) {
                    // Keep native playback paused — we drive currentTime manually
                    if (!video.videoPlayer.paused) video.videoPlayer.pause();
                    video.isPlaying = true; // mark as logically playing for texture updates
                    video.updatePingPong();
                }
            } else if (playbackMode === 'once') {
                // Play once then freeze on last frame.
                if (video.videoPlayer) {
                    video.videoPlayer.loop = false;
                }
                // NUR das aktive (zentrierte) Video spielt+toent. JEDES andere wird
                // ZURUECKGESETZT (nicht bloss pausiert) — sonst bleibt ein Video,
                // das als aktives durchgelaufen ist (hasEnded), am letzten Frame
                // kleben und spielt beim Zurueckscrollen nie wieder (WESER-KURIER
                // Freeze). Reset = an den Anfang + hasEnded loeschen ⇒ spielt frisch
                // beim Wieder-Zentrieren.
                //
                // BEKANNTER REST (Entscheidung Bene 2026-08-12): Das Titel-Gesicht
                // (`_7`) ist am Cover zentriert und spielt dort mit ab, statt als
                // Standbild auf Frame 0 zu stehen. Ein sauberes Standbild ist in
                // dieser Engine nicht erreichbar — ein nie gespieltes <video>
                // praesentiert WebGL kein Bild (schwarz), und Prime/Seek/Scrub
                // scheiterten alle daran. Bewusst so gelassen: lieber spielendes
                // Video als schwarzer Titel.
                // STUMMES VIDEO LAEUFT NEBEN DEM TON-VIDEO WEITER (Jans Fund
                // 16.09.2026, s. „Abspielen folgt dem Ton"): verdraengt das
                // Ton-Video ein stummes, das gerade laeuft, wuerde es hier
                // zurueckgesetzt und stuende auf Bild 0 — im Beispiel das
                // Tagesschau-Video hinter Becker. Ein stummes Video streitet
                // sich um keinen Ton; laeuft es schon und ist es im Bild, darf
                // es weiterlaufen (bzw. nach dem Ende auf dem letzten Bild
                // stehen, wie das aktive). Nie GESTARTET wird hier etwas —
                // Template-Stapel (5 deckungsgleiche Videos) starten weiter
                // nur ihren Sieger.
                const stummLaeuftWeiter =
                    !isActiveVideo &&
                    videoOnScreen &&
                    !!content.muted &&
                    (video.isPlaying || video.hasEnded);
                if ((isActiveVideo || stummLaeuftWeiter) && isVideoInLifetime) {
                    if (isActiveVideo && (!video.isPlaying || elementSteht) && !video.hasEnded) {
                        if (!playQueueNeeded) video.play();
                        else video.playVirtual();
                    }
                    // If the video has ended, keep it paused at the last frame
                    if (video.hasEnded && video.videoPlayer) {
                        video.pause();
                        video.videoPlayer.currentTime = Math.max(0, (video.videoPlayer.duration || 1) - 0.01);
                    }
                } else {
                    video.pause();
                    video.seek(resetTo);
                    video.hasEnded = false;
                }
            } else {
                // Loop mode (default): standard play/pause behavior
                if (video.videoPlayer) {
                    video.videoPlayer.loop = true;
                }
                // Auch bei desynchronisiertem isPlaying-Flag anhalten (z. B.
                // play()-Promise loeste erst NACH Verlassen des Fensters ein):
                // sonst laeuft der TON des laengst unsichtbaren Videos weiter
                // und taucht spaeter "aus dem Nichts" wieder auf (Jans
                // Komplex-Scrolly 01.08.2026, Ton kehrte bei der Goldkugel
                // zurueck). Deshalb zusaetzlich das ECHTE Element pruefen.
                if (!isVideoInLifetime && (video.isPlaying || (video.videoPlayer && !video.videoPlayer.paused))) {
                    video.pause();
                    video.seek(resetTo);
                } else if (isVideoInLifetime && (!video.isPlaying || elementSteht)) {
                    if (!content.loop && backwards && content.dontRestartWhenGoingBack) {
                        video.seekToEnd();
                        video.playVirtual();
                    } else if (!playQueueNeeded) {
                        video.play();
                    } else {
                        video.playVirtual();
                    }
                }
            }

            // Zerstoerungsfreies Trim: am Trim-ENDE zurueckspringen (Dauerschleife)
            // bzw. stehen bleiben (Jans 1.0). Ohne das lief das Video ueber das
            // gesetzte Ende hinaus weiter — vorne beschnitten, hinten nicht
            // (Jans Fund). Beim Scrubben nicht noetig: dort gibt die
            // Scroll-Position die Zeit vor.
            if (
                !editorScrub &&
                playbackMode !== "scroll" &&
                isVideoInLifetime &&
                content.trimEnd !== undefined &&
                !video.videoPlayer.paused &&
                video.videoPlayer.currentTime >= content.trimEnd
            ) {
                if (content.loop) {
                    video.seek(content.trimStart ?? 0);
                } else {
                    video.parkAtEnd();
                }
            }
            // Die native Dauerschleife des Browsers startet immer bei 0 — zurueck
            // ins Trim-Fenster holen (Jans 1.0).
            if (
                !editorScrub &&
                playbackMode !== "scroll" &&
                content.loop &&
                (content.trimStart ?? 0) > 0 &&
                !video.videoPlayer.paused &&
                video.videoPlayer.currentTime < (content.trimStart ?? 0) - 0.05
            ) {
                video.seek(content.trimStart ?? 0);
            }

            if (playQueueNeeded) {
                if (video.isPlaying) {
                    videosThatShouldBePlaying.add(video);
                } else {
                    videosThatShouldBePlaying.delete(video);
                }
            }
        }

        if (layer.type === LayerType.lottie) {
            const lottiePlayer = this.lottieCache[layer.id];
            const lottieContent = layer.content as LottieContent;
            // Read playbackMode from latest JSON (templateJson), not from the immutable
            // PlayablePresentation tree — this allows live mode switching via editor buttons.
            const jsonLottieLayer = this.templateJson?.data?.data?.layers?.find((l: any) => l?.id === layer.id);
            if (jsonLottieLayer?.content?.playbackMode) {
                (lottieContent as any).playbackMode = jsonLottieLayer.content.playbackMode;
            }

            if (lottiePlayer?.texture) {
                if (isInLifetime) {
                    lottiePlayer.texture.needsUpdate = true;
                }

                const playbackMode = lottieContent.playbackMode || 'loop';

                if (playbackMode === 'scroll') {
                    // Scroll-driven: frame position follows scroll progress
                    if (lottiePlayer.isPlaying) lottiePlayer.pause();
                    if (isInLifetime) {
                        const layerDuration = Math.max(1, layer.to - layer.from);
                        const progress = Math.max(0, Math.min(1, (timer - layer.from) / layerDuration));
                        lottiePlayer.seekToProgress(progress);
                        lottiePlayer.texture.needsUpdate = true;
                    }
                } else if (playbackMode === 'pingpong') {
                    if (!isInLifetime && lottiePlayer.isPlaying) {
                        lottiePlayer.pause();
                        lottiePlayer.seek(0);
                    } else if (isInLifetime) {
                        if (!lottiePlayer.isPlaying) {
                            lottiePlayer.play();
                        }
                        // Reverse direction at boundaries
                        if (lottiePlayer.animation) {
                            const currentFrame = lottiePlayer.animation.currentFrame;
                            const totalFrames = lottiePlayer.totalFrames;
                            if (currentFrame >= totalFrames - 1 && lottiePlayer.animation.playDirection === 1) {
                                lottiePlayer.animation.setDirection(-1);
                            } else if (currentFrame <= 1 && lottiePlayer.animation.playDirection === -1) {
                                lottiePlayer.animation.setDirection(1);
                            }
                        }
                    }
                } else if (playbackMode === 'once') {
                    if (!isInLifetime && lottiePlayer.isPlaying) {
                        lottiePlayer.pause();
                        lottiePlayer.seek(0);
                    } else if (isInLifetime && !lottiePlayer.isPlaying) {
                        if (lottiePlayer.animation) {
                            const currentFrame = lottiePlayer.animation.currentFrame;
                            if (currentFrame < lottiePlayer.totalFrames - 1) {
                                lottiePlayer.animation.setLoop(false);
                                lottiePlayer.play();
                            }
                        }
                    }
                } else {
                    // Loop mode (default)
                    if (!isInLifetime && lottiePlayer.isPlaying) {
                        lottiePlayer.pause();
                        lottiePlayer.seek(0);
                    } else if (isInLifetime && !lottiePlayer.isPlaying) {
                        if (lottiePlayer.animation) {
                            lottiePlayer.animation.setLoop(true);
                        }
                        lottiePlayer.play();
                    }
                }

                // Apply speed multiplier
                if (lottieContent.speed && lottiePlayer.animation) {
                    lottiePlayer.animation.setSpeed(lottieContent.speed);
                }
            }
        }

        const isParentVisible = layerMesh?.parent?.visible ?? true;

        // TODO: use path
        const visible =
            (this.visibilityOverrides[layer?.id] ?? layer?.visible ?? true) &&
            isInLifetime &&
            isParentVisible &&
            // Ohne Datei gibt es nichts zu zeigen — sonst stuende hier im
            // naechsten Bild wieder das geloeschte Bild.
            !leererMedienSlot;
        layerMesh.visible = visible;

        if (layer.type === LayerType.light) {
            const helperLight = this.lightHelpers[layer.id];
            const lightShadowHelper = this.lightShadowHelpers[layer.id];
            if (helperLight) {
                // Das Gizmo folgt der Sichtbarkeit seines Layers (Auge in der
                // Zeitleiste, Lebensdauer, versteckte Eltern-Gruppe). Vorher
                // blieb es bei ausgeblendetem Licht einfach stehen — das Licht
                // war aus, sein weisses Drahtgitter leuchtete weiter.
                helperLight.visible = visible;
                helperLight.update();
                // Schattenkamera-Frustum ist abgeschafft (siehe updateLight) —
                // der Eintrag ist jetzt immer null; ohne Guard crashte hier
                // JEDES Licht bei aktivem LICHTER-Toggle den Frame.
                lightShadowHelper?.update();
            }
        }

        if (!visible) {
            const pivotCircle = this.pivotsCircles[layer.id];
            if (pivotCircle) {
                pivotCircle.visible = false;
            }
            return usedChildren;
        }

        // Only change attributes when visible
        const layer3D = layer.layer3D(timer);
        const layer3Dscale = layer3D.scale!;
        const layer3Dopacity = layer3D.opacity!;
        const layer3Drotation = layer3D.rotation!;
        const layer3Dposition = layer3D.position!;
        const useZeroScale = layer3Dscale[0] === 0 || layer3Dscale[1] === 0 || layer3Dscale[2] === 0;
        const opacityZero = layer3Dopacity === 0;

        // 3d objects make a lot of problems with the ordering, which are not entirely clear why they happen
        // it seems to work when we don't set something for the object3d
        layerMesh.renderOrder = layer.type !== LayerType.object3d ? order : 0;

        if (layer.type === LayerType.text && layerMesh.children[0]) {
            const mesh = layerMesh.children[0] as THREE.Mesh;
            mesh.renderOrder = order;
        }

        // TODO: combine this with general visibility
        if (useZeroScale || opacityZero) {
            layerMesh.visible = false;
            return usedChildren;
        }

        // Karten-Ende wieder EINSCHLIESSLICH wie jede andere Ebene (Jans
        // Entscheidung 12.10.2026: „Ein Objekt muss an seinem End-Punkt zu
        // sehen sein"). Die Ausnahme vom 10.10. (Ende ausschließlich) ist
        // zurückgenommen — gegen das Übereinanderstehen helfen jetzt
        // Animation/Blende (Template+-Fenster-Modell) bzw. ein Ende einen
        // Tick vor der Grenze.

        let material = layerMesh.material as THREE.Material;
        const parentOpacity = parent ? parent.userData.opacity || 1 : 1;
        const opacity = layer3Dopacity * parentOpacity;
        // EIGENBEWEGUNG „Atmen" (Jans Fund 28.09.2026: „hat teilweise sehr lange
        // gedauert, bis das Atmen losging"): Die Deckkraft pendelt staendig bis
        // GENAU 100 % zurueck. Jeder Wechsel opak ↔ transparent verlangt einen
        // Shader-Recompile (needsUpdate) — zweimal pro Runde, der erste spuerbar
        // lang. Solche Ebenen gelten deshalb dauerhaft als „am Faden".
        const amFaden = opacity < 1 || !!(layer as LayerRender).loops?.opacity?.active;

        // SCENES BEIM ÜBERGANG (Jans Fund 19.10.2026, zwei Erdkugeln
        // hintereinander): Licht und Schatten zweier Scenes, die sich
        // überlappen, werden über die Überlappung übergeblendet (Gewicht s.
        // sceneLichtGewicht). Während der Überlappung (Gewicht unter 1)
        // werfen Scene-Ebenen keinen Schatten — sonst schattet die alte Kugel
        // die neue ab, die sich mit ihr durchdringt (dunkle Hälfte mit harter
        // Kante). Frei gebaute Ebenen im Pro-Editor (ohne Scene-Wurzel)
        // bleiben unberührt.
        const eigenesGewicht = (layer as TODOAny).tplusSceneId
            ? this._sceneLichtGewichte.get(layer.id)
            : undefined;
        const inScene = eigenesGewicht !== undefined || !!parent?.userData?.inScene;
        const sceneGewicht: number = eigenesGewicht ?? parent?.userData?.sceneGewicht ?? 1;
        if (inScene) this.sceneSchattenNachGewicht(layerMesh, sceneGewicht);

        // TON AN DIE SICHTBARKEIT KOPPELN (Jans Fund 04.08.2026: ein Video, das
        // ueber mehrere Slides laeuft und zwischendurch ausgeblendet ist, war
        // die ganze Zeit zu hoeren). Bis hierher haengt die Wiedergabe nur an
        // der Lebenszeit sowie — falls gemessen — an Blickfeld und Bildschirm;
        // die Deckkraft spielte nirgends eine Rolle.
        //
        // Nur stummschalten, nicht pausieren (Jans Wahl): das Video laeuft
        // unhoerbar weiter und steht beim Wiedereinblenden an der richtigen
        // Stelle, statt zu springen. Begruendung + Regel in utils/videoAudible.
        if (layer.type === LayerType.video) {
            const videoFuerTon = this.videoCache[layer.id];
            if (videoFuerTon?.videoPlayer) {
                // Kehrgang-Markierung (s. animate): dieses Video ist in diesem
                // Frame unter Verwaltung der Hoerbarkeits-Schicht.
                this._tonGepflegteVideos.add(layer.id);
                // Hat die Vorschau ueberhaupt eine Groesse? Bei 0x0 (Hintergrund-
                // Tab, Layout-Wechsel, vor der ersten Messung) melden die
                // Bildschirm-Pruefungen "nicht sichtbar" — dann waeren SCHLAGARTIG
                // alle Videos still. In dem Fall lieber "nicht gemessen"
                // durchreichen und den Ton lassen, wie er ist. Selbst beobachtet:
                // bei 0x0-Canvas schaltete sonst jedes Video stumm.
                const gl = this.webGLRenderer?.getContext();
                const flaecheGemessen = !!gl && gl.canvas.width > 0 && gl.canvas.height > 0;
                const hoerbar = shouldVideoBeAudible({
                    contentMuted: (layer.content as VideoContent)?.muted,
                    // Jans Regel 24.08.: höchstens EIN Video hörbar — das
                    // zentralste+vorderste (s. Ton-Auswahl oben). Editor:
                    // nicht angebunden.
                    einzigesTonVideo: this.props.renderer.isUsedInEditor()
                        ? undefined
                        : layer.id === this.props.renderer.tonVideoId,
                    opacity,
                    onScreen: flaecheGemessen ? checkForOnScreen : undefined,
                    inFrustum:
                        flaecheGemessen && layerMesh.userData.videoLiveUpdate
                            ? checkFrustum
                            : undefined,
                });
                // SANFTES EIN-/AUSBLENDEN statt hartem Stummschalter (Jans
                // Abnahme 25.08.2026: "Ton kommt und verschwindet abrupt").
                // Nutzt die vorhandene 1.0-Rampe (Video.startFade). Fester
                // Standard von 400 ms — die Einblenden/Ausblenden-Regler des
                // Videos gelten weiter fuer Lebenszeit-Anfang/-Ende, nicht
                // fuer diesen Hoerbarkeits-Wechsel.
                {
                    const TON_FADE_MS = 400;
                    const vid = videoFuerTon as TODOAny;
                    const el = videoFuerTon.videoPlayer;
                    if (hoerbar) {
                        if (el.muted || vid.tonFadeAus) {
                            vid.tonFadeAus = false;
                            const von = el.muted ? 0 : el.volume;
                            el.volume = von;
                            el.muted = false;
                            videoFuerTon.startFade(
                                von,
                                vid.targetVolume ?? 1,
                                TON_FADE_MS,
                            );
                        }
                    } else if (!el.muted && !vid.tonFadeAus) {
                        vid.tonFadeAus = true;
                        videoFuerTon.startFade(el.volume, 0, TON_FADE_MS, () => {
                            el.muted = true;
                            vid.tonFadeAus = false;
                        });
                    }
                }
                // (sichtbar steht seit 25.08. direkt im Score — dort aus
                // derselben Deckkraft berechnet, ohne Umweg ueber diesen Block.)
            }
        }

        // Karte im 3D-Raum: Deckkraft wie jede Fläche, Kamera aus der Zeit.
        if ((layer.type as LayerType) === LayerType.map) {
            material.opacity = opacity;
            const karte = this.kartenImRaum[layer.id];
            if (karte) {
                karte.setzeDeckkraft(opacity);
                karte.setzeGroesse(this.sceneUnitsToPx());
                // mit Fahrt-Keyframes nach Ort, sonst nach Zeit
                karte.stelleKamera(layer.from, layer.to, timer, layer3D.mapPlace);
            }
        }

        // Infografik als 3D-Flaeche (Checkbox "Als HTML rendern" aus). Steht vor
        // dem switch, weil der Infografik-Typ nicht Teil dessen Layer-Union ist —
        // im HTML-Modus gibt es hier gar kein Mesh, der Zweig laeuft dann nicht.
        if ((layer.type as LayerType) === LayerType.infographic) {
            material.opacity = opacity;
            // Voll deckende Grafik darf opak bleiben (durch Glas sichtbar);
            // beim Faden bzw. bei durchscheinendem Hintergrund transparent.
            const wantTransparent = amFaden || material.userData?.alphaNeeded !== false;
            if (material.transparent !== wantTransparent) {
                material.transparent = wantTransparent;
                material.needsUpdate = true;
            }
            // Aufbau: Wischblende statt wachsender Balken. progress ist
            // undefined, solange der Kanal nicht animiert -> voll sichtbar.
            const reveal = material.userData?.uReveal;
            if (reveal) reveal.value = layer3D.progress ?? 1;
        }

        switch (layer.type) {
            case LayerType.group:
                // save the opacity in the userdata of the group
                layerMesh.userData.opacity = layer3Dopacity * parentOpacity;
                // Scene-Zugehörigkeit + Licht-Gewicht an die Kinder weitergeben
                layerMesh.userData.inScene = inScene;
                layerMesh.userData.sceneGewicht = sceneGewicht;
                break;
            case LayerType.text: {
                if (layerMesh.children[0]) {
                    const mesh = layerMesh.children[0] as THREE.Mesh;
                    // Troika liefert ein Material-ARRAY [outline, fill], sobald ein
                    // Schlagschatten-Outline konfiguriert ist; .opacity auf dem Array
                    // verpufft — daher auf jedem Material setzen (der Outline-Schatten
                    // blendet so mit dem Fill mit). 1:1 aus Jans Original.
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const mat = (mesh as any).material;
                    if (Array.isArray(mat)) {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        mat.forEach((m: any) => {
                            m.opacity = opacity;
                        });
                        material = mat[mat.length - 1];
                    } else {
                        mat.opacity = opacity;
                        material = mat;
                    }
                    // Port "Anim-Kanaele": animierte Zeilenhoehe + Laufweite (Troika).
                    // Aktiver Kanal ueberschreibt, sonst der statische content-Wert.
                    // Quantisiert -> teures troika.sync() nur bei sichtbarer Aenderung.
                    const tc = layer.content as TODOAny;
                    const troika = mesh as TODOAny;
                    const lh0 = layer3D.lineHeight ?? tc.lineHeight;
                    // Troika behandelt lineHeight 0 als 'normal' -> kleiner Ersatzwert.
                    const lh = typeof lh0 === "number" && lh0 > 0 ? lh0 : lh0 === undefined ? undefined : 0.001;
                    const ls = layer3D.letterSpacing ?? tc.letterSpacing;
                    if (lh !== undefined || ls !== undefined) {
                        const key = `${Math.round((lh ?? 0) * 1000)}|${Math.round((ls ?? 0) * 1000)}`;
                        if (troika.__typoKey !== key) {
                            troika.__typoKey = key;
                            if (lh !== undefined) troika.lineHeight = lh;
                            if (ls !== undefined) troika.letterSpacing = ls;
                            troika.sync?.();
                        }
                    }
                    // ANIMIERTER SCHLAGSCHATTEN (Jans Fund 16.09.2026, Praesentation:
                    // „Keyframes des Text-Assets wurden nicht animiert"). Bild, Video
                    // und Vektor lesen die Kanaele shadowBlur/Opacity/OffsetX/Y aus
                    // layer3D — der Text las nur beim Aufbau die statischen Werte.
                    // Aktiver Kanal ueberschreibt, sonst der content-Wert (wie beim
                    // Aufbau). Troikas outline*-Werte gehen direkt ins Material,
                    // kein sync() noetig; nur bei Aenderung setzen.
                    if (tc.shadowEnabled && !tc.castShadowReal) {
                        const sOpacity = layer3D.shadowOpacity ?? tc.shadowOpacity ?? 0.25;
                        const sBlur = layer3D.shadowBlur ?? tc.shadowBlur ?? 25;
                        const sX = layer3D.shadowOffsetX ?? tc.shadowOffsetX ?? 2;
                        const sY = layer3D.shadowOffsetY ?? tc.shadowOffsetY ?? 2;
                        const schattenKey = `${sOpacity}|${sBlur}|${sX}|${sY}`;
                        if (troika.__schattenKey !== schattenKey) {
                            troika.__schattenKey = schattenKey;
                            troika.outlineOpacity = sOpacity;
                            troika.outlineBlur = `${sBlur}%`;
                            troika.outlineOffsetX = `${sX}%`;
                            troika.outlineOffsetY = `${sY}%`;
                        }
                    }
                }
                break;
            }

            case LayerType.video:
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                if ((layer.content as VideoContent).transparent && (material as any).uniforms) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (material as any).uniforms.opacity.value = opacity;
                } else {
                    material.opacity = opacity;
                }
                break;
            case LayerType.lottie:
                material.opacity = opacity;
                break;
            case LayerType.svg:
                // Port aus Jans Original (fehlte im geteilten Renderer -> Jans
                // Fund "Deckkraft funktioniert nicht im WebGL-Modus"): das
                // SVG ist eine GRUPPE aus Meshes (flache Pfade bzw. Koerper +
                // Deckflaechen) — Opacity auf JEDEM Kind-Material setzen.
                // Extrudierte Standard-Materialien sind nicht transparent:
                // fuer den Fade einschalten (und bei voller Deckkraft wieder
                // aus, sonst leidet die Tiefensortierung des Koerpers).
                layerMesh.traverse((child: TODOAny) => {
                    if (!child.isMesh || !child.material) return;
                    const mats = Array.isArray(child.material) ? child.material : [child.material];
                    for (const m of mats) {
                        // eigene Deckkraft eines Teils (Kontur mit Alpha, Jan
                        // 22.10.2026) bleibt erhalten, die Ebene multipliziert
                        const eigene = m.userData?.eigeneDeckkraft ?? 1;
                        m.opacity = opacity * eigene;
                        const wantTransparent = amFaden || eigene < 1 || m.depthWrite === false;
                        if (m.transparent !== wantTransparent) {
                            m.transparent = wantTransparent;
                            m.needsUpdate = true;
                        }
                    }
                    child.renderOrder = layerMesh.renderOrder;
                });
                // Grundform Linie/Pfeil: Aufbau über den Fortschritt (Jan 22.10.2026)
                if ((layer.content as TODOAny).grundform) grundformAufbau3d(layerMesh, layer3D.progress ?? 1);
                break;
            case LayerType.effect: {
                material.opacity = opacity;
                // Der alphaTest stanzt die TEILCHEN-FORM aus dem Quad: 0.5 fuer
                // runde Konfetti-Scheiben, 0.04 fuer Schneeflocken, 0 fuer
                // eckige Konfetti. Er vergleicht aber die FERTIGE Alpha, also
                // Deckkraft x Textur-Alpha. Sinkt die Layer-Deckkraft unter den
                // Schwellwert, verwirft der Shader JEDES Fragment und der Effekt
                // ist schlagartig weg statt durchzufaden — Jans Fund "bricht
                // genau bei 50% ab" (gemessen: 0.50 normal, 0.49 komplett
                // schwarz). Mit der Deckkraft mitskaliert bleibt die Form exakt
                // gleich (der Test liegt weiter bei 0.5 der TEXTUR-Alpha) und
                // der Effekt fadet sauber aus. Der Wert bleibt dabei >0, es gibt
                // also keinen USE_ALPHATEST-Wechsel und keinen Recompile.
                const baseAlphaTest = (material.userData.baseAlphaTest ??=
                    (material as TODOAny).alphaTest);
                (material as TODOAny).alphaTest = baseAlphaTest * opacity;
                // Loop (Vorgabe): die Simulation laeuft an der Wanduhr, damit sie
                // weiterlaeuft, waehrend der Leser auf einer Slide verweilt.
                // Scroll-getrieben: stattdessen dem Playhead folgen (der Effekt
                // wird mit dem Scrollen durchgescrubbt).
                const effectTimer = (layer.content as EffectContent).scrollDriven
                    ? timer
                    : performance.now();
                syncEffect(layerMesh as unknown as THREE.Points, layer.content as EffectContent, effectTimer, {
                    // Interpolierte Effekt-Einstellungen — undefined, solange ihr
                    // Kanal nicht aktiv animiert; dann greift content.effect.*
                    size: layer3D.effectSize,
                    speed: layer3D.effectSpeed,
                    wind: layer3D.effectWind,
                    depth: layer3D.effectDepth,
                    count: layer3D.effectCount,
                    edgeFalloff: layer3D.effectEdgeFalloff,
                    // Streuflaeche auf den SICHTBAREN Ausschnitt beziehen (Jans
                    // Fund 09.09.2026 "alle 4 nur Square"): Querformat zeigt
                    // die volle Breite und nur einen Teil der Hoehe, Hochformat
                    // umgekehrt — gleiche Regel wie calculateBounds/scaleFactor.
                    ...this.sichtbareStreuHalbmasse(),
                });
                break;
            }
            case LayerType.image:
                // Background image layers (isBackground / is_background) render at
                // 25 % opacity so the photo is a subtle backdrop behind the chart.
                // NUR in Studio-Dokumenten — Templates rendern ihr Hintergrundbild
                // voll deckend (studioBackdropAktiv).
                if (
                    ((layer as TODOAny).isBackground || (layer as TODOAny).is_background) &&
                    this.studioBackdropAktiv()
                ) {
                    material.opacity = 0.25;
                } else {
                    material.opacity = opacity;
                }
                break;
            case LayerType.svg:
                layerMesh.traverse((child) => {
                    if ((child as THREE.Mesh).isMesh) {
                        ((child as THREE.Mesh).material as THREE.Material).opacity = opacity;
                        child.renderOrder = layerMesh.renderOrder;
                    }
                });
                break;
            case LayerType.object3d:
                layerMesh.userData.opacity = opacity;
                // Voll deckend, aber raeumlich hinter einer frueheren Flaeche →
                // wie beim Faden in der EBENEN-FOLGE zeichnen (s. Helfer).
                const hinterFlaeche =
                    opacity >= 1 && this.liegtHinterFruehererFlaeche(layerMesh, order);
                // Opacity auf JEDES Mesh im Objekt anwenden, nicht nur den Root
                // (Jans Original): Primitives und importierte Modelle sind
                // GROUPS — der alte Root-only-Check (isMesh am Wrapper false)
                // ließ alle Kinder bei voller Deckkraft, Fades wirkten nie.
                layerMesh.traverse((o: TODOAny) => {
                    if (!(o as THREE.Mesh).isMesh || !(o as THREE.Mesh).material) return;
                    if ((o as THREE.Mesh).userData.tiefenVorpass === true) return;
                    // Beim Faden ODER bei Alpha-Map-Transparenz in die EBENEN-
                    // Sortierung eingliedern (wie jeder andere Layer-Typ): mit
                    // renderOrder 0 zeichnete das Objekt VOR dem Hintergrund-Bild,
                    // blendete gegen Loeschfarbe/HDRI-Kuppel und blockte per
                    // Depth-Write die BG-Pixel — "Deckkraft 10% ist eher schwarz"
                    // bzw. "durch die Alpha-Map scheint das HDRI statt des
                    // Hintergrundbilds" (Jans Funde). Voll deckend ohne Alpha-Map
                    // bleibt das alte renderOrder-0-Verhalten unveraendert.
                    const oMats = Array.isArray(o.material) ? o.material : [o.material];
                    const hasAlphaCut = oMats.some((m: TODOAny) => !!m?.alphaMap);
                    // TRANSPARENT HEISST: IN DER EBENEN-FOLGE BLEIBEN (Jans Fund
                    // 17.09.2026, Alt-Import „Erzliane": Erdkugel bei Deckkraft
                    // 100 WEG, bei 99 da — auch korrekt beleuchtet). Materialien,
                    // die bei voller Deckkraft transparent BLEIBEN (alphaNeeded
                    // !== false, z. B. Alt-Material mit transparent:true), laufen
                    // im Transparent-Pass. Dort entscheidet renderOrder: mit 0
                    // wurde die Kugel VOR allen Bildebenen gezeichnet, die danach
                    // darueber malten (Bilder schreiben keine Tiefe, liegen im
                    // Raum aber oft naeher) — Kugel weg. Bei 99 griff `order`,
                    // deshalb stimmte es dort. renderOrder 0 gilt jetzt nur noch
                    // fuer WIRKLICH opake Materialien (Opak-Pass, Tiefe regelt).
                    const bleibtTransparent = oMats.some(
                        (m: TODOAny) => m?.userData?.alphaNeeded !== false,
                    );
                    o.renderOrder =
                        amFaden || hasAlphaCut || bleibtTransparent || hinterFlaeche ? order : 0;
                    // Tiefen-Vorpass (s. tiefenVorpassAnlegen): nur beim Faden,
                    // im Transparent-Pass unmittelbar VOR dem Modell.
                    // NICHT bei (fast) unsichtbarem Objekt (Jans Fund 21.10.2026,
                    // Zitat-Text auf dem nächsten Slide angeschnitten): die
                    // Scene-Blende endet bei 0,001 statt 0 — der Vorpass schrieb
                    // dort weiter Tiefe und stanzte alles dahinter aus.
                    const vorpass = (o as THREE.Mesh).userData.tiefenVorpassMesh as THREE.Mesh | undefined;
                    if (vorpass) {
                        vorpass.visible = amFaden && opacity > 0.02;
                        vorpass.renderOrder = order - 0.5;
                    }
                    const mats = oMats;
                    mats.forEach((m: TODOAny) => {
                        m.opacity = opacity;
                        // Waehrend des Fadens / bei Alpha-Ausschnitt kein Depth-
                        // Write — sonst loescht das durchsichtige Objekt die
                        // dahinterliegenden Pixel.
                        m.depthWrite = amFaden || m.alphaMap ? false : m.userData?.baseDepthWrite ?? true;
                        // Opaque-faehige Materialien bei voller Deckkraft opak
                        // lassen (Glas-Durchsicht); transparent nur beim Faden
                        // oder wenn das Material Alpha wirklich braucht.
                        // hinterFlaeche: in den Transparent-Pass (Ebenen-Folge), Tiefe
                        // wird weiter geschrieben — 3D-Objekte verdecken sich korrekt.
                        const wantTransparent =
                            amFaden || m.userData?.alphaNeeded !== false || hinterFlaeche;
                        if (m.transparent !== wantTransparent) {
                            // `transparent` wechselt den Blending-Pfad — three
                            // uebernimmt das erst nach Shader-Recompile. Nur bei
                            // AENDERUNG flaggen (nie pro Frame).
                            m.transparent = wantTransparent;
                            m.needsUpdate = true;
                        }
                    });
                });
                // Port "Anim-Kanaele": animierter planarer Spiegel — aktive Kanaele
                // ueberschreiben den statischen mirrorStrength/mirrorBlur;
                // updateMirrors liest mirrorParams je Frame (laeuft danach in animate).
                if (this.mirrorParams[layer.id]) {
                    if (layer3D.mirrorStrength !== undefined) {
                        this.mirrorParams[layer.id].strength = layer3D.mirrorStrength;
                    }
                    if (layer3D.mirrorBlur !== undefined) {
                        this.mirrorParams[layer.id].blur = layer3D.mirrorBlur;
                    }
                }
                break;
            case LayerType.object3dChild:
                if (layerMesh.isMesh) {
                    layerMesh.userData.opacity = opacity;
                    (layerMesh.material as THREE.Material).opacity = opacity;
                }
                break;
            case LayerType.light: {
                // Port "Anim-Kanaele": animierte Licht-Einstellungen pro Frame.
                // Nur ein AKTIVER Kanal ueberschreibt — sonst bleibt der statische
                // updateLight-Wert (additiv-sicher: Bestands-Lichter unberuehrt).
                const light = this.lightState[layer.id] as TODOAny;
                if (light) {
                    // Point/Spot: animierte Werte nur als BASEN stashen — der
                    // Licht-Skalierungs-Pass (nach dem Crawl in animate) leitet
                    // daraus distance/intensity ratio-normalisiert ab. Direktes
                    // Setzen wuerde mit dem Pass pro Frame kaempfen. Directional
                    // hat keinen Falloff → direkt (inkl. Legacy-Faktor).
                    light.userData.animBaseIntensity = layer3D.intensity;
                    light.userData.animBaseDistance = layer3D.lightDistance;
                    light.userData.animBaseDecay = layer3D.lightDecay;
                    if (light.isDirectionalLight && layer3D.intensity !== undefined) {
                        // Legacy-Paritaet (siehe LEGACY_DIRECTIONAL_INTENSITY_SCALE)
                        light.intensity = layer3D.intensity * LEGACY_DIRECTIONAL_INTENSITY_SCALE;
                    }
                    // Globale Lichter: kein Falloff, kein Skalierungs-Pass —
                    // animierte Intensitaet direkt anwenden (Keyframe-Funktion
                    // fuer Umgebungs-/Halbkugel-Licht, Jans Fund).
                    if ((light.isAmbientLight || light.isHemisphereLight) && layer3D.intensity !== undefined) {
                        light.intensity = layer3D.intensity;
                    }
                    // LICHT ZWISCHEN SCENES ÜBERBLENDEN (Jans Fund 19.10.2026):
                    // Überlappen sich zwei Licht-Scenes, leuchtet die alte mit
                    // fallendem, die neue mit steigendem Gewicht — in der Summe
                    // gleich hell (vorher schienen zwei volle Sonnen, die neue
                    // Kugel war überstrahlt). Point/Spot: Faktor im Licht-
                    // Skalierungs-Pass nach dem Crawl.
                    const blendFaktor = inScene ? sceneGewicht : 1;
                    light.userData.blendFaktor = blendFaktor;
                    if (inScene) {
                        const lc = this.layerContentCache[layer.id] as TODOAny;
                        const basis = layer3D.intensity ?? lc?.intensity ?? 1;
                        if (light.isDirectionalLight) {
                            light.intensity = basis * LEGACY_DIRECTIONAL_INTENSITY_SCALE * blendFaktor;
                        } else if (light.isAmbientLight || light.isHemisphereLight) {
                            light.intensity = basis * blendFaktor;
                        }
                    }
                    if (layer3D.lightColor) {
                        light.color.setRGB(
                            layer3D.lightColor[0] / 255,
                            layer3D.lightColor[1] / 255,
                            layer3D.lightColor[2] / 255
                        );
                    }
                    if (layer3D.lightDecay !== undefined && "decay" in light) {
                        light.decay = layer3D.lightDecay;
                    }
                    if (light.isSpotLight) {
                        if (layer3D.lightAngle !== undefined) light.angle = layer3D.lightAngle;
                        if (layer3D.lightPenumbra !== undefined) light.penumbra = layer3D.lightPenumbra;
                    }
                    // Port "Anim-Kanaele": animierter Ziel-/Aim-Punkt (spot/directional).
                    // Skaliert wie Jans Ziel (× halbe SLIDE-Masse: 2048/2048/1024).
                    if (layer3D.lightTarget && (light.isSpotLight || light.isDirectionalLight) && light.target) {
                        const t = layer3D.lightTarget;
                        light.target.position.set(t[0] * 2048, t[1] * 2048, t[2] * 1024);
                        if (this.webGlScene && !light.target.parent) this.webGlScene.add(light.target);
                        light.target.updateMatrixWorld();
                    }
                }
                break;
            }
            default:
        }

        // Port 3 (Bild-Masken/Schatten/Blur): Jans image-Block aus renderLayer.
        // blur/mask/shadow sind animierbare Kanaele — die interpolierten Werte
        // aus layer3D haben Vorrang, sonst greift der statische content-Wert.
        if (layer.type === LayerType.image) {
            const imageContent = layer.content as ImageContent;
            const imageMesh = layerMesh as unknown as THREE.Mesh;
            // Erst Blur (tauscht die Map), damit Masken-UV und Schatten-
            // Silhouette auf der aktuellen Textur arbeiten.
            syncImageBlur(imageMesh, imageContent, layer3D.blur, this.props.maxTextureWidth);
            // Maske vor dem Schatten backen, damit der Schatten ihrer Form folgt.
            syncImageMask(this, imageMesh, imageContent, layer3D.mask);
            syncImageShadow(imageMesh, imageContent, {
                blur: layer3D.shadowBlur,
                opacity: layer3D.shadowOpacity,
                blurRange: this.weichheitsBereich(layer),
            });
            // Echte (licht-getriebene) Schatten, pro Layer opt-in.
            syncImageRealShadows(this, layerMesh, imageContent);

            // Fake-Schatten-Kind mit der Bild-Deckkraft faden und per
            // shadowOffsetX/Y (in % der Bildgroesse) versetzen. +X rechts, -Y unten.
            const shadowChild = layerMesh.children.find(
                (c) => c.name === "__fakeShadow"
            ) as THREE.Mesh | undefined;
            if (shadowChild) {
                shadowChild.renderOrder = layerMesh.renderOrder - 0.5;
                // Bild-Deckkraft × Schatten-Deckkraft (nicht mehr eingebacken,
                // s. syncImageShadow — Jans iPhone-Fund 27.09.2026)
                (shadowChild.material as THREE.MeshBasicMaterial).opacity =
                    opacity * ((shadowChild.userData.schattenDeckkraft as number | undefined) ?? 1);
                (shadowChild.material as THREE.MeshBasicMaterial).transparent = true;
                // zweite Schattenflaeche der Weichheits-Ueberblendung (28.09.2026)
                this.schattenKinderFaden(shadowChild, opacity);
                const ref = Math.max(imageContent.width, imageContent.height);
                const sox = layer3D.shadowOffsetX ?? imageContent.shadowOffsetX ?? 2;
                const soy = layer3D.shadowOffsetY ?? imageContent.shadowOffsetY ?? 2;
                shadowChild.position.set((sox / 100) * ref, -(soy / 100) * ref, -1);
            }
        }

        // Video-Schlagschatten (Jans Wunsch 02.08.2026): derselbe Fake-
        // Schatten wie beim Bild, Silhouette = Rechteck (syncImageShadow
        // erkennt die VideoTexture selbst). Masse aus der ECHTEN Mesh-
        // Geometrie, nicht aus content.width — bei .mov mit Rotations-
        // Metadaten weichen die gespeicherten Masse vom gerenderten Bild ab
        // (gleicher Grund wie beim Geometrie-Bau in updateVideo).
        if (layer.type === LayerType.video && !Array.isArray((layerMesh as TODOAny).material)) {
            const videoMesh = layerMesh as unknown as THREE.Mesh;
            const vg = videoMesh.geometry as THREE.BufferGeometry;
            if (vg && !vg.boundingBox) vg.computeBoundingBox();
            const vw = vg?.boundingBox ? vg.boundingBox.max.x - vg.boundingBox.min.x : 0;
            const vh = vg?.boundingBox ? vg.boundingBox.max.y - vg.boundingBox.min.y : 0;
            if (vw > 0 && vh > 0) {
                const vc = layer.content as unknown as ImageContent;
                const shadowContent = { ...vc, width: vw, height: vh } as ImageContent;
                syncImageShadow(videoMesh, shadowContent, {
                    blur: layer3D.shadowBlur,
                    opacity: layer3D.shadowOpacity,
                    blurRange: this.weichheitsBereich(layer),
                });
                const shadowChild = videoMesh.children.find(
                    (c) => c.name === "__fakeShadow"
                ) as THREE.Mesh | undefined;
                if (shadowChild) {
                    shadowChild.renderOrder = videoMesh.renderOrder - 0.5;
                    // Bild-Deckkraft × Schatten-Deckkraft (nicht mehr eingebacken,
                // s. syncImageShadow — Jans iPhone-Fund 27.09.2026)
                (shadowChild.material as THREE.MeshBasicMaterial).opacity =
                    opacity * ((shadowChild.userData.schattenDeckkraft as number | undefined) ?? 1);
                    (shadowChild.material as THREE.MeshBasicMaterial).transparent = true;
                    this.schattenKinderFaden(shadowChild, opacity);
                    const ref = Math.max(vw, vh);
                    const sox = layer3D.shadowOffsetX ?? vc.shadowOffsetX ?? 2;
                    const soy = layer3D.shadowOffsetY ?? vc.shadowOffsetY ?? 2;
                    shadowChild.position.set((sox / 100) * ref, -(soy / 100) * ref, -1);
                }
            }
        }

        // Port "3D-Text": per-Frame-Neuaufbau der number3d-Ziffern (und text3d
        // bei animierter Laufweite). number3d zaehlt ueber die Layer-Lebensdauer;
        // ohne progress-Kanal treibt der Lifetime-Fortschritt den Zaehler.
        if (layer.type === LayerType.object3d) {
            const o3d = layer.content as unknown as Object3DContent;
            const prim = o3d.primitive;
            const child = layerMesh.children[0] as THREE.Mesh | undefined;
            // Port "Anim-Kanaele": animierte Material-Displacement-Staerke (braucht
            // eine displacementMap, um sichtbar zu wirken).
            if (child && layer3D.displacementScale !== undefined) {
                const applyDisp = (m: TODOAny) => {
                    if (m && "displacementScale" in m) m.displacementScale = layer3D.displacementScale;
                };
                const mat = child.material as TODOAny;
                if (Array.isArray(mat)) mat.forEach(applyDisp);
                else applyDisp(mat);
            }
            // Textur-Fluss (Jans 1.0): die Farb-Textur wandert mit dem progress-
            // Kanal, sonst mit der Layer-Lebenszeit-Position. Nutzt den Kachel-
            // Klon aus applyMapTiling und behaelt den festen Versatz.
            {
                const mopts = (o3d as TODOAny).material?.options;
                if (mopts && (mopts.textureScrollX || mopts.textureScrollY)) {
                    const lebenszeit =
                        layer.to > layer.from
                            ? Math.min(1, Math.max(0, (timer - layer.from) / (layer.to - layer.from)))
                            : 1;
                    const versatz = texturFlussVersatz(mopts, layer3D.progress ?? lebenszeit);
                    if (versatz) {
                        layerMesh.traverse((o: TODOAny) => {
                            const mats = Array.isArray(o.material) ? o.material : [o.material];
                            for (const m of mats) {
                                const klon = m?.userData?.__tileClone;
                                if (klon?.offset && (klon.offset.x !== versatz.x || klon.offset.y !== versatz.y)) {
                                    klon.offset.set(versatz.x, versatz.y);
                                }
                            }
                        });
                    }
                }
            }
            // Port "Anim-Kanaele": star pro Frame neu bauen, wenn ein star-Kanal aktiv ist.
            if (child && prim === "star" && (layer3D.starOuterRadius !== undefined || layer3D.starInnerRadius !== undefined || layer3D.starPoints !== undefined)) {
                const p = (o3d.primitiveParams || {}) as TODOAny;
                const outer = layer3D.starOuterRadius ?? p.radius ?? 420;
                const inner = layer3D.starInnerRadius ?? p.innerRadius ?? 180;
                const points = layer3D.starPoints ?? p.points ?? 5;
                const key = `${Math.round(outer)}|${Math.round(inner)}|${Math.round(points)}`;
                if (child.userData.__starKey !== key) {
                    child.userData.__starKey = key;
                    (child.geometry as TODOAny)?.dispose?.();
                    child.geometry = buildStarGeometry({ ...p, radius: outer, innerRadius: inner, points }) as TODOAny;
                }
            }
            // Schach: Partie ueber den progress-Kanal nachspielen — ohne
            // Keyframes zaehlt die Layer-Lebenszeit-Position (Jans 1.0).
            if (prim === "chess" && (layerMesh.userData as TODOAny).__chess) {
                const lifeProgress =
                    layer.to > layer.from
                        ? Math.min(1, Math.max(0, (timer - layer.from) / (layer.to - layer.from)))
                        : 1;
                animateChess(layerMesh as unknown as THREE.Group, layer3D.progress ?? lifeProgress);
            }
            if (child && (prim === "number3d" || prim === "text3d" || prim === "progressBar" || prim === "progressRing")) {
                const p = (o3d.primitiveParams || {}) as TODOAny;
                // Fuell-Fortschritt 0..1: der animierte progress-Kanal, sonst die
                // Layer-Lebenszeit-Position (auto-Fuellung ueber den Layer-Balken).
                const lifeProgress =
                    layer.to > layer.from
                        ? Math.min(1, Math.max(0, (timer - layer.from) / (layer.to - layer.from)))
                        : 1;
                const progress = layer3D.progress ?? lifeProgress;
                const font3d = (layerMesh.userData as TODOAny).__font3d;
                if ((prim === "number3d" || prim === "text3d") && font3d) {
                    const ls = layer3D.letterSpacing ?? p.letterSpacing ?? 0;
                    const str = prim === "number3d" ? number3dString(p, progress) : p.text || "Text";
                    // nur neu bauen, wenn String oder (quantisierte) Laufweite sich aendern
                    const key = `${str}|${Math.round(ls * 100)}`;
                    if (layerMesh.userData.__txt3dKey !== key) {
                        layerMesh.userData.__txt3dKey = key;
                        (child.geometry as TODOAny)?.dispose?.();
                        // Schneller Pfad: Glyphen aus dem Cache zusammensetzen statt
                        // pro Bild neu extrudieren (Jans Counter-Ruckler 07.09.2026).
                        child.geometry = makeText3dGeometryFast(str, { ...p, letterSpacing: ls }, font3d) as TODOAny;
                    }
                } else if (prim === "progressBar") {
                    // Balken links -> rechts wachsen lassen (Box neu bauen, linke Kante
                    // auf dem Pivot). Quantisiert -> nur bei sichtbarer Aenderung.
                    const pct = Math.round(progress * 1000);
                    if (child.userData.__barPct !== pct) {
                        child.userData.__barPct = pct;
                        const fw = Math.max(1, (pct / 1000) * (p.width ?? 1400));
                        (child.geometry as TODOAny)?.dispose?.();
                        const geo = new THREE.BoxGeometry(fw, p.height ?? 140, p.depth ?? 80);
                        geo.translate(fw / 2, 0, 0);
                        child.geometry = geo as TODOAny;
                    }
                } else if (prim === "progressRing") {
                    // Ring-Bogen fuellen; nur bei (quantisierter) Winkel-Aenderung neu.
                    const deg = Math.round(progress * 360);
                    if (child.userData.__arcDeg !== deg) {
                        child.userData.__arcDeg = deg;
                        if ((child.geometry as TODOAny)?.userData?.__ringSweep) {
                            // Sweep-Ring: nur den sichtbaren Bogen zeichnen — kein
                            // Neuaufbau, kein Upload (Jans Ring-Ruckler 07.09.2026).
                            (child.geometry as TODOAny).setDrawRange(0, progressRingDrawCount(deg / 360));
                        } else {
                            // Bestand (Ring aus aelterem Aufbau): wie bisher neu bauen
                            (child.geometry as TODOAny)?.dispose?.();
                            child.geometry = buildProgressRingGeometry(p, deg / 360) as TODOAny;
                        }
                    }
                }
            }
        }

        layerMesh.name = layer.name;

        const pivotPointIsZero = layer.pivotPoint[0] === 0 && layer.pivotPoint[1] === 0 && layer.pivotPoint[2] === 0;
        const doNotUsePivot =
            (layer.type === LayerType.object3d && pivotPointIsZero) || layer.type === LayerType.object3dChild;
        const usePivot = !doNotUsePivot;
        const zPosition = layer3Dposition[2];

        // layers with a custom z position should be rendered first and compared among themselves
        // object3ds are making again problems here and are ignored for now, as it still works with
        // some internal THREE.js bugs probably
        if (zPosition > 0 && layer.type !== LayerType.object3d && layer.type !== LayerType.object3dChild) {
            layerMesh.renderOrder = 999;
        }

        const newPosition = new THREE.Vector3(
            layer3Dposition[0] + layer.pivotPoint[0],
            layer3Dposition[1] + layer.pivotPoint[1],
            zPosition + layer.pivotPoint[2]
        ).multiply(maxVector);

        const relativePivotOffset = new THREE.Vector3(
            -layer.pivotPoint[0],
            -layer.pivotPoint[1],
            -layer.pivotPoint[2]
        ).multiply(maxVector);
        // make sure we don't scale with zero
        let scaleVector = new THREE.Vector3(...(useZeroScale ? MIN_SCALE : layer3Dscale));

        if (layer.type === LayerType.text) {
            const textContent: TextContent = layer.content;
            // the responsive scale factor will adjust font sizes on smaller screens
            const scaleFactor = 1 + ((textContent.responsiveScaling || 0) / 100) * (responsiveScaleFactor - 1);
            scaleVector = scaleVector.multiplyScalar(scaleFactor);
        }

        const toRad = [
            degreeToRad(layer3Drotation[0]),
            degreeToRad(layer3Drotation[1]),
            degreeToRad(layer3Drotation[2]),
        ] as Vector3Tuple;

        // Live-Drehpunkt-Flag: `showPivotPoint` ist bewusst NICHT serialisiert
        // (transienter UI-State; Jans layer.json() setzt hart false) — der aus
        // JSON gebaute Render-Tree sieht also immer false, das Menu-"Show" kam
        // hier nie an. Im Editor den Live-Wert des SELEKTIERTEN MST-Layers ueber
        // die Editor-Hook-Bruecke lesen (gleiches Muster wie syncGizmo).
        const esPivotHook = this.props.editorStore;
        const showPivotPoint =
            layer.showPivotPoint ||
            !!(
                esPivotHook?.layer &&
                esPivotHook.layer.id === layer.id &&
                (esPivotHook.layer as TODOAny).showPivotPoint
            );

        if (usePivot) {
            let pivotCircle = this.pivotsCircles[layer.id];
            // Editor-Chrome lazy nachziehen (Muster wie safeSquare, Jans Fund #1):
            // Die Kugel entsteht im Erzeugungspfad nur bei !isProduction — der
            // geteilte Viewer laeuft aber IMMER mit isProduction:true, also gab es
            // im Port nie eine Drehpunkt-Anzeige ("Show" wirkungslos, Jans Fund).
            // Im Editor bei Bedarf erzeugen; Reader (editorMode false) unberuehrt.
            if (!pivotCircle && WebGLAssets.editorMode && showPivotPoint) {
                const geometry = new THREE.SphereGeometry(100, 4, 2);
                // Editor-Chrome: IMMER obenauf zeichnen (depthTest aus + spaeter
                // Render-Slot) — bei Pivot 0/0/0 steckt die Kugel sonst IM
                // extrudierten Koerper und ist unsichtbar (Jans Fund).
                const material = new THREE.MeshBasicMaterial({
                    color: 0xff0000,
                    wireframe: true,
                    depthTest: false,
                    transparent: true,
                });
                pivotCircle = new THREE.Mesh(geometry, material);
                pivotCircle.renderOrder = 999999;
                pivotCircle.visible = false;
                this.pivotsCircles[layer.id] = pivotCircle;
                pivotGroup.add(pivotCircle);
            }
            if (pivotCircle) {
                // Nach einem Mesh-Swap (Content-Rebuild) haengt die Kugel u.U.
                // nicht mehr unter der pivotGroup — wieder anhaengen.
                if (pivotCircle.parent !== pivotGroup) pivotGroup.add(pivotCircle);
                pivotCircle.visible = isActive && showPivotPoint;
                pivotCircle.position.setZ(zPosition);
            }
            // Waehrend der Anfasser GENAU DIESE Pivot-Gruppe zieht, gehoert
            // ihre Position dem Gizmo (Jans Fund 02.08.2026, "Kugel laesst
            // sich nicht ziehen"): der Frame schrieb sonst die — dem MST um
            // einige Frames NACHLAUFENDE — Animations-Position zurueck, der
            // Drag konnte nie Bewegung ansammeln und der Write-back las die
            // zurueckgeschnappte Position (Netto: nichts). Betraf Ebenen mit
            // AKTIVEM Positions-Kanal (Keyframes); statische wirkten nur
            // leicht zittrig. Reader: isDraggingGizmo bleibt immer false.
            // Dasselbe fuer die Rotation: im Dreh-Modus (gizmoMode "rotate")
            // gehoert sie waehrend des Drags dem Gizmo, sonst schnappte sie
            // jeden Frame auf den MST-Wert zurueck (identisches Tauziehen).
            const gizmoZiehtPivot = this.isDraggingGizmo && this.transformControls?.object === pivotGroup;
            if (!gizmoZiehtPivot) {
                pivotGroup.position.copy(newPosition);
                pivotGroup.rotation.set(...toRad);
            }
            pivotGroup.scale.copy(scaleVector);
            layerMesh.position.copy(relativePivotOffset);
            // layerMesh.scale.set(1, 1, 1);
            layerMesh.rotation.set(0, 0, 0);
        } else {
            if (pivotGroup) {
                pivotGroup.position.set(0, 0, 0);
                pivotGroup.scale.set(1, 1, 1);
                pivotGroup.rotation.set(0, 0, 0);
                const pivotCircle = this.pivotsCircles[layer.id];
                if (pivotCircle) {
                    pivotCircle.visible = isActive && showPivotPoint;
                }
            }
            if (layer.type === LayerType.object3dChild) {
                layerMesh.position.set(...layer3D.position!);
            } else if (!(this.isDraggingGizmo && this.transformControls?.object === layerMesh)) {
                // Tauziehen-Guard auch HIER (Jans Kugel, 4. Schicht): fuer
                // object3d mit Drehpunkt 0/0/0 traegt das MESH die Transform
                // (usePivot=false, Pivot parkt im Ursprung) — der Gizmo haengt
                // bei diesen Ebenen am Mesh (syncGizmo) und darf waehrend des
                // Drags nicht pro Frame ueberschrieben werden.
                layerMesh.position.copy(newPosition);
            }
            layerMesh.scale.copy(scaleVector);
            // Rotation ebenso nicht ueberschreiben, solange der Gizmo dieses
            // Mesh dreht (Dreh-Modus, object3d mit Drehpunkt 0/0/0).
            if (!(this.isDraggingGizmo && this.transformControls?.object === layerMesh)) {
                layerMesh.rotation.set(...toRad);
            }
        }

        // Port "Zielpunkt" (Jans Original, fehlte im geteilten Renderer —
        // Jans Fund "der Zielpunkt funktioniert nicht"): Spot- UND
        // Richtungslicht zielen bei three ueber position→target; three parkt
        // das target unbewegt im Ursprung. Aus content.target (bzw. dem
        // animierten lightTarget-Kanal) treiben: neben das Licht parenten
        // (gleiche Gruppe) und wie die Licht-Position mit maxVector skalieren
        // — [0,0,0] behaelt damit das alte "zielt auf die Mitte".
        if (layer.type === LayerType.light) {
            const aimedLight = this.lightState[layer.id] as TODOAny;
            if (aimedLight?.isSpotLight || aimedLight?.isDirectionalLight) {
                const aimTarget = aimedLight.target as THREE.Object3D;
                const lc = layer.content as TODOAny;
                const t = (layer3D as TODOAny).lightTarget ?? lc.target ?? [0, 0, 0];
                if (aimTarget.parent !== parent) {
                    aimTarget.parent?.remove(aimTarget);
                    parent.add(aimTarget);
                }
                aimTarget.position.set(t[0], t[1], t[2]).multiply(maxVector as THREE.Vector3);
                // als benutztes Kind melden, sonst haelt der Tree-Cleanup das
                // target fuer verwaist und baut die Szene endlos neu (Original)
                usedChildren.push(aimTarget);

                // Zielpunkt-Marker (Jans Wunsch): gleiche Drahtgitter-Kugel wie
                // der Drehpunkt, nur GELB. Nur im Editor; Reader unberuehrt.
                // Seit 20.09.2026 wie der Drehpunkt nur mit Haekchen „Show"
                // (neben TARGET): vorher stand er bei JEDEM gewaehlten Licht da —
                // Jan hielt ihn fuer den Drehpunkt, der trotz „Show aus" erscheint.
                if (WebGLAssets.editorMode) {
                    const zeigeZiel = isActive && !!this.props.editorStore?.showLightTarget;
                    let marker = this.lightTargetMarkers[layer.id];
                    if (!marker && zeigeZiel) {
                        marker = new THREE.Mesh(
                            new THREE.SphereGeometry(100, 4, 2),
                            new THREE.MeshBasicMaterial({
                                color: 0xffbb00,
                                wireframe: true,
                                depthTest: false,
                                transparent: true,
                            })
                        );
                        marker.renderOrder = 999999;
                        this.lightTargetMarkers[layer.id] = marker;
                        parent.add(marker);
                    }
                    if (marker) {
                        if (marker.parent !== parent) parent.add(marker);
                        marker.position.copy(aimTarget.position);
                        marker.visible = zeigeZiel;
                        usedChildren.push(marker);
                    }
                }
            }
        }

        return usedChildren;
    };

    /** Sentence-Bloom quote SVG must not appear on the cover (beforeStory scroll zone). */
    private hideSentenceBloomSvgForCover(layerId: string, timer: number, layerFrom: number): boolean {
        if (layerId !== 'svg-layer-1') return false;
        if (this.props.isCoverActive?.()) return true;
        if (timer <= 0) return true;
        if (timer < Math.max(1, layerFrom)) return true;
        return false;
    }

    renderLayerHTML = (
        layer: LayerRender,
        parent: CSS3DObject,
        timer: number,
        _: boolean,
        maxVector: TODOAny,
        isActive: boolean,
        order: number,
        responsiveScaleFactor: number
    ): Array<CSS3DObject> => {
        const usedChildren: Array<CSS3DObject> = [];
        let layerMesh = this.layerStateCSS3d[layer.id];
        // currently only used for the text, we need to access it for changing opacity and display
        const innerMesh = this.layerStateCSS3d[`${layer.id}-inner`];
        const isGroup = layer.type === LayerType.group;

        const isHtmlLayer = layer.type === LayerType.svg && layer.content.asHtml;
        // Pro-Editor-Karten liegen als Fläche im 3D-Raum (renderLayer), nur
        // Studio-Karten laufen als Schicht über dem Raum hier durch.
        const isMapLayer = layer.type === LayerType.map && !istKarteImRaum(layer.content);
        // Infografik: DOM-Overlay nur im HTML-Modus. Ist die Checkbox "Als HTML
        // rendern" aus, liegt sie als echte Flaeche in der Szene und wird vom
        // 3D-Zweig behandelt — hier darf sie dann nicht mitlaufen.
        const isInfographicLayer =
            layer.type === LayerType.infographic &&
            infographicIsHtml(layer.content as unknown as InfographicContent);

        // Mode-Switch-Aufraeumen (Jans Vektor-Fund): wechselt ein SVG-Layer von
        // "Als HTML rendern" (DOM-Overlay) zurueck zu WebGL, ist er hier kein
        // isHtmlLayer mehr und wuerde nie wieder angefasst — sein Overlay bliebe
        // sichtbar LIEGEN und verdeckt das WebGL-Mesh. (Jans Original brauchte
        // das nicht: dort raeumt der CSS3D-usedChildren-Mechanismus auf; der
        // geteilte Renderer verwaltet DOM-Overlays manuell.)
        if (layer.type === LayerType.svg && !layer.content.asHtml) {
            const staleOverlay = this.svgOverlays[layer.id];
            if (staleOverlay) staleOverlay.style.display = "none";
        }

        if (!(isGroup || isHtmlLayer || isMapLayer || isInfographicLayer)) {
            return usedChildren;
        }

        // Interactive map layers: drive camera + overlay transforms per frame
        if (isMapLayer) {
            this.renderMapLayer(layer, parent, timer);
            return usedChildren;
        }

        // SVG + Infografik overlay layers: handle visibility/opacity/position directly on the DOM element
        if (isHtmlLayer || isInfographicLayer) {
            const overlay = this.svgOverlays[layer.id];
            if (!overlay) return usedChildren;
            // Selbstheilung: ein Overlay, das aus dem Dokument gefallen ist
            // (Host neu aufgebaut), wieder einhaengen — sonst bliebe es fuer
            // immer unsichtbar, obwohl der Renderer es fuer vorhanden haelt.
            if (!overlay.isConnected && this.cssRendererDom) this.cssRendererDom.appendChild(overlay);

            if (this.hideSentenceBloomSvgForCover(layer.id, timer, layer.from)) {
                overlay.style.display = 'none';
                return usedChildren;
            }

            const isInLifetime = layer.from <= timer && layer.to >= timer;
            // ELTERN-SICHTBARKEIT mitnehmen (Jans Pro-Editor-Fund 12.10.2026,
            // „das Data-Asset in einer Gruppe wird nicht mit ausgeblendet"):
            // Das DOM-Overlay haengt als Geschwister am Host und erbt weder
            // Szenengraph- noch DOM-Sichtbarkeit. `parent` ist das CSS3D-
            // Objekt der Elterngruppe — deren visible setzt der Gruppen-Lauf
            // frueher im selben Frame (der Baum wird in Eltern-vor-Kind-
            // Reihenfolge gecrawlt), inklusive der GROSSELTERN ueber dessen
            // eigene isParentVisible-Kette.
            const elternSichtbar = !parent || parent.visible !== false;
            const visible = (this.visibilityOverrides[layer?.id] ?? layer?.visible ?? true) && isInLifetime && elternSichtbar;

            if (!visible) {
                overlay.style.display = 'none';
                return usedChildren;
            }

            const layer3D = layer.layer3D(timer);
            const parentOpacity = parent ? parent.userData.opacity || 1 : 1;
            const effectiveOpacity = layer3D.opacity! * parentOpacity;

            // Accumulate scale from parent groups (multiplicative)
            const parentScale = parent?.userData.scale || [1, 1, 1];
            const scaleX = layer3D.scale![0] * parentScale[0];
            const scaleY = layer3D.scale![1] * parentScale[1];

            if (effectiveOpacity === 0 || scaleX === 0 || scaleY === 0) {
                overlay.style.display = 'none';
                return usedChildren;
            }

            // Accumulate position from parent groups (stored in userData by group rendering)
            const parentPos = parent?.userData.position || [0, 0];
            // Accumulate rotation from parent groups (additive)
            const parentRotation = parent?.userData.rotation || [0, 0, 0];

            const transforms: string[] = [];
            // Infografiken laufen ebenfalls ueber den WebGL-Paritaets-Transform.
            // Der Legacy-Zweig unten kann nur Position, Skalierung und Z-Drehung
            // — ohne Drehpunkt und ohne raeumliche Drehung. Deshalb liess sich
            // eine Infografik weder kippen noch um ihren Drehpunkt skalieren
            // (Jans Fund). Der Paritaets-Zweig bringt beides mit.
            if (overlay.dataset.peLayerSvg === '1' || isInfographicLayer) {
                // WebGL-Paritaets-Transform fuer Jans Layer-SVGs (Vektor-Fund):
                // - Position: normiert 1.0 == halbe Slide-Breite -> 50% (nicht
                //   100% wie die Legacy-Formel; das Overlay ist containergross).
                // - Z-Tiefe + X/Y/Z-Rotation: echtes CSS-3D mit perspective()
                //   aus der realen Kamera-FOV. Vorzeichen: CSS teilt mit THREE
                //   die +Z-Achse zum Betrachter, aber Y zeigt nach UNTEN —
                //   die Y-Spiegelung invertiert X- und Z-Rotation, Y bleibt.
                //   (Im Original erledigte CSS3DRenderer genau diese Mathematik.)
                // POSITION IN PIXELN, nicht in Prozent (Jans Fund 14.10.2026:
                // Infografiken abseits der Mitte sassen im Querformat nur bei
                // ~70 % ihres Abstands). Prozent beziehen sich auf die EIGENE
                // Breite bzw. Hoehe des containergrossen Overlays — 50 % je
                // Einheit stimmen also nur auf der Achse, an die die Kamera die
                // Slide einpasst (Querformat: Breite), auf der anderen nicht.
                // sceneUnitsToPx() kennt die echte Einpassung (calculateBounds)
                // und gilt fuer beide Achsen gleich — dieselbe Groesse, mit der
                // sizeInfographicCard die Karte selbst bemisst.
                const unitsToPx = this.sceneUnitsToPx();
                const einheitPx = (RendererStore.SLIDE_WIDTH / 2) * unitsToPx;
                // in einer Gruppe: deren Skalierung/Drehung wirkt auch auf die
                // LAGE des Kindes (userData.affin der Gruppe, s. dort)
                const affin = parent?.userData.affin as number[] | undefined;
                const kx = layer3D.position![0];
                const ky = layer3D.position![1];
                const weltX = affin ? affin[0] * kx + affin[2] * ky + affin[4] : kx + parentPos[0];
                const weltY = affin ? affin[1] * kx + affin[3] * ky + affin[5] : ky + parentPos[1];
                const posX = weltX * einheitPx;
                const posY = weltY * -einheitPx;
                const rx = layer3D.rotation![0] + parentRotation[0];
                const ry = layer3D.rotation![1] + parentRotation[1];
                const rz = layer3D.rotation![2] + parentRotation[2];
                // DREHPUNKT-Versatz (Jans Fund "dreht sich um den eigenen
                // Mittelpunkt"): der Anker verschiebt die Rotations-/Skalier-
                // Achse — in WebGL erledigt das die pivotGroup, hier der CSS-
                // transform-origin (wirkt auf rotate UND scale, nicht auf
                // translate — exakt die Original-Semantik). Normiert 1.0 =
                // halbe Slide-Breite = 50% Container; Y invertiert (CSS: unten
                // positiv), Z als Laenge in Container-px. Kleine bekannte
                // Abweichung: perspective() nimmt den origin als Fluchtpunkt.
                const pp = (layer.pivotPoint as [number, number, number]) || [0, 0, 0];
                // Drehpunkt ebenfalls in Pixeln (gleicher Grund wie bei posX/posY)
                overlay.style.transformOrigin = `calc(50% + ${pp[0] * einheitPx}px) calc(50% - ${
                    pp[1] * einheitPx
                }px) ${pp[2] * (RendererStore.SLIDE_DEPTH / 2) * unitsToPx}px`;
                const zPx = layer3D.position![2] * (RendererStore.SLIDE_DEPTH / 2) * unitsToPx;
                const fov = this.camera?.fov ?? 50;
                const perspectivePx =
                    (0.5 * (overlay.clientHeight || 1)) / Math.tan(degreeToRad(fov) / 2);
                if (posX !== 0 || posY !== 0) transforms.push(`translate(${posX}px, ${posY}px)`);
                transforms.push(`perspective(${perspectivePx}px)`);
                if (zPx !== 0) transforms.push(`translateZ(${zPx}px)`);
                if (rx !== 0) transforms.push(`rotateX(${-rx}deg)`);
                if (ry !== 0) transforms.push(`rotateY(${ry}deg)`);
                if (rz !== 0) transforms.push(`rotateZ(${-rz}deg)`);
                if (scaleX !== 1 || scaleY !== 1) transforms.push(`scale(${scaleX}, ${scaleY})`);
            } else {
                // Legacy-Formel der Vollbild-Story-SVGs/Infografiken — unveraendert.
                const posX = (layer3D.position![0] + parentPos[0]) * 100;
                const posY = (layer3D.position![1] + parentPos[1]) * -100; // Y inverted: WebGL up = CSS down
                const rotationZ = layer3D.rotation![2] + parentRotation[2];
                if (posX !== 0 || posY !== 0) {
                    transforms.push(`translate(${posX}%, ${posY}%)`);
                }
                if (scaleX !== 1 || scaleY !== 1) {
                    transforms.push(`scale(${scaleX}, ${scaleY})`);
                }
                if (rotationZ !== 0) {
                    transforms.push(`rotate(${rotationZ}deg)`);
                }
            }

            overlay.style.display = 'block';
            // STAPEL-REIHENFOLGE wie im Ebenen-Stapel (Jans Fund 23.09.2026 am
            // importierten „Heatscore": die Lochmaske lag ueber dem Textkasten,
            // die Schrift war durchloechert und unlesbar). Die CSS3D-Elemente
            // bekommen ihren z-index schon lange aus `order` (= numLayers −
            // layerIndex); die SVG-Overlays hingen ohne z-index im selben
            // Elternknoten und stapelten sich nach EINFUEGE-Reihenfolge, also
            // danach, welches SVG zuerst geladen war. Im Original (CSS3D)
            // ergab sich die Tiefe automatisch aus der Szene.
            overlay.style.zIndex = order.toString();
            overlay.style.opacity = `${effectiveOpacity}`;
            overlay.style.transform = transforms.join(' ');

            // Schlagschatten fuer asHtml-SVG (Jans Vektor-Fund, 1:1 aus Jans
            // Original): echter CSS-drop-shadow-Filter, alle vier Kanaele
            // animierbar (layer3D-Wert waehrend der Kanal laeuft, sonst der
            // statische content-Wert). Offsets/Blur sind % der SVG-Groesse in
            // SLIDE-Einheiten — im Original war Element-px == Welt-Einheit
            // (CSS3D), hier muss auf Container-px umgerechnet werden.
            if (isHtmlLayer) {
                const sc = layer.content as TODOAny;
                const l3d = layer3D as TODOAny;
                // Grundform Linie/Pfeil: Aufbau über den Fortschritt (Jan 22.10.2026)
                if (sc.grundform) grundformAufbau(overlay, (l3d.progress as number | undefined) ?? 1);
                // Ecken „gleichmäßig rund“ / „fester Radius“ für die AKTUELLE
                // Skalierung — auch wenn sie per Keyframes animiert (Jan 26.10.2026)
                if (sc.grundform?.eckenGleich || sc.grundform?.eckenFest) {
                    grundformEcken(overlay, l3d.scale?.[0] ?? 1, l3d.scale?.[1] ?? 1);
                }
                // GRÖSSE IN PIXELN wie die Position (Jan 22.10.2026, Sechseck-
                // Kachelboden: „je nach Preview sind die Sechsecke an
                // unterschiedlichen Positionen“): Die Breite stand als Anteil
                // der CONTAINERBREITE (Breite/4096) — richtig nur, wenn die
                // Slide in die Breite eingepasst ist (Querformat). Hochformat
                // und 1:1 passen in die Höhe ein: die Position (sceneUnitsToPx)
                // stimmte, die Ebene war zu klein. Jetzt dieselbe Einpassung.
                const unitsToPx = this.sceneUnitsToPx();
                if (overlay.dataset.peLayerSvg === '1') {
                    const svgEl = overlay.querySelector('svg') as SVGElement | null;
                    const breite = `${((sc.width || 0) * unitsToPx).toFixed(2)}px`;
                    if (svgEl && sc.width && svgEl.style.width !== breite) svgEl.style.width = breite;
                }
                if (sc.shadowEnabled) {
                    const ref = (Math.max(sc.width || 0, sc.height || 0) || 100) * unitsToPx;
                    const ox = ((l3d.shadowOffsetX ?? sc.shadowOffsetX ?? 2) / 100) * ref;
                    const oy = ((l3d.shadowOffsetY ?? sc.shadowOffsetY ?? 2) / 100) * ref;
                    // Blur ist im Original ein Absolutwert in Welt-Einheiten
                    // (kein %-Anteil) — nur in Container-px umrechnen.
                    const blur = ((l3d.shadowBlur ?? sc.shadowBlur ?? 25) as number) * unitsToPx;
                    const c = sc.shadowColor ?? { r: 0, g: 0, b: 0 };
                    const a = l3d.shadowOpacity ?? sc.shadowOpacity ?? 0.25;
                    overlay.style.filter = `drop-shadow(${ox}px ${oy}px ${blur}px rgba(${c.r},${c.g},${c.b},${a}))`;
                } else if (overlay.style.filter) {
                    overlay.style.filter = '';
                }
            }

            // Infografik-Karte: Schlagschatten + Milchglas-Hintergrund (Jans 1.0,
            // eigene Felder bgShadow/bgBlur — NICHT die shadowEnabled/blur des
            // SVG-Layers). Im Port wurde beides nirgends gesetzt, die Checkboxen
            // blieben wirkungslos (Jans Fund). Auf die KARTE (nicht das
            // containergrosse DIV, sonst deckt die Unschaerfe die ganze Vorschau;
            // und nicht das SVG, das beim Fortschritt staendig neu entsteht).
            if (isInfographicLayer) {
                const ic = layer.content as TODOAny;
                const card = overlay.firstElementChild as HTMLElement | null;
                // Aufbau der KARTEN (Jans 1.0, 1:1): ihr SVG ist statisch, jede
                // Region traegt ein data-ri (0..n-1, von links nach rechts). Der
                // Aufbau laeuft daher ueber die Deckkraft der Regionen, die
                // gestaffelt und ueberlappend erscheinen — statt das ~800 KB
                // grosse SVG pro Schritt neu zu bauen. Ohne diesen Block bauten
                // sich Karten gar nicht auf (Jans Frage nach den anderen Typen).
                if (card && isMapChart(ic.chartType)) {
                    // Staffelung nach der Zahl der FLÄCHEN; eingeblendet wird
                    // alles mit data-ri — auch die Regionsnamen auf der Karte,
                    // die dieselbe Nummer wie ihre Fläche tragen (Jans Fund
                    // 17.10.2026: die Namen standen sofort da).
                    const n = card.querySelectorAll("path[data-ri]").length;
                    const regions = card.querySelectorAll<SVGElement>("[data-ri]");
                    const fade = 0.35; // jede Region blendet ueber 35 % des Aufbaus ein
                    const startSpan = 1 - fade;
                    const p = layer3D.progress ?? 1;
                    regions.forEach((el) => {
                        const ri = Number(el.getAttribute("data-ri")) || 0;
                        const start = n > 1 ? (ri / (n - 1)) * startSpan : 0;
                        const local = fade > 0 ? (p - start) / fade : 1;
                        el.style.opacity = `${Math.max(0, Math.min(1, local)).toFixed(3)}`;
                    });
                    // Titel am Anfang, Quelle am Ende des Aufbaus (Jan
                    // 18.10.2026), wie bei den übrigen Typen (dort die Engine)
                    card.querySelectorAll<SVGElement>("[data-titel]").forEach((el) => {
                        el.style.opacity = titelDeckkraft(p).toFixed(3);
                    });
                    card.querySelectorAll<SVGElement>("[data-quelle]").forEach((el) => {
                        el.style.opacity = quelleDeckkraft(p).toFixed(3);
                    });
                    // Legende (Jan 19.10.2026: „baut nicht mit auf“) — kurz
                    // nach dem Titel, vor den Flächen
                    card.querySelectorAll<SVGElement>("[data-legende]").forEach((el) => {
                        el.style.opacity = textAufbauDeckkraft(p, 0.05, 0.3).toFixed(3);
                    });
                    // ORTE (Jan 22.10.2026): nach den Flächen nacheinander,
                    // von 40 % bis zum Ende des Aufbaus
                    const orte = card.querySelectorAll<SVGElement>("[data-ort]");
                    const m = orte.length;
                    orte.forEach((el) => {
                        const k = Number(el.getAttribute("data-ort")) || 0;
                        const start = 0.4 + (m > 1 ? k / (m - 1) : 0) * 0.45;
                        el.style.opacity = `${Math.max(0, Math.min(1, (p - start) / 0.15)).toFixed(3)}`;
                    });
                }
                if (card?.dataset.peInfoCard === "1") {
                    // Groesse pro Bild nachziehen: beim Formatwechsel aendert
                    // sich der Buehnen-Massstab, ohne dass sich der Inhalt
                    // aendert — updateInfographic wuerde also nicht neu bauen.
                    this.sizeInfographicCard(card, ic as InfographicContent);
                    // Zweischichtig wie der Standard-Schlagschatten der
                    // HTML-Boxen (TextOverlayNormal: 0 8px 32px + 0 2px 8px):
                    // weicher Ambient-Schatten plus knackiger Kontaktschatten.
                    // Eine einzelne weiche Schicht wirkte daneben zu diffus
                    // (Jans Fund "Schlagschatten zu weich").
                    // Weichheit/Stärke einstellbar (bgShadowWeich/-Staerke,
                    // Jan 14.10.2026); ohne Angabe exakt die alten Werte.
                    const shadowCss = ic.bgShadow ? infografikSchattenCss(ic) : "";
                    const blurCss = ic.bgBlur ? "blur(8px)" : "";
                    // Nur bei AENDERUNG schreiben: `backdrop-filter` neu zu setzen
                    // legt beim Compositor eine neue Ebene an — pro Bild waere das
                    // sichtbares Flackern.
                    if (card.style.filter !== shadowCss) card.style.filter = shadowCss;
                    if (card.style.backdropFilter !== blurCss) {
                        card.style.backdropFilter = blurCss;
                        (card.style as TODOAny).webkitBackdropFilter = blurCss;
                    }
                }
            }
            return usedChildren;
        }

        if (isGroup && (!layerMesh || !this.groupsCSS[layer.id])) {
            const element = document.createElement("div");
            layerMesh = new CSS3DObject(element);
            this.layerStateCSS3d[layer.id] = layerMesh;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            this.groupsCSS[layer.id] = layerMesh as any;
            layerMesh.name = layer.name;
        }

        if (!layerMesh) {
            return usedChildren;
        }

        let pivotGroup = this.pivotsCSS[layer.id];

        // add the layerMesh to the scene, but only when it's not a group
        // special handling for object3dchild
        if (!pivotGroup) {
            // first we create a pivot object
            const element = document.createElement("div");
            pivotGroup = new CSS3DObject(element);
            this.pivotsCSS[layer.id] = pivotGroup;
            pivotGroup.add(layerMesh);
            parent.add(pivotGroup);
        }
        if (pivotGroup) {
            usedChildren.push(pivotGroup);
        }

        const isInLifetime = isGroup || (layer.from <= timer && layer.to >= timer);

        const isParentVisible = layerMesh?.parent?.parent?.visible ?? true;

        // TODO: use path
        const visible = (this.visibilityOverrides[layer?.id] ?? layer?.visible ?? true) && isInLifetime && isParentVisible;
        // we need to apply opacity and display directly if we have an innermesh
        (innerMesh || layerMesh).visible = visible;
        (innerMesh || layerMesh).element.style.display = visible ? "block" : "none";

        if (!visible) {
            return usedChildren;
        }

        // Only change attributes when visible
        const layer3D = layer.layer3D(timer);
        const useZeroScale = layer3D.scale!.some((n) => n === 0);
        const opacityZero = layer3D.opacity === 0;

        (innerMesh || layerMesh).element.style.zIndex = order.toString();

        // TODO: combine this with general visibility
        if (useZeroScale || opacityZero) {
            // we need to apply opacity and display directly
            (innerMesh || layerMesh).visible = false;
            (innerMesh || layerMesh).element.style.display = "none";
            return usedChildren;
        }

        const parentOpacity = parent ? parent.userData.opacity || 1 : 1;
        const opacity = layer3D.opacity! * parentOpacity;

        switch (layer.type) {
            case LayerType.group: {
                // save the opacity, position, scale and rotation in the userdata of the group
                // so SVG overlay children can read them
                layerMesh.userData.opacity = layer3D.opacity! * parentOpacity;
                const parentPos = parent?.userData.position || [0, 0];
                layerMesh.userData.position = [
                    layer3D.position![0] + parentPos[0],
                    layer3D.position![1] + parentPos[1],
                ];
                // Accumulate scale (multiplicative) for SVG overlay children
                const parentScale = parent?.userData.scale || [1, 1, 1];
                layerMesh.userData.scale = [
                    layer3D.scale![0] * parentScale[0],
                    layer3D.scale![1] * parentScale[1],
                    layer3D.scale![2] * parentScale[2],
                ];
                // Accumulate rotation (additive) for SVG overlay children
                const parentRotation = parent?.userData.rotation || [0, 0, 0];
                layerMesh.userData.rotation = [
                    layer3D.rotation![0] + parentRotation[0],
                    layer3D.rotation![1] + parentRotation[1],
                    layer3D.rotation![2] + parentRotation[2],
                ];
                // GRUPPE ALS TRANSFORMATION (Jan 22.10.2026: Pentagramme in
                // einer Gruppe, Gruppe vergrößert — die Sterne wuchsen, ihre
                // Abstände nicht). Die Summe der Positionen oben reicht nur ohne
                // Skalierung/Drehung. Wie der Szenengraph in 3D: Kind-Lage =
                // Gruppe + Drehpunkt + Drehung(Z) · Skalierung · (Kind − Drehpunkt),
                // verschachtelt über die Eltern.
                {
                    const pv = (layer.pivotPoint as number[] | undefined) || [0, 0, 0];
                    const w = degreeToRad(layer3D.rotation![2]);
                    const cos = Math.cos(w);
                    const sin = Math.sin(w);
                    const sx = layer3D.scale![0];
                    const sy = layer3D.scale![1];
                    // eigene Abbildung: x' = M·x + t (M = R·S)
                    const m = [cos * sx, sin * sx, -sin * sy, cos * sy];
                    const t = [
                        layer3D.position![0] + pv[0] - (m[0] * pv[0] + m[2] * pv[1]),
                        layer3D.position![1] + pv[1] - (m[1] * pv[0] + m[3] * pv[1]),
                    ];
                    const e = parent?.userData.affin as number[] | undefined;
                    layerMesh.userData.affin = e
                        ? [
                              e[0] * m[0] + e[2] * m[1],
                              e[1] * m[0] + e[3] * m[1],
                              e[0] * m[2] + e[2] * m[3],
                              e[1] * m[2] + e[3] * m[3],
                              e[0] * t[0] + e[2] * t[1] + e[4],
                              e[1] * t[0] + e[3] * t[1] + e[5],
                          ]
                        : [m[0], m[1], m[2], m[3], t[0], t[1]];
                }
                break;
            }
            case LayerType.svg:
                layerMesh.element.style.opacity = `${opacity}`;
                break;
            case LayerType.text:
                innerMesh.element.style.opacity = `${opacity}`;
                break;
            default:
        }

        layerMesh.name = layer.name;

        const usePivot = true;
        const zPosition = layer3D.position![2];

        // layers with a custom z position should be rendered first and compared among themselves
        if (zPosition > 0) {
            (innerMesh || layerMesh).element.style.zIndex = "100";
        }

        const newPosition = new THREE.Vector3(
            layer3D.position![0] + layer.pivotPoint[0],
            layer3D.position![1] + layer.pivotPoint[1],
            zPosition + layer.pivotPoint[2]
        ).multiply(maxVector);

        const relativePivotOffset = new THREE.Vector3(
            -layer.pivotPoint[0],
            -layer.pivotPoint[1],
            -layer.pivotPoint[2]
        ).multiply(maxVector);
        // make sure we don't scale with zero
        let scaleVector = new THREE.Vector3(...(useZeroScale ? MIN_SCALE : layer3D.scale!));

        const textContent = layer.content as TextContent;
        if (layer.type === LayerType.text) {
            const scaleFactor = 1 + ((textContent.responsiveScaling || 0) / 100) * (responsiveScaleFactor - 1);
            scaleVector = scaleVector.multiplyScalar(scaleFactor);
        }

        const toRad = layer3D.rotation!.map(degreeToRad) as Vector3Tuple;

        if (usePivot) {
            const pivotCircle = this.pivotsCircles[layer.id];
            if (pivotCircle) {
                pivotCircle.visible = isActive && layer.showPivotPoint;
                pivotCircle.position.setZ(zPosition);
            }
            // Waehrend der Anfasser GENAU DIESE Pivot-Gruppe zieht, gehoert
            // ihre Position dem Gizmo (Jans Fund 02.08.2026, "Kugel laesst
            // sich nicht ziehen"): der Frame schrieb sonst die — dem MST um
            // einige Frames NACHLAUFENDE — Animations-Position zurueck, der
            // Drag konnte nie Bewegung ansammeln und der Write-back las die
            // zurueckgeschnappte Position (Netto: nichts). Betraf Ebenen mit
            // AKTIVEM Positions-Kanal (Keyframes); statische wirkten nur
            // leicht zittrig. Reader: isDraggingGizmo bleibt immer false.
            // Dasselbe fuer die Rotation: im Dreh-Modus (gizmoMode "rotate")
            // gehoert sie waehrend des Drags dem Gizmo, sonst schnappte sie
            // jeden Frame auf den MST-Wert zurueck (identisches Tauziehen).
            const gizmoZiehtPivot = this.isDraggingGizmo && this.transformControls?.object === pivotGroup;
            if (!gizmoZiehtPivot) {
                pivotGroup.position.copy(newPosition);
                pivotGroup.rotation.set(...toRad);
            }
            pivotGroup.scale.copy(scaleVector);
            layerMesh.position.copy(relativePivotOffset);
            // layerMesh.scale.set(1, 1, 1);
            layerMesh.rotation.set(0, 0, 0);
        } else {
            if (pivotGroup) {
                pivotGroup.position.set(0, 0, 0);
                pivotGroup.scale.set(1, 1, 1);
                pivotGroup.rotation.set(0, 0, 0);
                const pivotCircle = this.pivotsCircles[layer.id];
                if (pivotCircle) {
                    pivotCircle.visible = isActive && layer.showPivotPoint;
                }
            }
            layerMesh.position.copy(newPosition);
            layerMesh.scale.copy(scaleVector);
            layerMesh.rotation.set(...toRad);
        }

        return usedChildren;
    };

    // Port 1 (Choropleth/Infografik): erzeugt das SVG synchron via
    // buildInfographicSvg und rendert es als DOM-Overlay — analog zu
    // updateHTMLSvg (asHtml-SVG), nur ohne fetch. Positioniert wird das
    // Overlay in renderLayerHTML (gleiche svgOverlays-Map wie asHtml-SVG).
    /**
     * Massstab einer Layer-Einheit in Container-Pixeln auf der Ebene z = 0 —
     * derselbe, mit dem die WebGL-Ebenen gezeichnet werden.
     *
     * Wird gebraucht, weil eine Infografik im HTML-Modus als DOM-Overlay lebt
     * und ihre Groesse selbst ausrechnen muss. Der naheliegende Weg (Anteil an
     * der Buehnenbreite) ist FALSCH: die Kamera hat einen festen SENKRECHTEN
     * Blickwinkel, also bleibt beim Formatwechsel die sichtbare Hoehe gleich und
     * nur die Breite beschneidet — 3D-Ebenen behalten dabei ihre Pixelgroesse.
     * Am Anteil der Breite aufgehaengt schrumpfte die Infografik im Hochformat
     * mit (Jans Fund). Nur bei 16:9 fielen beide Rechenwege zufaellig zusammen.
     *
     * Massgebend ist die Anpassungsregel der Buehne selbst (calculateBounds +
     * der scaleFactor darunter): im QUERFORMAT wird die Slide-BREITE eingepasst,
     * im HOCHFORMAT die Slide-HOEHE. Genau diese Fallunterscheidung fehlte —
     * fest an der Breite gerechnet schrumpfte die Infografik im Hochformat mit,
     * waehrend die 3D-Ebenen daneben ihre Groesse behielten.
     */
    sceneUnitsToPx(): number {
        const store = this.props.renderer as TODOAny;
        const fallback = (this.cssRendererDom?.clientWidth || 1) / RendererStore.SLIDE_WIDTH;
        if (typeof store?.calculateBounds !== "function") return fallback;
        const { bounds, useMaxWidth } = store.calculateBounds();
        const f = useMaxWidth
            ? bounds.newWidth / RendererStore.SLIDE_WIDTH
            : bounds.newHeight / RendererStore.SLIDE_HEIGHT;
        return f > 0 ? f : fallback;
    }

    /**
     * "Ebene geladen" melden — aber nur EINMAL.
     *
     * Eine Infografik baut sich oft neu auf (Fortschritt, Inhaltsaenderung,
     * Moduswechsel) und meldete bisher jedes Mal erneut fertig. Der Store wehrt
     * das korrekt ab, protokolliert dabei aber jedes Mal einen Fehler
     * ("Tried to insert duplicate into loadedLayers") — bei einer laufenden
     * Aufbau-Animation hunderte Male, was die Konsole zumuellt und echte
     * Meldungen untergehen laesst (Jans Fund). Jans 1.0 hatte genau diese
     * Pruefung vor dem Aufruf; im Port fehlte sie.
     */
    markLayerLoaded(layerId: string, url: string) {
        const store = this.props.renderer as TODOAny;
        if (store?.loadedLayers?.includes?.(layerId)) return;
        store?.loadedLayer?.(layerId, url);
    }

    /**
     * Karte auf ihre echten Masse bringen. Schreibt nur bei Aenderung: jeder
     * Style-Schreibvorgang auf einem Element mit `backdrop-filter` laesst den
     * Compositor die Ebene neu aufbauen — pro Bild waere das sichtbares Flackern.
     */
    sizeInfographicCard(card: HTMLElement, content: InfographicContent) {
        const { width: pxW, height: pxH } = infographicPixelSize(content);
        const f = this.sceneUnitsToPx();
        const w = `${(pxW * f).toFixed(2)}px`;
        const h = `${(pxH * f).toFixed(2)}px`;
        if (card.style.width !== w) card.style.width = w;
        if (card.style.height !== h) card.style.height = h;
    }

    updateInfographic(layerId: string, content: InfographicContent, progress?: number) {
        const existingOverlay = this.svgOverlays[layerId];
        // Reveal-Kanal: undefined (Layer ohne progress-Gruppe) = fertig aufgebaut.
        // PROGRESS-SCHALTER (Jans T+-Wunsch 13.10.2026): progressAus im CONTENT
        // friert das Chart im fertigen Zustand ein — im Content, damit der
        // Zustand den Scene-Neubau ueberlebt (Carry-over) und in beiden
        // Editoren gilt.
        const p = content.progressAus
            ? 1
            : typeof progress === "number"
              ? Math.min(Math.max(progress, 0), 1)
              : 1;
        // Quantisieren: progress ändert sich beim Scrollen pro Frame. Ohne
        // Rasterung würde das ~113 KB grosse Choropleth-SVG jeden Frame neu
        // gebaut. 100 Stufen sind visuell stufenlos, begrenzen aber die
        // Neuaufbauten über den gesamten Reveal auf max. 100.
        const pq = Math.round(p * 100) / 100;
        // KARTEN sind vom Fortschritt unabhaengig (mapSvg ignoriert ihn) und mit
        // ~800 KB das mit Abstand schwerste SVG. Waere der Fortschritt Teil der
        // Signatur, wuerde beim Scrollen bis zu 100x dieselbe Karte neu gebaut —
        // fuer nichts. Ihr Aufbau laeuft stattdessen unten pro Bild ueber die
        // Deckkraft der einzelnen Regionen (1:1 aus Jans 1.0).
        const isMap = isMapChart(content.chartType);
        let sig = "";
        try {
            sig = isMap ? `map|${JSON.stringify(content)}` : `${pq}|${JSON.stringify(content)}`;
        } catch {
            sig = `${Date.now()}`;
        }
        const isDirty = !existingOverlay || this.infographicSig[layerId] !== sig;
        if (!isDirty) return;
        if (!infografikBauErlaubt(this, layerId)) return;
        // Wortwolke: erst bauen, wenn ihre Schriften geladen sind (die Engine
        // misst die Wörter) — danach selbst neu bauen: der Editor zeichnet nur
        // bei Änderungen, ein nächster Frame käme nicht von allein.
        if (!wortwolkenSchriftenBereit(content)) {
            if (!this.wolkeWartet[layerId]) {
                this.wolkeWartet[layerId] = true;
                void ladeWortwolkenSchriften(content).then(() => {
                    this.wolkeWartet[layerId] = false;
                    try {
                        if (this.cssRendererDom) this.updateInfographic(layerId, content, progress);
                    } catch {
                        // Renderer inzwischen abgebaut — nichts zu tun
                    }
                });
            }
            return;
        }

        let svg = "";
        try {
            svg = buildInfographicSvg(content, pq);
        } catch (error) {
            // Signatur NICHT speichern und die bestehende Karte stehen lassen:
            // vorher blieb nach einem Fehlbau eine leere Karte, bis sich der
            // Inhalt aenderte (Jans Welcome-Scrolly, 09.09.2026). Jetzt kommt
            // nach der Wartezeit der naechste Versuch.
            meldeInfografikBauFehler(this, layerId, content, sig, "HTML", error);
            return;
        }
        // Null-sicher: Test-Hosts (infographic-wiring.test) haben die Karte nicht.
        if (this.infographicRetryAt) delete this.infographicRetryAt[layerId];

        const isNew = !this.svgOverlays[layerId];
        const element = this.svgOverlays[layerId] || (document.createElement("div") as HTMLDivElement);
        if (isNew) {
            element.style.position = "absolute";
            element.style.top = "0";
            element.style.left = "0";
            element.style.width = "100%";
            element.style.height = "100%";
            element.style.pointerEvents = "none";
            element.style.transformOrigin = "center center";
            element.style.display = "none"; // bis renderLayerHTML sie zeigt
        }

        // Aufbau in DREI Ebenen (entspricht Jans 1.0, wo die KARTE das
        // CSS3D-Element war):
        //   element  containergross, traegt den Layer-Transform (die Rechnung in
        //            renderLayerHTML setzt Containergroesse voraus)
        //   card     die Karte in ihren echten Massen, mittig
        //   svg      fuellt die Karte
        // Die Karte ueberlebt jeden Neuaufbau der Grafik. Das ist entscheidend:
        // beim Fortschritt wird das SVG bis zu 100x neu gebaut, und Schatten/
        // Milchglas haengen an der Karte — lagen sie am SVG, wuerde der
        // Compositor sie bei JEDEM Schritt neu anlegen (Jans Fund "die ganze
        // Grafik flackert beim Aufbau").
        let card = element.firstElementChild as HTMLDivElement | null;
        if (!card || card.dataset.peInfoCard !== "1") {
            element.innerHTML = "";
            card = document.createElement("div");
            card.dataset.peInfoCard = "1";
            element.appendChild(card);
        }

        // FESTE Groesse statt formatfuellend: die Karte bekommt ihre echten
        // Masse im Massstab der Buehne (siehe sceneUnitsToPx). Vorher wurde jede
        // Infografik auf 100 % der Vorschau gestreckt — sie wirkte viel zu gross
        // und die Regler fuer Breite/Hoehe blieben wirkungslos (Jans Fund).
        // Mittig ueber die eigene Groesse, damit kein Prozentwert am Container
        // haengt.
        if (isNew || card.style.position !== "absolute") {
            card.style.position = "absolute";
            card.style.left = "50%";
            card.style.top = "50%";
            card.style.transform = "translate(-50%, -50%)";
        }
        this.sizeInfographicCard(card, content);

        // GEFILTERT wie die SVG-Ebenen (03./04.10.2026): buildInfographicSvg
        // maskiert nur Texte, Farben und Schriftname aus dem Scrolly-JSON
        // stehen roh in Attributen (`fill="${textColor}"`). Ein Wert wie
        // `"><img onerror=…>` liefe sonst als Code in unserer Seite.
        card.innerHTML = filtereSvg(svg);
        const svgEl = card.querySelector("svg");
        if (svgEl) {
            svgEl.setAttribute("width", "100%");
            svgEl.setAttribute("height", "100%");
            svgEl.style.width = "100%";
            svgEl.style.height = "100%";
            svgEl.style.display = "block";
        }

        if (!this.svgOverlays[layerId]) {
            this.cssRendererDom.appendChild(element);
        }
        this.svgOverlays[layerId] = element;
        this.infographicSig[layerId] = sig;
        if (this.props.isCoverActive?.()) {
            element.style.display = "none";
        }
        this.markLayerLoaded(layerId, `infographic:${layerId}`);
    }

    /**
     * Port "Mirrors": Flags + Parameter einer 3D-Ebene aus ihrem Content
     * ableiten. Teil A: realtimeReflections → CubeCamera-Rig; Teil B:
     * planarMirror (nur `plane`) → Reflector. Wird je Bild in updateLayer
     * gerufen — und VORAB im Aufwaerm-Bild, damit Rigs, Spiegel und die
     * envMap-Programmvarianten nicht erst beim Eintritt entstehen.
     */
    private spiegelFlagsSetzen(layerId: string, oc: Object3DContent) {
        this.reflectiveLayers[layerId] = !!oc.realtimeReflections;
        const isMirror = !!(oc.planarMirror && oc.primitive === "plane");
        this.mirrorLayers[layerId] = isMirror;
        if (isMirror) {
            this.mirrorParams[layerId] = {
                color: oc.mirrorColor,
                // Grundfarbe (mix-Ziel bei strength < 1) = Material-Farbe der Ebene.
                mixColor: (oc.material?.options as TODOAny)?.color,
                blur: oc.mirrorBlur ?? 0,
                strength: oc.mirrorStrength ?? 1,
                // Auslauf: fehlt das Feld (Bestand), gilt die Vorgabe (s. spiegelAuslaufLaenge)
                fade: (oc as TODOAny).mirrorFade,
                // Blickwinkel (Fresnel): fehlt das Feld, gilt 1 (Jans „Variante 3")
                fresnel: (oc as TODOAny).mirrorFresnel,
            };
        }
    }

    updateLayer(layer: LayerRender, timer: number) {
        // Instanz-Schalter, nicht das globale Feld (s. alleEbenenRegistriert).
        if (!this.alleEbenenRegistriert) return;
        switch (layer.type) {
            case LayerType.object3d: {
                // Port "Mirrors": Opt-in-Flags pro Frame lesen (Teil A Cube-
                // Reflexionen, Teil B planarer Spiegel) — dieselbe Ableitung
                // nutzt das Aufwaerm-Bild (spiegelFlagsSetzen).
                this.spiegelFlagsSetzen(layer.id, layer.content as Object3DContent);
                this.updateObject(layer.id, layer.content, layer.setObject!);
                break;
            }
            case LayerType.object3dChild:
                this.updateMaterial(layer.id, layer.content);
                break;
            case LayerType.light:
                this.updateLight(layer.id, layer.content);
                break;
            case LayerType.text:
                this.updateText(layer.id, layer.content);
                break;
            case LayerType.image:
                this.updateImage(layer.id, layer.content);
                break;
            case LayerType.video:
                this.updateVideo(layer.id, layer.content, false, layer.from);
                break;
            case LayerType.lottie:
                this.updateLottie(layer.id, layer.content as LottieContent);
                break;
            case LayerType.svg:
                if (layer.content.asHtml) {
                    this.updateHTMLSvg(layer.id, layer.content as SvgContent, timer);
                } else {
                    this.updateSvg(layer.id, layer.content);
                }
                break;
            case LayerType.map:
                if (istKarteImRaum(layer.content)) {
                    this.updateKarteImRaum(layer.id, layer.content as MapContent);
                } else {
                    this.updateMap(layer.id, layer.content as MapContent);
                }
                break;
            case LayerType.infographic: {
                // Zwei Wege (Jans 1.0): HTML-Overlay oder 3D-Flaeche. Die
                // Checkbox "Als HTML rendern" entscheidet; nur so sortiert sich
                // eine Infografik mit 3D-Objekten in die Tiefe ein.
                const ic = layer.content as unknown as InfographicContent;
                if (infographicIsHtml(ic)) {
                    // Die 3D-Flaeche raeumt der Baum-Aufraeumer am Ende von
                    // animate ab (gleiche Stelle wie beim SVG-Moduswechsel).
                    this.updateInfographic(layer.id, ic, layer.layer3D(timer).progress);
                } else {
                    // umgekehrt: Overlay des HTML-Modus entfernen
                    const staleOverlay = this.svgOverlays[layer.id];
                    if (staleOverlay) {
                        staleOverlay.parentElement?.removeChild(staleOverlay);
                        delete this.svgOverlays[layer.id];
                        delete this.infographicSig[layer.id];
                    }
                    // Massstab mitgeben: der Schlagschatten wird in die Textur
                    // eingebacken und muss so gross ausfallen wie im HTML-Modus.
                    updateInfographicMesh(this as unknown as TODOAny, layer.id, ic, this.sceneUnitsToPx());
                }
                break;
            }
            case LayerType.effect:
                updateEffect(
                    this,
                    layer.id,
                    layer.content as EffectContent,
                    // Ist der Count-Kanal animiert, gleich am Maximum allokieren,
                    // damit pro Frame eine Teilmenge gezeichnet werden kann
                    // (kein Neuaufbau -> kein Flackern).
                    layer.animationsGroups?.[AnimationType.effectCount]?.active
                        ? EFFECT_COUNT_MAX
                        : undefined,
                );
                break;
            default:
        }
    }

    /**
     * Liegt dieses 3D-Objekt raeumlich HINTER einer sichtbaren Flaeche (Bild,
     * Video, SVG …), die in der Ebenen-Folge FRUEHER kommt? Dann wuerde es als
     * opakes Objekt (Opak-Pass, zuerst gezeichnet) von dieser Flaeche uebermalt
     * — Flaechen schreiben keine Tiefe und werden danach in Ebenen-Folge
     * gezeichnet; liegt eine naeher an der Kamera, besteht sie den Tiefentest
     * ueber dem Objekt (Jans Fund 17.09.2026, Alt-Import „Erzliane": Erdkugel
     * bei Deckkraft 100 WEG, bei 99 da; UFO raeumlich vorn — nie betroffen).
     * Gemessen: Planeten z = -1253 / -3472, opak, renderOrder 0.
     */
    private liegtHinterFruehererFlaeche(objekt: THREE.Object3D, order: number): boolean {
        const kamera = this.camera;
        if (!kamera || !(order > 0)) return false;
        const pos = this._tmpVecA ?? (this._tmpVecA = new THREE.Vector3());
        const pos2 = this._tmpVecB ?? (this._tmpVecB = new THREE.Vector3());
        const abstandObjekt = kamera.position.distanceTo(objekt.getWorldPosition(pos));
        for (const mesh of Object.values(this.layerMeshes) as THREE.Object3D[]) {
            if (!mesh || mesh === objekt || !mesh.visible || !mesh.parent) continue;
            const ro = mesh.renderOrder;
            if (!(ro > 0) || ro >= order) continue; // nur FRUEHERE Ebenen
            const mat = (mesh as THREE.Mesh).material as THREE.Material | THREE.Material[] | undefined;
            const m0 = Array.isArray(mat) ? mat[0] : mat;
            // Nur Flaechen, die im Transparent-Pass ohne Tiefenschreiben laufen
            // und wirklich etwas zeigen.
            if (!m0 || m0.depthWrite !== false || !(m0.opacity > 0.02)) continue;
            if (kamera.position.distanceTo(mesh.getWorldPosition(pos2)) < abstandObjekt) return true;
        }
        return false;
    }
    private _tmpVecA: THREE.Vector3 | undefined;
    private _tmpVecB: THREE.Vector3 | undefined;

    invalidateTree() {
        // NIE über das LIVE mutierende children-Array iterieren: remove()
        // splict das Array, die for..of-Schleife übersprang dadurch jedes
        // ZWEITE Kind — der Baum-Neuaufbau startete mit Halb-Leichen, die
        // danach für immer in der Szene hingen (Jans Fund 13.08.2026: Ebene
        // einer Glaskugel gelöscht, schwarze Fläche bleibt stehen).
        // clear() = remove(...children) arbeitet auf einem Schnappschuss.
        // Bewusst OHNE dispose: die Meshes leben in layerMeshes weiter und
        // werden beim Neuaufbau wieder angehängt.
        // VOR DEM ERSTEN BILD gibt es noch keine Wurzel (sie entsteht im
        // Render-Loop) — dann ist auch nichts abzuhängen (Jans Fund
        // 14.10.2026: der einmalige Scenes-Neubau beim Öffnen rief das über
        // den Live-Edit-Effekt der Vorschau so früh auf, der Editor stürzte
        // mit „reading 'clear'“ ab).
        this.groupRoot?.clear();
        this.groups = {};
        this.pivots = {};
    }

    // Port "GLOBAL-Tab" (Jans Original, 1:1): Helper immer obenauf zeichnen,
    // damit Szenen-Geometrie sie beim Orbiten nicht verdeckt.
    makeHelperAlwaysOnTop(obj: THREE.Object3D | null | undefined) {
        if (!obj) return;
        obj.renderOrder = 999;
        obj.traverse((child: TODOAny) => {
            child.renderOrder = 999;
            const mat = child.material;
            if (!mat) return;
            const mats = Array.isArray(mat) ? mat : [mat];
            mats.forEach((m: TODOAny) => {
                m.depthTest = false;
                m.depthWrite = false;
            });
        });
    }

    // Kleines Kamera-Gizmo (Gehaeuse + Objektiv + zwei Filmrollen) aus unlit
    // Basic-Meshes, in Unit-Space gebaut (~2 Einheiten lang); das Objektiv zeigt
    // nach -Z, sodass Object3D.lookAt es aufs Kamera-Ziel richtet. (Jans Original)
    buildCameraBody(): THREE.Group {
        const group = new THREE.Group();
        // Helle Grautoene, damit das Gizmo auf dem schwarzen Orbit-Hintergrund lesbar ist
        const bodyMat = new THREE.MeshBasicMaterial({ color: 0x9a9aa2 });
        const lensMat = new THREE.MeshBasicMaterial({ color: 0xd0d0d8 });

        const housing = new THREE.Mesh(new THREE.BoxGeometry(1.4, 1.0, 1.8), bodyMat);
        group.add(housing);

        const lens = new THREE.Mesh(new THREE.CylinderGeometry(0.5, 0.36, 0.7, 24), lensMat);
        lens.rotation.x = Math.PI / 2;
        lens.position.set(0, 0, -1.2);
        group.add(lens);

        const reelGeo = new THREE.CylinderGeometry(0.5, 0.5, 0.16, 24);
        const reelFront = new THREE.Mesh(reelGeo, bodyMat);
        reelFront.rotation.z = Math.PI / 2;
        reelFront.position.set(0, 0.78, -0.35);
        group.add(reelFront);
        const reelBack = new THREE.Mesh(reelGeo, bodyMat);
        reelBack.rotation.z = Math.PI / 2;
        reelBack.position.set(0, 0.78, 0.45);
        group.add(reelBack);

        return group;
    }

    // Frustum-Drahtgitter der Presentation-Kamera + Kamera-Gizmo, sichtbar bei
    // rendererStore.showCamera (GLOBAL-Tab). (Jans Original, 1:1 portiert)
    syncCameraHelper(rendererStore: TODOAny) {
        if (!rendererStore.showCamera) {
            if (this.cameraHelper) this.cameraHelper.visible = false;
            if (this.cameraBody) this.cameraBody.visible = false;
            return;
        }
        if (!this.camera || !this.webGlScene) return;
        if (!rendererStore.cameraLookAt || !rendererStore.cameraPosition) return;
        const lookAt = new THREE.Vector3(...(rendererStore.cameraLookAt as [number, number, number]));
        const pos = new THREE.Vector3(...(rendererStore.cameraPosition as [number, number, number]));
        if (!this.presentationCamera) {
            this.presentationCamera = new THREE.PerspectiveCamera(rendererStore.fov, this.camera.aspect, 1, 1);
        }
        const cam = this.presentationCamera;
        cam.position.copy(pos);
        cam.lookAt(lookAt);
        cam.fov = rendererStore.fov;
        cam.aspect = this.camera.aspect;
        // Moderates far, damit das Frustum eine saubere Box um die Szene ist
        // und kein riesiger Splitter bis zur echten Far-Plane
        cam.far = Math.max(2, pos.distanceTo(lookAt) * 1.6);
        cam.updateProjectionMatrix();
        if (!this.cameraHelper) {
            this.cameraHelper = new THREE.CameraHelper(cam);
            this.makeHelperAlwaysOnTop(this.cameraHelper);
            this.webGlScene.add(this.cameraHelper);
        }
        this.cameraHelper.update();
        this.cameraHelper.visible = true;

        if (!this.cameraBody) {
            this.cameraBody = this.buildCameraBody();
            this.makeHelperAlwaysOnTop(this.cameraBody);
            this.webGlScene.add(this.cameraBody);
        }
        this.cameraBody.position.copy(pos);
        this.cameraBody.lookAt(lookAt);
        const bodyScale = Math.max(1, pos.distanceTo(lookAt) * 0.05);
        this.cameraBody.scale.setScalar(bodyScale);
        this.cameraBody.visible = true;
    }

    invalidateCSSTree() {
        // destroy complete scene with fire
        //this.cssRendererDom.removeChild(this.css3DRenderer.domElement);
        //this.css3DRenderer = new CSS3DRenderer();
        //this.css3DRenderer.setSize(_rendererStore.width, _rendererStore.height);
        //this.cssRendererDom.appendChild(this.css3DRenderer.domElement);
        const element = document.createElement("div");
        this.groupRootCSS = new CSS3DObject(element);
        // this.css3DScene = new THREE.Scene();
        // this.css3DScene.add(this.groupRootCSS);
        // do not trash the content cache
        // this.layerContentCache = {};
        this.layerStateCSS3d = {};
        this.pivotsCSS = {};
        this.groupsCSS = {};
        // Remove SVG overlay DOM elements
        for (const overlay of Object.values(this.svgOverlays)) {
            overlay.remove();
        }
        this.svgOverlays = {};
        this.svgHtmlFehlgeschlagen = {};

        this.kartenAbbauen();
    }

    /**
     * Versteckte MapLibre-Karten abbauen (Karte im Raum + interaktive Karten).
     * Jede hat ein EIGENES WebGL-Canvas am <body>. Auch beim dispose() des
     * Renderers (Jans iPhone-Fund 22.10.2026, im Simulator gemessen: nach
     * wenigen Bearbeitungen 8 Karten derselben Ebene am <body>, „Unexpected
     * loss of WebGL context“, Absturz beim Wechsel Vorschau → Bearbeiten) —
     * vorher räumte nur invalidateCSSTree sie ab.
     */
    private kartenAbbauen() {
        for (const karte of Object.values(this.kartenImRaum)) karte.entferne();
        this.kartenImRaum = {};
        // Remove interactive map instances and their DOM containers
        for (const [layerId, map] of Object.entries(this.mapInstances)) {
            try { map.remove?.(); } catch { /* swallow */ }
            // Clean up marker refs
            const markers = (this as any)[`${layerId}_markers`] as any[] | undefined;
            if (markers) markers.forEach((m: any) => m.remove?.());
            delete (this as any)[`${layerId}_markers`];
            delete (this as any)[`${layerId}_markers_hash`];
        }
        for (const container of Object.values(this.mapContainers)) {
            container.remove();
        }
        this.mapInstances = {};
        this.mapContainers = {};
    }

    /**
     * Cutover Schritt 2: Editor-Chrome (Safe-Area-Overlay + Helfer-Grid) pro
     * Frame syncen. Getrieben vom optionalen editorStore — ohne ihn (Reader)
     * bleibt alles unsichtbar/ungebaut. Chrome wird LAZY erzeugt, sobald ein
     * Editor sie einblenden will, unabhaengig von isProduction.
     */
    syncEditorChrome() {
        const es = this.props.editorStore;
        // EDITOR: Canvas deckend schwarz wie in Jans Original (setClearColor
        // alpha 1). Der geteilte Viewer clear't bewusst TRANSPARENT (alpha 0),
        // damit die weisse Lese-Seite als Hintergrund wirkt — im Editor blitzte
        // dadurch das Weiss der Viewer-Wrapper an Sub-Pixel-Fugen und
        // Antialiasing-Kanten durch (duenne weisse "Blitzlinien" oben/rechts),
        // und bewegte Kanten funkelten beim Abspielen (Grid-"Flackern").
        // Nur bei vorhandenem editorStore — der Viewer bleibt transparent.
        //
        // GRUNDFARBE DER SZENE (Jan 19.09.2026): deckend bleibt es (Fugen-
        // Schutz oben), aber nicht mehr fest SCHWARZ — sondern in der Farbe,
        // die der Reader hinter den Ebenen zeigt. Sonst sieht eine
        // halbtransparente unterste Ebene im Editor anders aus als
        // veroeffentlicht (Jans Hintergrundbild mit 58 % Deckkraft).
        if (es && this.webGLRenderer) {
            const grund = resolveSzenenGrundfarbe(this.props.presentation?.presentationOptions);
            this.webGLRenderer.setClearColor(grund.hex, 1);
        }
        // Safe-Area-Quadrat — wie das Grid LAZY erzeugen, sobald ein Editor da
        // ist. Vorher entstand es nur im allerersten animate()-Frame und nur bei
        // SHOW_HELPERS (= !isProduction) — im geteilten Viewer ist das dauerhaft
        // false, und der editorStore wird ohnehin erst NACH dem ersten Paint
        // injiziert. Folge: trotz aktivem "Sicherer Bereich"-Haken erschien nie
        // ein gruenes Quadrat (Jans Fund #1). Geometrie/Material 1:1 aus dem
        // Erst-Frame-Block uebernommen.
        if (es && !this.safeSquare && this.webGlScene) {
            if (!this.uiRoot) {
                this.uiRoot = new THREE.Group();
                this.webGlScene.add(this.uiRoot);
            }
            const safeSquareGeometry = new THREE.PlaneGeometry(1, 1, 1);
            const safeSquareMaterial = new THREE.MeshBasicMaterial({
                color: 0x00ff00,
                opacity: 0.3,
                transparent: true,
                wireframe: true,
            });
            this.safeSquare = new THREE.Mesh(safeSquareGeometry, safeSquareMaterial);
            this.uiRoot.add(this.safeSquare);
        }
        if (this.safeSquare) {
            this.safeSquare.renderOrder = 9999;
            // AUSBLENDEN statt Deckkraft 0 (Jans Fund 22.09.2026: „die Safe Area
            // wird gespiegelt, obwohl sie aus ist"). Unsichtbar per Deckkraft
            // zeichnete der Rahmen weiter in die TIEFE — der Spiegel-Auslauf liest
            // genau die und blendete entlang der Linie ab: ein dunkler Strich
            // quer durch die Spiegelung.
            (this.safeSquare.material as THREE.MeshBasicMaterial).opacity = 1;
            this.safeSquare.visible = !!es?.showSafeArea;
        }
        // Grid — lazy erzeugen, sobald ein Editor es einblenden will.
        if (es?.showGrid && !this.grid && this.webGlScene) {
            if (!this.uiRoot) {
                this.uiRoot = new THREE.Group();
                this.webGlScene.add(this.uiRoot);
            }
            this.grid = this.buildEditorGrid();
            this.uiRoot.add(this.grid);
        }
        if (this.grid) this.grid.visible = !!es?.showGrid;
        // Schritt 2b: Transform-Gizmo an die Auswahl heften.
        this.syncGizmo();
    }

    /**
     * 1024er-Helfer-Grid ueber die 4096×4096-Slide (GRID-Toggle). Faithful aus
     * Jans Renderer: mid-grey Linien, depthTest aus, hoher renderOrder.
     */
    private buildEditorGrid(): THREE.LineSegments {
        const positions: number[] = [];
        const halfW = 4096 / 2; // SLIDE_WIDTH
        const halfH = 4096 / 2; // SLIDE_HEIGHT
        const STEP = 1024;
        // NUR die inneren Linien zeichnen (±1024, 0) — Jans Original zeichnete
        // auch die Randlinien (±2048), die dort aber EXAKT auf der Schnittkante
        // des Frames lagen und damit unsichtbar waren. Im Port liegt die Kante
        // durch Ganzzahl-Rundung der Vorschau-Groesse mal einen Sub-Pixel
        // daneben → die Randlinie ragte als duenne weisse "Blitzlinie" rechts
        // ins Bild (Jans Fund). Ohne Randlinien: visuell identisch zum
        // Original, robust gegen Rundung.
        for (let x = -halfW + STEP; x <= halfW - STEP + 0.5; x += STEP) positions.push(x, -halfH, 0, x, halfH, 0);
        for (let y = -halfH + STEP; y <= halfH - STEP + 0.5; y += STEP) positions.push(-halfW, y, 0, halfW, y, 0);
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
        const material = new THREE.LineBasicMaterial({
            color: 0x808080,
            transparent: true,
            opacity: 1,
            depthTest: false,
            depthWrite: false,
        });
        const grid = new THREE.LineSegments(geometry, material);
        grid.renderOrder = 9998; // ueber Content, unter Safe-Area (9999)
        grid.visible = false;
        return grid;
    }

    /**
     * Halbe Breite/Hoehe des sichtbaren Buehnenausschnitts in Buehnen-
     * Einheiten. Im Querformat (Aspekt > 1) ist die volle Breite sichtbar und
     * die Hoehe = Breite / Aspekt; im Hochformat die volle Hoehe und die
     * Breite = Hoehe * Aspekt. Der Aspekt ist der von calculateBounds
     * begrenzte (0,5..2), derselbe, mit dem die Kamera rechnet.
     */
    /**
     * AUSRICHTEN AN KANTEN (Jan 24.10.2026: „fehlt da nicht noch links-,
     * rechtsbündig, oben/unten?“): sichtbarer Rahmen jeder Ebene — der Kasten,
     * der sie auf der Bühne umschließt, mit Drehung und Skalierung — in
     * POSITIONSEINHEITEN (1 = halbe Bühne) im Koordinatenrahmen ihrer
     * Eltern-Gruppe, damit er direkt mit Position X/Y vergleichbar ist.
     * Dazu die Bühnenkanten des gerade gewählten Formats im selben Rahmen
     * (Eltern der ersten Ebene). WebGL-Ebenen über ihr 3D-Objekt; HTML-Ebenen
     * (SVG „als HTML“, HTML-Infografik) aus Position, Maßen und Skalierung.
     */
    ausrichtRahmen(
        ids: string[],
        timer: number,
    ): {
        ebenen: Record<string, { links: number; rechts: number; oben: number; unten: number }>;
        buehne: { links: number; rechts: number; oben: number; unten: number } | null;
    } {
        const { SLIDE_WIDTH, SLIDE_HEIGHT } = RendererStore;
        const ex = SLIDE_WIDTH / 2;
        const ey = SLIDE_HEIGHT / 2;
        const ebenen: Record<string, { links: number; rechts: number; oben: number; unten: number }> = {};
        let ersterEltern: THREE.Object3D | null = null;
        // alle Ebenen des Baums (auch Kinder von Gruppen) samt Eltern-Id
        const renderEbenen: Record<string, LayerRender> = {};
        const elternId: Record<string, string | undefined> = {};
        crawl(
            this.props.presentation.groupTree,
            (l: LayerRender, ctx: TODOAny) => {
                if (!l?.id) return;
                renderEbenen[l.id] = l;
                elternId[l.id] = ctx?.parent?.id;
            },
            { getChildren: (l: TODOAny) => l?.layers }
        );
        // Ecken einer Ebene (Bildebene, Bühnen-Pixel) im Rahmen ihres
        // Eltern-Knotens. Gruppen: alle Kinder zusammen (Jan 24.10.2026:
        // „gehen auch Gruppen auszurichten?“) — auch HTML-Kinder, die im
        // 3D-Objekt der Gruppe nicht stecken. Ton, Karte, Effekt zählen nicht.
        const ohneRahmen = [LayerType.audio, LayerType.map, LayerType.effect];
        const eckenVon = (id: string): { punkte: THREE.Vector3[]; rahmen: THREE.Object3D } | null => {
            const lr = renderEbenen[id];
            if (lr && ohneRahmen.includes(lr.type as LayerType)) return null;
            const knoten = (this.pivots[id] ?? this.layerMeshes[id]) as THREE.Object3D | undefined;
            const objekt = this.layerMeshes[id] as THREE.Object3D | undefined;
            if (lr?.type === LayerType.group) {
                const ziel = knoten?.parent;
                if (!ziel) return null;
                ziel.updateWorldMatrix(true, false);
                const punkte: THREE.Vector3[] = [];
                for (const kind of ((lr as TODOAny).layers ?? []) as LayerRender[]) {
                    const r = eckenVon(kind.id);
                    if (!r) continue;
                    r.rahmen.updateWorldMatrix(true, false);
                    for (const p of r.punkte) punkte.push(ziel.worldToLocal(r.rahmen.localToWorld(p.clone())));
                }
                return punkte.length ? { punkte, rahmen: ziel } : null;
            }
            // Licht: kein Kasten — richtet sich als PUNKT aus (s. unten)
            if (objekt && knoten?.parent && objekt.parent && lr?.type !== LayerType.light) {
                const eltern = knoten.parent;
                objekt.updateWorldMatrix(true, true);
                const box = new THREE.Box3().setFromObject(objekt);
                if (box.isEmpty()) return null;
                // die 4 Ecken (Bildebene) in den Rahmen der Eltern umrechnen
                const z = (box.min.z + box.max.z) / 2;
                const punkte = [
                    new THREE.Vector3(box.min.x, box.min.y, z),
                    new THREE.Vector3(box.max.x, box.min.y, z),
                    new THREE.Vector3(box.min.x, box.max.y, z),
                    new THREE.Vector3(box.max.x, box.max.y, z),
                ].map((v) => eltern.worldToLocal(v));
                return { punkte, rahmen: eltern };
            }
            // HTML-Ebene und Licht: aus Position (und Maßen/Skalierung), im
            // Rahmen der Eltern-Gruppe wie der Anfasser-Proxy (syncGizmo)
            if (!lr) return null;
            const ePid = elternId[id];
            const rahmen = ((ePid && this.layerMeshes[ePid]) as THREE.Object3D | undefined) ?? this.groupRoot;
            if (!rahmen) return null;
            const l3d = lr.layer3D(timer);
            const px = (l3d.position?.[0] ?? 0) * ex;
            const py = (l3d.position?.[1] ?? 0) * ey;
            // Punkt-/Spotlicht (Jan 24.10.2026: „Lichter lassen sich nicht
            // ausrichten?“): ohne Fläche — alle vier Kanten liegen im Lichtpunkt
            if (lr.type === LayerType.light) return { punkte: [new THREE.Vector3(px, py, 0)], rahmen };
            const inhalt = lr.content as TODOAny;
            if (!inhalt?.width || !inhalt?.height) return null;
            const sx = Math.abs(l3d.scale?.[0] ?? 1);
            const sy = Math.abs(l3d.scale?.[1] ?? 1);
            const halbB = (inhalt.width * sx) / 2;
            const halbH = (inhalt.height * sy) / 2;
            // Linie/Pfeil mit Nullpunkt am Anfang: das Bild ist doppelt breit,
            // sichtbar ist nur die rechte Hälfte
            const links = inhalt.grundform?.nullpunkt === "anfang" ? px : px - halbB;
            return {
                punkte: [new THREE.Vector3(links, py - halbH, 0), new THREE.Vector3(px + halbB, py + halbH, 0)],
                rahmen,
            };
        };
        for (const id of ids) {
            const r = eckenVon(id);
            if (!r) continue;
            if (!ersterEltern) ersterEltern = r.rahmen;
            ebenen[id] = {
                links: Math.min(...r.punkte.map((v) => v.x)) / ex,
                rechts: Math.max(...r.punkte.map((v) => v.x)) / ex,
                oben: Math.max(...r.punkte.map((v) => v.y)) / ey,
                unten: Math.min(...r.punkte.map((v) => v.y)) / ey,
            };
        }
        let buehne: { links: number; rechts: number; oben: number; unten: number } | null = null;
        const bezug = ersterEltern ?? this.groupRoot ?? null;
        if (bezug) {
            // sichtbareStreuHalbmasse liefert BÜHNEN-Einheiten (Rahmen der
            // Wurzelgruppe, nicht Welt — die Wurzel ist skaliert); für Ebenen
            // in einer Gruppe über die Welt in deren Rahmen umrechnen
            const { visibleHalfW, visibleHalfH } = this.sichtbareStreuHalbmasse();
            const wurzel = this.groupRoot;
            bezug.updateWorldMatrix(true, false);
            const inBezug = (x: number, y: number) => {
                const v = new THREE.Vector3(x, y, 0);
                if (wurzel && bezug !== wurzel) {
                    wurzel.updateWorldMatrix(true, false);
                    wurzel.localToWorld(v);
                    bezug.worldToLocal(v);
                }
                return v;
            };
            const a = inBezug(-visibleHalfW, -visibleHalfH);
            const b = inBezug(visibleHalfW, visibleHalfH);
            buehne = {
                links: Math.min(a.x, b.x) / ex,
                rechts: Math.max(a.x, b.x) / ex,
                oben: Math.max(a.y, b.y) / ey,
                unten: Math.min(a.y, b.y) / ey,
            };
        }
        return { ebenen, buehne };
    }

    private sichtbareStreuHalbmasse(): { visibleHalfW: number; visibleHalfH: number } {
        const { SLIDE_WIDTH, SLIDE_HEIGHT } = RendererStore;
        const store = this.props.renderer as TODOAny;
        const aspect: number = Number(store?.calculateBounds?.()?.closestPossibleAspect) || 1;
        if (aspect >= 1) return { visibleHalfW: SLIDE_WIDTH / 2, visibleHalfH: SLIDE_WIDTH / 2 / aspect };
        return { visibleHalfW: (SLIDE_HEIGHT / 2) * aspect, visibleHalfH: SLIDE_HEIGHT / 2 };
    }

    /** Three.js-Pivot-Position -> MST-Position (faithful aus Jans Renderer). */
    private computeMstPos(pivot: THREE.Object3D, layer: TODOAny): [number, number, number] {
        // SLIDE_WIDTH/HEIGHT = 4096, SLIDE_DEPTH = 2048 (RendererStore).
        const mx = 4096 / 2;
        const my = 4096 / 2;
        const mz = 2048 / 2;
        const pp = (layer?.pivotPoint as [number, number, number]) || [0, 0, 0];
        const p = pivot.position;
        return [p.x / mx - pp[0], p.y / my - pp[1], p.z / mz - pp[2]];
    }

    /**
     * Three.js-Pivot-Rotation -> MST-Rotation in Grad (Euler XYZ, wie
     * renderLayer sie mit degreeToRad anwendet). Auf 0,1 Grad gerundet.
     */
    private computeMstRot(pivot: THREE.Object3D): [number, number, number] {
        const r = pivot.rotation;
        return [radToDegree(r.x), radToDegree(r.y), radToDegree(r.z)];
    }

    /**
     * Dreh-Drag -> neue MST-Rotation, JE ACHSE (Jans Fund 09.09.2026: "bei Y
     * und Z aendern sich auch die anderen Werte, nur X stimmt").
     *
     * three verkettet die Ring-Drehung im Weltraum mit der Start-Quaternion
     * und wir lasen danach alle drei Euler-Winkel neu aus. Bei X stimmt das
     * zufaellig exakt (Rx ist in der XYZ-Reihenfolge ganz links), bei Y und Z
     * verteilt sich die Drehung auf alle drei Werte. Der Nutzer erwartet aber
     * das Verhalten der Regler: der gruene Ring aendert nur Y.
     *
     * Deshalb: Start-Winkel beim Drag-Start merken, den Drehwinkel des Rings
     * (tc.rotationAngle, um tc.rotationAxis) nur auf die Komponente des
     * gezogenen Rings addieren und das Pivot fuer die Vorschau auf genau diese
     * Euler-Winkel setzen (three's Weltverkettung ueberschreiben). Der aeussere
     * Ring "E" (Bildschirmebene) zaehlt als Z; nur der freie Trackball (XYZE)
     * darf alle drei Werte aendern.
     */
    private gizmoRotationDeg(tc: TODOAny, pivot: THREE.Object3D, dragEnded: boolean): [number, number, number] {
        const rund = (d: number) => Math.round(d * 10) / 10;
        if (dragEnded && this.gizmoLetzteRotDeg) return this.gizmoLetzteRotDeg;
        const axis = tc.axis as string | null;
        const start = this.gizmoStartRotDeg;
        let deg: [number, number, number];
        const idx: Record<string, number> = { X: 0, Y: 1, Z: 2, E: 2 };
        if (start && axis && axis in idx) {
            const i = idx[axis];
            // Vorzeichen: three dreht um tc.rotationAxis (bei Ringen, die
            // parallel zur Blickachse liegen, um die Blickachse selbst) —
            // relativ zur positiven Achse der Komponente werten.
            const achse = tc.rotationAxis as THREE.Vector3 | undefined;
            const komp = achse ? [achse.x, achse.y, achse.z][i] : 1;
            const vorzeichen = komp < 0 ? -1 : 1;
            deg = [start[0], start[1], start[2]];
            deg[i] = start[i] + vorzeichen * radToDegree(Number(tc.rotationAngle) || 0);
            // Vorschau: genau diese Euler-Winkel zeigen, nicht three's Verkettung.
            pivot.rotation.set(degreeToRad(deg[0]), degreeToRad(deg[1]), degreeToRad(deg[2]));
        } else {
            deg = this.computeMstRot(pivot);
        }
        deg = [rund(deg[0]), rund(deg[1]), rund(deg[2])];
        this.gizmoLetzteRotDeg = deg;
        return deg;
    }

    /**
     * Cutover Schritt 2b: Transform-Gizmo LAZY aufbauen — nur wenn ein editorStore
     * ihn treibt. Eigener transparenter Canvas ueber dem Content, damit die
     * Handles vorn liegen. Drag schreibt NICHT selbst in die MST, sondern ruft
     * editorStore.onLayerTransform(mstPos, timer, dragEnded) — der Editor schreibt.
     */
    private ensureGizmo() {
        if (this.transformControls || !this.props.editorStore) return;
        if (!this.webGLRenderer || !this.camera || !this.webglRendererDom) return;

        // Adaption r139->0.183: TransformControls ist kein Object3D mehr. Man
        // fuegt tc.getHelper() der Szene hinzu; Sichtbarkeit liegt auf dem Helper.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const tc = new TransformControls(this.camera, this.webGLRenderer.domElement) as any;
        tc.size = 0.75;
        tc.enabled = false;
        const helper = tc.getHelper();
        helper.visible = false;
        this.transformControls = tc;
        dickereGizmoLinien(tc);

        this.gizmoScene = new THREE.Scene();
        this.gizmoScene.add(helper);
        // antialias: die Gizmo-Pfeile sind Linien-Geometrie — ohne MSAA dieselbe
        // Flacker-/Treppen-Anfaelligkeit wie Grid/Safe-Area unter Interface-Zoom.
        // Existiert ohnehin nur im Editor (editorStore-gated).
        this.gizmoRenderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
        this.gizmoRenderer.setClearColor(0x000000, 0);
        // Retina: ohne Pixel-Ratio wird der Gizmo-Canvas 1:1 in CSS-Pixeln
        // gemalt und hochskaliert — die Linien wirkten verwaschen.
        if (typeof window !== "undefined") this.gizmoRenderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        this.gizmoRenderer.setSize(this.props.renderer.width, this.props.renderer.height);
        const gizmoCanvas = this.gizmoRenderer.domElement;
        gizmoCanvas.style.pointerEvents = "none";
        const overlay = document.createElement("div");
        overlay.style.cssText =
            "position:absolute;top:0;left:0;right:0;bottom:0;display:flex;justify-content:center;align-items:center;pointer-events:none;z-index:2147483645";
        overlay.appendChild(gizmoCanvas);
        const host = this.webglRendererDom.parentElement || this.webglRendererDom;
        host.appendChild(overlay);

        const emit = (dragEnded: boolean) => {
            const layer = this.props.editorStore?.layer;
            const pivot = tc.object as THREE.Object3D | undefined;
            if (!layer || !pivot) return;
            // Nur in die Ebene schreiben, an der der Anfasser hängt — wechselt
            // die Auswahl mitten im Zug (Klick wählte die Ebene dahinter), ging
            // die Bewegung sonst in die falsche Ebene.
            if (this._gizmoZielId && layer.id !== this._gizmoZielId) return;
            const es = this.props.editorStore!;
            const timer = es.timer ?? es.playablePresentation?.slidePlayer?.timer ?? 0;
            if (tc.mode === "rotate") {
                es.onLayerRotate?.(this.gizmoRotationDeg(tc, pivot, dragEnded), timer, dragEnded);
            } else {
                es.onLayerTransform?.(this.computeMstPos(pivot, layer), timer, dragEnded);
            }
        };
        // Live waehrend des Ziehens.
        tc.addEventListener("change", () => {
            if (this.isDraggingGizmo) emit(false);
        });
        // Drag-Start/Ende + Orbit-Controls sperren.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        tc.addEventListener("dragging-changed", (event: any) => {
            if (this.controls) this.controls.enabled = !event.value;
            if (event.value) {
                this.isDraggingGizmo = true;
                // Dreh-Modus: Startwinkel merken (das Pivot traegt hier noch
                // den vom Frame gesetzten MST-Wert).
                const pivot = tc.object as THREE.Object3D | undefined;
                this.gizmoStartRotDeg = pivot ? this.computeMstRot(pivot) : null;
                this.gizmoLetzteRotDeg = null;
            } else {
                emit(true);
                this.isDraggingGizmo = false;
                this.gizmoStartRotDeg = null;
                this.gizmoLetzteRotDeg = null;
            }
        });
        // HÄNGENDES ZIEHEN (Jan 22.10.2026, Kunde: „Anfasser sichtbar, aber
        // nicht mehr anfassbar“; gemessen: dragging=true, axis=null nach dem
        // ersten Ziehen): three beendet ein Ziehen nur, wenn der Gizmo beim
        // Loslassen `enabled` ist (onPointerUp: if (!enabled) return). syncGizmo
        // schaltet enabled pro Frame — war die Ebene im Moment des Loslassens
        // kurz nicht zugeordnet (Write-back baut den Editor neu), blieb dragging
        // für immer true: jeder neue Griff wurde abgewiesen, die Pfeile
        // verschwanden. Darum beendet JEDES Loslassen/Abbrechen/Verlassen des
        // Fensters ein laufendes Ziehen selbst (window, Bubble-Phase — nach
        // dem eigenen Handler des Gizmos).
        const ziehEnde = () => {
            if (!tc.dragging) return;
            try {
                tc.domElement?.removeEventListener("pointermove", tc._onPointerMove);
            } catch {
                /* ignore */
            }
            tc.pointerUp(null);
        };
        this._gizmoZiehEnde = ziehEnde;
        if (typeof window !== "undefined") {
            window.addEventListener("pointerup", ziehEnde);
            window.addEventListener("pointercancel", ziehEnde);
            window.addEventListener("blur", ziehEnde);
        }
    }

    /**
     * Cutover Schritt 2b: Gizmo pro Frame an den ausgewaehlten Layer heften.
     * Attach an this.pivots[editorStore.layer.id], Sichtbarkeit ueber showHandler.
     * (SVG/asHtml-Proxy-Sonderfall bewusst noch nicht portiert.)
     */
    syncGizmo() {
        const es = this.props.editorStore;
        if (!es) {
            if (this.transformControls?.object) {
                this.transformControls.detach();
                this.transformControls.getHelper().visible = false;
                this.transformControls.enabled = false;
            }
            return;
        }
        this.ensureGizmo();
        if (!this.transformControls) return;
        const helper = this.transformControls.getHelper();
        // Anfasser-Modus vom Editor (W = verschieben, E = drehen). setMode
        // feuert change-Events — nur bei echtem Wechsel und nie mitten im Drag.
        const wunschModus: GizmoMode = es.gizmoMode === "rotate" ? "rotate" : "translate";
        if (!this.isDraggingGizmo && this.transformControls.mode !== wunschModus) {
            this.transformControls.setMode(wunschModus);
        }
        const selected = es.layer;
        let pivot = selected ? this.pivots[selected.id] : undefined;

        // 3D-Objekte mit Drehpunkt 0/0/0 (der Standardfall): renderLayer
        // faehrt dort den Nicht-Pivot-Zweig (doNotUsePivot) — die Pivot-Gruppe
        // parkt JEDEN Frame im Ursprung, die Transform traegt das MESH. Der
        // Gizmo hing bisher an der geparkten Gruppe: er wurde in der
        // Vorschau-MITTE gezeichnet (egal wo das Objekt steht), raycastete
        // dort und jede Bewegung wurde sofort auf 0 zurueckgesetzt (Jans
        // Kugel-Fund 02.08.2026, Screenshot: Kugel oben rechts, Anfasser in
        // der Mitte). Deshalb hier ans MESH heften — es traegt dieselbe
        // newPosition-Formel, computeMstPos rechnet mit pivotPoint 0 identisch
        // zurueck, und der Tauziehen-Guard im Nicht-Pivot-Zweig schuetzt es
        // waehrend des Drags.
        if (
            selected &&
            selected.type === LayerType.object3d &&
            this.layerMeshes[selected.id]
        ) {
            const pp = (selected.pivotPoint as [number, number, number]) || [0, 0, 0];
            if (!pp[0] && !pp[1] && !pp[2]) {
                pivot = this.layerMeshes[selected.id] as unknown as THREE.Group;
            }
        }

        // Port "asHtml-Proxy" (Jans Original svgHandlerProxy — hier bislang
        // "bewusst noch nicht portiert", Jans Vektor-Fund): HTML-Overlay-Layer
        // haben kein WebGL-Pivot -> Gizmo und Drehpunkt-Kugel fehlten komplett.
        // Eine unsichtbare Proxy-Gruppe unter groupRoot traegt dieselbe
        // Welt-Position (newPosition-Formel: (position+pivotPoint)*max), damit
        // der Gizmo attach't; computeMstPos rechnet identisch zurueck, der
        // Write-back stimmt also unveraendert.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const selContent: any = selected?.content;
        const rendersAsHtml =
            !!selected &&
            ((selected.type === LayerType.svg && !!selContent?.asHtml) ||
                selected.type === LayerType.infographic);
        // Bei rendersAsHtml IMMER den Proxy nehmen: nach einem WebGL-
        // Zwischenspiel lingert this.pivots[id] (Original-Kommentar: "a stale
        // pivot can linger from a brief WebGL pass ... skip it") — attach't
        // der Gizmo daran, sitzt er am unsichtbaren alten Mesh.
        if (selected && rendersAsHtml) pivot = undefined;
        if (selected && rendersAsHtml && this.groupRoot) {
            if (!this.svgHandlerProxy) this.svgHandlerProxy = new THREE.Group();
            // IN DIE ELTERNGRUPPE hängen statt an groupRoot (Jan 22.10.2026:
            // „der Anfasser funktioniert bei den neuen SVGs nicht — und auch
            // nicht bei der Gruppe, in der sie sich befinden“): Der Proxy
            // bekam nur die EIGENE Position der Ebene; in einer verschobenen/
            // skalierten/gedrehten Gruppe saß der Anfasser neben dem sichtbaren
            // SVG, und computeMstPos rechnete beim Ziehen ohne die Gruppe
            // zurück. Unter dem 3D-Knoten der Gruppe rechnet der Szenengraph
            // beides mit — wie bei den 3D-Ebenen.
            {
                let eltern: TODOAny = null;
                crawl(
                    this.props.presentation.groupTree,
                    (l: LayerRender, ctx: TODOAny) => {
                        if (l?.id === selected.id) eltern = ctx?.parent ?? null;
                    },
                    { getChildren: (l: TODOAny) => l?.layers }
                );
                const ziel =
                    (eltern && (this.layerMeshes[eltern.id] as unknown as THREE.Object3D)) || this.groupRoot;
                if (this.svgHandlerProxy.parent !== ziel) ziel.add(this.svgHandlerProxy);
            }
            // Waehrend des Drags gehoert die Proxy-Transform dem Gizmo.
            if (!this.isDraggingGizmo) {
                const t = es.timer ?? es.playablePresentation?.slidePlayer?.timer ?? 0;
                // LayerRender (Render-Tree) zur Auswahl finden — nur er traegt
                // die interpolierte layer3D-Funktion.
                let lr: LayerRender | undefined;
                crawl(
                    this.props.presentation.groupTree,
                    (l: LayerRender) => {
                        if (l?.id === selected.id) lr = l;
                    },
                    { getChildren: (l: TODOAny) => l?.layers }
                );
                if (lr) {
                    const l3d = lr.layer3D(t);
                    const pp = (lr.pivotPoint as [number, number, number]) || [0, 0, 0];
                    const { SLIDE_WIDTH, SLIDE_HEIGHT, SLIDE_DEPTH } = RendererStore;
                    this.svgHandlerProxy.position.set(
                        (l3d.position![0] + pp[0]) * (SLIDE_WIDTH / 2),
                        (l3d.position![1] + pp[1]) * (SLIDE_HEIGHT / 2),
                        (l3d.position![2] + pp[2]) * (SLIDE_DEPTH / 2)
                    );
                    // Rotation mitfuehren: der Dreh-Modus startet sonst bei 0
                    // statt beim aktuellen Wert (computeMstRot liest den Proxy).
                    const rot = (l3d.rotation as [number, number, number] | undefined) || [0, 0, 0];
                    this.svgHandlerProxy.rotation.set(degreeToRad(rot[0]), degreeToRad(rot[1]), degreeToRad(rot[2]));
                }
                // Drehpunkt-Kugel auch fuer HTML-Layer: unter den Proxy haengen
                // (renderLayer parkt sie beim Rueckwechsel selbst wieder um).
                const showPivot =
                    !!(selected as TODOAny).showPivotPoint && WebGLAssets.editorMode;
                let circle = this.pivotsCircles[selected.id];
                if (!circle && showPivot) {
                    const geometry = new THREE.SphereGeometry(100, 4, 2);
                    const material = new THREE.MeshBasicMaterial({
                        color: 0xff0000,
                        wireframe: true,
                        depthTest: false,
                        transparent: true,
                    });
                    circle = new THREE.Mesh(geometry, material);
                    circle.renderOrder = 999999;
                    this.pivotsCircles[selected.id] = circle;
                }
                if (circle && showPivot) {
                    // In die GIZMO-Szene (eigener Canvas UEBER den DOM-Overlays):
                    // im HTML-Modus deckt das DOM-SVG sonst die Kugel im Haupt-
                    // Canvas ab (Jans Fund; gleiche Loesung wie beim Gizmo selbst).
                    // renderLayer parkt die Kugel beim Rueckwechsel zu WebGL selbst
                    // wieder unter die pivotGroup um (parent-Check).
                    // WICHTIG: WELT-Transform uebernehmen, nicht die Roh-Position —
                    // der Proxy haengt unter groupRoot, und der ist auf die Kamera
                    // SKALIERT (Buehnen-Einheit != Szenen-Einheit). Mit Rohwerten
                    // sass die Kugel bei versetzter Position weit ausserhalb des
                    // Sichtfelds (Jans Fund "Drehpunkt bleibt nicht in der Mitte").
                    if (this.gizmoScene && circle.parent !== this.gizmoScene) this.gizmoScene.add(circle);
                    circle.visible = true;
                    this.svgHandlerProxy.updateWorldMatrix(true, false);
                    this.svgHandlerProxy.getWorldPosition(circle.position);
                    this.svgHandlerProxy.getWorldScale(circle.scale);
                } else if (circle && this.gizmoScene && circle.parent === this.gizmoScene) {
                    circle.visible = false;
                }
            }
            pivot = this.svgHandlerProxy;
        } else {
            // Kein HTML-Layer selektiert: in der Gizmo-Szene geparkte
            // Drehpunkt-Kugeln ausblenden (sonst blieben sie an der alten
            // Position stehen); Proxy-Anhaenge ebenso.
            if (this.gizmoScene) {
                for (const c of Object.values(this.pivotsCircles)) {
                    if (c && c.parent === this.gizmoScene) c.visible = false;
                }
            }
            if (this.svgHandlerProxy) {
                this.svgHandlerProxy.children.forEach((c) => {
                    c.visible = false;
                });
            }
        }

        // Wechselt das Ziel oder wird der Gizmo abgeschaltet, während er noch
        // „zieht“: erst sauber beenden (sonst hängt dragging, s. ensureGizmo).
        // Ziel ist die EBENE, nicht ihr 3D-Knoten (Jan 22.10.2026: „Anfasser
        // flackert beim Anfassen“): Jede Änderung im Editor — auch jeder Zug
        // am Anfasser — baut den Baum neu (useInitializeViewerState →
        // invalidateTree), dabei entsteht ein NEUER Drehpunkt-Knoten. Der wird
        // mitten im Ziehen einfach umgehängt; bis er da ist, bleibt der alte.
        const zielWechsel = !selected || selected.id !== this._gizmoZielId || !es.showHandler;
        if (zielWechsel && this.transformControls.dragging) this._gizmoZiehEnde?.();
        if (selected && !pivot && this.transformControls.dragging) return;
        if (selected && pivot) {
            if (this.transformControls.object !== pivot) this.transformControls.attach(pivot);
            this._gizmoZielId = selected.id;
            helper.visible = !!es.showHandler;
            // enabled folgt der Sichtbarkeit (Jans Fund 02.08.2026): vorher
            // blieben die Anfasser bei abgehaktem ANZEIGEN>ANFASSER zwar
            // unsichtbar, aber AKTIV — sie schluckten Drags an Stellen, die
            // man nicht sehen (und daher nicht treffen) kann. Wichtig auch,
            // weil die Ansicht-Schalter PRO BROWSER gespeichert sind
            // (localStorage): in einem frisch benutzten Browser kann das
            // Haekchen anders stehen als gewohnt.
            this.transformControls.enabled = !!es.showHandler;
        } else {
            if (this.transformControls.object) this.transformControls.detach();
            this._gizmoZielId = undefined;
            helper.visible = false;
            this.transformControls.enabled = false;
        }
    }

    /**
     * Canvas-Klick-Selektion (additiv, über Jan-Parität hinaus — Jans Original
     * hat kein Raycast-Select): Strahl von der Kamera durch den Klickpunkt gegen
     * die sichtbaren Layer-Meshes. Liefert die layer.id des nächstliegenden
     * Treffers (= topmost sichtbare Ebene) oder null. Der Adapter mappt die id
     * auf die MST-ILayer und ruft editorStore.selectLayer. Out-of-lifetime-Layer
     * (userData.opacity ~0) und Lichter werden übersprungen.
     */
    pickLayerIdAt(clientX: number, clientY: number): string | null {
        if (!this.camera || !this.webGLRenderer) return null;
        const canvas = this.webGLRenderer.domElement;
        const rc = canvas.getBoundingClientRect();
        if (rc.width === 0 || rc.height === 0) return null;
        const ndc = new THREE.Vector2(
            ((clientX - rc.left) / rc.width) * 2 - 1,
            -((clientY - rc.top) / rc.height) * 2 + 1,
        );
        const ray = new THREE.Raycaster();
        ray.setFromCamera(ndc, this.camera);
        // Reverse-Map Object3D → layer.id. Ebenen können in Gruppen liegen; damit
        // ein Treffer die BLATT-Ebene (nicht die Gruppe) liefert, walken wir vom
        // getroffenen Objekt nach oben zur NÄCHSTEN registrierten layerMeshes-Ebene.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const objToId = new Map<any, string>();
        const targets: THREE.Object3D[] = [];
        for (const id of Object.keys(this.layerMeshes)) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const obj = this.layerMeshes[id] as any;
            if (!obj || obj.isLight || obj.visible === false) continue;
            const op = obj.userData?.opacity;
            // out-of-lifetime / vollständig transparente Ebenen nicht anklickbar
            if (typeof op === "number" && op <= 0.02) continue;
            objToId.set(obj, id);
            targets.push(obj as THREE.Object3D);
        }
        const hits = ray.intersectObjects(targets, true);
        for (const h of hits) {
            let o: THREE.Object3D | null = h.object;
            while (o) {
                const id = objToId.get(o);
                if (id) return id;
                o = o.parent;
            }
        }
        return null;
    }

    /** Cutover Schritt 2b: den Gizmo-Overlay-Pass rendern (eigene Kamera, near fix). */
    private renderGizmoPass() {
        if (!this.gizmoRenderer || !this.gizmoScene || !this.camera || !this.webGLRenderer) return;
        // Gizmo-Canvas auf die Groesse des Haupt-Canvas syncen (Buffer + CSS 100%).
        // Ohne das bleibt er bei der Lazy-Init-Groesse (evtl. 0×0) haengen.
        const main = this.webGLRenderer.domElement;
        const g = this.gizmoRenderer.domElement;
        if (g.width !== main.width || g.height !== main.height) {
            this.gizmoRenderer.setPixelRatio(1);
            this.gizmoRenderer.setSize(main.width, main.height, false);
            g.style.width = "100%";
            g.style.height = "100%";
        }
        if (!this.gizmoCamera) this.gizmoCamera = new THREE.PerspectiveCamera();
        this.gizmoCamera.copy(this.camera, false);
        this.gizmoCamera.near = 1;
        this.gizmoCamera.updateProjectionMatrix();
        this.gizmoRenderer.render(this.gizmoScene, this.gizmoCamera);
    }

    // Main loop, where we animate everything
    /** Buehne so klein, dass die 720p-Mobilfassung reicht (s. buehneKleinFuerMobilfassung). */
    buehneKlein(): boolean {
        const breite = this.webglRendererDom?.clientWidth || this.props.renderer.width || 0;
        return buehneKleinFuerMobilfassung(breite, wirksameDpr(this.props.renderer.dpr));
    }

    /**
     * Die beim Boot zurueckgehaltenen Videos (Reader: alle ausser dem
     * Startvideo, s. updateVideo) bekommen ihre Quelle und puffern los.
     * Einmalig; laeuft ueber rendererStore.beiLadeEnde (auch ohne rAF) und
     * zur Sicherheit aus animate().
     */
    zurueckgehalteneVideosFreigeben() {
        if (this.nachBootFreigegeben) return;
        this.nachBootFreigegeben = true;
        for (const eintrag of Object.values(this.videoCache)) {
            // Zurueckgehaltene Quelle setzen + laden (Video.quelleFreigeben);
            // Elemente, die schon eine Quelle haben, nur auf "auto" heben.
            eintrag?.quelleFreigeben?.();
            const el = eintrag?.videoPlayer;
            if (!el || el.preload !== "none") continue;
            el.preload = "auto";
            try {
                el.load();
            } catch {
                /* ignore */
            }
        }
    }

    /**
     * Weichheits-Ueberblendung (28.09.2026): Der Fake-Schatten traegt bei
     * animierter Weichheit eine zweite Flaeche (`__fakeShadow2`) als Kind —
     * ihre Deckkraft ist Bild-Deckkraft × Anteil der oberen Stufe.
     */
    /**
     * Spanne der Weichheits-Keyframes einer Ebene (min, max) — fuers
     * Vorwaermen der Schatten-Stufen (syncImageShadow). undefined = nicht
     * animiert.
     */
    private weichheitsBereich(layer: LayerRender): [number, number] | undefined {
        const g = layer.animationsGroups?.shadowBlur as
            | { active?: boolean; keyframes?: Array<{ value: unknown }> }
            | undefined;
        if (!g?.active || !g.keyframes?.length) return undefined;
        const werte = g.keyframes.map((k) => Number(k.value)).filter(Number.isFinite);
        if (!werte.length) return undefined;
        return [Math.min(...werte), Math.max(...werte)];
    }

    private schattenKinderFaden(shadowChild: THREE.Object3D, bildDeckkraft: number) {
        for (const kind of shadowChild.children) {
            const mat = (kind as THREE.Mesh).material as THREE.MeshBasicMaterial | undefined;
            if (!mat) continue;
            mat.opacity = bildDeckkraft * ((kind.userData.schattenDeckkraft as number | undefined) ?? 0);
            mat.transparent = true;
        }
    }

    animate(timer: number) {
        if (!this.webGLRenderer || !this.camera || !this.webGlScene || !this.ambientLight || !this.controls) {
            throw new Error("Called RendererWebGL#animate before calling init function.");
        }
        // console.log("test-animate", timer, this);

        const SHOW_HELPERS = !this.props.isProduction;
        const { backwards } = this.props.slidePlayer;
        const { webGlScene } = this;
        void this.css3DScene; // legacy: CSS3D scene reference kept for future use

        const rendererStore = this.props.renderer;

        // Aufwaermen (einmalig, sobald alle Layer geladen sind): drei kompiliert
        // Shader-Programme sonst erst, wenn ihr Material zum ERSTEN Mal sichtbar
        // wird — mitten im Scrollen, als Ruckler im ersten Durchlauf (Jans Fund
        // 06.08.2026, zusammen mit dem Textur-Upload in updateImage).
        // compileAsync nutzt KHR_parallel_shader_compile und blockiert den
        // Haupt-Thread nicht; Materialien, die spaeter dazukommen (Editor),
        // kompilieren wie bisher lazy.
        if (!this.shaderAufwaermenErledigt && rendererStore.finishedLoading && this.umgebungBereit()) {
            this.shaderAufwaermenErledigt = true;
            // LICHT-VARIANTEN mitkompilieren (Jans Ruckler 26.08.2026): three
            // schluesselt Shader-Programme nach der Anzahl SICHTBARER Lichter.
            // Ein Licht-Layer ausserhalb seiner Lebensdauer ist unsichtbar —
            // betritt es sein Zeitfenster, muessen ALLE Materialien im Bild
            // neu kompilieren (der Haenger genau beim Szenenwechsel). Darum
            // fuer den Compile-Scan kurz alle Lichter sichtbar schalten; der
            // Scan von compileAsync laeuft synchron, nur das Warten auf die
            // parallele Kompilierung ist async — direkt danach zuruecksetzen.
            const kurzSichtbar: THREE.Object3D[] = [];
            Object.keys(this.lightState || {}).forEach((id) => {
                const licht = this.lightState[id] as TODOAny;
                if (licht && !licht.visible) {
                    licht.visible = true;
                    kurzSichtbar.push(licht);
                }
            });
            const kompilate: Promise<unknown>[] = [];
            kompilate.push(
                this.webGLRenderer.compileAsync(this.webGlScene, this.camera).catch(() => {
                    // Aufwaermen ist reine Optimierung — ein Fehler hier darf
                    // niemals das Rendern selbst betreffen.
                })
            );
            // AUCH DIE ABGEHAENGTEN EBENEN (Jans Welcome-Scrolly, 08.09.2026,
            // gemessen: 8 bzw. 6 Shader-Kompilierungen genau beim ersten
            // Erscheinen von Ring und Counter): Ebenen ausserhalb ihrer
            // Lebensdauer haengen beim Aufwaermen nicht im Szenenbaum, der
            // Scan ueber die Szene sieht sie nicht. Jede Ebenen-Wurzel darum
            // einzeln gegen die Szene (Lichter!) kompilieren — bereits
            // kompilierte Programme kosten dabei nichts.
            for (const id in this.layerMeshes) {
                const wurzel = this.layerMeshes[id] as TODOAny;
                if (!wurzel || wurzel.parent) continue;
                kompilate.push(
                    this.webGLRenderer.compileAsync(wurzel, this.camera, this.webGlScene).catch(() => {
                        /* reine Optimierung, s. o. */
                    })
                );
            }
            // AUFWAERM-BILD (Jans Auftrag 08.09.2026): compileAsync erzeugt nur
            // die Bildschirm-Varianten der Programme. Beim ersten Erscheinen von
            // Ring/Counter im Welcome-Scrolly kompilierten trotzdem drei
            // Programme nach (renderer.info.programs): das Tiefenprogramm des
            // SCHATTENPASSES (castShadow-Spotlight) und zwei Material-Varianten
            // fuer RENDER-TARGETS (Farbraum srgb-linear — Glas-/Zwischenpuffer-
            // Pass). Beides entsteht in three erst beim echten Rendern. Darum
            // einmal ALLES sichtbar in ein winziges Render-Target zeichnen,
            // solange der Nutzer auf den Startbildschirm schaut.
            this.waermeBildRendern();
            kurzSichtbar.forEach((licht) => {
                licht.visible = false;
            });
            void Promise.all(kompilate).then(() => {
                this.shaderKompiliert = true;
            });
        }
        // Textur-Aufwaermen: wenige Texturen pro Frame, damit kein Einzel-Frame
        // einfriert. Laeuft vor dem vollen Ladebalken (s. u.).
        if (this.shaderAufwaermenErledigt && !this.texturAufwaermenErledigt) {
            this.waermeTexturenSchrittweise();
        }
        // Schrift-Atlas nachziehen, wenn spaet fertige Glyphen ihn geaendert
        // haben (z. B. der Grundsatz aus fonts.ts) — sonst laedt er beim ersten
        // sichtbaren Text hoch. Unveraendert ist initTexture nur ein Binden.
        if (this.shaderAufwaermenErledigt && this.webGLRenderer) {
            for (const atlas of this.troikaAtlanten) this.webGLRenderer.initTexture(atlas);
        }
        // Erst wenn Shader, Texturen und Schrift-Atlas auf der GPU sind, wird
        // der Ladebalken voll und START frei (RendererStore.aufwaermenAbgeschlossen;
        // vorher fiel das bei sofortigem START in den Start-Flug).
        if (
            !rendererStore.aufwaermenFertig &&
            this.shaderKompiliert &&
            this.texturAufwaermenErledigt &&
            this.offeneTextSyncs === 0
        ) {
            rendererStore.aufwaermenAbgeschlossen();
        }

        const rendererPresentation = this.props.presentation;

        const { presentationOptions, slides, groupTree } = rendererPresentation;

        // Video-Hintergrund-Vorlader: startet EINMAL nach dem START-Klick
        // (Gesten-Moment; der Ladebalken bleibt unberuehrt). Editor bleibt
        // aussen vor — dort werden Quellen laufend getauscht.
        // MOBIL ERST NACH DER STARTPHASE (Jans iPhone-Fund 02.09.2026, Start-
        // video ruckelt): Direkt nach dem Klick dekodiert das Geraet bereits
        // mehrere Videos und faehrt die Intro-Fahrt — die 13-18-MB-Downloads
        // samt Blob-Aufbau des Vorladers fielen genau in diesen Moment. Auf
        // Mobilgeraeten deshalb ein paar Sekunden Luft, bis das Startvideo
        // ruhig laeuft; Desktop startet wie bisher sofort.
        const startedNow = (this.props.renderer as TODOAny).playbackStarted;
        // NACH DEM BOOT (nicht erst nach START — Jans Regel 05.09.2026: „die
        // weiteren Videos koennen ruhig schon laden, wenn das erste fertig
        // ist; sonst ist beim schnellen Durchscrollen ein Video schwarz"):
        // Sobald der Boot durch ist — und der wartet jetzt auf das
        // abspielbereite Startvideo —, bekommen die zurueckgehaltenen Videos
        // ihre Quelle und puffern ihre ersten Sekunden. Der Vorlader (ganze
        // Dateien als Blob) kommt weiterhin erst nach START.
        // (Primaer laeuft das ueber rendererStore.beiLadeEnde, s. init —
        // hier nur der Fallback, falls der Store vor der Anmeldung fertig war.)
        if (rendererStore.finishedLoading) this.zurueckgehalteneVideosFreigeben();
        if (startedNow && this.vorladerFreiAb === 0) {
            const jetztMs = typeof performance !== "undefined" ? performance.now() : Date.now();
            this.vorladerFreiAb = jetztMs + (istMobilesGeraet() ? VORLADER_MOBIL_SCHONFRIST_MS : 0);
        }
        const vorladerFrei =
            this.vorladerFreiAb > 0 &&
            (typeof performance !== "undefined" ? performance.now() : Date.now()) >= this.vorladerFreiAb;
        if (
            !this.videoVorlader.gestartet &&
            !WebGLAssets.editorMode &&
            rendererStore.finishedLoading &&
            startedNow &&
            vorladerFrei
        ) {
            const eintraege: VorladerEintrag[] = [];
            // STARTVIDEO ZUERST SATT (Jans Slow-4G-Test am WESER-KURIER-Scrolly,
            // 05.09.2026): Der Vorlader lief auf dem Desktop sofort nach START
            // los und holte alle weiteren Videos komplett — waehrend das
            // 16,8-MB-Startvideo noch streamte und sich die Leitung mit ihm
            // teilte. Ergebnis: Video 2 (1,5 MB) lief, Video 1 hungerte.
            // Deshalb wartet der Vorlader jetzt auf ALLEN Geraeten, bis jedes
            // Startvideo (Lebensdauer ab dem Start-Slide) wirklich laeuft:
            // genug Puffer (readyState >= HAVE_FUTURE_DATA) oder schon ueber
            // die erste halbe Sekunde hinaus. Ein Startvideo, das gar nicht
            // laden kann (Fehler), blockiert nicht ewig: dann zaehlt der
            // Fehlerzustand des Elements als "erledigt".
            let startvideoWartet = false;
            crawl(
                groupTree,
                (layer: LayerRender) => {
                    if (layer?.type !== LayerType.video || !layer.content) return;
                    const url = getLayerContentUrl(layer.content as AssetContent, this.buehneKlein());
                    if (!url) return;
                    eintraege.push({ layerId: layer.id, url, von: layer.from ?? 0 });
                    if (this.istStartVideo(layer.from, url)) {
                        const el = this.videoCache[layer.id]?.videoPlayer;
                        const laeuft =
                            !el ||
                            !!el.error ||
                            el.readyState >= 3 ||
                            (!el.paused && el.currentTime > 0.5);
                        if (!laeuft) startvideoWartet = true;
                    }
                },
                { getChildren: (l: TODOAny) => l?.layers }
            );
            if (!startvideoWartet) {
                this.videoVorlader.start(eintraege, (id) => this.videoCache[id]);
            }
        }

        // Safe-Area-Ausrichtung pro Slide. Regel + Begruendung (zwei NaN-Quellen,
        // die die ganze Szene unsichtbar machten) in utils/safeAreaAlignment.
        const safeAreas = buildSafeAreaStops(
            slides as TODOAny,
            presentationOptions.slideOptions as TODOAny
        );
        this.ambientLight!.intensity = presentationOptions.ambientIntensity;
        // Schatten-Staerke des 3D-WELT-Tabs uebernehmen (wirkt im Licht-Pass
        // unten auf alle Schatten-Lichter; deckt auch die ShadowMaterial-
        // Empfaenger der 2D-Bilder ab, da three die Abschwaechung pro LICHT
        // in getShadow() verrechnet).
        this.shadowStrength = Math.max(
            0,
            Math.min(1, (presentationOptions as TODOAny).shadowStrength ?? 1)
        );

        // Port "HDRI": Image-Based-Lighting pro Frame synchronisieren. No-op wenn
        // presentationOptions.environmentHdri leer ist (Bestand ohne HDRI).
        syncEnvironment(this as unknown as EnvironmentHost, presentationOptions, timer);

        // Mobile-Deckel (Jans iPhone-Absturz 26.08.2026): iPhone Pro meldet
        // dpr 3 — Canvas, Glas-Transmission-Target und jeder Vollbild-Pass
        // wachsen damit um Faktor 2,25 gegenueber dpr 2, und WebKit killt den
        // Tab beim Speicherdeckel. dpr 2 ist auf Handy-Groesse visuell nicht
        // unterscheidbar. Desktop bleibt ungedeckelt (Bild-Pipeline rechnet
        // weiter mit der echten rendererStore.dpr).
        //
        // AUSNAHME (Jan 20.09.2026, iPhone: „sehr pixelig, vor allem die
        // gerundeten Kanten der Balken"): LEICHTE 3D-Szenen rechnen in der
        // echten Geraete-Aufloesung (bis dpr 3). Bei 3D stimmt „visuell nicht
        // unterscheidbar" nicht — dpr 2 auf einem dpr-3-Schirm wird 1,5-fach
        // hochskaliert, und die feinen Glanzlinien auf Rundungen flimmern.
        // Nur mit echten 3D-Ebenen UND ohne Glas/Riesen-Geometrie
        // (leichtGemerkt); reine Bild-Scrollys — die Absturz-Szenen vom
        // August — bleiben bei 2.
        const mobilMaxDpr =
            this.hat3d && leichtGemerkt(this as unknown as Parameters<typeof leichtGemerkt>[0]) ? 3 : 2;
        // Vergroesserte Karte (utils/kartenSkala): Puffer k-fach feiner, sonst
        // wird das WebGL-Bild nur weich hochgezogen. Mobil gibt es keine Karte.
        const zielDpr = istMobilesGeraet()
            ? Math.min(rendererStore.dpr, mobilMaxDpr)
            : wirksameDpr(rendererStore.dpr);
        if (this.webGLRenderer.getPixelRatio() !== zielDpr) {
            this.webGLRenderer.setPixelRatio(zielDpr);
        }

        // update renderer stuff
        // Auch im Editor-Modus (GLOBAL-Tab Orbit): SHOW_HELPERS (= !isProduction)
        // ist im geteilten Viewer dauerhaft false — ohne das Editor-Gate springt
        // die Kamera nach dem Orbiten nie auf ihre Ausgangsposition zurueck.
        if (SHOW_HELPERS || WebGLAssets.editorMode) {
            if (!rendererStore.useOrbitControls) {
                this.camera.lookAt(new THREE.Vector3(...rendererStore.cameraLookAt!));
                this.camera.position.copy(new THREE.Vector3(...rendererStore.cameraPosition!));
            }
            this.camera.fov = rendererStore.fov!;
            this.camera.far = rendererStore.far!;
            this.camera.updateProjectionMatrix();
        }

        this.controls.enabled = rendererStore.useOrbitControls;
        // Kamera-Drahtgitter + Gizmo (GLOBAL-Tab "Camera", Jans Original)
        this.syncCameraHelper(rendererStore);

        // update height/width stuff
        const size = new THREE.Vector2();
        this.webGLRenderer.getSize(size);
        const sizeChanged = size.width !== rendererStore.width || size.height !== rendererStore.height;

        if (sizeChanged) {
            this.webGLRenderer.setSize(rendererStore.width, rendererStore.height, false);
            //this.css3DRenderer.setSize(rendererStore.width, rendererStore.height);
            this.camera.updateProjectionMatrix();
        }

        const { SLIDE_WIDTH, SLIDE_HEIGHT, SLIDE_DEPTH } = RendererStore;
        const { bounds, cutOffFillerX, cutOffFillerY, closestPossibleAspect, useMaxWidth } =
            rendererStore.calculateBounds();

        const boundingBox = {
            min: new THREE.Vector3(SLIDE_WIDTH / -2, SLIDE_HEIGHT / -2, SLIDE_DEPTH / -2),
            max: new THREE.Vector3(SLIDE_WIDTH / 2, SLIDE_HEIGHT / 2, SLIDE_DEPTH / 2),
        };

        this.webGLRenderer.setViewport(bounds.newLeft, bounds.newTop, bounds.newWidth, bounds.newHeight);
        this.webGLRenderer.setScissor(bounds.newLeft, bounds.newTop, bounds.newWidth, bounds.newHeight);

        this.camera.aspect = closestPossibleAspect;
        this.camera.updateProjectionMatrix();

        // visibleHeight and visibleWidth is the camera calculation to see what our ranges actually are
        const { height: visibleHeight, width: visibleWidth } = rendererStore.useOrbitControls
            ? {
                  width: rendererStore.width,
                  height: rendererStore.width * this.camera.aspect,
              }
            : RendererStore.visibleSize(this.camera.fov, this.camera.position.z, this.camera.aspect);

        // how much to we need to scale down according to slide-width and available width?
        const scaleFactor = useMaxWidth ? visibleWidth / SLIDE_WIDTH : visibleHeight / SLIDE_HEIGHT;

        // Scaling values available for debugging if needed:
        // containerW, containerH, cameraAspect, scaleFactor, useMaxWidth, bounds

        // we start to scale the font when we get to small screens
        const responsiveScaleFactor = Math.max(900 / Math.min(rendererStore.width, rendererStore.height), 1);

        if (!this.groupRoot) {
            this.groupRoot = new THREE.Group();
            const element = document.createElement("div");
            this.groupRootCSS = new CSS3DObject(element);
            this.webGlScene.add(this.groupRoot);
            //this.css3DScene!.add(this.groupRootCSS);

            // set up a mesh used as the boundary
            if (SHOW_HELPERS) {
                this.uiRoot = new THREE.Group();
                this.webGlScene.add(this.uiRoot);

                const safeSquareGeometry = new THREE.PlaneGeometry(1, 1, 1);
                const safeSquareMaterial = new THREE.MeshBasicMaterial({
                    color: 0x00ff00,
                    opacity: 0.3,
                    transparent: true,
                    wireframe: true,
                });
                this.safeSquare = new THREE.Mesh(safeSquareGeometry, safeSquareMaterial);
                this.uiRoot.add(this.safeSquare);
            }
        }
        // Cutover Schritt 2: Editor-Chrome (Safe-Area + Grid) pro Frame syncen.
        // No-op ohne editorStore (Reader) -> additiv-sicher.
        this.syncEditorChrome();

        this.groupRoot!.scale.set(scaleFactor, scaleFactor, scaleFactor);
        this.groupRootCSS!.scale.set(scaleFactor, scaleFactor, scaleFactor);

        // TODO: this shouldn't be calculated on every frame
        const slideToIndex = safeAreas.findIndex((s) => s.from >= timer);
        const nextSlideSafeArea = slideToIndex !== -1 ? safeAreas[slideToIndex] : safeAreas[safeAreas.length - 1];
        const currentSlideSafeArea = slideToIndex > 0 ? safeAreas[slideToIndex - 1] : nextSlideSafeArea;
        const progress = safeAreaProgress(
            slideToIndex,
            timer,
            currentSlideSafeArea,
            nextSlideSafeArea
        );

        const safeXAlignment = interpolateLinearDirectly(
            currentSlideSafeArea.x,
            nextSlideSafeArea.x,
            Easings.quadInOut(progress)
        );
        const safeYAlignment = interpolateLinearDirectly(
            currentSlideSafeArea.y,
            nextSlideSafeArea.y,
            Easings.quadInOut(progress)
        );

        const safeXPosition = safeXAlignment / -100; // flipped sign
        const safeYPosition = safeYAlignment / 100;
        const cutOffFillerXOffset = (cutOffFillerX * safeXPosition * SLIDE_WIDTH * scaleFactor) / 2;
        const cutOffFillerYOffset = (cutOffFillerY * safeYPosition * SLIDE_HEIGHT * scaleFactor) / 2;

        // LETZTE SICHERUNG gegen NaN an der gefaehrlichsten Stelle ueberhaupt:
        // groupRoot traegt ALLE Ebenen. Ein NaN hier macht die komplette Szene
        // unsichtbar (Jans Fehlerbild 04.08.2026: "nur blauer Hintergrund und
        // die HTML-Boxen"), waehrend die separat positionierten HTML-Overlays
        // stehenbleiben — ein Bild, das kaum als Rechenfehler zu erkennen ist.
        //
        // Zwei Quellen sind oben behoben (Safe-Area-Felder, Fenster der Laenge
        // null). Eine dritte kommt von aussen: misst der Vorschau-Container 0
        // Pixel — im Hintergrund-Tab, waehrend eines Layout-Wechsels oder bevor
        // der ResizeObserver das erste Mal liefert — teilt die Seitenverhaeltnis-
        // Rechnung durch null und cutOffFillerX/Y werden NaN. Nachgemessen: in
        // einem 0x0-Container stand groupRoot.position auf [NaN, 0, 0].
        //
        // Statt die Szene wegzuwerfen behalten wir die letzte gueltige Position.
        // Sobald der Container wieder misst, rechnet der naechste Frame sauber
        // weiter. Nur der zweite Teil einer korrekten Rechnung wird nie
        // uebernommen — beide Achsen zusammen oder gar nicht.
        if (Number.isFinite(cutOffFillerXOffset) && Number.isFinite(cutOffFillerYOffset)) {
            this.groupRoot.position.set(cutOffFillerXOffset, cutOffFillerYOffset, 0);
            this.groupRootCSS!.position.set(cutOffFillerXOffset, cutOffFillerYOffset, 0);
        }

        // An die OBJEKT-Existenz koppeln, nicht an SHOW_HELPERS (= !isProduction,
        // im geteilten Viewer dauerhaft false): das Editor-Chrome entsteht lazy in
        // syncEditorChrome, sobald ein editorStore injiziert ist. Ohne diese
        // Kopplung blieb uiRoot UNSKALIERT — das 4096er-Grid war dadurch um den
        // Slide-Skalierungsfaktor zu gross und zeigte nur sein Mittelkreuz
        // (Jans Fund #1: „Linie alle 1024px fehlt"). Im Viewer existieren die
        // Objekte nie → Bloecke bleiben No-ops, Verhalten unveraendert.
        // HILFSLINIEN AUF PIXELMITTEN LEGEN (Jans Fund "Safe-Area und Grid
        // flackern beim Scrollen/Animieren").
        //
        // Safe-Area und Grid sind ein Pixel breite Linien. Ungerastert fallen
        // sie genau auf eine PIXELGRENZE und verteilen sich damit auf zwei
        // Pufferzeilen mit je halber Deckung (gemessen: 2x192 statt 1x384). Halb
        // gedeckt heisst halb durchsichtig — und dahinter laeuft bewegter Inhalt
        // (Video, Bild, Animation). Die Linienfarbe mischt sich also in jedem
        // Bild neu mit einem anderen Hintergrund: das ist das Flackern. Der
        // Interface-Zoom (<100 % rechnet die ganze Oberflaeche per
        // transform:scale herunter) verstaerkt es zusaetzlich.
        //
        // Um eine HALBE Pufferpixel-Breite versetzt liegt die Linie mittig IN
        // einer Zeile und deckt sie voll — dann bleibt sie deckend, egal was
        // dahinter passiert. Der Versatz ist ein halbes Geraetepixel und damit
        // unsichtbar.
        const helperPxScale = this.sceneUnitsToPx() * (this.webGLRenderer?.getPixelRatio() || 1);
        // Layer-Einheiten je Pufferpixel (safeSquare rechnet in Layer-Einheiten)
        const layerPerPx = helperPxScale > 0 ? 1 / helperPxScale : 0;
        // Welt-Einheiten je Pufferpixel (uiRoot rechnet in Welt-Einheiten)
        const worldPerPx = layerPerPx * scaleFactor;
        // ganze Pixel — fuer alles, was den Halbpixel-Versatz nicht selbst setzt
        const snapWhole = (value: number, step: number) =>
            step > 0 ? Math.round(value / step) * step : value;
        // Pixelmitte — den halben Versatz traegt die Wurzel der Hilfsebene
        const snapCenter = (value: number, step: number) =>
            step > 0 ? (Math.round(value / step - 0.5) + 0.5) * step : value;

        if (this.safeSquare) {
            this.safeSquare.scale.set(SLIDE_WIDTH / 2, SLIDE_HEIGHT / 2, 2);
            // ganze Pixel, damit der Halbpixel-Versatz von uiRoot erhalten bleibt
            this.safeSquare.position.set(
                snapWhole(-(safeXPosition / 2) * SLIDE_WIDTH, layerPerPx),
                snapWhole(-(safeYPosition / 2) * SLIDE_HEIGHT, layerPerPx),
                1
            );
        }

        if (this.uiRoot) {
            this.uiRoot.scale.copy(this.groupRoot.scale);
            this.uiRoot.position.set(
                snapCenter(this.groupRoot.position.x, worldPerPx),
                snapCenter(this.groupRoot.position.y, worldPerPx),
                this.groupRoot.position.z
            );
        }

        // Jans Original las hier die Editor-Auswahl (editorStore.currentLayer);
        // der Port hatte sie hart genullt (altes TODO DESCR-218) — dadurch war
        // `active`/`isActive` fuer JEDEN Layer false und die Drehpunkt-Kugel
        // (visible = isActive && showPivotPoint) konnte nie erscheinen. Jetzt
        // wie syncGizmo ueber die Editor-Hook-Bruecke; Reader: kein Hook -> null,
        // Verhalten unveraendert. Vergleich per id statt Objekt-Identitaet, weil
        // der Render-Tree aus JSON gebaut ist (nicht Jans MST-Nodes).
        const currentlySelectedLayerId: string | null = this.props.editorStore?.layer?.id ?? null;

        const usedChildren: Record<string, THREE.Object3D[]> = {};
        this._tonGepflegteVideos.clear();

        let numLayers = 0;
        crawl(
            groupTree,
            () => {
                numLayers += 1;
            },
            { getChildren: (l: TODOAny) => l?.layers }
        );

        let layerIndex = 0;
        WebGLAssets.layerIndex = 0;
        this._sceneLichtGewichte = sceneLichtGewichte(groupTree, timer);
        crawl(
            groupTree,
            (layer: LayerRender, context: TODOAny) => {
                // Fehlerfest pro Layer (2026-08-04): wirft die Verarbeitung EINES
                // Layers (z.B. 3D-Objekt/Licht oder ein Text-Layer mit einem
                // problematischen Custom-OTF-Font), darf das NICHT die ganze
                // Layer-Schleife killen — sonst rendert die komplette Slide nicht
                // mehr (Benedikts Fund: Mega-Demo mit 3D + "Matcha Mint"-OTF zeigte
                // kein Bild). Fehlerhaften Layer ueberspringen, Rest rendert weiter.
                try {
                    this.updateLayer(layer, timer);
                } catch (err) {
                    logError(new Error(`updateLayer fehlgeschlagen fuer Layer '${(layer as TODOAny)?.id}': ${(err as Error)?.message ?? err}`));
                    return;
                }
                const { parent } = context;
                const layerParent = parent ? this.layerMeshes[parent.id] : this.groupRoot!;
                const parentId = parent ? parent.id : "stage";
                const active = currentlySelectedLayerId !== null && layer.id === currentlySelectedLayerId;
                usedChildren[parentId] = usedChildren[parentId] || [];
                const order = numLayers - layerIndex;

                const newUsedChildren = this.renderLayer(
                    layer,
                    layerParent,
                    timer,
                    backwards,
                    boundingBox.max,
                    active,
                    order,
                    responsiveScaleFactor
                );
                usedChildren[parentId].push(...newUsedChildren);
                layerIndex += 1;
            },
            { getChildren: (l: TODOAny) => l?.layers }
        );

        // TON-KEHRGANG (Jans Cover-Fund 27.08.2026, HUD-Beleg: Video
        // "SPIELT TON" am Cover bei tonVideoId=-): Die Hoerbarkeits-Schicht
        // laeuft nur fuer Videos, die der Render-Baum in diesem Frame
        // behandelt. Ein Video AUSSERHALB (Cover, Nachbar-Slide, Deckkraft 0
        // mit Fruehausstieg) wurde von niemandem mehr stummgeschaltet —
        // die unmute-on-play-Automatik des Wrappers hatte es aber hoerbar
        // gemacht. Regel bleibt Jans "nur stummschalten, nicht pausieren".
        Object.entries(this.videoCache).forEach(([id, eintrag]) => {
            if (this._tonGepflegteVideos.has(id)) return;
            const el = (eintrag as TODOAny)?.videoPlayer as HTMLVideoElement | undefined;
            if (el && !el.muted) {
                el.muted = true;
                (eintrag as TODOAny).tonFadeAus = false;
            }
        });

        // Point-/Spot-Lichter ratio-unabhaengig halten (Jans Original, 1:1):
        // Die ganze Szene (inkl. Lichter) wird uniform mit `scaleFactor`
        // skaliert, aber Distanz-/Decay-Falloff eines Lichts lebt in festen
        // WELT-Einheiten — dieselbe Distanz wirkt je Seitenverhaeltnis anders
        // (und "8000" ist in der geschrumpften Szene praktisch unendlich →
        // Distanz-Regler wirkungslos, Jans Fund). Distanz mit k/k_ref und
        // Intensitaet mit (k/k_ref)^decay nachziehen hebt die Szenen-Skalierung
        // exakt auf (inkl. 1/d^decay-Term). Referenz-Framing 16:9. Directional
        // hat keinen Falloff und bleibt unberuehrt. Laeuft NACH dem Layer-Crawl,
        // damit die dort gestashten animierten Basen im SELBEN Frame greifen.
        {
            const LIGHT_REF_ASPECT = 16 / 9;
            const lightRefScale = (visibleHeight / SLIDE_HEIGHT) * LIGHT_REF_ASPECT;
            const lightScaleNorm = lightRefScale > 0 ? scaleFactor / lightRefScale : 1;
            Object.keys(this.lightState).forEach((lightLayerId) => {
                const light = this.lightState[lightLayerId] as TODOAny;
                const lc = this.layerContentCache[lightLayerId] as TODOAny;
                if (!light || !lc) return;
                // globale "Schatten-Staerke" (3D-WELT): three-Builtin, live &
                // ohne Recompile; Ambient/Hemisphere haben kein .shadow
                if (light.shadow) {
                    light.shadow.intensity = this.shadowStrength;
                }
                // Richtungslicht hat keinen Falloff, aber seine ORTHO-Schatten-
                // kamera lebt in Welt-Einheiten und muss mit der Szene schrumpfen
                // (sonst gerasterte Schattenraender, s. Kommentar an der Funktion).
                if (light.isDirectionalLight) {
                    scaleDirectionalShadowFrustum(light, scaleFactor);
                }
                if (light.isPointLight || light.isSpotLight) {
                    // animierte Basen (im per-Frame-Licht-Pass gestasht) haben
                    // Vorrang, sonst die statischen content-Werte
                    const decay =
                        light.userData?.animBaseDecay ?? (typeof lc.decay === "number" ? lc.decay : 1);
                    const baseDistance = light.userData?.animBaseDistance ?? lc.distance ?? 0;
                    light.distance = baseDistance * lightScaleNorm;
                    const baseIntensity = light.userData?.animBaseIntensity ?? lc.intensity ?? 1;
                    light.intensity =
                        baseIntensity * Math.pow(lightScaleNorm, decay) * (light.userData?.blendFaktor ?? 1);
                }
            });
        }

        const usedCSSChildren: Record<string, CSS3DObject[]> = {};
        // Welche Layer existieren in diesem Bild noch? Gebraucht fuer das
        // Aufraeumen der DOM-Overlays unten.
        const liveLayerIds = new Set<string>();
        layerIndex = 0;
        crawl(
            groupTree,
            (layer: LayerRender, context: TODOAny) => {
                const { parent } = context;
                const layerParent = parent ? this.layerStateCSS3d[parent.id] : this.groupRootCSS!;
                const parentId = parent ? parent.id : "stage";
                const active = currentlySelectedLayerId !== null && layer.id === currentlySelectedLayerId;
                usedCSSChildren[parentId] = usedCSSChildren[parentId] || [];
                liveLayerIds.add(layer.id);
                const order = numLayers - layerIndex;

                const newUsedChildren = this.renderLayerHTML(
                    layer,
                    layerParent,
                    timer,
                    backwards,
                    boundingBox.max,
                    active,
                    order,
                    responsiveScaleFactor
                );
                usedCSSChildren[parentId].push(...newUsedChildren);
                layerIndex += 1;
            },
            { getChildren: (l: TODOAny) => l?.layers }
        );

        // Verwaiste DOM-Overlays entfernen (Infografik + asHtml-SVG). Diese
        // Elemente haengen NICHT am WebGL-Baum, den der Cleanup unten aufraeumt
        // — der geteilte Renderer verwaltet sie manuell. Ohne diesen Schritt
        // blieb eine geloeschte Infografik als Leiche im Bild stehen und
        // stapelte sich bei mehreren Loeschungen (Jans Fund).
        for (const id of Object.keys(this.svgOverlays)) {
            if (liveLayerIds.has(id)) continue;
            const orphan = this.svgOverlays[id];
            orphan?.parentElement?.removeChild(orphan);
            delete this.svgOverlays[id];
            delete this.infographicSig[id];
        }

        // Verwaiste LICHT-Helfer entfernen (gleiche Leichen-Klasse): die
        // Drahtgitter-Gizmos (PointLight-/SpotLight-/DirectionalLightHelper)
        // haengen in der Gizmo-/Hauptszene, NICHT im Layer-Baum — der
        // usedChildren-Cleanup sieht sie nie. Aufgeraeumt wurden sie bisher nur
        // in updateLight, und das laeuft nur fuer Layer, die es noch GIBT: ein
        // geloeschter Licht-Layer liess sein Gizmo fuer immer stehen (Jans
        // weisses Quadrat, das auf keinen Schalter mehr reagierte).
        for (const id of Object.keys(this.lightHelpers)) {
            if (liveLayerIds.has(id)) continue;
            const orphan = this.lightHelpers[id];
            if (orphan) {
                this.webGlScene?.remove(orphan);
                this.gizmoScene?.remove(orphan);
            }
            const orphanShadow = this.lightShadowHelpers[id];
            if (orphanShadow) this.webGlScene?.remove(orphanShadow);
            delete this.lightHelpers[id];
            delete this.lightShadowHelpers[id];
        }
        // Auch die Zielpunkt-Marker und Licht-Zustaende der geloeschten Layer:
        // Marker raeumt zwar der usedChildren-Sweep aus der Gruppe, aber der
        // Eintrag bliebe stehen und wuerde beim naechsten gleichnamigen Layer
        // wiederverwendet; lightState haelt sonst tote three-Instanzen im
        // lightScaleNorm-Pass am Leben.
        for (const id of Object.keys(this.lightTargetMarkers)) {
            if (liveLayerIds.has(id)) continue;
            this.lightTargetMarkers[id]?.parent?.remove(this.lightTargetMarkers[id]);
            delete this.lightTargetMarkers[id];
        }
        for (const id of Object.keys(this.lightState)) {
            if (liveLayerIds.has(id)) continue;
            delete this.lightState[id];
            delete this.lightSigCache[id];
        }

        // after rendering and setting the meshes
        // we check if we need to rebuild the tree
        // this only needs to be done when editing
        let invalidateTree = false;
        if (!this.props.isProduction) {
            crawl(
                groupTree,
                (layer: LayerRender, context: TODOAny) => {
                    const layerMesh = this.layerMeshes[layer.id];

                    // deleting is not as clean when the layer is not discarded, so we have to clean up somewhere
                    if (LayerType.svg === layer.type) {
                        if (layerMesh && layer.content.asHtml) {
                            delete this.layerMeshes[layer.id];
                            delete this.layerState3d[layer.id];
                            invalidateTree = true;
                        }
                    }
                    if (layer.type === LayerType.group) {
                        const children = usedChildren[layer.id];
                        if (!children) {
                            return;
                        }

                        // Delete unused groups from parent
                        // We don't need to do this in production as the layers won't change
                        for (const child of layerMesh.children) {
                            // when we currently change the scene tree we start over and completely create the scene
                            if (!children.find((c: TODOAny) => c.uuid === child.uuid)) {
                                invalidateTree = true;
                                context.break();
                                break;
                            }
                        }
                    }
                },
                { getChildren: (l: TODOAny) => l?.layers }
            );

            if (invalidateTree) {
                this.invalidateTree();
            }

            let invalidateCSSTree = false;
            crawl(
                groupTree,
                (layer: LayerRender, context: TODOAny) => {
                    const layerMesh = this.layerStateCSS3d[layer.id];
                    // deleting is not as clean when the layer is not discarded, so we have to clean up somewhere
                    if (LayerType.svg === layer.type) {
                        if (layerMesh && !layer.content.asHtml) {
                            invalidateCSSTree = true;
                            context.break();
                            return;
                        }
                    }
                    if (layer.type === LayerType.group) {
                        const children = usedCSSChildren[layer.id];
                        if (!children) {
                            return;
                        }

                        // Delete unused groups from parent
                        // We don't need to do this in production as the layers won't change
                        for (const child of layerMesh.children) {
                            // when we currently change the scene tree we start over and completely create the scene
                            if (!children.find((c: TODOAny) => c.uuid === child.uuid)) {
                                invalidateCSSTree = true;
                                context.break();
                                return;
                            }
                        }
                    }
                },
                { getChildren: (l: TODOAny) => l?.layers }
            );

            if (invalidateCSSTree) {
                this.invalidateCSSTree();
            }

            if (invalidateTree || invalidateCSSTree) {
                this.animate(timer);
                return;
            }
        }

        // Delete-Cleanup für den Pro-Editor: der obige Cleanup hängt an
        // `!isProduction` — der eingebettete Shared-Renderer läuft im Editor aber mit
        // isProduction=true (Reader-Config; Editor-Features laufen über editorMode/
        // editorStore). Ohne diesen Block bliebe eine gelöschte Ebene als verwaistes
        // Mesh sichtbar (Delete ohne Wirkung im Preview). Wir vergleichen alle
        // registrierten layerMeshes gegen die IDs im aktuellen groupTree; fehlt eine
        // → stale Referenz entfernen (sonst feuert die Erkennung jeden Frame erneut →
        // Endlos-Rebuild) + Baum neu bauen (entfernt das Mesh aus dem groupRoot).
        // Deckt Top-Level- UND Gruppen-Deletions ab. Nur im Editor aktiv.
        if (WebGLAssets.editorMode) {
            const presentLayerIds = new Set<string>();
            crawl(groupTree, (l: LayerRender) => presentLayerIds.add(l.id), {
                getChildren: (l: TODOAny) => l?.layers,
            });
            let deletedAny = false;
            for (const id of Object.keys(this.layerMeshes)) {
                if (!presentLayerIds.has(id)) {
                    delete this.layerMeshes[id];
                    if (this.layerState3d) delete this.layerState3d[id];
                    // WICHTIG: auch den Content-Cache leeren. renderLayer erzeugt das
                    // Bild-Mesh nur bei contentChange (cachedContent.material-Vergleich).
                    // Bleibt der Cache stehen, sieht updateLayer beim Wieder-Einfügen
                    // derselben ID (Undo) „keine Änderung" und baut das Mesh nie neu →
                    // die Ebene käme nach Undo nicht zurück.
                    delete this.layerContentCache[id];
                    deletedAny = true;
                }
            }
            if (deletedAny) {
                this.invalidateTree();
                this.animate(timer);
                return;
            }
        }

        rendererStore.collectStats();

        // Port "Mirrors" (Teil A): reflektierende Objekte pro Frame in ihre
        // CubeCamera rendern, bevor das Hauptbild gezeichnet wird. No-op ohne
        // gesetztes realtimeReflections-Flag.
        updateRealtimeReflections(this as unknown as ReflectionHost);
        // Port "Mirrors" (Teil B): planare Reflector-Spiegel an-/abbauen + live
        // tunen. No-op ohne gesetztes planarMirror-Flag.
        // Spiegel-Auslauf: laeuft in die Hintergrundfarbe der Szene aus; Massstab =
        // sichtbare Bildhoehe auf Buehnentiefe (Kamera schaut aus z auf z = 0).
        {
            const grund = resolveSzenenGrundfarbe(this.props.presentation?.presentationOptions);
            const lin = new THREE.Color().setRGB(grund.r / 255, grund.g / 255, grund.b / 255, THREE.SRGBColorSpace);
            this.spiegelAuslaufZiel = { r: lin.r, g: lin.g, b: lin.b };
            if (this.camera) {
                const abstand = Math.abs(this.camera.position.z) || 4000;
                this.spiegelBildHoehe = 2 * abstand * Math.tan((this.camera.fov * Math.PI) / 360);
            }
        }
        this.spiegelBildNr++;
        updateMirrors(this as unknown as MirrorHost);

        // VORSCHAUBILD ohne Editor-Hilfen (Jans Fund 22.09.2026: die Safe Area
        // war im Kachelbild). captureRendererFrame setzt stillCapture fuer
        // genau diese Aufnahme-Durchlaeufe; der Anfasser liegt ohnehin in
        // einer eigenen Szene/Leinwand.
        const hilfenAus = WebGLAssets.stillCapture
            ? this.spiegelEditorZeichen().filter((z) => z.visible)
            : [];
        for (const z of hilfenAus) z.visible = false;
        try {
            this.webGLRenderer.render(webGlScene, this.camera);
        } finally {
            for (const z of hilfenAus) z.visible = true;
        }
        //this.css3DRenderer.render(css3DScene!, this.camera);

        // Cutover Schritt 2b: Gizmo-Overlay zuletzt, im eigenen Canvas ueber dem
        // Content. No-op ohne editorStore (Reader).
        this.renderGizmoPass();
    }

    start = () => {
        this.props.renderer.resetLayers();
        const launchPreso = () => {
            this.init();
            this.animationTimeOut = setTimeout(() => {
                this.props.start(this.animate);
            }, 0);

            const layerIds: string[] = [];
            crawl(
                this.props.presentation.groupTree,
                (layer: LayerRender) => {
                    if (
                        ([
                            LayerType.image,
                            LayerType.text,
                            LayerType.object3d,
                            LayerType.video,
                            LayerType.lottie,
                            LayerType.light,
                            LayerType.svg,
                            LayerType.map,
                            LayerType.infographic,
                            // Port 2: muss hier stehen, sonst registriert
                            // setLayers den Layer nicht und `loadedLayer`
                            // weist ihn spaeter ab ("unknown layer as loaded").
                            LayerType.effect,
                        ] as string[]).includes(layer.type)
                    ) {
                        layerIds.push(layer.id);
                    } else if (
                        !([
                            LayerType.group,
                            LayerType.audio,
                            // object3dChild hat KEINEN eigenen Ladeschritt: das
                            // Modell laedt der Eltern-object3d-Layer (objLoaded
                            // haengt die Kinder nur als `${parent}-${index}` in
                            // den objectCache), und NIEMAND ruft je
                            // loadedLayer(kindId). Registriert man die Kinder
                            // trotzdem, wartet der Boot stur auf den
                            // 10-s-Watchdog (Jans Top-5-Fund 31.08.2026:
                            // 8 Buchstaben-Kinder = START erst nach 10,4 s).
                            LayerType.object3dChild,
                        ] as string[]).includes(layer.type)
                    ) {
                        // audio: bekannt, aber ohne Lade-Schritt — spielt rein
                        // zeitgesteuert (AudioLayers) und zaehlt nicht in die
                        // Ladeliste. group: reiner Container.
                        logError(new Error(`Layer with unknown type: ${layer.type}`));
                    }
                },
                { getChildren: (l: TODOAny) => l?.layers }
            );
            this.props.renderer.setLayers(layerIds);
            this.alleEbenenRegistriert = true;
            WebGLAssets.allLayersSet = true;
        };

        const startPresentation = () => {
            launchPreso();
        };

        if (!this.props.isProduction) {
            startPresentation();
        } else {
            this.props.renderer.setStartFunction(startPresentation);
        }
    };

    stop = () => {
        if (this.animationTimeOut) {
            clearTimeout(this.animationTimeOut);
        }
        if (this.interval) {
            clearInterval(this.interval);
        }
        // Remove document-level event listener to prevent leaks across re-inits
        if (this._visibilityHandler) {
            document.removeEventListener("visibilitychange", this._visibilityHandler);
        }
        if (this._gizmoZiehEnde && typeof window !== "undefined") {
            window.removeEventListener("pointerup", this._gizmoZiehEnde);
            window.removeEventListener("pointercancel", this._gizmoZiehEnde);
            window.removeEventListener("blur", this._gizmoZiehEnde);
            this._gizmoZiehEnde = undefined;
        }
        if (this._pagehideHandler) {
            window.removeEventListener("pagehide", this._pagehideHandler);
            this._pagehideHandler = undefined;
            this._visibilityHandler = undefined;
        }
        if (this._nachzueglerPrimer) {
            document.removeEventListener("pointerdown", this._nachzueglerPrimer, {
                capture: true,
            });
            this._nachzueglerPrimer = undefined;
        }
        if (this._tonDebugTimer) {
            clearInterval(this._tonDebugTimer);
            this._tonDebugTimer = undefined;
        }
        if (this._tonDebugHud) {
            this._tonDebugHud.remove();
            this._tonDebugHud = undefined;
        }
        if (this._startDebugTimer) {
            clearInterval(this._startDebugTimer);
            this._startDebugTimer = undefined;
        }
        if (this._startDebugHud) {
            this._startDebugHud.remove();
            this._startDebugHud = undefined;
        }
        this.props.stop();
        this.videoVorlader.dispose();
        this.startVideoBlobUrls.forEach((u) => {
            try {
                URL.revokeObjectURL(u);
            } catch {
                /* ignore */
            }
        });
        this.startVideoBlobUrls = [];
        for (const video of Object.values(this.videoCache)) {
            video.pause();
            video.delete();
        }
        this.videoCache = {};
        this.audioLayers.dispose();
        for (const lottiePlayer of Object.values(this.lottieCache)) {
            lottiePlayer.pause();
            lottiePlayer.delete();
        }
        this.lottieCache = {};
        try {
            if (this.webGLRenderer) {
                this.webGLRenderer.forceContextLoss();
            }
        } catch (error) {
            logError(error as Error);
        }
    };

    dispose = () => {
        // Dispose OrbitControls (removes its internal event listeners)
        this.controls?.dispose();

        // Dispose CSS3DRenderer DOM element
        this.css3DRenderer?.domElement?.remove();

        // Remove SVG overlay DOM elements
        for (const overlay of Object.values(this.svgOverlays)) {
            overlay.remove();
        }
        this.svgOverlays = {};
        this.svgHtmlFehlgeschlagen = {};
        this.kartenAbbauen();

        // Safety cleanup: remove global THREE reference if it exists
        delete (window as any).THREE;

        // Traverse the entire scene graph and dispose all GPU resources:
        // geometries, materials (including all texture maps), and troika text meshes.
        // Without this, each editor session leaks textures / geometries on the GPU.
        if (this.webGlScene) {
            this.webGlScene.traverse((object: THREE.Object3D) => {
                // Dispose troika-three-text meshes (they allocate SDF textures)
                if ((object as any).dispose && (object as any).text !== undefined) {
                    (object as any).dispose();
                }

                if (object instanceof THREE.Mesh) {
                    object.geometry?.dispose();

                    const materials = Array.isArray(object.material)
                        ? object.material
                        : [object.material];

                    for (const material of materials) {
                        if (!material) continue;
                        // Dispose all texture slots
                        const mat = material as any;
                        if (mat.map) mat.map.dispose();
                        if (mat.lightMap) mat.lightMap.dispose();
                        if (mat.bumpMap) mat.bumpMap.dispose();
                        if (mat.normalMap) mat.normalMap.dispose();
                        if (mat.specularMap) mat.specularMap.dispose();
                        if (mat.envMap) mat.envMap.dispose();
                        if (mat.alphaMap) mat.alphaMap.dispose();
                        if (mat.aoMap) mat.aoMap.dispose();
                        if (mat.displacementMap) mat.displacementMap.dispose();
                        if (mat.emissiveMap) mat.emissiveMap.dispose();
                        if (mat.metalnessMap) mat.metalnessMap.dispose();
                        if (mat.roughnessMap) mat.roughnessMap.dispose();
                        material.dispose();
                    }
                }
            });
            this.webGlScene.clear();
        }

        // Clear object caches so GC can collect them
        this.groups = {};
        this.groupsCSS = {};
        this.layerMeshes = {};
        this.slideLayerMeshes = {};
        this.layerContentCache = {};
        this.objectCache = {};
        this.layer3DCache = {};
        this.layerState3d = {};
        this.layerStateCSS3d = {};
        this.lightState = {};
        this.lightHelpers = {};
        this.lightShadowHelpers = {};
        this.pivots = {};
        this.pivotsCSS = {};
        this.pivotsCircles = {};

        if (this.webGLRenderer) {
            this.webGLRenderer.dispose(); // frees GPU memory
            try {
                const gl = this.webGLRenderer.getContext();
                const extension = gl.getExtension("WEBGL_lose_context");
                if (extension) {
                    // force WebGL context release — critical for Safari
                    extension.loseContext();
                }
            } catch {
                // Context may already be lost
            }
        }
    };
}
